(function (global) {
  'use strict';

  /* =========================================================================
     RELATED ASSETS -- what this lib loads alongside and talks to.
     (All loaded by snippets/configurator-sauna.liquid, before boot().)

     configurator-ui.js  ->  global.ConfiguratorUI  (+ ConfiguratorStage, MapManager)
         The chrome and the viewport. Supplies the wizard (wizard()), the
         footprint drag handles (handles()), the pitch slider (slider()), the
         undo button (undoButton()) and dim formatting (fmtDim). It also carries
         ConfiguratorStage -- the THREE.js scene/camera, aliased here as
         SaunaEngine -- and MapManager, the Mapbox address/scenery map behind the
         PLACE step. Snapshot line: "ui | stage= | map=".

     configurator-floorplan.js  ->  GONE. It was the editor lifted out of the
         master house -- _Floorplan (the JPG underlay) and _Partitions (interior
         walls, doors, the room decomposition). This product never wired it: the
         partition editor it actually runs is this lib's own SaunaPartitions,
         and the note here said INERT for as long as the tag existed. The script
         tag is coming off the template, so the file is not expected any more
         and its absence is not a fault. Snapshot line: "floorplan | wired= |
         INERT (stage 0 ...)". Because floorplan.js is meant to be reused across
         projects, reusable partition changes (e.g. a cut-handle on/off toggle)
         belong THERE -- but they only reach the sauna once it is switched over
         to it. Until then a sauna-only change lives in SaunaPartitions.

     PDF plan  ->  NOT an external asset: it lives IN this lib (the _pdf* family,
         ~ln 6930-7320: _pdfDrawPlan / _pdfDrawFloor / _pdfDrawPassages /
         _pdfDrawTerraces / _pdfDrawPergolas / _pdfDims / _pdfLeader /
         _pdfSvgToPng). It is a <canvas> MIRROR of the floor plan for the
         downloadable documentation (the cf-btn-pdf button). Consequence worth
         remembering: the plan is drawn TWICE -- the live 2D overlay AND the PDF
         -- so any change to what the plan shows or hides has to be made in both
         to stay consistent.

     Also loaded by the liquid, not plan/UI related: configurator-share.js
     (share sheet), configurator-budget-bridge.js (budget totals),
     configurator-asset-drawer.js (the openings / doors drawer).
     ========================================================================= */

  /* shelter-v119 (edit-mode UI thread, as core-v706): a change of the
     building's size framed again from the visitor's own angle. */
  var VERSION = 'shelter-v120-ref';   /* v120 (bench thread, Marek 2026-10-22 16:51: chtelo by to jeste udelat referencni projekty sauna, shelters. Aspon 20 od kazdeho): 24 LEVA reference designs of the shelter (REF_SET -- carports, an RV and a boat cover, pavilions, a hot tub cover, firewood / tractor / loafing / livestock / hay shelters, a bus stop ...; every one its size, post height, roof, pitch, covering, foundation, facade, infill and filled sides) with US towns of their own and the bench's pool; the snippet's REF (bref as the bench's) builds each, waits for the budget, saves it to leva_configs and publishes budget_details <admin>__shelters__ref-<id>; writes held while it runs; on top of shelter-v119-fitall */
  /* previous: 'shelter-v119-fitall'; */

  var IN = 0.0254, FT = 0.3048, M_TO_FT = 3.280839895;

  /* THE GREAT STRIP-DOWN. The model is reduced to the bare timber frame --
     foundations, floor structure, wall frames, roof frame, openings and
     partitions stay; every SKIN LAYER (facade, wall sheathing, roof covering,
     railing) is hidden. From here the layers return ONE AT A TIME, each
     modelled on request, each checked visually, each verified against its
     BoQ line -- so that when the flag falls, every layer on the model is a
     layer we can vouch for. The assemblies panel is untouched. */
  var FRAME_ONLY = true;
  var SHED_BARE = true;   /* shed = non-insulated: no interior lining, no wool, no ceiling -- bare frame inside */
  var SHELTER_NO_FLOOR = true;   /* shelter = no floor structure at all -- posts stand on their own point footings, nothing else */
  var SHELTER_SCREEN_WALLS = true;   /* shelter filled side = a cladding SCREEN between the posts (rails + nailers + boards), never the insulated wall sandwich */
  var SHELTER_NO_CEILING = true;   /* shelter: no ceiling structure at all -- open to the rafters */
  /* Concrete pad build-up, shared by the geometry and the BoQ. */
  var PAD_W = 0.40, PAD_H = 0.40, PAD_PROUD = 0.05;      /* concrete pad, head proud of grade */
  var PAD_GRAVEL_W = 0.50, PAD_GRAVEL_H = 0.15;          /* macadam bed under the pad */
  var PAD_GEO_W = 0.60;                                  /* geotextile sheet under the macadam */
  var SHELTER_FIXED_ROOF = true;   /* shelter: the roof never overhangs the posts -- fixed 100 mm drip edge, no roof-footprint dragging, no overhang posts */

  function _log(msg) {
    try {
      if (global.Configurator && global.Configurator.log && global.Configurator.log.log) {
        global.Configurator.log.log('[sauna] ' + msg);
      }
    } catch (e) {}
  }
  function _dbg(msg) {
    _log(msg);
    try { if (global.console && global.console.log) global.console.log('SAUNA ' + msg); } catch (e) {}
  }
  function $(id) { return document.getElementById(id); }
  function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }

  var BEARER = { L: 0.178, W: 0.162, H: 0.118 };
  var POST = 0.16;
  var PANEL_INSET = 0.055;
  /* The height stack of a timber building, bottom up, and the end of the
     container's ghost. PANEL_BASE used to be BEARER.H + 0.14 -- the container's
     bottom-rail zone, 118 + 140 mm of steel baked into the wall geometry of a
     WOODEN cabin, which is the collision: a 200 mm joist cannot live in a
     140 mm band, so the floor structure and the walls were fighting over the
     same air. Now the stack is the build order on site:

         bearer 160 (on the foundation heads)
       + rim joist / joists 200
       + OSB deck 15
       = 375: and the walls STAND ON THE DECK. Nothing shares a millimetre
       with anything, because that is what standing on top of each other
       means. */
  /* THE GRID. One structural grammar, two moduli: metric walls step 600 for
     1200/1250 sheets; imperial steps 24 inches on centre for the 4x8 sheet --
     48 inches = exactly two bays, every sheet edge on a stud, and a third
     fewer studs than 16 oc, which is what "simplest possible production"
     costs out to. Everything that steps -- studs, cripples, joists, rafters,
     panel joints, opening snaps -- asks THIS, never a literal. */
  function _GRID() { return STATE.isMetric ? 0.6 : 24 * IN; }
  /* Dimension stepping, tied to the same modulus. Plan dimensions step by
     HALF a bay (12 in / 300), so a wall is always whole bays plus one honest
     remainder and panels come out in whole sheets. */
  function _snapDim(v) { var g = _GRID() / 2; return Math.round(v / g) * g; }
  /* Height has STOPS, not steps: the vertical sheet is 8 ft (or 2.5 m), and
     a clear height held to the sheet means the wall closes without a seam and
     without a rip cut -- a bigger production saving than any plan snap. Two
     stops per unit system: the sheet, and the sheet-and-a-foot. */
  function _snapH(h) {
    /* a shelter's post height is a slider, so it snaps to 5 cm and nothing
       else; the two sauna clears below are a sauna's door-and-header sum */
    if (SHELTER_SCREEN_WALLS) return Math.round(clamp(h, FOOTPRINT.minH, FOOTPRINT.maxH) * 20) / 20;
    var clears = STATE.isMetric ? [2.5, 2.9] : [8 * FT, 9 * FT];
    var best = h, bd = 1e9;
    for (var i = 0; i < clears.length; i++) {
      var H = clamp(FLOOR_TOP + clears[i] + 0.09, FOOTPRINT.minH, FOOTPRINT.maxH);
      var d = Math.abs(H - h);
      if (d < bd) { bd = d; best = H; }
    }
    return best;
  }
  var FLOOR_FRAME = { joist: { w: 0.06, h: 0.20 }, deck: 0.015, spacing: 0.6 };
  var FLOOR_Y = 0.17;   /* legacy interior finish plane; superseded by the deck */

  /* One parametric cabin instead of fixed container types. The footprint is
     what the user drags in STEP 1; every consumer (foundations, partitions,
     openings, BoQ, PDF) still asks for a "type" object, so we just compute it
     on the fly and the rest of the file needs no rework. */
  /* P1: the height budget. t.H is EXTERIOR height, and since the real floor
     went in (v96) it pays for the whole stack: 375 of bearer+joists+deck below,
     90 of top-plate zone above, and only what is left is a room. minH 2.0 was
     from the container era, when the floor was 140 mm of steel: at 2.05 the
     clear height came to 1.59 m -- a crawl space with a stove. The minimum now
     is derived, not guessed: door 2.0 + header 0.16 + plate 0.06 above the
     deck. Windows never enter the plate band by the same arithmetic (see the
     openings clamp). */

  /* One parametric cabin instead of fixed container types. The footprint is
     what the user drags in STEP 1; every consumer (foundations, partitions,
     openings, BoQ, PDF) still asks for a "type" object, so we just compute it
     on the fly and the rest of the file needs no rework. */
  /* DOWN TO A BIN STORE (Marek). The 6 m floor was the two-car carport --
     "two cars enter from the gable end, so the span never drops under 6 m" --
     and it made a small shelter impossible. 1.5 m each way and 1.2 m of post
     lets the same product be a roof over three wheelie bins; the centre post
     still appears above 6 m, that rule did not move. */
  var FOOTPRINT = { minW: 1.5, maxW: 12.0, minL: 1.5, maxL: 12.0, minH: 1.2, maxH: 3.4 };

  /* ---- DRAG HANDLES (drawer) ------------------------------------------
     Reference implementation for every rectangular footprint in LEVA.
     The one rule that matters: hit zones and handle sizes are in SCREEN
     PIXELS, never in metres. A metre-sized grip shrinks to a few pixels when
     the map is zoomed out, which is why dragging has been unusable on a
     tablet. Sprites with sizeAttenuation:false stay a constant size on
     screen for free; hit testing projects the point and compares in px.
     Edge grips are bigger than corner grips on purpose: a finger arcs, so a
     diagonal two-axis drag is the imprecise one and gets used least. ---- */
  var HANDLE = {
    cornerPx: 30,   /* drawn diameter */
    edgePx:   40,
    hitCorner: 26,  /* hit radius, >= 22 keeps the 44px minimum target */
    hitEdge:   32,
    accent:  '#D2A878',
    ring:    '#1c1d1f',
    ghost:   0xD2A878
  };
  var WALL_THK = 0.12;

  function _extW() { return STATE.footprint.w; }

  function _saunaType() {
    var f = STATE.footprint;
    return {
      key: 'sauna',
      L: f.l, W: f.w, H: f.h,
      intL: Math.max(0.2, f.l - 2 * WALL_THK),
      intW: Math.max(0.2, f.w - 2 * WALL_THK),
      i18n: 'nearby_type_sauna', def: 'Sauna'
    };
  }
  var VARIANT_KEYS = ['sauna'];

  var COLORS = [
    { key: 'blue',  hex: 0x1e4e79, ink: '#ffffff', i18n: 'container_c1', def: 'Blue' },
    { key: 'red',   hex: 0x87352a, ink: '#ffffff', i18n: 'container_c2', def: 'Red' },
    { key: 'green', hex: 0x2f5136, ink: '#ffffff', i18n: 'container_c3', def: 'Green' },
    { key: 'grey',  hex: 0x82858a, ink: '#1c1d1f', i18n: 'container_c4', def: 'Grey' }
  ];
  var PRESET_COUNT = COLORS.length;

  var MAX_MODULES = 1;
  var MAX_LEVEL = 2;
  var SNAP_TOL = 0.8;

  var STATE = {
    modules: [{ type: '20', x: 0, z: 0, rot: 0, lvl: 0, color: 1 }],
    selected: 0,
    variant: 1,
    vizStage: 0,
    mode: 1,
    foundation: 'screws',
    openings: [],
    partitions: [],
    doors: [],
    /* Room names are pinned to a POINT, not to an index. A flood fill hands
       back its rooms in scan order, and that order changes the moment a wall
       moves -- so "room 2 is the shower" is a fact with a shelf life of one
       drag. A point inside the room survives the wall moving, and if the room
       is swallowed by its neighbour the point simply lands in the neighbour
       and the name goes with it, which is what a person would expect. */
    roomNames: [],
    /* A garden sauna is born with its whole wall. 'interior' -- the sauna
       built inside a house, no membrane, no cavity, no cladding -- is a real
       variant, but it is the EXCEPTION, and it was the default: so the ASSEMBLY
       step opened on four layers, the person who remembered seven concluded
       three had been deleted, and they were right to -- the drawing showed less
       building than they were buying. A saved project keeps whatever envelope
       it saved; only what a fresh page starts as changes. */
    envelope: 'exterior',
    facadePattern: 'wood1',
    facadeColor: 'larch',
    colorView: 0,
    wallFill: { front: false, rear: false, sideL: false, sideR: false },
    fillType: 'boards',
    slatGap: 0.04,
    showDetail: false,
    climateZone: 0,
    mapLocked: false,
    pergolas: [],
    pergolaVariant: 'wood_slat',
    pergolaOn: false,
    terraces: [],
    terraceMode: false,
    terraceTops: {},
    terraceSurface: 'deck',
    /* THE TERRACE -- a wrap-around deck driven by an overhang footprint, edited
       exactly like the roof: terraceOver is the reach past each wall, and a deck
       is generated on every side where that reach is at least TERR_MIN (1 m).
       terraceOn gates the whole feature. Built bottom-up: foundation (same type
       as the sauna), then joists (continuing the sauna grid), then boards. */
    terraceOn: false,
    terraceOver: { x0: 0, x1: 0, z0: 0, z1: 0 },
    footprint: { w: 7.0, l: 6.0, h: 2.7 },   /* SHELTER default: 7 m span (Marek's 'radeji 7'), 6 m deep */
    roofPitch: 18,
    roofType: 'mono',
    roofOver: { x0: 0.18, x1: 0.18, z0: 0.18, z1: 0.18 },
    view: '3d',
    bodyVisible: false,
    bodyX: null,
    bodyZ: null,
    isMetric: false
  };
  /* THE THREE COLOUR VIEWS -- tiles in the EXTERIOR step, picked by sight.
     Each is a full scheme:
     facade colour + board width + orientation, terrace board colour, roof
     covering colour + optional shingle pattern. facCol keeps a legacy facade
     key so the BoQ finish logic still resolves. */
  var COLOR_VIEWS = [
    { key: 'nordic',   facade: 0xB9855A, boardW: 0.190, pattern: 'wood1', deck: 0xB9855A, roof: 0x33363b, roofPat: 'plain',   facCol: 'larch' },
    { key: 'charcoal', facade: 0x242428, boardW: 0.115, pattern: 'wood1', deck: 0x242428, roof: 0x18181c, roofPat: 'shingle', facCol: 'shou' },
    { key: 'ivory',    facade: 0xE7E0CF, boardW: 0.160, pattern: 'wood2', deck: 0xC98A44, roof: 0x9a6242, roofPat: 'plain',   facCol: 'larch' }
  ];
  /* THE PALETTE THE HOUSE LINE USES -- the same names and the same values,
     copied, so a larch here is the larch on an ADU and a terracotta roof is the
     same terracotta (Marek: krytiny a barvy co uz mame). */
  var PALETTE = {
    anthracite: 0x3A3D40, graphite: 0x5A5D60, terracotta: 0xA5553B, moss: 0x5C6B4E,
    cedar: 0xB98B57, larch: 0xD3B07A, walnut: 0x7A5A3A, charcoal: 0x2B2B2B,
    white: 0xF2F0EA, lightGrey: 0x8E918E, copper: 0xB87333, patina: 0x6E9C8F,
    zinc: 0xA7ABAF
  };
  /* what each slot may be turned to: timber stains for the facade, roof
     colours for the roof -- the ADU's own two rows */
  /* FOUR SLOTS, ONE PER THING YOU CAN SEE, each with the row the house line
     gives that thing (Marek: zvlast cladding, zvlast nosna konstrukce, a
     accessories jako jinde). The cladding is the boards and slats between the
     posts; the timber is the frame that carries the roof; the metal is the
     gutters, the drip edges and the flashings. */
  var SLOT_PAL = {
    facade: ['cedar', 'larch', 'walnut', 'charcoal', 'lightGrey', 'white'],
    timber: ['cedar', 'larch', 'walnut', 'charcoal', 'lightGrey', 'white'],
    roof:   ['anthracite', 'graphite', 'terracotta', 'moss', 'copper', 'patina'],
    metal:  ['anthracite', 'graphite', 'zinc', 'white', 'copper', 'patina']
  };
  /* what each slot is when nobody has chosen */
  var SLOT_DEFAULT = { facade: 0xB9855A, timber: 0x9c7248, roof: 0x33363b, metal: 0x8a8f96 };
  /* THE SCHEMES ARE GONE FROM THE BUYER'S HANDS (Marek: nevim k cemu je).
     One of them clashed with the timber row and none of them said what it
     was for. COLOR_VIEWS[0] stays underneath as the silent source of the
     board width and the facade pattern; every colour is its own slot now,
     null meaning the default for that slot. */
  function _ov() { return STATE.colorOv || (STATE.colorOv = { facade: null, timber: null, roof: null, metal: null }); }
  function _cv() {
    var s = COLOR_VIEWS[0], o = _ov(), out = {}, k;
    for (k in s) if (s.hasOwnProperty(k)) out[k] = s[k];
    ['facade', 'timber', 'roof', 'metal'].forEach(function (slot) {
      out[slot] = (o[slot] && PALETTE[o[slot]] != null) ? PALETTE[o[slot]] : SLOT_DEFAULT[slot];
    });
    return out;
  }
  /* set one slot outright; null puts it back on the scheme */
  function setSlot(slot, key) {
    var o = _ov();
    o[slot] = (key && PALETTE[key] != null) ? key : null;
    rebuild(); refreshUI();
    _log('colour ' + slot + ' -> ' + (o[slot] || 'scheme') + ' (' + _hex6(_cv()[slot]) + ')');
  }

  /* THE COLOURS PANEL -- the ADU's, line for line where it could be: a row per
     slot, a small-caps muted label, a ring of dots, the one in force wearing a
     ring. The rows are this product's: the three schemes first (each dot in
     that scheme's facade colour), then facade and roof. */
  var COLOR_ROWS = [
    { key: 'facade', i18n: 'sauna_ui_cladding',          def: 'Cladding' },
    { key: 'timber', i18n: 'bbq_v2',                     def: 'Timber' },
    { key: 'roof',   i18n: 'roof',                       def: 'Roof' },
    { key: 'metal',  i18n: 'pdf_pool_cat_accessories',   def: 'Accessories' }
  ];
  function _colorsPanel(pane) {
    if (!pane) return;
    pane.innerHTML = '';
    var wrap = document.createElement('div');
    wrap.style.cssText = 'padding:6px 2px 12px;display:flex;flex-direction:column;gap:14px;';
    COLOR_ROWS.forEach(function (row) {
      var block = document.createElement('div');
      var name = document.createElement('div');
      name.textContent = _t(row.i18n, row.def);
      name.style.cssText = 'font:700 10px/1.2 Montserrat,sans-serif;letter-spacing:.08em;' +
        'text-transform:uppercase;color:#9CA3AF;margin:0 0 7px;';
      var dots = document.createElement('div');
      dots.style.cssText = 'display:flex;flex-wrap:wrap;gap:12px;';
      var items = [];   /* { hex, on, title, pick } */
      var cur = _ov()[row.key];
      (SLOT_PAL[row.key] || []).forEach(function (k) {
        items.push({ hex: PALETTE[k], on: (cur === k), title: k,
                     pick: function () { setSlot(row.key, k); } });
      });
      items.forEach(function (it) {
        var b = document.createElement('button');
        b.type = 'button';
        b.title = it.title;
        b.style.cssText = 'width:26px;height:26px;border-radius:50%;cursor:pointer;padding:0;' +
          'background:' + _hex6(it.hex) + ';border:1px solid rgba(0,0,0,0.14);' +
          (it.on ? 'box-shadow:0 0 0 3px #fff, 0 0 0 4px #2C2D2E;' : '');
        b.addEventListener('click', function () { it.pick(); _colorsPanel(pane); });
        dots.appendChild(b);
      });
      block.appendChild(name);
      block.appendChild(dots);
      wrap.appendChild(block);
    });
    pane.appendChild(wrap);
  }

  /* THE ROOF COVERING -- the house line's three, with their codes and the
     pitch each one tolerates. Ported, not invented: the same table the ADU and
     the shed bill from. Below its minimum pitch a covering is not offered,
     and one saved at a pitch that has since dropped falls back to the best
     covering that pitch allows (Marek: krytiny co uz mame). */
  var COVERINGS = [
    { key: 'shingle', i18n: 'mc_cov_shingle', def: 'Asphalt shingle',   code: 'ROR00001', minPitch: 9.5 },
    { key: 'trapez',  i18n: 'mc_cov_trapez',  def: 'Trapezoidal sheet', code: 'ROR12004', minPitch: 5 },
    { key: 'seam',    i18n: 'mc_cov_seam',    def: 'Standing seam',     code: 'ROR12003', minPitch: 3 }
  ];
  function _covPitch() { return clamp(STATE.roofPitch || 0, 0, 45); }
  function _covAllowed(key) {
    var i, pit = _covPitch();
    for (i = 0; i < COVERINGS.length; i++) if (COVERINGS[i].key === key) return pit + 1e-6 >= COVERINGS[i].minPitch;
    return false;
  }
  function _covDef() {
    var i, want = STATE.covering || 'trapez', best = null, low = COVERINGS[0];
    for (i = 0; i < COVERINGS.length; i++) if (COVERINGS[i].key === want && _covAllowed(want)) return COVERINGS[i];
    for (i = 0; i < COVERINGS.length; i++) {
      var c = COVERINGS[i];
      if (c.minPitch < low.minPitch) low = c;
      if (_covAllowed(c.key) && (!best || c.minPitch > best.minPitch)) best = c;
    }
    return best || low;
  }
  function nextCovering() {
    var i, cur = _covDef().key, from = 0;
    for (i = 0; i < COVERINGS.length; i++) if (COVERINGS[i].key === cur) from = i;
    for (i = 1; i <= COVERINGS.length; i++) {
      var c = COVERINGS[(from + i) % COVERINGS.length];
      if (_covAllowed(c.key)) { STATE.covering = c.key; break; }
    }
    rebuild(); savePref(); refreshUI();
    _log('covering -> ' + _covDef().key + ' (' + _covDef().code + ') at ' + _covPitch().toFixed(1) + ' deg');
  }
  var DEFAULTS = { variant: 1, preset: 1 };

  function _type() { return _typeOf(STATE.modules[Math.min(STATE.selected, STATE.modules.length - 1)]); }
  function _typeOf(mod) { return _saunaType(); }
  function _colorOf(mod) { return COLORS[clamp((mod.color | 0) - 1, 0, COLORS.length - 1)]; }
  function _quarter(mod) { return Math.round((mod.rot || 0) / (Math.PI / 2)) & 3; }

  function _isOrtho(mod) {
    var r = mod.rot || 0;
    var q = Math.round(r / (Math.PI / 2)) * (Math.PI / 2);
    return Math.abs(r - q) < 0.02;
  }
  function _fpW(mod) {
    var t = _typeOf(mod);
    if (_isOrtho(mod)) return (_quarter(mod) & 1) ? t.L : _extW();
    var r = mod.rot || 0, c = Math.abs(Math.cos(r)), s = Math.abs(Math.sin(r));
    return _extW() * c + t.L * s;
  }
  function _fpL(mod) {
    var t = _typeOf(mod);
    if (_isOrtho(mod)) return (_quarter(mod) & 1) ? _extW() : t.L;
    var r = mod.rot || 0, c = Math.abs(Math.cos(r)), s = Math.abs(Math.sin(r));
    return t.L * c + _extW() * s;
  }

  var OVERHANG_POST = 1.5;   /* an overhang beyond this (m) earns support posts */
  function _foundLift() { return (STATE.foundation === 'piers') ? 0.45 : (STATE.foundation === 'pads') ? 0.30 : 0.35; }
  /* One cabin, always on the ground. The container version walked a stack to
     find what a module rested on; a garden building has nothing to stack.
     Signature kept so the ~14 call sites need no edit. */
  function _yFor() {
    return _foundLift();
  }

  function totalFloorArea() {
    var a = 0;
    for (var i = 0; i < STATE.modules.length; i++) {
      var t = _typeOf(STATE.modules[i]);
      a += t.intL * t.intW;
    }
    return a;
  }
  function _locale() { return (global.SAUNA_BOOT && global.SAUNA_BOOT.locale) || 'en'; }
  function _locNum(v, dec) {
    try { return v.toLocaleString(_locale(), { minimumFractionDigits: dec, maximumFractionDigits: dec }); }
    catch (e) { return v.toFixed(dec); }
  }
  function fmtArea(m2) {
    return STATE.isMetric ? (_locNum(m2, 1) + ' m\u00B2') : (_locNum(m2 * 10.7639, 0) + ' ft\u00B2');
  }
  function _deent(str) {
    if (typeof str !== 'string' || str.indexOf('&') === -1) return str;
    try {
      var d = document.createElement('textarea');
      d.innerHTML = str;
      return d.value;
    } catch (e) { return str; }
  }

  function statVal(m) {
    return STATE.isMetric ? m.toFixed(2) : (m * M_TO_FT).toFixed(1);
  }
  /* US architectural dimension: feet, hyphen, whole inches, reduced fraction to
     the nearest 1/16 -- 5.00 m reads 16'-4 7/8", never 196.9". Zero inches still
     print (16'-0") because a bare 16' reads as an unfinished dimension. Under a
     foot the feet part is dropped. Metric side is whole millimetres. */

  function fmtLen(m) {
    return STATE.isMetric ? (m.toFixed(2) + ' m') : ((m * M_TO_FT).toFixed(1) + ' ft');
  }

  /* The scene/camera/controls/grid live in the shared map-manager asset now
     (ConfiguratorStage). SaunaEngine stays as the local handle so the rest
     of this file keeps calling eng.scene / eng.group / eng.setView unchanged. */
  global.SaunaEngine = global.ConfiguratorStage || global.SaunaEngine || null;

  function _initEngine() {
    var S = global.ConfiguratorStage;
    if (!S) { _log('engine init failed: map-manager.js asset not loaded'); return false; }
    global.SaunaEngine = S;
    return S.init({
      rootId: 'cf-root',
      canvasId: 'cf-canvas',
      fov: 40, near: 0.1, far: 200,
      cameraPos: [7, 4, 8],
      target: [0, 1.2, 0],
      minDistance: 1.5, maxDistance: 40,
      maxPolarAngle: Math.PI * 0.85,
      enablePan: false, enableDamping: true, dampingFactor: 0.08,
      lights: true,
      /* The stage's own onResize does "if (view === '2d') setView('2d')" -- with
         no focus. That recomputes the ortho span from the bounding box of the
         whole group and re-aims at the origin, which throws away everything
         _fitCamera asked for. Rotate the tablet, switch windows and come back,
         open the keyboard: the plan is framed by something that has never heard
         of the plan. So the sauna puts its own framing back afterwards. */
      onResize: function () {
        try {
          if (STATE.view === '3d') _fit3D();
        } catch (eR) {}
      },
      log: _log
    });
  }

  /* K1 of the structural model: the foundation layout, as data.

     What stood here: 3 bearers whatever the building (6 above ten metres),
     supports only at bearer ENDS -- so a 2.4 m sauna and a 5 x 8 m one both
     got 6 points, and switching screws/piers/pads changed which catalog item
     those 6 points were, never how many. Dimensioning that does not respond
     to dimensions is a picture of engineering.

     Now, the base configurator's own rule set, applied here: the GRID is
     load-level independent -- what changes with foundation type is the
     support spacing along a bearer, what changes with size is everything.
     Bearers run across the width (joists will sit on them, across the
     length); bearer-to-bearer spacing is the joist span. Both spacings are
     derived so they land inside their range, exactly like _spacingRangeFor
     in configurator-base-lib.

     Steel RHS is a container-house answer to a container-house problem
     (point loads from corner castings). A timber cabin bears along its whole
     sill, so the bearer is timber: KVH 100 x 160 on edge. Steel remains the
     container's option; it was never this building's. */
  /* Alive only in the EXTERIOR step. This module was gated on mode 6, and no
     step sets 6 since the wizard was consolidated -- the whole drag and resize
     system for pergolas was built, bound and DEAD, waiting for a number nobody
     would ever dial. Mode is the wrong key anyway: EXTERIOR shares mode 5 with
     the finished view, where a ghost with grips must not appear. The wizard's
     own step name is the fact being asked about, so ask it. */
  /* K2: the floor frame, as data. Joists run along the length, ON the
     bearers, spanning bearer to bearer, spaced across the width; a rim joist
     rings the perimeter; OSB decks the lot. One authority: the drawing takes
     positions from it, the BoQ takes PIECES from it -- each joist with its own
     length, which is the first real cut list this project has produced. */

  /* K3: the wall frames, as data. Per wall: a bottom plate on the deck, a
     top plate under the roof, studs at 600 between them -- and every opening
     framed the way a framer frames it: a king stud each side, jack studs
     carrying a doubled header. Pieces with lengths, same contract as the
     floor: the BoQ reads cuts, the drawing reads positions, one authority. */
  global.SaunaWalls = {
    STUD: { w: 0.06, d: 0.16 },
    PANEL_MAX: 6.0,
    /* Split one wall into manufacturable panels: equal parts under PANEL_MAX,
       then each joint walks to the nearest stud station that is clear of
       every opening by 300 mm -- a joint in a window is a panel you cannot
       build. The joint itself is the correct detail for free: each panel ends
       with its own end stud, so a joint reads as the doubled stud a framer
       would put there anyway. */
    _panelize: function (len, spans) {
      var n = Math.max(1, Math.ceil(len / this.PANEL_MAX));
      var cuts = [];
      for (var i = 1; i < n; i++) {
        var c = -len / 2 + len * (i / n);
        var G0 = _GRID();
        c = Math.round(c / G0) * G0;
        var ok = function (x) {
          for (var sp = 0; sp < spans.length; sp++)
            if (x > spans[sp][0] - 0.3 && x < spans[sp][1] + 0.3) return false;
          return true;
        };
        if (!ok(c)) {
          for (var step = G0; step < len; step += G0) {
            if (ok(c - step)) { c = c - step; break; }
            if (ok(c + step)) { c = c + step; break; }
          }
        }
        cuts.push(c);
      }
      cuts.sort(function (a, b) { return a - b; });
      var edges = [-len / 2].concat(cuts).concat([len / 2]);
      var panels = [];
      for (var e = 0; e < edges.length - 1; e++) panels.push([edges[e], edges[e + 1]]);
      return panels;
    },
    layout: function () {
      var out = { plates: [], studs: [], headers: [], sills: [], caps: [], panels: [], modules: [] };
      for (var mi = 0; mi < STATE.modules.length; mi++) {
        var mod = STATE.modules[mi];
        if ((mod.lvl | 0) !== 0) { continue; }
        var t = _typeOf(mod);
        var hSt = _wallClearH(t) - 2 * this.STUD.w;   /* bottom plate to cap underside */
        /* MONO: the walls climb to the roof. The mono plane runs
           y = t.H + (W/2 + x) * tan -- low at sideL, high at sideR -- and
           with the skins stripped there is nobody left to fake the gap: the
           high wall's studs grow by the full rise, the raking front and rear
           walls grow per stud, and their caps follow (level but lifted on the
           high side, RAKED on the gables). */
        var monoW = (STATE.roofType === 'mono');
        var tanMo = monoW ? Math.tan(((STATE.roofPitch || 7) * Math.PI) / 180) : 0;
        var extW2 = _extW();
        function monoRise(xw) { return monoW ? Math.max(0, (extW2 / 2 + xw) * tanMo) : 0; }
        /* THE CORNER, measured on ONE ring. The frames stand on the cavity
           ring -- the rectangle the geometry actually draws on -- and both
           wall directions must speak its coordinates, or plates end where
           their own axis thinks the wall ends while the perpendicular stud
           stands somewhere else's idea of the corner. That mismatch was the
           defect: front rails stopping short of the side wall's end stud.

           On the ring: FRONT and REAR run long -- out to the OUTER FACE of
           the side walls' end studs (ring + half a frame depth), so their
           rails cap the corner column exactly. SIDES butt BETWEEN the front
           frames' inner faces (ring - half a depth). Flush by arithmetic,
           both directions, all four corners. */
        var inset = (INNER_MARGIN + PANEL_INSET) / 2;
        var rx = _extW() / 2 - inset, rz = t.L / 2 - inset;
        var hw = rx + this.STUD.d / 2, hz = rz;   /* hw doubles as the half-run of front/rear */
        var walls = [
          { wall: 'rear',  o: 'h', c: -rz, len: 2 * (rx + this.STUD.d / 2) },
          { wall: 'front', o: 'h', c: rz,  len: 2 * (rx + this.STUD.d / 2) },
          { wall: 'sideL', o: 'v', c: -rx, len: 2 * (rz - this.STUD.d / 2) },
          { wall: 'sideR', o: 'v', c: rx,  len: 2 * (rz - this.STUD.d / 2) }
        ];
        var ops = STATE.openings || [];
        /* THE LOOK GLAZING FRAMES TOO. The product's window bands live in
           _holesFor -- the facade's holes -- not in STATE.openings, and the
           wall layout only read the latter: so the stud grid marched straight
           through the big glazing, verticals across the glass. Every hole in
           the facade is synthesized here into the same opening record the
           framing code already understands -- centre, width, sill, head --
           with the facade's own per-side mirroring, so kings, headers, sills
           and the grid clearance all apply to the look glazing for free. */
        var wh0 = _holesFor(mi);
        var segSide = t.L - 2 * POST, segEnd = _extW() - 2 * POST;
        function synth(list, segLen, mirror, wallKey) {
          for (var sy = 0; sy < (list || []).length; sy++) {
            var hh = list[sy];
            var lo2 = mirror ? (segLen - hh.hi) : hh.lo;
            var hi2 = mirror ? (segLen - hh.lo) : hh.hi;
            var offS = (lo2 + hi2) / 2, wS = hi2 - lo2;
            /* DEDUPE: _holesFor reports the facade's holes -- and the facade
               cuts holes for the USER'S openings too, so every placed window
               came back as a second, ghost opening: two frames per hole,
               kings twice, and before the origin fix the pair sat 165 mm
               apart -- the doubled and tripled clusters in the photo. A hole
               that matches an existing opening on this wall is that opening,
               already framed; only the LOOK-only glazing gets synthesized. */
            var dup = false;
            var exi = STATE.openings || [];
            for (var dx = 0; dx < exi.length; dx++) {
              var eo = exi[dx];
              if (eo.mi !== mi || eo.wall !== wallKey) continue;
              if (Math.abs(eo.off - offS) < 0.10 && Math.abs(eo.w - wS) < 0.12) { dup = true; break; }
            }
            if (dup) continue;
            ops = ops.concat([{ mi: mi, wall: wallKey, off: offS, w: wS,
              sill: Math.max(0, hh.b0 || 0), h: Math.max(0.2, (hh.b1 || 0) - (hh.b0 || 0)), _synth: true }]);
          }
        }
        synth(wh0.sideL, segSide, false, 'sideL');
        synth(wh0.sideR, segSide, true, 'sideR');
        synth(wh0.rear, segEnd, true, 'rear');
        synth(wh0.front, segEnd, false, 'front');
        for (var wi = 0; wi < walls.length; wi++) {
          var Wd = walls[wi];
          /* openings first: the panel cuts must know where they are */
          var spans = [];
          for (var oi = 0; oi < ops.length; oi++) {
            var op = ops[oi];
            if (op.mi !== mi || op.wall !== Wd.wall) continue;
            /* ONE ORIGIN, at last. Real openings are placed on the SEGMENT
               (wall length minus the corner posts) and their off is measured
               from the segment's start -- but the frame layout was centring
               them on the RING run, a different length, so every framed
               opening sat ~165 mm off its own glass: kings beside the pane,
               grid clearance guarding the wrong stretch, studs marching over
               the glazing edge. The synth branch already spoke segment; now
               everything does. */
            /* AND THE MIRROR. The segment definitions are the verdict:
               sideL and front run positive from their start, but sideR
               starts at +len/2 with dir -1 and rear at +w/2 with dir -1 --
               the same two mirrored sides the facade mapping always had.
               v143 unified the origin but forgot the mirror, which is why
               the side walls kept framing the door's reflection. */
            var segHalf = (Wd.o === 'h' ? _extW() : t.L) / 2 - POST;
            var mirW = (Wd.wall === 'sideR' || Wd.wall === 'rear');
            var ctr = op._synth ? (op.off - segHalf)
                    : (mirW ? (segHalf - op.off) : (op.off - segHalf));
            spans.push([ctr - op.w / 2, ctr + op.w / 2]);
            var uK1 = ctr - op.w / 2 - this.STUD.w / 2, uK2 = ctr + op.w / 2 + this.STUD.w / 2;
            out.studs.push({ mi: mi, wall: Wd.wall, u: uK1, len: hSt + ((Wd.o === 'h') ? monoRise(uK1) : monoRise(Wd.c)), role: 'king' });
            out.studs.push({ mi: mi, wall: Wd.wall, u: uK2, len: hSt + ((Wd.o === 'h') ? monoRise(uK2) : monoRise(Wd.c)), role: 'king' });
            /* ONE origin. Openings are measured from the DECK; the stud zone
               starts a bottom plate higher. Mixing the two put every header
               60 mm above its true place -- so at a full-height opening a
               strip of daylight opened between header and plate, and the two
               read as senselessly doubled longitudinal beams. Converted, the
               header of a max-height opening sits FLUSH under the plate:
               header + plate = one deep pozednice over the glazing, and the
               rafters end on it. A lower window's header floats mid-wall with
               its cripples, as it should. */
            var hdT = (op.sill || 0) + op.h - this.STUD.w;   /* deck -> stud origin */
            out.studs.push({ mi: mi, wall: Wd.wall, u: ctr - op.w / 2 + this.STUD.w / 2, len: hdT, role: 'jack' });
            out.studs.push({ mi: mi, wall: Wd.wall, u: ctr + op.w / 2 - this.STUD.w / 2, len: hdT, role: 'jack' });
            out.headers.push({ mi: mi, wall: Wd.wall, u: ctr, len: op.w + 2 * this.STUD.w, y: hdT });
            /* P2: the horizontals of a window that does not reach the floor.
               A sill plate spans the jacks at sill height, cripple studs
               carry it from the bottom plate, and more cripples run from the
               header up to the top plate -- the wall stays a wall above and
               below the hole, not a void with glass in it. Full-height
               openings get neither: there is no wall there to frame. */
            var sillH = Math.max(0, (op.sill || 0) - this.STUD.w);   /* deck -> stud origin */
            if (sillH > 0.15) {
              out.sills.push({ mi: mi, wall: Wd.wall, u: ctr, len: op.w, y: sillH });
              for (var cu = ctr - op.w / 2 + _GRID() / 2; cu < ctr + op.w / 2 - 0.05; cu += _GRID()) {
                out.studs.push({ mi: mi, wall: Wd.wall, u: cu, len: Math.max(0.05, sillH - this.STUD.w), role: 'cripple', y0: 0 });
              }
            }
            var upY = hdT + this.STUD.w;   /* the head is flat now: 60, not 160 */
            /* the mono rise applies ABOVE the openings too: the cripples over
               the header climb to the raised (or raking) cap exactly like the
               grid studs beside them -- this was the half-height gap over the
               windows on the high wall */
            var riseHere = function (uu) { return (Wd.o === 'h') ? monoRise(uu) : monoRise(Wd.c); };
            if ((hSt + riseHere(ctr)) - upY > 0.08) {
              for (var cu2 = ctr - op.w / 2 + _GRID() / 2; cu2 < ctr + op.w / 2 - 0.05; cu2 += _GRID()) {
                out.studs.push({ mi: mi, wall: Wd.wall, u: cu2, len: (hSt + riseHere(cu2)) - upY, role: 'cripple', y0: upY });
              }
            }
          }
          if (!out._spans) out._spans = {};
          out._spans[mi + ':' + Wd.wall] = spans;
          /* the wall becomes PANELS: each with its own bottom and top plate
             and its own pair of end studs -- and one continuous cap plate over
             the lot, tying the joints, carrying the rafters. The pozednice
             stays whole; only the panel plates divide. */
          var pans = this._panelize(Wd.len, spans);
          for (var pn = 0; pn < pans.length; pn++) {
            var pa = pans[pn], pid = Wd.wall + ':' + pn;
            var pLen = pa[1] - pa[0], pCtr = (pa[0] + pa[1]) / 2;
            out.panels.push({ mi: mi, wall: Wd.wall, id: pid, from: pa[0], to: pa[1], len: pLen, idx: pn });
            /* ONE top rail. The panel used to carry its own top plate with
               the continuous cap stacked above -- structurally the classic
               double top plate, visually two colliding rails, and the photo
               is right that it reads wrong at this scale. The panel now owns
               its BOTTOM plate and its studs; the studs run straight to the
               underside of the continuous cap, and the cap alone is the top
               rail -- tying the joints, carrying the rafters, one profile. */
            out.plates.push({ mi: mi, wall: Wd.wall, len: pLen, u: pCtr, kind: 'bottom', pid: pid });
            var uE1 = pa[0] + this.STUD.w / 2, uE2 = pa[1] - this.STUD.w / 2;
            out.studs.push({ mi: mi, wall: Wd.wall, u: uE1, len: hSt + ((Wd.o === 'h') ? monoRise(uE1) : monoRise(Wd.c)), role: 'end', pid: pid });
            out.studs.push({ mi: mi, wall: Wd.wall, u: uE2, len: hSt + ((Wd.o === 'h') ? monoRise(uE2) : monoRise(Wd.c)), role: 'end', pid: pid });
          }
          /* the cap plate is what the rafters BEAR ON -- and the roof is
             longer than the building: the end trusses sit past the wall ends
             on the z-overhang, so the side walls' caps must run out with
             them, all the way to the roof's end. A rafter with nothing under
             it is not a rafter, it is a shelf. Front/rear caps stay wall
             length; the eave-side caps stretch by the roof's own overhangs
             and shift with their asymmetry. */
          if (Wd.o === 'v') {
            var ovC = STATE.roofOver || { z0: 0, z1: 0 };
            /* the cap runs to where the END RAFTERS actually sit -- same rule
               as the rafter layout (v312): tucked to L/2 - RDw/2 for a small
               z-overhang, projecting to L/2+ov only for a real gable. So the
               cap no longer sticks out past a hidden end rafter. */
            var GPROJ_C = 0.30, RDwC = (global.SaunaRoofFrame && global.SaunaRoofFrame.RAFTER ? global.SaunaRoofFrame.RAFTER.w : 0.06);
            function capEnd(ovz) { return (ovz > GPROJ_C) ? (t.L / 2 + ovz) : (t.L / 2 - RDwC / 2 - 0.005); }
            var capNeg = capEnd(ovC.z0 || 0), capPos = capEnd(ovC.z1 || 0);
            out.caps.push({ mi: mi, wall: Wd.wall, len: capNeg + capPos,
              u: (capPos - capNeg) / 2, dy: monoRise(Wd.c) });
          } else {
            /* mono gables carry a RAKED cap: chord length, roof angle */
            out.caps.push({ mi: mi, wall: Wd.wall, len: monoW ? (Wd.len / Math.cos(Math.atan(tanMo))) : Wd.len,
              u: 0, rake: monoW ? Math.atan(tanMo) : 0,
              dy: monoW ? (monoRise(0)) : 0 });
          }
          function pidAt(u2) {
            for (var pq = 0; pq < pans.length; pq++) if (u2 >= pans[pq][0] - 1e-6 && u2 <= pans[pq][1] + 1e-6) return Wd.wall + ':' + pq;
            return Wd.wall + ':0';
          }
          /* stamp the members already emitted for this wall with their panel */
          for (var st2 = 0; st2 < out.studs.length; st2++) {
            var S2 = out.studs[st2];
            if (S2.mi === mi && S2.wall === Wd.wall && !S2.pid) S2.pid = pidAt(S2.u);
          }
          for (var hd2 = 0; hd2 < out.headers.length; hd2++) {
            var H2 = out.headers[hd2];
            if (H2.mi === mi && H2.wall === Wd.wall && !H2.pid) H2.pid = pidAt(H2.u);
          }
          for (var sl2 = 0; sl2 < out.sills.length; sl2++) {
            var L2 = out.sills[sl2];
            if (L2.mi === mi && L2.wall === Wd.wall && !L2.pid) L2.pid = pidAt(L2.u);
          }
          /* Stations the way a sheet is HUNG: exact grid multiples measured
             from each panel's start edge, remainder bay at the far end. The
             old loop divided the wall EVENLY -- pitch 543 instead of 600,
             21.4 in instead of 24 -- which kept studs pretty and put every
             sheet edge in the air. True on-centre from the edge is the whole
             point of a modulus; the last bay takes what is left, exactly like
             the last sheet takes the saw. Kings and panel ends keep their
             clearances: a station inside an opening, or within 200 mm of a
             king, or hard against an edge, yields to the stud already there. */
          var G1 = _GRID();
          for (var pn2 = 0; pn2 < pans.length; pn2++) {
            var pa2 = pans[pn2];
            for (var u = pa2[0] + G1; u <= pa2[1] - 0.20 + 1e-6; u += G1) {
              var inOp = false;
              for (var sp = 0; sp < spans.length; sp++) if (u > spans[sp][0] - 0.03 && u < spans[sp][1] + 0.03) { inOp = true; break; }
              var nearKing = false;
              for (var nk = 0; nk < spans.length; nk++) {
                if (Math.abs(u - spans[nk][0]) < 0.20 || Math.abs(u - spans[nk][1]) < 0.20) { nearKing = true; break; }
              }
              if (!inOp && !nearKing) {
                var mrG = (Wd.o === 'h') ? monoRise(u) : monoRise(Wd.c);
                out.studs.push({ mi: mi, wall: Wd.wall, u: u, len: hSt + mrG, role: 'common', pid: pidAt(u) });
              }
            }
          }
        }
        out.modules.push({ mi: mi });
      }
      /* THE COLLISION GUARD. Enough origin archaeology: whatever coordinate
         system anyone lied in, NO STUD BODY MAY CROSS AN INFILL. This is the
         last pass and it is absolute: every stud whose body overlaps any
         opening span on its wall is deleted -- ends and kings live at the
         edges and survive by geometry; anything standing in the glass dies
         here. The count of removals is kept and printed by the snapshot, so
         a surviving mismatch names itself in production instead of hiding
         behind clean-looking code. */
      var GUARD_W = this.STUD.w / 2 + 0.005;
      this._guarded = 0;
      var self3 = this;
      var spansByWall = {};
      var opsAllG = (out._spans || []);
      out.studs = out.studs.filter(function (st) {
        if (st.role !== 'common') return true;   /* cripples and sill posts LIVE inside spans, below and above the glass */
        var sp = (out._spans && out._spans[st.mi + ':' + st.wall]) || [];
        for (var gi = 0; gi < sp.length; gi++) {
          if (st.u - GUARD_W > sp[gi][0] + 0.01 && st.u + GUARD_W < sp[gi][1] - 0.01) {
            self3._guarded++;
            return false;
          }
        }
        return true;
      });
      return out;
    }
  };

  /* K4: the roof frame, as data. The v87 fix established the rafter line --
     through the plate at wall height, up to the ridge over the wall centre,
     eave dropping with the overhang. These are the PIECES on that line:
     rafter pairs at 600 along the length, a ridge board between them, and a
     collar tie (klestina) in the upper third of every pair. Mono: single
     rafters plate to plate. Same contract as floor and walls: the BoQ reads
     cuts, the drawing reads positions. */
  global.SaunaRoofFrame = {
    RAFTER: { w: 0.06, h: 0.16 },
    COLLAR_FRAC: 0.5,   /* the collar rides at HALF the rafter run (Marek: it sat too high and too short under the apex) */
    layout: function () {
      var out = { rafters: [], ridge: [], collars: [], fascias: [], chords: [], ceilRing: [] };
      var pitch = clamp(STATE.roofPitch || 0, 0, 45);
      if (pitch < 0.5) return out;
      var tanP = Math.tan(pitch * Math.PI / 180);
      var ov = STATE.roofOver;
      for (var mi = 0; mi < STATE.modules.length; mi++) {
        var mod = STATE.modules[mi];
        if ((mod.lvl | 0) !== 0) continue;
        var t = _typeOf(mod);
        var W = _extW(), L = t.L;
        var rl = L + ov.z0 + ov.z1;
        var nP = Math.max(2, Math.ceil(rl / _GRID()) + 1);
        var ang = Math.atan2((W / 2) * tanP, W / 2);
        if (STATE.roofType === 'gable') {
          /* ONE EAVE MINIMUM for the whole gable eave (Marek): the ceiling
             joists are sawn onto the rafter TOP plane, so their ends run to
             W/2 + kTop/tanP past the wall. The roof edge -- rafters, covering,
             fascia, flashing, gutter -- therefore never stops short of that
             + 0.12 m. ONE floor, applied to EVERYTHING on this eave. */
          var kTopE = (this.RAFTER.h) / Math.cos(ang);
          var eaveMinG = 0.10;   /* SHELTER: fixed drip edge -- the joists are trimmed plumb at the roof edge, no joist-driven floor */
          var exE0 = Math.max(ov.x0, eaveMinG), exE1 = Math.max(ov.x1, eaveMinG);
          var lenL = (W / 2 + exE0) / Math.cos(ang);
          var lenR = (W / 2 + exE1) / Math.cos(ang);
          /* END RAFTER RULE: when the gable is NOT extended (small z-overhang)
             the last common rafter tucks INSIDE the wall, hidden under the
             cladding -- outer face at L/2 - RDw/2 - a hair. Only a genuine
             gable overhang (> GABLE_PROJ) pushes it out to carry the eave.
             The covering + barge board always run to the roof edge regardless. */
          var GABLE_PROJ = 0.30;                         /* z-overhang beyond this = intended gable projection */
          var RDwz = this.RAFTER.w;
          function endZ(ovz, sg) {
            if (ovz > GABLE_PROJ) return sg * (L / 2 + ovz);   /* projects, carries the gable */
            return sg * (L / 2 - RDwz / 2 - 0.005);            /* tucked inside, under the cladding */
          }
          var zEnd0 = endZ(ov.z0, -1), zEnd1 = endZ(ov.z1, 1);
          var rspanR = zEnd1 - zEnd0;
          for (var i = 0; i < nP; i++) {
            var z = zEnd0 + rspanR * (i / (nP - 1));
            var tid = 'truss:' + mi + ':' + i;
            out.rafters.push({ mi: mi, z: z, side: -1, len: lenL, ang: ang, pid: tid });
            out.rafters.push({ mi: mi, z: z, side: 1, len: lenR, ang: ang, pid: tid });
            /* The collar earns its place by SPAN. Under four metres the
               ceiling joist ties the triangle alone -- a second rail there
               would sit a hand's width from the first, doing nothing but
               costing timber. From four metres up the collar appears, and it
               lives where a collar lives: TUCKED UNDER THE APEX, its top
               edge against the ridge board's bottom edge, a short strut
               between the rafters right at the peak. */
            /* SHELTER has no ceiling joists any more (v34), so on a gable the
               collar is the ONLY tie -- it is built at every span, not just
               over 4 m. */
            if (W > 4.0 || SHELTER_NO_CEILING) {
              var angC = Math.atan2((W / 2) * tanP, W / 2);
              var kC = this.RAFTER.h / Math.cos(angC);
              /* Marek: tucked under the apex the collar read as a short stub
                 high in the peak. A collar belongs part way DOWN the rafter
                 run -- placed here by rise fraction (COLLAR_FRAC = half way),
                 which also makes it longer, spanning where the rafters are
                 further apart. */
              var cMid = (t.H - 0.09) + this.COLLAR_FRAC * (W / 2) * tanP;
              var cTop = cMid + 0.06;
              var cLen = 2 * Math.max(0.2, W / 2 - (cMid - t.H - kC) / tanP);
              out.collars.push({ mi: mi, z: z, len: cLen, y: cTop, pid: tid });
            }
            /* the CEILING JOIST -- the textbook tie, correcting a wrong
               guess: the tension member of a rafter roof runs at the TOP
               PLATE level, wall to wall, nailed to the rafter flanks where
               they seat. It does NOT chase the overhang tails below the wall
               -- those are cantilevers, carried by the rafter alone, dressed
               by the fascia. Rafter + rafter + ceiling joist closes the
               triangle; the collar beam stiffens its top. */
            out.chords.push({ mi: mi, z: z, pid: tid, len: W, y: t.H - 0.09 });
          }
          out.ridge.push({ mi: mi, len: rl });
          /* the profile joining the rafter ENDS: a fascia along each eave, so
             the tails read as one built edge, and the rafters between fascia
             and cap plate visibly LIE ON the wall panels */
          /* HOLD THE EAVE where the covering actually stops: the rafter tail
             is Math.max(W/2+ov, jCorner+0.12), jCorner = W/2 + kTop/tanP, so
             the eave never comes closer than that. The fascia (and the gutter
             reading it) must use the SAME floor or it drifts in past the roof
             when the footprint shrinks. */
          var kTopF = (this.RAFTER.h) / Math.cos(ang);
          var eaveFloor0 = kTopF / (tanP || 1) + 0.12;
          var exG0 = exE0, exG1 = exE1;   /* fascia holds the SAME floored eave edge as the rafters */
          out.fascias.push({ mi: mi, side: -1, len: rl, x: -(W / 2 + exG0), y: t.H - exG0 * tanP });
          out.fascias.push({ mi: mi, side: 1, len: rl, x: (W / 2 + exG1), y: t.H - exG1 * tanP });
        } else {
          var angM = Math.atan2(W * tanP, W);
          var lenM = (W + ov.x0 + ov.x1) / Math.cos(angM);
          var GABLE_PROJ_M = 0.30, RDwzM = this.RAFTER.w;
          function endZM(ovz, sg) {
            if (ovz > GABLE_PROJ_M) return sg * (L / 2 + ovz);
            return sg * (L / 2 - RDwzM / 2 - 0.005);
          }
          var zmEnd0 = endZM(ov.z0, -1), zmEnd1 = endZM(ov.z1, 1);
          var rspanM = zmEnd1 - zmEnd0;
          for (var j = 0; j < nP; j++) {
            var zm = zmEnd0 + rspanM * (j / (nP - 1));
            out.rafters.push({ mi: mi, z: zm, side: 0, len: lenM, ang: angM });
          }
          /* THE MONO CEILING IS A FRAME, not floating sticks -- the review
             was right. Around the room, at the bearing line, runs a LEDGER
             RING of the same KVH60160: its outer faces sit 1 mm off the wall
             studs' inner faces (stud inner face = W/2 - 0.1175, from
             inset 37.5 + depth/2 80), screwed to every stud it crosses. The
             joists then hang INSIDE the ring on their hangers, ends 1 mm off
             the ledger's inner face, stations on the same 600 raster as the
             studs -- everything lines up and everything is fixed to
             something, exactly like the floor below it. */
          var stIn = 0.1175;                      /* stud inner face offset */
          var ledT = 0.06, ledOut = stIn + 0.001; /* ledger outer face */
          var ledIn = ledOut + ledT;              /* ledger inner face */
          out.ceilRing = out.ceilRing || [];
          /* two long ledgers on the x-sides (joist bearers) */
          /* the TRUE cap top: base plate 60 + studs + cap 60 lands the cap's
             top face at t.H - 0.09 -- the review measured it on screen. The
             ceiling frame's top edge goes THERE, flush with the plate. */
          var capTop = t.H - 0.09;
          out.ceilRing.push({ mi: mi, o: 'v', x: -(W / 2 - ledOut - ledT / 2), len: t.L - 2 * stIn - 0.002, y: capTop });
          out.ceilRing.push({ mi: mi, o: 'v', x: (W / 2 - ledOut - ledT / 2), len: t.L - 2 * stIn - 0.002, y: capTop });
          /* two closers on the z-sides, between the long ones */
          out.ceilRing.push({ mi: mi, o: 'h', z: -(t.L / 2 - ledOut - ledT / 2), len: W - 2 * ledIn - 0.004, y: capTop });
          out.ceilRing.push({ mi: mi, o: 'h', z: (t.L / 2 - ledOut - ledT / 2), len: W - 2 * ledIn - 0.004, y: capTop });
          var mcW = W - 2 * (ledIn + 0.001);
          for (var mz = 0; mz < out.rafters.length; mz++) {
            var MR = out.rafters[mz];
            if (MR.mi !== mi || MR.side !== 0) continue;
            if (Math.abs(MR.z) > t.L / 2 - stIn - ledT) continue;
            out.chords.push({ mi: mi, z: MR.z, pid: MR.pid, len: mcW, y: capTop, mono: true });
          }
          /* THE EAVE LINE, published so the gutter can HOLD it (it must not
             recompute and drift when the roof changes). Mono sheds across the
             width: the LOW eave at -x sits at the plate, the HIGH eave at +x
             rides up by the full rise. Both get a fascia entry with the true
             seat y; the gutter reads these, never its own arithmetic. */
          var riseM = W * tanP;
          /* same eave floor as the covering (mono tail uses the same rule) */
          var kTopM = (this.RAFTER.h) / Math.cos(angM);
          var eaveFloorM = kTopM / (Math.tan(pitch * Math.PI / 180) || 1) + 0.12;
          var exM0 = ov.x0, exM1 = ov.x1;   /* fascia tied to the eave edge */
          out.fascias.push({ mi: mi, side: -1, len: rl, x: -(W / 2 + exM0), y: capTop - exM0 * tanP, low: true });
          out.fascias.push({ mi: mi, side: 1, len: rl, x: (W / 2 + exM1), y: capTop + riseM + exM1 * tanP, low: false });
        }
      }
      /* SHELTER: no ceiling structure -- an open shelter is open to the
         rafters, so the plate-level ceiling joists (chords) and the mono
         ledger ring go, with their BoQ rows (both read these arrays). The
         COLLARS stay: on a gable they are what stops the rafter pairs from
         spreading. */
      if (SHELTER_NO_CEILING) { out.chords = []; out.ceilRing = []; }
      return out;
    }
  };

  /* the highest an opening head may reach: clear wall height minus the top
     plate and the header that carries the load over the hole */
  function _openingHeadMax(mi) {
    var t = _typeOf(STATE.modules[mi]);
    return Math.max(0.6, _wallClearH(t) - 0.06 - 0.16);
  }
  function _reclampOpenings() {
    var ops = STATE.openings || [];
    for (var i = 0; i < ops.length; i++) {
      var op = ops[i], hd = _openingHeadMax(op.mi);
      if ((op.sill || 0) + op.h > hd) {
        op.sill = Math.max(0, Math.min(op.sill || 0, hd - 0.30));
        op.h = Math.min(op.h, hd - op.sill);
      }
    }
  }

  /* Scenery: a curated surrounding instead of the map. Paper-model trees --
     low-poly cones and blobs, flat colours, the architect's cardboard idiom --
     scattered on a seeded ring around the building, fading with distance so
     the eye stays on the sauna. It lives in eng.SCENE, never eng.group: the
     fit box and the orbit centre measure the group, so however much forest is
     planted, the centre of the scene stays the centre of the sauna. The map
     canvas hides while a preset is up and comes back untouched when the
     person cycles home to it. */
  global.SaunaScenery = {
    _grp: null,
    PRESETS: ['map', 'meadow', 'forest'],
    _rng: function (seed) {
      var t = seed >>> 0;
      return function () { t = (t * 1664525 + 1013904223) >>> 0; return t / 4294967296; };
    },
    cycle: function () {
      var i = this.PRESETS.indexOf(STATE.scenery || 'map');
      STATE.scenery = this.PRESETS[(i + 1) % this.PRESETS.length];
      this.apply();
      savePref();
    },
    apply: function () {
      var T = global.THREE, eng = global.SaunaEngine;
      if (!T || !eng || !eng.scene) return;
      if (this._grp) {
        eng.scene.remove(this._grp);
        this._grp.traverse(function (o) { if (o.geometry) o.geometry.dispose(); });
        this._grp = null;
      }
      var mode = STATE.scenery || 'map';
      var mapDiv = document.getElementById('cf-mapbox');
      if (mapDiv) mapDiv.style.visibility = (mode === 'map') ? '' : 'hidden';
      if (mode === 'map') return;
      var g = new T.Group();
      g.userData.arSkip = true;
      this._grp = g;
      eng.scene.add(g);
      /* No ground. The scene's floor is the page's own #f5f5f5 -- the
         scenery is trees standing in white space, the way a cardboard model
         sits on a white table. Anything painted under them would fight the
         background it was asked to keep. */
      /* the building's own clearing: trees start outside the footprint plus
         a garden's breathing room */
      var f = STATE.footprint;
      var clear = Math.max(f.w, f.l) / 2 + (mode === 'forest' ? 3.2 : 4.5);
      var rMax = 34;
      var n = (mode === 'forest') ? 110 : 34;
      var rng = this._rng(mode === 'forest' ? 1337 : 4242);
      /* paper palette: whites and near-whites, form read from shading and
         silhouette, not from colour */
      /* Card, not polygons. A paper-model tree is a SILHOUETTE cut from white
         board -- so each tree here is a flat outline, extruded 12 mm for the
         cardboard edge, and crossed with a second copy at 90 degrees: the
         X-section every architect glues into a competition model. The trunk is
         part of the cut, one piece of card, the way you would actually
         scissor it. Two dies only -- a tiered spruce and a round-crown
         deciduous -- because a sheet of card has two dies, not a forest of
         variants. */
      function _spruceShape(sc) {
        var sh = new T.Shape();
        var w0 = 0.95 * sc, hT = 2.9 * sc, st = 0.09 * sc;
        sh.moveTo(-st, 0);
        sh.lineTo(-st, 0.5 * sc);
        sh.lineTo(-w0, 0.55 * sc);
        sh.lineTo(-w0 * 0.55, 1.35 * sc);
        sh.lineTo(-w0 * 0.78, 1.30 * sc);
        sh.lineTo(-w0 * 0.38, 2.05 * sc);
        sh.lineTo(-w0 * 0.55, 2.02 * sc);
        sh.lineTo(0, hT);
        sh.lineTo(w0 * 0.55, 2.02 * sc);
        sh.lineTo(w0 * 0.38, 2.05 * sc);
        sh.lineTo(w0 * 0.78, 1.30 * sc);
        sh.lineTo(w0 * 0.55, 1.35 * sc);
        sh.lineTo(w0, 0.55 * sc);
        sh.lineTo(st, 0.5 * sc);
        sh.lineTo(st, 0);
        sh.lineTo(-st, 0);
        return sh;
      }
      function _crownShape(sc) {
        var sh = new T.Shape();
        var st = 0.08 * sc, r = 0.85 * sc, cy = 1.75 * sc;
        sh.moveTo(-st, 0);
        sh.lineTo(-st, cy - r * 0.75);
        /* CLOCKWISE over the TOP: with ccw the arc sneaks the short way
           around the BOTTOM of the circle -- and the crown never gets drawn,
           leaving bare stems in the meadow. The crown is the top of the
           circle, so the path must go over the top. */
        sh.absarc(0, cy, r, Math.PI * 1.28, Math.PI * -0.28, true);
        sh.lineTo(st, cy - r * 0.75);
        sh.lineTo(st, 0);
        sh.lineTo(-st, 0);
        return sh;
      }
      var EXT = { depth: 0.012, bevelEnabled: false };
      for (var i = 0; i < n; i++) {
        var a = rng() * Math.PI * 2;
        var r = clear + (rMax - clear) * Math.sqrt(rng());
        var x = Math.cos(a) * r, z = Math.sin(a) * r;
        var far = clamp((r - clear) / (rMax - clear), 0, 1);
        var op = 1 - far * 0.45;                    /* the ghost: distance thins them,
                                                       but never to bare sticks */
        var sc = 0.8 + rng() * 0.7;
        var spruce = (mode === 'forest') ? (rng() < 0.8) : (rng() < 0.35);
        var shp = spruce ? _spruceShape(sc) : _crownShape(sc);
        /* UNLIT white. Lambert was the grey: a shaded material turns every
           face away from the light into pencil grey, which is physics, not
           paper. Card is matte and reads by CONTOUR -- so the fill is Basic
           (no lighting, pure #ffffff on the #f5f5f5 page) and the drawing is
           the cut edge: a hairline outline around each silhouette, exactly
           the line a knife leaves in board. */
        var mat = new T.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: op, side: T.DoubleSide });
        var edgeMat = new T.LineBasicMaterial({ color: 0xcfcfcb, transparent: true, opacity: Math.min(1, op + 0.1) });
        var tree = new T.Group();
        for (var xk = 0; xk < 2; xk++) {
          var cardGeo = new T.ExtrudeGeometry(shp, EXT);
          var card = new T.Mesh(cardGeo, mat);
          card.position.z = -EXT.depth / 2;
          var edge = new T.LineSegments(new T.EdgesGeometry(cardGeo, 20), edgeMat);
          edge.position.z = -EXT.depth / 2;
          var holder = new T.Group();
          holder.add(card);
          holder.add(edge);
          holder.rotation.y = xk * Math.PI / 2;
          tree.add(holder);
        }
        tree.position.set(x, 0, z);
        tree.rotation.y = rng() * Math.PI * 2;
        g.add(tree);
      }
    }
  };

  function _sitePergActive() {
    try { return !!(_wiz && _wiz.isOpen() && _wiz.current() && _wiz.current().key === 'look'); }
    catch (e) { return false; }
  }

  global.SaunaFoundation = {
    /* support spacing along a bearer, per foundation type. Screws take the
       widest grid (steel shaft, deep bearing); pads the middle; precast piers
       the tightest (surface bearing on compacted gravel). */
    _SPACING: { screws: { min: 1.0, max: 1.5 }, piers: { min: 0.9, max: 1.2 }, pads: { min: 1.0, max: 1.4 } },
    _JOIST_SPAN_MAX: 1.8,     /* bearer-to-bearer: 60x200 joists at 600 carry this easily */
    BEARER: { w: 0.10, h: 0.16 },   /* KVH10160 on edge */
    _fit: function (len, rng) {
      /* smallest count of equal bays whose pitch lands inside [min,max] */
      var n = Math.max(1, Math.ceil(len / rng.max));
      while (len / n < rng.min && n > 1) n--;
      return n;
    },
    layout: function () {
      var out = { bearers: [], points: [], type: STATE.foundation || 'screws', modules: [] };
      var rngP = this._SPACING[out.type] || this._SPACING.screws;
      var terrOn = !!STATE.terraceOn;
      for (var mi = 0; mi < STATE.modules.length; mi++) {
        var mod = STATE.modules[mi];
        if ((mod.lvl | 0) !== 0) continue;
        var t = _typeOf(mod);
        var hw = _extW() / 2, hz = t.L / 2;
        /* terrace reaches, on each side that is at least TERR_MIN past the wall */
        var ov = STATE.terraceOver || { x0: 0, x1: 0, z0: 0, z1: 0 };
        var tx0 = (terrOn && ov.x0 >= TERR_MIN) ? ov.x0 : 0;
        var tx1 = (terrOn && ov.x1 >= TERR_MIN) ? ov.x1 : 0;
        var tz0 = (terrOn && ov.z0 >= TERR_MIN) ? ov.z0 : 0;
        var tz1 = (terrOn && ov.z1 >= TERR_MIN) ? ov.z1 : 0;
        /* bearer X-span EXTENDED by the side terraces -- the cabin bearer physically
           runs on into the terrace, so there is one continuous member, not two */
        var bx0 = -(hw + tx0), bx1 = (hw + tx1);
        var bcx = (bx0 + bx1) / 2, bLen = (bx1 - bx0) - 0.04;
        /* supports along each bearer at the SAME spacing, across the whole span */
        var pxLo = bx0 + 0.12, pxHi = bx1 - 0.12, runP = Math.max(0.01, pxHi - pxLo);
        var nPt = this._fit(runP, rngP) + 1;
        /* ONE uniform Z-grid across the WHOLE area (cabin + terrace) at the cabin's
           spacing rule -- no separate cabin / terrace rows, so nothing doubles at
           the wall line. A row is flagged terr when it falls in the terrace. */
        var zLo = -(hz + tz0), zHi = (hz + tz1);
        var zRunA = Math.max(0.01, (zHi - zLo) - 0.30);
        var nBearT = this._fit(zRunA, { min: this._JOIST_SPAN_MAX * 0.55, max: this._JOIST_SPAN_MAX }) + 1;
        var zs = [];
        for (var zbi = 0; zbi < nBearT; zbi++) {
          var zz = (nBearT === 1) ? ((zLo + zHi) / 2) : (zLo + 0.15 + zRunA * (zbi / (nBearT - 1)));
          zs.push({ z: zz, terr: Math.abs(zz) > hz + 0.01 });
        }
        var mm = { mi: mi, bearers: zs.length, ptsPerBearer: nPt, bearLen: bLen };
        for (var zi = 0; zi < zs.length; zi++) {
          var Z = zs[zi].z, zterr = zs[zi].terr;
          /* a cabin bearer's terrace share is only its X-extension; a terrace
             bearer (front / rear) is terrace along its whole length */
          var terrLen = zterr ? bLen : (tx0 + tx1);
          out.bearers.push({ mi: mi, z: Z, len: bLen, cx: bcx, terr: zterr, terrLen: terrLen });
          for (var pi = 0; pi < nPt; pi++) {
            var X = (nPt === 1) ? bcx : (pxLo + runP * (pi / (nPt - 1)));
            var pterr = zterr || (Math.abs(X) > hw + 0.01);
            out.points.push({ mi: mi, x: X, z: Z, terr: pterr });
          }
        }
        out.modules.push(mm);
      }
      return out;
    }
  };

  global.SaunaRenderer = {
    materials: null,
    _grimeTex: null,
    _shadowTex: null,
    _labelCache: {},

    _grime: function () {
      if (this._grimeTex) return this._grimeTex;
      var T = global.THREE;
      var c = document.createElement('canvas');
      c.width = 256; c.height = 256;
      var ctx = c.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, 256, 256);
      var i;
      for (i = 0; i < 70; i++) {
        var x = Math.random() * 256;
        var wdt = 1 + Math.random() * 5;
        var a = 0.02 + Math.random() * 0.05;
        var g = ctx.createLinearGradient(0, 0, 0, 256);
        g.addColorStop(0, 'rgba(40,40,45,0)');
        g.addColorStop(0.25 + Math.random() * 0.3, 'rgba(40,40,45,' + a.toFixed(3) + ')');
        g.addColorStop(1, 'rgba(30,30,35,' + (a * 1.6).toFixed(3) + ')');
        ctx.fillStyle = g;
        ctx.fillRect(x, 0, wdt, 256);
      }
      for (i = 0; i < 400; i++) {
        ctx.fillStyle = 'rgba(60,55,50,' + (0.015 + Math.random() * 0.035).toFixed(3) + ')';
        ctx.fillRect(Math.random() * 256, Math.random() * 256, 1 + Math.random() * 2, 1 + Math.random() * 2);
      }
      var grad = ctx.createLinearGradient(0, 200, 0, 256);
      grad.addColorStop(0, 'rgba(50,42,35,0)');
      grad.addColorStop(1, 'rgba(50,42,35,0.10)');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 200, 256, 56);
      var tex = new T.CanvasTexture(c);
      tex.wrapS = T.RepeatWrapping;
      tex.wrapT = T.RepeatWrapping;
      tex.colorSpace = T.SRGBColorSpace;
      this._grimeTex = tex;
      return tex;
    },

    _shadow: function () {
      if (this._shadowTex) return this._shadowTex;
      var T = global.THREE;
      var c = document.createElement('canvas');
      c.width = 256; c.height = 256;
      var ctx = c.getContext('2d');
      var g = ctx.createRadialGradient(128, 128, 20, 128, 128, 126);
      /* Halved. It was reading as a hole in the ground rather than a shadow --
         a garden building on grass in daylight does not sit in a pool of ink. */
      g.addColorStop(0, 'rgba(0,0,0,0.17)');
      g.addColorStop(0.55, 'rgba(0,0,0,0.10)');
      g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, 256, 256);
      var tex = new T.CanvasTexture(c);
      this._shadowTex = tex;
      return tex;
    },

    _stencilCache: {},
    _erode: function (ctx, x0, y0, x1, y1, n) {
      ctx.save();
      ctx.globalCompositeOperation = 'destination-out';
      var i, cx2, cy2;
      for (i = 0; i < n; i++) {
        ctx.fillStyle = 'rgba(0,0,0,' + (0.18 + Math.random() * 0.5).toFixed(2) + ')';
        var w = 1 + Math.random() * 4, h = 1 + Math.random() * 3;
        ctx.fillRect(x0 + Math.random() * (x1 - x0), y0 + Math.random() * (y1 - y0), w, h);
      }
      for (i = 0; i < Math.round(n / 14); i++) {
        cx2 = x0 + Math.random() * (x1 - x0);
        cy2 = y0 + Math.random() * (y1 - y0);
        var cl = Math.round(4 + Math.random() * 14);
        for (var b = 0; b < cl; b++) {
          ctx.fillStyle = 'rgba(0,0,0,' + (0.25 + Math.random() * 0.45).toFixed(2) + ')';
          ctx.beginPath();
          ctx.arc(cx2 + (Math.random() - 0.5) * 26, cy2 + (Math.random() - 0.5) * 20, 1 + Math.random() * 4.5, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      for (i = 0; i < Math.round(n / 20); i++) {
        cx2 = x0 + Math.random() * (x1 - x0);
        cy2 = y0 + Math.random() * (y1 - y0);
        ctx.fillStyle = 'rgba(0,0,0,' + (0.15 + Math.random() * 0.3).toFixed(2) + ')';
        ctx.save();
        ctx.translate(cx2, cy2);
        ctx.rotate((Math.random() - 0.5) * 0.5);
        ctx.fillRect(0, 0, 18 + Math.random() * 70, 1 + Math.random() * 2.5);
        ctx.restore();
      }
      ctx.restore();
    },


    _ensure: function () {
      if (this.materials) return;
      var T = global.THREE;
      var grime = this._grime();
      this.materials = {
        casting: new T.MeshStandardMaterial({ color: 0x2e3238, roughness: 0.6, metalness: 0.7 }),
        steelDark: new T.MeshStandardMaterial({ color: 0x3c4046, roughness: 0.55, metalness: 0.65 }),
        rubber: new T.MeshStandardMaterial({ color: 0x141518, roughness: 0.9, metalness: 0.05 }),
        galv: new T.MeshStandardMaterial({ color: 0xb8bcc2, roughness: 0.42, metalness: 0.85 }),
        ply: new T.MeshStandardMaterial({ color: 0xa8804e, roughness: 0.85, metalness: 0.03 }),
        opFrame: new T.MeshStandardMaterial({ color: 0x2f3338, roughness: 0.45, metalness: 0.6 }),
        glass: new T.MeshStandardMaterial({ color: 0x9fc4d8, roughness: 0.08, metalness: 0.1, transparent: true, opacity: 0.34, side: T.DoubleSide }),
        opGhost: new T.MeshStandardMaterial({ color: 0x3f8a4a, roughness: 0.5, metalness: 0.1, transparent: true, opacity: 0.5, side: T.DoubleSide }),
        doorPanel: new T.MeshStandardMaterial({ color: 0xe8e4dc, roughness: 0.55, metalness: 0.08 }),
        plate: new T.MeshStandardMaterial({ color: 0xd9dade, roughness: 0.4, metalness: 0.5 })
      };
      /* THE FRAME'S COLOUR IS A SLOT, SO THE MATERIAL IS A GETTER. Twenty
         places read this.materials.timber and half of them lazily made it
         once and kept it forever, in two slightly different pines. As an
         accessor it hands back a material keyed on the timber colour in
         force, cached per colour, and the guards that used to create it find
         it already there. The setter swallows those old assignments instead
         of letting strict mode throw on them. */
      var self = this;
      self._timberBy = {};
      function timberMat(dark) {
        var hex = _cv().timber, f = dark ? 0.80 : 1.0;
        var k = (dark ? 'd' : 'l') + hex;
        if (!self._timberBy[k]) {
          var r = ((hex >> 16) & 255) * f, gg = ((hex >> 8) & 255) * f, b = (hex & 255) * f;
          var col = (Math.round(r) << 16) | (Math.round(gg) << 8) | Math.round(b);
          self._timberBy[k] = new T.MeshStandardMaterial({ color: col, roughness: dark ? 0.82 : 0.78, metalness: 0.02 });
        }
        return self._timberBy[k];
      }
      Object.defineProperty(this.materials, 'timber',     { get: function () { return timberMat(false); }, set: function () {}, enumerable: true });
      Object.defineProperty(this.materials, 'timberDark', { get: function () { return timberMat(true);  }, set: function () {}, enumerable: true });
      this._paintCache = {};
      this._grimeMap = grime;
    },


    _corrPanel: function (w, h, pitch, depth, holes) {
      var T = global.THREE;
      var n = Math.max(1, Math.round(w / pitch));
      var p = w / n;
      var fo = p * 0.30, sl = p * 0.20, fi = p * 0.30;
      var xs = [-w / 2], zs = [depth];
      var x = -w / 2;
      for (var i = 0; i < n; i++) {
        xs.push(x + fo); zs.push(depth);
        xs.push(x + fo + sl); zs.push(0);
        xs.push(x + fo + sl + fi); zs.push(0);
        xs.push(x + p); zs.push(depth);
        x += p;
      }
      var pos = [], uv = [];
      function emit(lo, hi, y0, y1) {
        if (hi - lo < 0.004 || y1 - y0 < 0.004) return;
        var ya = y0 - h / 2, yb = y1 - h / 2;
        for (var si = 0; si < xs.length - 1; si++) {
          var xa = xs[si], xb = xs[si + 1];
          if (xb <= lo || xa >= hi) continue;
          var za = zs[si], zb = zs[si + 1];
          var ca = Math.max(xa, lo), cb = Math.min(xb, hi);
          var t0 = (ca - xa) / (xb - xa || 1), t1 = (cb - xa) / (xb - xa || 1);
          var cza = za + (zb - za) * t0, czb = za + (zb - za) * t1;
          var ua = (ca + w / 2) / w, ub = (cb + w / 2) / w;
          var va = y0 / h, vb = y1 / h;
          pos.push(ca, ya, cza, cb, ya, czb, cb, yb, czb);
          uv.push(ua, va, ub, va, ub, vb);
          pos.push(ca, ya, cza, cb, yb, czb, ca, yb, cza);
          uv.push(ua, va, ub, vb, ua, vb);
        }
      }
      var hs = (holes || []).slice().sort(function (a, b) { return a.lo - b.lo; });
      var cursor = -w / 2;
      for (var hi2 = 0; hi2 < hs.length; hi2++) {
        var hle = hs[hi2];
        var lo = clamp(hle.lo - w / 2, -w / 2, w / 2);
        var hi3 = clamp(hle.hi - w / 2, -w / 2, w / 2);
        if (lo > cursor) emit(cursor, lo, 0, h);
        var b0 = clamp(hle.b0, 0, h), b1 = clamp(hle.b1, 0, h);
        emit(lo, hi3, 0, b0);
        emit(lo, hi3, b1, h);
        cursor = Math.max(cursor, hi3);
      }
      if (cursor < w / 2) emit(cursor, w / 2, 0, h);
      var g = new T.BufferGeometry();
      g.setAttribute('position', new T.Float32BufferAttribute(pos, 3));
      g.setAttribute('uv', new T.Float32BufferAttribute(uv, 2));
      g.computeVertexNormals();
      return g;
    },



    /* solo = the ASSEMBLY step: centred, larger, and pulled apart so each layer
       is a thing you can point at rather than a stripe in a sandwich. Otherwise
       it is the small sample that stands beside the cabin. */

    _buildRoofFlashing: function (grp, mi, t) {
      /* ROOF EDGE METAL in 3D, matching the v267 BoQ: a DRIP EDGE along every
         eave, a GABLE FLASHING up every rake, and on a gable a RIDGE CAP over
         the apex with a VENTILATION STRIP beneath it. Each is a thin folded
         metal strip laid on the covering edge, following the roof planes. */
      try {
        var T = global.THREE;
        var pitch = clamp(STATE.roofPitch || 0, 0, 45);
        if (pitch < 0.5) return;
        var ov = STATE.roofOver || { x0: 0.18, x1: 0.18, z0: 0.18, z1: 0.18 };
        var W = _extW(), L = t.L, tanP = Math.tan(pitch * Math.PI / 180);
        var ang = Math.atan2((W / 2) * tanP, W / 2);
        var cosP = Math.cos(pitch * Math.PI / 180);
        var metal = this._metalMat(0.86);   /* flashings a shade darker than the gutter */
        var capT = t.H - 0.09;
        var RDh = (global.SaunaRoofFrame && global.SaunaRoofFrame.RAFTER ? global.SaunaRoofFrame.RAFTER.h : 0.16);
        var kTop = RDh / cosP;
        /* the covering TOP sits a little above the seat line; this is the same
           stack the covering mesh uses (counter-batten + air + covering). */
        var coverUp = 0.031 + 0.030;   /* counter-batten + covering: covering TOP = f.y + kTop + 0.061 (measured, was overstated at 0.096 -> flashing floated 35mm high) */
        /* the SAME eave floor the fascia/covering use (v309): the roof edge
           never comes closer than jCorner+0.12 = W/2 + kTop/tanP + 0.12, so
           the effective overhang is Math.max(ov, kTop/tanP+0.12). The rake
           flashing must use it too or it shortens with the shrinking footprint
           while the covering has already stopped. */
        var eaveFloor = kTop / (tanP || 1) + 0.12;
        var exX0 = ov.x0, exX1 = ov.x1;   /* SHELTER: drip at the fixed roof edge */
        var rl = L + ov.z0 + ov.z1;
        var zc = (ov.z1 - ov.z0) / 2;
        var lay = (global.SaunaRoofFrame && global.SaunaRoofFrame.layout) ? global.SaunaRoofFrame.layout() : { fascias: [], ridge: [] };
        var fasc = (lay.fascias || []).filter(function (f) { return f.mi === mi; });

        /* DRIP EDGE along each eave: a small folded angle sitting on the
           covering at the eave, running the full eave length (z). */
        fasc.forEach(function (f) {
          /* MONO drains one way: the drip edge belongs to the LOW eave. The
             copy on the HIGH fascia sat up at the top edge with nothing under
             it -- the flashing Marek saw hanging in the air above the roof. */
          if (STATE.roofType === 'mono' && !f.low) return;
          var sgf = (f.x < 0) ? -1 : 1;
          /* the drip edge sits on the EAVE PROFILE BOARD that caps the rafter
             tails (top of rafter = f.y + kTop), UNDER the covering -- the
             covering laps over it from above. Not on the covering surface. */
          /* A drip edge LIES ON THE DECK and reaches INWARD from the eave,
             with only its lip turning down outside the fascia. It used to sit
             20 mm proud of the rafter top and reach OUTWARD across the fascia
             board (which spans f.x .. f.x + 0.025) -- that is both the profile
             overlap at the eave edge and the flashing riding high (Marek). */
          var yBoard = (f.y + kTop) + 0.004;             /* on the rafter top, under the covering */
          var xEdgeF = f.x;                              /* the eave line itself */
          /* The drip follows the ROOF PLANE on both roof types. It used to be
             left flat on a gable, so the strip lay horizontal under a sloping
             roof and its outer edge stood proud -- the flashing reading high
             (Marek). The inner end rises toward the ridge, hence -sgf. */
          var dripTilt = -sgf * Math.atan(tanP || 0);
          var drip = new T.Mesh(new T.BoxGeometry(0.06, 0.008, rl + 0.04), metal);
          drip.position.set(xEdgeF - sgf * 0.03, yBoard - 0.004 + sgf * 0.03 * (tanP || 0), zc);   /* bottom face flush on the rafter top, at the sloped edge */
          drip.rotation.z = dripTilt;
          drip.userData.viz = 'layer_roof_flash';
          grp.add(drip);
          /* the downturned lip hugging the fascia face, hanging below the
             eave board (over the fascia), catching the drip */
          var lip = new T.Mesh(new T.BoxGeometry(0.006, 0.06, rl + 0.04), metal);
          lip.position.set(xEdgeF + sgf * 0.030, yBoard - 0.026, zc);   /* clear of the fascia face */
          lip.rotation.z = dripTilt;
          lip.userData.viz = 'layer_roof_flash';
          grp.add(lip);
        });

        if (STATE.roofType === 'mono') {
          /* GABLE (rake) flashing up the two sloping side edges: front and
             rear walls at z = +/-(L/2+ov). Each runs from the low eave to the
             high eave along x, tilted to the roof slope. */
          [-1, 1].forEach(function (sgz) {
            var zEdge = sgz * (L / 2 + (sgz < 0 ? ov.z0 : ov.z1));
            var xLo = -(W / 2 + exX0), xHi = (W / 2 + exX1);
            var yLo = (capT + kTop) + 0.061 - exX0 * tanP;           /* low eave, covering top */
            var yHi = (capT + kTop) + 0.061 + W * tanP + exX1 * tanP; /* high eave, covering top */
            var len = Math.hypot(xHi - xLo, yHi - yLo);
            var rot = Math.atan2(yHi - yLo, xHi - xLo);
            var rake = new T.Mesh(new T.BoxGeometry(len, 0.010, 0.06), metal);
            rake.position.set((xLo + xHi) / 2, (yLo + yHi) / 2 + 0.008, zEdge + sgz * 0.02);
            rake.rotation.z = rot;
            rake.userData.viz = 'layer_roof_flash';
            grp.add(rake);
            var lipG = new T.Mesh(new T.BoxGeometry(len, 0.045, 0.008), metal);
            lipG.position.set((xLo + xHi) / 2, (yLo + yHi) / 2 - 0.018, zEdge + sgz * 0.05);
            lipG.rotation.z = rot;
            lipG.userData.viz = 'layer_roof_flash';
            grp.add(lipG);
          });
        } else {
          /* GABLE flashing up all four rakes (both gable ends, both slopes) */
          /* the rake runs the FULL roof: from each side's own eave edge
             (W/2 + that side's overhang) up to the ridge. Per-side overhang so
             an asymmetric roof gets the right length on each side. It sits on
             the covering plane, on the gable edge board, under the covering. */
          [-1, 1].forEach(function (sgz) {
            var zEdge = sgz * (L / 2 + (sgz < 0 ? ov.z0 : ov.z1));
            [['x0', -1], ['x1', 1]].forEach(function (XE) {
              var ovx = (ov[XE[0]] || 0), sgx = XE[1];   /* SHELTER: rake at the fixed roof edge */
              var halfW = W / 2 + ovx;                                       /* THIS side's edge */
              var xEdge = sgx * halfW;
              /* the flashing caps the gable edge OVER the covering (covering
                 top = board + 0.061), sitting proud so it's visible, with a
                 downturn lip hanging over the edge. */
              var yOuter = (capT + kTop) + 0.061 + (W / 2 - halfW) * tanP;   /* covering top, low, at eave edge */
              var yRidge = (capT + kTop) + 0.061 + (W / 2) * tanP;           /* covering top, high, at ridge */
              var len = Math.hypot(xEdge - 0, yRidge - yOuter);
              var rot = Math.atan2(yRidge - yOuter, 0 - xEdge);
              /* the top cap plane, lying on the covering, proud of the edge */
              var rake = new T.Mesh(new T.BoxGeometry(len, 0.010, 0.06), metal);
              rake.position.set(xEdge / 2, (yOuter + yRidge) / 2 + 0.008, zEdge + sgz * 0.02);
              rake.rotation.z = rot;
              rake.userData.viz = 'layer_roof_flash';
              grp.add(rake);
              /* the downturn lip over the gable edge, hanging ~40mm */
              var lipG = new T.Mesh(new T.BoxGeometry(len, 0.045, 0.008), metal);
              lipG.position.set(xEdge / 2, (yOuter + yRidge) / 2 - 0.018, zEdge + sgz * 0.05);
              lipG.rotation.z = rot;
              lipG.userData.viz = 'layer_roof_flash';
              grp.add(lipG);
            });
          });
          /* RIDGE CAP over the apex + VENT STRIP beneath, running the ridge (z).
             ON A SHINGLE ROOF THE CAP IS A STRIP OF THE SAME ASPHALT, not a
             folded metal in the accessories colour: the vent strip beneath
             stays metal, the cap takes the covering's own material (Marek). */
          var capMat = (_covDef().key === 'shingle') ? this._roofTopMat() : metal;
          var yRid = (capT + kTop) + (W / 2) * tanP + 0.06;   /* over the covering at the ridge */
          var vent = new T.Mesh(new T.BoxGeometry(0.12, 0.02, rl + 0.02), metal);
          vent.position.set(0, yRid + 0.012, zc);
          vent.userData.viz = 'layer_roof_flash';
          grp.add(vent);
          /* the cap: two folded planes meeting at the ridge -- draw as a
             shallow inverted V from two tilted strips */
          [-1, 1].forEach(function (sgx) {
            var cap = new T.Mesh(new T.BoxGeometry(0.13, 0.010, rl + 0.02), capMat);
            cap.position.set(sgx * 0.05, yRid + 0.03, zc);
            cap.rotation.z = -sgx * ang;
            cap.userData.viz = 'layer_roof_flash';
            grp.add(cap);
          });
        }
      } catch (eF) {}
    },
    _buildOpenPosts: function (grp, mi, t) {
      /* OPEN-SIDE POSTS: a side with its fill turned off keeps its top beam
         (cap) but loses the studs, so the beam wants carrying. A 4x4 timber
         post drops from just under the cap to the bottom plate -- at a corner
         ONLY where the neighbour side is also open (otherwise the filled wall's
         corner stud is already there), plus intermediate stations along a long
         open run. The post stands on the same perimeter foundation the wall
         does (the open side keeps its bottom plate + bearers). */
      try {
        var T = global.THREE;
        if (!this.materials.timber) this.materials.timber = new T.MeshStandardMaterial({ color: 0xb59a63, roughness: 0.85, metalness: 0.02 });
        var POSTW = _postProfile().w;
        var SW = global.SaunaWalls.STUD;
        var yBot = PANEL_BASE + SW.w;
        var hSt = _wallClearH(t) - 2 * SW.w;
        if (hSt < 0.2) return;
        var yc = yBot + hSt / 2;
        var ir = _innerRect(t), hx = ir.hx, hz = ir.hz, inset = (INNER_MARGIN + PANEL_INSET) / 2;
        var wg = {
          rear:  { pz: -(hz + INNER_MARGIN) + inset, horiz: true },
          front: { pz: (hz + INNER_MARGIN) - inset, horiz: true },
          sideL: { px: -(hx + INNER_MARGIN) + inset, horiz: false },
          sideR: { px: (hx + INNER_MARGIN) - inset, horiz: false }
        };
        var self = this;
        if (!self.materials.galv) self.materials.galv = new T.MeshStandardMaterial({ color: 0x9a9ea3, roughness: 0.5, metalness: 0.55 });
        /* NO FLOOR on the shelter: every post is founded on its OWN point
           footing, straight from grade to the cap -- same footing kit the
           roof-overhang posts use (screw / pad / pier by STATE.foundation). */
        var groundY = -_foundLift();
        /* MONO: the roof climbs across the width, so each post climbs with it
           -- its own profile reaches all the way up to the rafter it carries
           (the same rise the wall studs use: (extW/2 + x) * tan). On a gable
           the cap is level and this is a no-op. */
        var _monoP = (STATE.roofType === 'mono');
        var _tanMoP = _monoP ? Math.tan(clamp(STATE.roofPitch || 0, 0, 45) * Math.PI / 180) : 0;
        var _hWp = _extW() / 2;
        var _beamH = _postProfile().w, _swW = global.SaunaWalls.STUD.w;
        function postTopAt(px) { return yBot + hSt + _swW - _beamH + (_monoP ? Math.max(0, (_hWp + px) * _tanMoP) : 0); }
        function socket(lx, lz, seatY) {
          var W2 = POSTW + 0.006, cheekH = 0.09, cheekT = 0.006;
          var base = new T.Mesh(new T.BoxGeometry(W2 + 0.02, 0.010, W2 + 0.02), self.materials.galv);
          base.position.set(lx, seatY - 0.005, lz); base.userData.viz = 'foundation'; grp.add(base);
          [-1, 1].forEach(function (sgc) {
            var ch = new T.Mesh(new T.BoxGeometry(cheekT, cheekH, W2), self.materials.galv);
            ch.position.set(lx + sgc * (W2 / 2 + cheekT / 2), seatY + cheekH / 2, lz);
            ch.userData.viz = 'foundation'; grp.add(ch);
          });
        }
        /* PAD BUILD-UP (Marek: model the whole footing like the fence does) --
           geotextile at the bottom of the pit, a macadam bed on it, the
           concrete pad cast on that, its head standing a little proud of grade
           so water runs off. PAD_* are the single source of truth: the BoQ
           volumes come from the same numbers. */
        function footing(lx, lz) {
          if (STATE.foundation === 'pads') {
            if (!self.materials.gravel) {
              /* MACADAM: a procedural pebble texture, otherwise a flat grey box
                 reads as more concrete (Marek). Crushed stone, not sand. */
              var gcv = document.createElement('canvas'); gcv.width = gcv.height = 128;
              var gx = gcv.getContext('2d');
              gx.fillStyle = '#3a3833'; gx.fillRect(0, 0, 128, 128);
              for (var gi9 = 0; gi9 < 260; gi9++) {
                var rr9 = 2 + Math.random() * 4;
                var sh9 = 58 + Math.floor(Math.random() * 46);
                gx.fillStyle = 'rgb(' + sh9 + ',' + (sh9 - 3) + ',' + (sh9 - 8) + ')';
                gx.beginPath();
                gx.ellipse(Math.random() * 128, Math.random() * 128, rr9, rr9 * (0.6 + Math.random() * 0.5), Math.random() * 3.14, 0, 6.283);
                gx.fill();
              }
              var gtex = new T.CanvasTexture(gcv);
              gtex.wrapS = gtex.wrapT = T.RepeatWrapping; gtex.repeat.set(3, 3);
              self.materials.gravel = new T.MeshStandardMaterial({ map: gtex, roughness: 1.0, metalness: 0.0 });
            }
            if (!self.materials.geotex) self.materials.geotex = new T.MeshStandardMaterial({ color: 0x2f3a3a, roughness: 0.95, metalness: 0.0 });
            /* the pad is CONCRETE -- materials.concrete is created by the cabin
               builder, which may not have run yet, and the old fallback was
               materials.timber: that is why the pad rendered timber brown. */
            if (!self.materials.concrete) self.materials.concrete = new T.MeshStandardMaterial({ color: 0xb6b2a8, roughness: 0.92, metalness: 0.02 });
            var padTopY = groundY + PAD_PROUD, padBotY = padTopY - PAD_H;
            var grvTopY = padBotY, grvBotY = grvTopY - PAD_GRAVEL_H;
            var geo = new T.Mesh(new T.BoxGeometry(PAD_GEO_W, 0.006, PAD_GEO_W), self.materials.geotex);
            geo.position.set(lx, grvBotY - 0.003, lz); geo.userData.viz = 'foundation'; grp.add(geo);
            var grv = new T.Mesh(new T.BoxGeometry(PAD_GRAVEL_W, PAD_GRAVEL_H, PAD_GRAVEL_W), self.materials.gravel);
            grv.position.set(lx, (grvTopY + grvBotY) / 2, lz); grv.userData.viz = 'foundation'; grp.add(grv);
            var pad = new T.Mesh(new T.BoxGeometry(PAD_W, PAD_H, PAD_W), self.materials.concrete);
            pad.position.set(lx, (padTopY + padBotY) / 2, lz); pad.userData.viz = 'foundation'; grp.add(pad);
            socket(lx, lz, padTopY);
            return padTopY;
          } else if (STATE.foundation === 'piers') {
            var pier = new T.Mesh(new T.BoxGeometry(0.19, 0.30, 0.19), self.materials.concrete || self.materials.timber);
            pier.position.set(lx, groundY - 0.15, lz); pier.userData.viz = 'foundation'; grp.add(pier);
            socket(lx, lz, groundY + 0.006);
            return groundY + 0.006;
          } else {
            var rad = 0.038, tipRad = rad * 0.22;
            var aboveLen = 0.02, belowLen = 1.0;
            var cylLen = aboveLen + belowLen * 0.42;
            var taperLen = belowLen * 0.58 - 0.06;
            var topY = groundY + 0.02;
            var shaftUp = new T.Mesh(new T.CylinderGeometry(rad, rad, topY + 0.02 - groundY, 12), self.materials.galv);
            shaftUp.position.set(lx, groundY + (topY + 0.02 - groundY) / 2, lz); shaftUp.userData.viz = 'foundation'; grp.add(shaftUp);
            var shaftDn = new T.Mesh(new T.CylinderGeometry(rad, rad, cylLen, 12), self.materials.galv);
            shaftDn.position.set(lx, groundY - cylLen / 2, lz); shaftDn.userData.viz = 'foundation'; grp.add(shaftDn);
            var taperTopY = groundY - cylLen;
            var taper = new T.Mesh(new T.CylinderGeometry(rad, tipRad, taperLen, 12), self.materials.galv);
            taper.position.set(lx, taperTopY - taperLen / 2, lz); taper.userData.viz = 'foundation'; grp.add(taper);
            var tip = new T.Mesh(new T.CylinderGeometry(tipRad, 0.002, 0.06, 10), self.materials.galv);
            tip.position.set(lx, taperTopY - taperLen - 0.03, lz); tip.userData.viz = 'foundation'; grp.add(tip);
            socket(lx, lz, groundY + 0.03);
            return groundY + 0.03;
          }
        }
        function post(px, pz) {
          /* The post STANDS ON THE U BASE. It used to start at a flat
             groundY + 0.11 while the seat is at +0.03 (screw) or on the pad
             head -- an 80 mm gap between the timber and the bottom of the
             holder (Marek). footing() now reports its seat. */
          var seatY = footing(px, pz);
          var h = postTopAt(px) - seatY;
          if (h < 0.2) return;
          var m = new T.Mesh(new T.BoxGeometry(POSTW, h, POSTW), self.materials.timber);
          m.position.set(px, seatY + h / 2, pz);
          m.userData.viz = 'wallframe'; m.userData.prof = 'post';
          grp.add(m);
        }
        /* KNEE BRACE along the wall line (dx or dz = +-1, the other 0): from D
           below the cap on the post, up to the cap D along the wall. 45 deg,
           same profile as the post. Never across the opening. */
        function brace(px, pz, dx, dz) {
          var braceTopY = postTopAt(px);   /* the brace meets the cap where THIS post does */
          var D = Math.min(0.6, Math.max(0.3, hSt * 0.5));
          if (D < 0.3) return;
          /* MITRED 45-deg knee brace: square section in the wall plane (dir dx/dz
             along the wall), post end cut VERTICAL, beam end cut HORIZONTAL
             (flush under the cap -> cut face parallel to the ground). Section
             normal = wall normal (-dz,0,dx), so no arbitrary roll. */
          var s2 = POSTW / Math.SQRT2;
          /* MONO: the cap this brace dies into is RAKED, so the top cut has to
             follow that rake, not a single level line -- k is the cap's rise
             per metre along THIS brace's direction. The 45-deg legs then meet
             the cap at t1 / t2 instead of at D +/- s2. k = 0 (level cap, or a
             brace running across the slope) gives the old horizontal cut. */
          var k = _monoP ? Math.max(-0.7, Math.min(0.7, _tanMoP * dx)) : 0;
          var t1 = (D + s2) / (1 - k), t2 = (D - s2) / (1 - k);
          var shp = new T.Shape();
          shp.moveTo(0, -s2); shp.lineTo(t1, D + k * t1); shp.lineTo(t2, D + k * t2); shp.lineTo(0, s2); shp.lineTo(0, -s2);
          var geo = new T.ExtrudeGeometry(shp, { depth: POSTW, bevelEnabled: false });
          geo.translate(0, 0, -POSTW / 2);
          var mtx = new T.Matrix4();
          mtx.makeBasis(new T.Vector3(dx, 0, dz), new T.Vector3(0, 1, 0), new T.Vector3(-dz, 0, dx));
          mtx.setPosition(px, braceTopY - D, pz);
          geo.applyMatrix4(mtx);
          var bm = new T.Mesh(geo, self.materials.timber);
          bm.userData.viz = 'wallframe'; bm.userData.prof = 'post';
          bm.userData.sect = [POSTW, POSTW];   /* brace = same profile as the post -> same section colour */
          grp.add(bm);
        }
        function open(w) { return !_sideFilled(w); }
        /* Braces are STRUCTURE: they stay on every side, clad or open (Marek:
           they must not disappear when a wall is switched on). The screen
           boards sit on the outer face, so a brace reads from inside. */
        post(wg.sideR.px, wg.front.pz);
        brace(wg.sideR.px, wg.front.pz, -1, 0);
        brace(wg.sideR.px, wg.front.pz, 0, -1);
        post(wg.sideL.px, wg.front.pz);
        brace(wg.sideL.px, wg.front.pz, 1, 0);
        brace(wg.sideL.px, wg.front.pz, 0, -1);
        post(wg.sideR.px, wg.rear.pz);
        brace(wg.sideR.px, wg.rear.pz, -1, 0);
        brace(wg.sideR.px, wg.rear.pz, 0, 1);
        post(wg.sideL.px, wg.rear.pz);
        brace(wg.sideL.px, wg.rear.pz, 1, 0);
        brace(wg.sideL.px, wg.rear.pz, 0, 1);
        function run(w, a0, a1) {
          var span = a1 - a0, isOpen = open(w);
          /* Post bays 2.5-4 m (max 4 m grid, Marek). Exception: an OPEN gable
             side (front/rear) is the drive-in end -- a single centre post and
             only over 6 m, so two cars still fit the bay. A FILLED side always
             follows the 4 m grid: the screen wants its posts. Braces only on
             open sides -- inside a clad screen they would pierce the boards. */
          var n;
          if (isOpen && (w === 'front' || w === 'rear')) n = (Math.abs(span) > 6.0 ? 1 : 0);
          else n = Math.max(0, Math.ceil(Math.abs(span) / 4.0) - 1);
          for (var k = 1; k <= n; k++) {
            var v = a0 + span * k / (n + 1);
            if (wg[w].horiz) { post(v, wg[w].pz); brace(v, wg[w].pz, 1, 0); brace(v, wg[w].pz, -1, 0); }
            else { post(wg[w].px, v); brace(wg[w].px, v, 0, 1); brace(wg[w].px, v, 0, -1); }
          }
        }
        run('front', wg.sideL.px, wg.sideR.px);
        run('rear', wg.sideL.px, wg.sideR.px);
        run('sideL', wg.rear.pz, wg.front.pz);
        run('sideR', wg.rear.pz, wg.front.pz);
      } catch (e) {}
    },
    _buildScreens: function (grp, mi, t) {
      /* SHELTER FILLED SIDE = a CLADDING SCREEN between the posts, never a
         stud-wall sandwich. Bottom rail (KVH 80x60) ~50 mm above grade;
         VERTICAL boards ride horizontal girts (max 0.75 m apart), HORIZONTAL
         boards ride vertical KVH 80x60 nailers (each post bay split to max
         1.25 m). Boards run grade to cap and reuse the boards/slats fill +
         gap the wall fill already had. */
      try {
        var T = global.THREE;
        if (!this.materials.timber) this.materials.timber = new T.MeshStandardMaterial({ color: 0xb59a63, roughness: 0.85, metalness: 0.02 });
        /* THE CLADDING SLOT. This read COLOR_VIEWS straight, past _cv(), so
           the Cladding row painted the sample and never the screens (Marek). */
        var facCol = _cv().facade;
        if (!this._scrMats) this._scrMats = {};
        if (!this._scrMats[facCol]) this._scrMats[facCol] = new T.MeshStandardMaterial({ color: facCol, roughness: 0.8, metalness: 0.02 });
        var bMat = this._scrMats[facCol];
        var SW = global.SaunaWalls.STUD;
        var POSTW = _postProfile().w;
        var ir = _innerRect(t), hx = ir.hx, hz = ir.hz, inset = (INNER_MARGIN + PANEL_INSET) / 2;
        var groundY = -_foundLift();
        var yBot = PANEL_BASE + SW.w;
        var hSt = _wallClearH(t) - 2 * SW.w;
        if (hSt < 0.2) return;
        var capUnder = yBot + hSt;
        var yRail = groundY + 0.05;
        var RH = 0.06, RD = 0.08;   /* KVH08060: 60 high x 80 deep */
        var _slats = (STATE.fillType === 'slats');
        var gap = _slats ? Math.max(0, Math.min(0.3, (STATE.slatGap != null ? STATE.slatGap : 0.04))) : 0.003;
        var PL = _slats ? 0.06 : (_cv().boardW || 0.19);   /* the board width the base view carries */
        var THK = 0.02;
        var vert = (STATE.facadePattern !== 'wood2');   /* wood1 = vertical boards */
        var self = this;
        var wg = {
          rear:  { c: -(hz + INNER_MARGIN) + inset, horiz: true },
          front: { c: (hz + INNER_MARGIN) - inset, horiz: true },
          sideL: { c: -(hx + INNER_MARGIN) + inset, horiz: false },
          sideR: { c: (hx + INNER_MARGIN) - inset, horiz: false }
        };
        ['front', 'rear', 'sideL', 'sideR'].forEach(function (w) {
          if (!_sideFilled(w)) return;
          var G = wg[w], horiz = G.horiz;
          var a0 = horiz ? (-(hx + INNER_MARGIN) + inset) : (-(hz + INNER_MARGIN) + inset);
          var a1 = -a0;
          var span = a1 - a0;
          var out = G.c > 0 ? 1 : -1;
          function box(u0, u1, y0, y1, depth, off, mat, viz) {
            var len = u1 - u0, h = y1 - y0; if (len <= 0.005 || h <= 0.005) return;
            var g3 = horiz ? new T.BoxGeometry(len, h, depth) : new T.BoxGeometry(depth, h, len);
            var m = new T.Mesh(g3, mat);
            var uc = (u0 + u1) / 2, cN = G.c + out * off;
            m.position.set(horiz ? uc : cN, (y0 + y1) / 2, horiz ? cN : uc);
            m.userData.viz = viz; grp.add(m);
          }
          /* KROK 3 (Marek): a filled GABLE wall is clad to the RIDGE. The
             screen boards continue above the cap, sawn to the rafter TOP
             plane (sharp tip, boards tuck behind the barge), same rhythm and
             gap as below -- front/rear only, gable roof only. */
          var gable = horiz && (STATE.roofType === 'gable');
          var gTan = Math.tan(clamp(STATE.roofPitch || 0, 0, 45) * Math.PI / 180) || 0.0001;
          var GLAP2 = ((global.SaunaRoofFrame && global.SaunaRoofFrame.RAFTER) ? global.SaunaRoofFrame.RAFTER.h : 0.16) / Math.cos(Math.atan(gTan));
          var capR = t.H - 0.09 - 0.001;
          var hWex = _extW() / 2;
          var apexY = capR + hWex * gTan + GLAP2;
          function roofY2(x) { return capR + (hWex - Math.abs(x)) * gTan + GLAP2; }
          function xAtY2(y) { return Math.max(0, Math.min(a1, hWex - (y - capR - GLAP2) / gTan)); }
          function sbox(u0, u1, y0, yT0, yT1) {
            if (u1 - u0 < 0.004) return;
            var t0 = Math.max(y0 + 0.002, yT0), t1 = Math.max(y0 + 0.002, yT1);
            if (t0 - y0 < 0.004 && t1 - y0 < 0.004) return;
            var shp = new T.Shape();
            shp.moveTo(u0, y0); shp.lineTo(u1, y0); shp.lineTo(u1, t1); shp.lineTo(u0, t0); shp.lineTo(u0, y0);
            var g3 = new T.ExtrudeGeometry(shp, { depth: THK, bevelEnabled: false });
            g3.translate(0, 0, -THK / 2);
            var m = new T.Mesh(g3, bMat);
            m.position.set(0, 0, G.c + out * off);
            m.userData.viz = 'layer_wall_clad';
            grp.add(m);
          }
          /* MONO: the screen top follows the same climbing cap the posts now
             reach -- raked on front/rear, lifted (level) on the sides. */
          var monoR = (STATE.roofType === 'mono');
          var tanMo = monoR ? Math.tan(clamp(STATE.roofPitch || 0, 0, 45) * Math.PI / 180) : 0;
          var monoRake = monoR && horiz;
          function capAtU(u) { return capUnder + (monoR ? Math.max(0, (hWex + (horiz ? u : G.c)) * tanMo) : 0); }
          var capW = capAtU(0);
          /* The boards must not stop under the cap: they close over the cap AND
             the rafter sitting on it, tucking under the covering (Marek: the
             rafter must be covered). GLAP2 is the rafter's vertical depth. */
          var capRaf = SW.w + GLAP2;
          function topAtU(u) { return capAtU(u) + capRaf; }
          function invTop(y) { return (y - capRaf - capUnder) / (tanMo || 1) - hWex; }
          /* post stations: same 4 m grid the posts use on a filled side */
          var nP = Math.max(0, Math.ceil(Math.abs(span) / 4.0) - 1);
          var st = [a0]; for (var k = 1; k <= nP; k++) st.push(a0 + span * k / (nP + 1)); st.push(a1);
          /* bottom rail, full span, on the wall line */
          box(a0, a1, yRail, yRail + RH, RD, 0, self.materials.timber, 'wallframe');
          var off = POSTW / 2 + THK / 2 + 0.002;
          if (vert) {
            /* GIRTS all the way up: they stopped at the cap (three of them off
               the ground) while the boards now run on over the cap and rafter,
               higher still on a raked or gable wall. Regular ~0.75 m spacing to
               the real top, each girt trimmed where the roof line crosses it. */
            var yBaseG = yRail + RH;
            var yTopG = gable ? apexY : (monoRake ? topAtU(a1) : (capW + capRaf));
            var Hg = Math.max(0.1, yTopG - yBaseG);
            var nG = Math.max(1, Math.round(Hg / 0.75));
            for (var gi = 1; gi < nG; gi++) {
              var gy = yBaseG + Hg * gi / nG;
              var gu0 = a0, gu1 = a1;
              if (monoRake) gu0 = Math.max(a0, invTop(gy + RH / 2));
              else if (gable) { var gxT = xAtY2(gy + RH / 2); gu0 = -gxT; gu1 = gxT; }
              if (gu1 - gu0 > 0.05) box(gu0, gu1, gy - RH / 2, gy + RH / 2, RD, 0, self.materials.timber, 'wallframe');
            }
            for (var u = a0; u < a1 - 0.01; u += PL + gap) {
              var u1c = Math.min(a1, u + PL);
              if (gable) {
                /* ONE piece ground to roof (Marek's rule), top sawn to the slope;
                   the apex-straddling board splits at x=0 so the tip stays sharp */
                if (u < 0 && u1c > 0) { sbox(u, 0, yRail, roofY2(u), apexY); sbox(0, u1c, yRail, apexY, roofY2(u1c)); }
                else sbox(u, u1c, yRail, roofY2(u), roofY2(u1c));
              } else if (monoRake) {
                sbox(u, u1c, yRail, topAtU(u), topAtU(u1c));
              } else {
                box(u, u1c, yRail, capW + capRaf, THK, off, bMat, 'layer_wall_clad');
              }
            }
          } else {
            for (var b = 0; b < st.length - 1; b++) {
              var b0 = st[b], b1 = st[b + 1], bl = b1 - b0;
              var nn = Math.max(0, Math.ceil(bl / 1.25) - 1);
              for (var q = 1; q <= nn; q++) {
                var ux = b0 + bl * q / (nn + 1);
                box(ux - 0.03, ux + 0.03, yRail + RH, (gable ? roofY2(ux) : topAtU(ux)), RD, 0, self.materials.timber, 'wallframe');
              }
            }
            if (gable) {
              /* ABOVE EACH POST the boards have nothing to fix to once the wall
                 stops -- the post ends at the cap. So every post station gets
                 its own vertical nailer carrying on up the triangle, the centre
                 post included (Marek: chybi kolma vzpera nad stredovym sloupem). */
              for (var sv = 0; sv < st.length; sv++) {
                var uv = st[sv], yv = roofY2(uv);
                if (yv > capUnder + 0.05 && Math.abs(uv) < a1 - 0.02) box(uv - 0.03, uv + 0.03, capUnder, yv, RD, 0, self.materials.timber, 'wallframe');
              }
            }
            var yTop2 = gable ? apexY : (monoRake ? topAtU(a1) : (capW + capRaf));
            for (var y = yRail; y < yTop2 - 0.01; y += PL + gap) {
              var y1c = Math.min(yTop2, y + PL);
              if (monoRake) {
                /* raked top: a full course where the roof already runs above it,
                   a sawn wedge where the roof line crosses this course */
                var uS = Math.max(a0, Math.min(a1, invTop(y)));
                var uF = Math.max(a0, Math.min(a1, invTop(y1c)));
                if (uF > uS + 0.004) sbox(uS, uF, y, Math.max(y + 0.002, topAtU(uS)), y1c);
                if (a1 - uF > 0.004) box(uF, a1, y, y1c, THK, off, bMat, 'layer_wall_clad');
                continue;
              }
              var xT2 = gable ? xAtY2(y1c) : a1;
              var xB2 = gable ? xAtY2(y) : a1;
              if (xB2 < 0.02) continue;
              if (xT2 > 0.02) box(-xT2, xT2, y, y1c, THK, off, bMat, 'layer_wall_clad');
              if (gable && xB2 - xT2 > 0.006) {
                sbox(xT2, xB2, y, y1c, Math.max(y + 0.002, roofY2(xB2)));
                sbox(-xB2, -xT2, y, Math.max(y + 0.002, roofY2(xB2)), y1c);
              }
            }
          }
        });
      } catch (eSC) {}
    },
    _buildGutters: function (grp, mi, t) {
      /* STEP 2 IN 3D: gutters ride the eaves, downspouts drop to the ground.
         Auto by pitch, matching the BoQ: mono drains its LOW eave (min x),
         gable drains both long eaves (+-x). A gutter is a half-round channel
         (drawn as a shallow open trough), brackets under it @600, a hopper at
         one end, then a round downspout with a top offset elbow clearing the
         overhang and an outlet elbow at the base. */
      try {
        var T = global.THREE;
        var pitch = clamp(STATE.roofPitch || 0, 0, 45);
        if (pitch < 0.5) return;
        var ov = STATE.roofOver || { x0: 0.18, x1: 0.18, z0: 0.18, z1: 0.18 };
        var W = _extW(), L = t.L, tanP = Math.tan(pitch * Math.PI / 180);
        var GUT = 0.063;                 /* half of o125 */
        var metal = this._metalMat(1.0);
        var self = this;
        var kUnd = 0.16 / Math.cos(Math.atan(tanP || 0.0001));   /* rafter depth along vertical */
        var wallFaceX = W / 2 + 0.108 + 0.02;   /* outboard cladding face, the downspout hugs this */
        function gutterRun(xEave, yEave, zc, zlen, wallSign, targetZ, targetX, onPost) {
          /* the trough: an open half-pipe approximated by a thin box channel
             just outside the fascia, its top at the eave line minus a touch */
          /* B2: the gutter face is the FULL height of the cover board -- 140
             mm for now (Marek will refine). Top at the eave line, hanging
             down its own height. */
          var GUT_H = 0.14;
          var GUT_W = 0.11;
          var sgE = (xEave < 0) ? -1 : 1;
          /* ONE eave-metal line, shared by the gutter, the hopper and the
             downspout so they never disconnect. The trough hangs just OUTBOARD
             of the covering drip edge (the covering is ~20mm proud of the
             tail, so 40mm out clears it at any overhang without floating off
             on a long roof) and just BELOW the eave line so the covering sheds
             in from above. */
          /* the fascia BOARD closing the rafter tails has its outer face at
             the eave edge (xEave). The gutter hangs BESIDE it -- its inboard
             face just outboard of the board's outer face -- and BELOW the
             eave line so its top never rises into the rafter/covering zone
             (the collision at minimum overhang: the roof stops moving at
             OVER_LIM.min but the gutter kept tracking f.x). Its top sits a
             touch under the eave line; the body hangs down from there. */
          var boardOuter = 0.025;                             /* fascia board thickness */
          var xTrough = xEave + sgE * (boardOuter + GUT_W / 2 + 0.010);   /* fully beside the board */
          var yTrough = yEave + 0.09;                         /* raised to sit just under the drip edge */
          var xBody = xTrough;
          var yTop = yTrough;
          var body = new T.Mesh(new T.BoxGeometry(GUT_W, GUT_H, zlen), metal);
          body.position.set(xBody, yTop - GUT_H / 2, zc);
          body.userData.viz = 'layer_roof_gutter';
          grp.add(body);
          /* brackets @600 */
          var nB = Math.max(2, Math.ceil(zlen / 0.6));
          for (var b = 0; b <= nB; b++) {
            var zb = zc - zlen / 2 + zlen * (b / nB);
            var brk = new T.Mesh(new T.BoxGeometry(0.13, 0.012, 0.03), metal);
            brk.position.set(xBody, yTop - 0.02, zb);
            brk.userData.viz = 'layer_roof_gutter';
            grp.add(brk);
          }
          /* end caps */
          [zc - zlen / 2, zc + zlen / 2].forEach(function (ze) {
            var cap = new T.Mesh(new T.BoxGeometry(GUT_W, GUT_H, 0.006), metal);
            cap.position.set(xBody, yTop - GUT_H / 2, ze);
            cap.userData.viz = 'layer_roof_gutter';
            grp.add(cap);
          });
          /* THE DOWNSPOUT: a 5 cm standoff from the cladding, ROUNDED
             elbows (not sharp), the classic swan-neck off the hopper -- a
             little drop, a curved 45 in toward the wall, a curved 45 back to
             vertical -- then straight down to the ground on brackets. If the
             overhang is deep (> 0.5 m) the offset cannot reach the wall
             sanely, so the pipe simply drops straight from the hopper (a
             support post will carry it -- next step). */
          var R = 0.04;
          var standoff = 0.05;                              /* gap wall<->pipe */
          var xg = xTrough;                                /* hopper directly under the gutter, joined */
          var zd = zc + zlen / 2 - 0.12;
          var xpipe = wallSign * (wallFaceX + standoff);    /* 5 cm off the cladding */
          var groundY = -0.35;
          var deepOverhang = (Math.abs(xg) - Math.abs(xpipe)) > OVERHANG_POST;
          var hop = new T.Mesh(new T.BoxGeometry(0.09, 0.05, 0.09), metal);
          hop.position.set(xg, yEave - 0.09, zd);
          hop.userData.viz = 'layer_roof_gutter';
          grp.add(hop);
          /* a rounded elbow = a torus quarter; approximate with a short
             low-segment torus arc oriented in the x-y plane at z=zd */
          function elbowArc(cx, cy, a0, a1, tubeR) {
            var arc = new T.Mesh(new T.TorusGeometry(0.05, tubeR, 8, 8, Math.abs(a1 - a0)), metal);
            arc.position.set(cx, cy, zd);
            arc.rotation.z = a0;
            arc.userData.viz = 'layer_roof_gutter';
            grp.add(arc);
          }
          function joint(x, y) {
            var b = new T.Mesh(new T.SphereGeometry(R * 1.05, 8, 8), metal);
            b.position.set(x, y, zd);
            b.userData.viz = 'layer_roof_gutter';
            grp.add(b);
          }
          function joint3(x, y, z) {
            var b = new T.Mesh(new T.SphereGeometry(R * 1.05, 8, 8), metal);
            b.position.set(x, y, z);
            b.userData.viz = 'layer_roof_gutter';
            grp.add(b);
          }
          function seg(x0, y0, x1, y1) {
            var dx = x1 - x0, dy = y1 - y0, len = Math.hypot(dx, dy);
            if (len < 0.005) return;
            var m = new T.Mesh(new T.CylinderGeometry(R, R, len, 12), metal);
            m.position.set((x0 + x1) / 2, (y0 + y1) / 2, zd);
            m.rotation.z = Math.atan2(dy, dx) - Math.PI / 2;
            m.userData.viz = 'layer_roof_gutter';
            grp.add(m);
          }
          function vpipe3(x, y0, y1, z) {
            var m = new T.Mesh(new T.CylinderGeometry(R, R, y1 - y0, 12), metal);
            m.position.set(x, (y0 + y1) / 2, z);
            m.userData.viz = 'layer_roof_gutter';
            grp.add(m);
            var nPB = Math.max(1, Math.ceil((y1 - y0) / 1.5));
            var brDepth = onPost ? (0.045 + R + 0.003) : standoff;   /* strap wraps to the post face */
            for (var pb = 1; pb <= nPB; pb++) {
              var brP = new T.Mesh(new T.BoxGeometry(brDepth, 0.02, 0.02), metal);
              brP.position.set(x - wallSign * (brDepth / 2 + R), y0 + (y1 - y0) * (pb / (nPB + 1)), z);
              brP.userData.viz = 'layer_roof_gutter';
              grp.add(brP);
            }
          }
          function vpipe(x, y0, y1) {
            var m = new T.Mesh(new T.CylinderGeometry(R, R, y1 - y0, 12), metal);
            m.position.set(x, (y0 + y1) / 2, zd);
            m.userData.viz = 'layer_roof_gutter';
            grp.add(m);
            var nPB = Math.max(1, Math.ceil((y1 - y0) / 1.5));
            for (var pb = 1; pb <= nPB; pb++) {
              var brP = new T.Mesh(new T.BoxGeometry(standoff, 0.02, 0.02), metal);
              brP.position.set(x - wallSign * (standoff / 2 + R), y0 + (y1 - y0) * (pb / (nPB + 1)), zd);
              brP.userData.viz = 'layer_roof_gutter';
              grp.add(brP);
            }
          }
          /* B-fix: when the roof projects in BOTH directions the eave has no
             wall behind it -- the downspout must travel to the nearest hard
             point: the building CORNER, or a support POST if one stands
             there. targetZ/targetX say where; the pipe leans to it. */
          var tZ = (typeof targetZ === 'number') ? targetZ : zd;
          var tX = (typeof targetX === 'number') ? targetX : xpipe;
          function seg3(x0, y0, z0, x1, y1, z1) {
            var dx = x1 - x0, dy = y1 - y0, dz = z1 - z0, len = Math.hypot(dx, dy, dz);
            if (len < 0.005) return;
            var m = new T.Mesh(new T.CylinderGeometry(R, R, len, 12), metal);
            m.position.set((x0 + x1) / 2, (y0 + y1) / 2, (z0 + z1) / 2);
            /* orient the cylinder (default axis +Y) along (dx,dy,dz) */
            var up = new T.Vector3(0, 1, 0), dir = new T.Vector3(dx, dy, dz).normalize();
            var q = new T.Quaternion().setFromUnitVectors(up, dir);
            m.quaternion.copy(q);
            m.userData.viz = 'layer_roof_gutter';
            grp.add(m);
          }
          /* CLEAN SWAN NECK: stub off the hopper, a short drop, ONE straight
             45-degree limb reaching (tX,tZ) -- the limb's vertical fall
             equals its true horizontal travel, so it is a real 45 -- then a
             joint and a plain vertical straight down to the ground. tX/tZ is
             the post face (hug it) or the building corner. */
          /* SWAN NECK WITH REAL BENDS. The run used to be mitre-cut straights
             meeting at a point, which reads as a kink rather than a fitting.
             A downspout is made of pipe and ELBOWS, so this walks the actual
             path: a short drop off the hopper, a rounded 45 into the lean, a
             straight limb, a rounded 45 back to vertical, the drop, and an
             outlet elbow kicking clear at the base. The bends are swept as
             short chords -- at 90 mm pipe and a 120 mm radius that reads as a
             curve and costs nothing. */
          var BR = 0.12;                                  /* bend radius */
          var horiz = Math.hypot(tX - xg, tZ - zd);       /* horizontal run to the target */
          var uX = horiz > 1e-4 ? (tX - xg) / horiz : 0;  /* unit direction toward it */
          var uZ = horiz > 1e-4 ? (tZ - zd) / horiz : 0;
          var DOWN = [0, -1, 0], LEAN = [uX * 0.70711, -0.70711, uZ * 0.70711];
          function bend(p, d0, d1, r) {
            var dot = d0[0] * d1[0] + d0[1] * d1[1] + d0[2] * d1[2];
            dot = Math.max(-1, Math.min(1, dot));
            var ang = Math.acos(dot);
            if (ang < 0.02) return p;
            var nx = d1[0] - dot * d0[0], ny = d1[1] - dot * d0[1], nz = d1[2] - dot * d0[2];
            var nl = Math.hypot(nx, ny, nz) || 1;
            nx /= nl; ny /= nl; nz /= nl;
            var cx = p[0] + r * nx, cy = p[1] + r * ny, cz = p[2] + r * nz;
            var N = 5, prev = p;
            for (var bi = 1; bi <= N; bi++) {
              var a = ang * bi / N, ca = Math.cos(a), sa = Math.sin(a);
              var q = [cx + r * (-ca * nx + sa * d0[0]),
                       cy + r * (-ca * ny + sa * d0[1]),
                       cz + r * (-ca * nz + sa * d0[2])];
              seg3(prev[0], prev[1], prev[2], q[0], q[1], q[2]);
              prev = q;
            }
            return prev;
          }
          /* THE PIPE STARTS UNDER THE HOPPER. It used to be struck from the
             gutter line, so the first bend sat up at the eave with the hopper
             beside it rather than above it (Marek). The hopper body is 50 mm
             deep, centred 90 mm below the eave, so its underside is the real
             head of the downpipe: 50 mm of straight pipe from there, and only
             then the bend. */
          var yHopBot = yEave - 0.09 - 0.025;
          if (horiz < 0.10) {
            /* the outlet already stands over its target -- a swan neck here
               would be a kink for its own sake, so the pipe simply drops */
            vpipe3(xg, groundY + 0.12, yHopBot, zd);
            var pe0 = bend([xg, groundY + 0.12, zd], DOWN, [wallSign, -0.70711, 0], BR * 0.6);
            seg3(pe0[0], pe0[1], pe0[2], pe0[0] + wallSign * 0.05, pe0[1] - 0.03, pe0[2]);
            return;
          }
          var yB = yHopBot - 0.05;                        /* 50 mm of straight before the first bend */
          seg3(xg, yHopBot, zd, xg, yB, zd);
          var p1 = bend([xg, yB, zd], DOWN, LEAN, BR);    /* rounded 45 into the lean */
          /* the straight limb carries whatever horizontal the two bends do not */
          var hBends = 2 * BR * (1 - 0.70711);
          var runL = Math.max(0.02, (horiz - hBends) / 0.70711);
          var p2 = [p1[0] + LEAN[0] * runL, p1[1] + LEAN[1] * runL, p1[2] + LEAN[2] * runL];
          seg3(p1[0], p1[1], p1[2], p2[0], p2[1], p2[2]);
          var p3 = bend(p2, LEAN, DOWN, BR);              /* rounded 45 back to vertical */
          vpipe3(p3[0], groundY + 0.12, p3[1], p3[2]);    /* straight down at post or wall */
          var p4 = bend([p3[0], groundY + 0.12, p3[2]], DOWN, [wallSign, -0.70711, 0], BR * 0.6);
          seg3(p4[0], p4[1], p4[2], p4[0] + wallSign * 0.05, p4[1] - 0.03, p4[2]);   /* outlet elbow kick */
        }
        var zc = (ov.z1 - ov.z0) / 2;
        var zlen = L + ov.z0 + ov.z1 - 0.04;
        /* HOLD THE EAVE LINE: read the fascia entries the roof layout
           publishes -- their x is the eave, their y is the seat there -- and
           hang the gutter at (fascia.y - rafter depth). No local arithmetic
           that could drift when the pitch or overhang changes. Mono drains
           only its LOW eave; gable drains both. */
        var layG = (global.SaunaRoofFrame && global.SaunaRoofFrame.layout) ? global.SaunaRoofFrame.layout() : { fascias: [] };
        var fasc = (layG.fascias || []).filter(function (f) { return f.mi === mi; });
        function eaveUnder(f) { return f.y - 0.005; }   /* the EAVE LINE itself; gutter TOP hangs here, at the covering, holding the edge */
        /* the corner/post the downspout should reach: nearest building
           corner in z ( +/- L/2 ), pulled to a post's x if the side eave is
           post-deep (a post stands inboard by INSET at that eave). */
        var cornerZ = L / 2 - 0.05;                 /* +z building corner, just inside */
        function targetFor(fx) {
          var sgx = (fx < 0) ? -1 : 1;
          /* SHELTER: there are no overhang posts and often no wall either --
             the downspout belongs on the MAIN structural corner post, hugging
             its outer face (Marek: okapy maji byt u hlavnich nosnych sloupu). */
          if (SHELTER_FIXED_ROOF) {
            var irG = _innerRect(t), insG = (INNER_MARGIN + PANEL_INSET) / 2;
            var pwG = _postProfile().w;
            var pXg = sgx * ((irG.hx + INNER_MARGIN) - insG);
            var pZg = (irG.hz + INNER_MARGIN) - insG;
            return { z: pZg, x: pXg + sgx * (pwG / 2 + 0.045 + 0.003), onPost: true };
          }
          /* is there a POST on this side? one exists if the SIDE eave is
             post-deep (A2) -- a post at x = fx - sgx*INSET, at each gable end
             -- OR if a GABLE overhang is post-deep (A3) and this corner is
             carried. Either way, if a post stands near this downspout's z
             (the +z drop end), hug it. */
          var sideDeep = Math.abs(fx) - (W / 2) > OVERHANG_POST;
          var gablePosDeep = (ov.z1 || 0) > OVERHANG_POST;
          if (sideDeep) {
            /* A2 post at this eave, at the +z gable end */
            var postX = fx - sgx * 0.15;
            var pipeX = postX + sgx * (0.045 + 0.04 + 0.003);   /* against its outer face */
            var postZ = (L / 2 + (ov.z1 || 0)) - 0.15;
            return { z: postZ, x: pipeX, onPost: true };
          }
          if (gablePosDeep) {
            /* A3 gable post stands UNDER THE POZEDNICE at the wall line
               x = +/-(W/2 - INSET) (not out at the eave). The downspout runs
               down that post; the gutter's elbow reaches inboard from the
               eave to meet it. Pipe against the post's OUTER (eave-facing)
               face. */
            var gpX = sgx * (W / 2 - (0.095 + 0.055) / 2);  /* the pozednice axis = post axis (2.025), was W/2-0.15 -> crossed the post */
            var gpPipeX = gpX + sgx * (0.045 + 0.04 + 0.003);
            var gpZ = (L / 2 + (ov.z1 || 0)) - 0.15;        /* the +z post, inboard of gable edge */
            return { z: gpZ, x: gpPipeX, onPost: true };
          }
          /* no post: HUG THE WALL. The gutter holds the eave out at fx; the
             elbow bridges inboard from the eave to the cladding, and the pipe
             runs straight down the wall face (5 cm off). Whatever the
             overhang, the pipe lands at the wall -- the elbow just gets longer
             as the eave moves out. */
          var wallX = wallFaceX * sgx + sgx * 0.05;        /* wall face + 5cm */
          return { z: cornerZ, x: wallX, onPost: false, toWall: true };
        }
        if (STATE.roofType === 'mono') {
          for (var fi = 0; fi < fasc.length; fi++) {
            if (fasc[fi].low) { var tg = targetFor(fasc[fi].x); gutterRun(fasc[fi].x, eaveUnder(fasc[fi]), zc, zlen, (fasc[fi].x < 0 ? -1 : 1), tg.z, tg.x, tg.onPost); }
          }
        } else {
          for (var fj = 0; fj < fasc.length; fj++) {
            var tg2 = targetFor(fasc[fj].x); gutterRun(fasc[fj].x, eaveUnder(fasc[fj]), zc, zlen, (fasc[fj].x < 0 ? -1 : 1), tg2.z, tg2.x, tg2.onPost); }
        }
      } catch (eG) {}
    },
    _facMat: function () {
      var cv = _cv(), k = 'fac_' + _hex6(cv.facade);   /* keyed on the colour it shows, not the scheme index */
      if (!this.materials[k]) this.materials[k] = new global.THREE.MeshStandardMaterial({ color: cv.facade, roughness: 0.82, metalness: 0.03 });
      return this.materials[k];
    },
    /* THE ACCESSORIES SLOT: gutters, drip edges, flashings, all one metal in
       the colour the buyer chose, cached per colour like the timber */
    _metalMat: function (f) {
      var T = global.THREE, hex = _cv().metal, k = 'metal_' + hex + '_' + f;
      if (!this.materials[k]) {
        var r = ((hex >> 16) & 255) * f, gg = ((hex >> 8) & 255) * f, b = (hex & 255) * f;
        var col = (Math.round(r) << 16) | (Math.round(gg) << 8) | Math.round(b);
        this.materials[k] = new T.MeshStandardMaterial({ color: col, roughness: 0.45, metalness: 0.6 });
      }
      return this.materials[k];
    },
    _roofTopMat: function () {
      var T = global.THREE, cv = _cv(), cov = _covDef(), k = 'roofTop_' + cov.key + '_' + _hex6(cv.roof);
      if (!this.materials[k]) {
        var opt = { color: cv.roof, roughness: 0.55, metalness: 0.25 }, tex = null;
        /* the covering decides the face: shingle keeps its procedural courses,
           the two metals get their ribs -- wide and shallow for a trapezoidal
           sheet, narrow and sharp for a standing seam */
        /* BACK TO THE FIRST SETTING (Marek: ted je to extremni, to bylo lepsi).
           Deepening the fold made the sheet look corrugated rather than
           profiled; these are the widths and the contrast it had before. */
        if (cov.key === 'shingle')     tex = this._shingleTex(cv.roof);
        else if (cov.key === 'trapez') tex = this._ribTex(cv.roof, 0.21, 0.36);
        else if (cov.key === 'seam')   tex = this._ribTex(cv.roof, 0.50, 0.06);
        if (tex) {
          opt.map = tex; opt.color = 0xffffff;
          if (cov.key === 'shingle') { opt.roughness = 0.78; opt.metalness = 0.06; }
        }
        this.materials[k] = new T.MeshStandardMaterial(opt);
      }
      return this.materials[k];
    },
    _ribTex: function (baseHex, pitch, width) {
      /* RIBS as a procedural canvas texture: a rib every pitch of the tile
         (as a fraction of its width), width wide, lit on one flank and
         shaded on the other so it reads as a fold and not as a stripe. */
      try {
        var doc = global.document; if (!doc || !doc.createElement) return null;
        var T = global.THREE;
        var cnv = doc.createElement('canvas'); cnv.width = 256; cnv.height = 256;
        var g = cnv.getContext('2d'); if (!g) return null;
        function sh(hex, ff) {
          var r = (hex >> 16) & 255, gg = (hex >> 8) & 255, b = hex & 255;
          return 'rgb(' + Math.min(255, Math.round(r * ff)) + ',' + Math.min(255, Math.round(gg * ff)) + ',' + Math.min(255, Math.round(b * ff)) + ')';
        }
        g.fillStyle = sh(baseHex, 1.0); g.fillRect(0, 0, 256, 256);
        /* ALONG CANVAS Y, NOT X. The first cut drew the ribs as vertical
           stripes and they came out running ACROSS the slope, parallel to
           the eave -- a corrugation laid sideways. The shingle texture's
           courses vary in y and read correctly, so the slope runs along y in
           this mapping, and a rib that follows the slope is a stripe of
           constant x... which is what came out wrong. The mapping is the
           other way round from what the shingle suggests; measured on the
           roof, not reasoned (Marek). */
        var step = Math.max(8, Math.round(256 * pitch)), w = Math.max(3, Math.round(256 * pitch * width)), y;
        for (y = 0; y < 256; y += step) {
          g.fillStyle = sh(baseHex, 1.18); g.fillRect(0, y, 256, Math.ceil(w / 2));
          g.fillStyle = sh(baseHex, 0.78); g.fillRect(0, y + Math.ceil(w / 2), 256, Math.ceil(w / 2));
        }
        var tex = new T.CanvasTexture(cnv);
        tex.wrapS = tex.wrapT = T.RepeatWrapping;
        tex.repeat.set(3, 3);
        if (T.SRGBColorSpace) tex.colorSpace = T.SRGBColorSpace;
        return tex;
      } catch (e) { return null; }
    },
    _shingleTex: function (baseHex) {
      /* SHINGLE look as a procedural canvas texture on the covering: larger
         rectangles behind (course bands), small colour-varied shingle tiles in
         front (brick-offset rows). Falls back to a plain colour if no canvas. */
      try {
        var doc = global.document; if (!doc || !doc.createElement) return null;
        var T = global.THREE;
        var cnv = doc.createElement('canvas'); cnv.width = 256; cnv.height = 256;
        var g = cnv.getContext('2d'); if (!g) return null;
        function sh(hex, ff) {
          var r = (hex >> 16) & 255, gg = (hex >> 8) & 255, b = hex & 255;
          r = Math.max(0, Math.min(255, Math.round(r * ff)));
          gg = Math.max(0, Math.min(255, Math.round(gg * ff)));
          b = Math.max(0, Math.min(255, Math.round(b * ff)));
          return 'rgb(' + r + ',' + gg + ',' + b + ')';
        }
        g.fillStyle = sh(baseHex, 0.55); g.fillRect(0, 0, 256, 256);
        var bandH = 64;
        for (var by = 0; by < 256; by += bandH) { g.fillStyle = sh(baseHex, 0.82); g.fillRect(0, by + 2, 256, bandH - 4); }
        var rows = 8, cols = 8, rh = 256 / rows, cw = 256 / cols;
        for (var r = 0; r < rows; r++) {
          var off = (r % 2) ? cw / 2 : 0;
          for (var c = -1; c <= cols; c++) {
            var x = c * cw + off, y = r * rh;
            var ff = 0.92 + ((r * 7 + (c + 1) * 3) % 5) * 0.03;
            g.fillStyle = sh(baseHex, ff);
            g.fillRect(x + 1.2, y + 1.2, cw - 2.4, rh - 2.4);
          }
        }
        var tex = new T.CanvasTexture(cnv);
        tex.wrapS = tex.wrapT = T.RepeatWrapping;
        tex.repeat.set(2, 3);
        if (T.SRGBColorSpace) tex.colorSpace = T.SRGBColorSpace;
        return tex;
      } catch (e) { return null; }
    },
    _buildFacade: function (grp, mi, t) {
      var T = global.THREE;
      var cv = _cv();
      var _slats = (STATE.fillType === 'slats');
      var _slatGap = Math.max(0, Math.min(0.3, (STATE.slatGap != null ? STATE.slatGap : 0.04)));
      var pattern = _slats ? 'wood2' : (STATE.facadePattern || 'wood1');
      if (!this.materials.membrane) {
        this.materials.membrane = new T.MeshStandardMaterial({ color: 0x3a3d40, roughness: 0.95, metalness: 0.05 });
      }
      var mat = this._facMat();
      /* W5: the planks land ON THE SUBFRAME, 1 mm of air: with a vertical
         cladding they ride the horizontal counter-battens (outer face
         half+0.108); with wood2 they nail straight onto the vertical
         battens (half+0.077). The old flat 0.135 floated in the void. */
      var OFF = (STATE.facadePattern === 'wood2') ? (0.077 + 0.001 + 0.010) : (0.108 + 0.001 + 0.010);
      var THK = 0.02, PLANK = _slats ? 0.06 : cv.boardW, GAP = _slats ? _slatGap : 0.003;   /* near-closed joint: the
         8 mm gap let the horizontal counter-battens flash through as false
         'profiles' across the facade -- the review saw stripes, not boards */
      var hw = _extW() / 2, hl = t.L / 2;
      var mod5 = STATE.modules[mi];
      var covered = _coverFrac(mi) > 0.3;
      var isUpper = (mod5.lvl | 0) >= 1;

      global.SaunaRenderer._cladW = { n: 0, above: 0, gable: (STATE.roofType === 'gable') };
      var yB = isUpper ? -0.06 : 0.05;
      /* The cladding stops UNDER the roof. t.H + 0.10 was the container
         detail: planks running past the wall top to mask the flat roof's edge
         band. On a pitched roof there is no band to mask -- the planes sit ON
         the plate -- so those 100 mm became a horizontal collar cutting
         through both roof types just above the eaves line: "a plane passing
         through the roof", gable and flat alike. It surfaced now because v90
         made the exterior envelope the default; before that the cladding
         never drew at all. */
      var yT = t.H - 0.02;
      var rawHoles = _holesFor(mi);

      var segSideLen = t.L - 2 * POST, segEndW = _extW() - 2 * POST;
      function mapHoles(list, segLen, mirror) {
        /* the boards run TO THE FRAME, not to the rough opening -- the
           review could see the wall panels' bones around every window. The
           hole shrinks 25 mm per side and top; and under a floor-level door
           the cladding CONTINUES (the old rule cut the whole plinth band
           away below doors and HS portals). */
        var out = [];
        for (var i2 = 0; i2 < (list || []).length; i2++) {
          var h2 = list[i2];
          /* 25 mm was not enough -- the rough hole carries the structural
             margin too, and the membrane still showed around every frame.
             55 mm per side and top (and up from a window sill) lands the
             boards on the frame line. */
          var a2 = (mirror ? (segLen / 2 - h2.hi) : (h2.lo - segLen / 2)) + 0.075;
          var b3 = (mirror ? (segLen / 2 - h2.lo) : (h2.hi - segLen / 2)) - 0.075;
          var isDoor = h2.b0 < 0.03;
          out.push({ lo: a2, hi: b3, b0: isDoor ? -0.02 : (h2.b0 + 0.075), b1: h2.b1 - 0.075, door: isDoor });
        }
        return out;
      }
      var holes = {
        sideL: mapHoles(rawHoles.sideL, segSideLen, false),
        sideR: mapHoles(rawHoles.sideR, segSideLen, true),
        rear: mapHoles(rawHoles.rear, segEndW, true),
        front: mapHoles(rawHoles.front, segEndW, false)
      };
      var pos = [];
      /* a box whose TOP EDGE is sawn to the roof slope: x0->yTa, x1->yTb.
         Only the front/rear planes ever need it (the mono side eaves are
         level), so the prism lives in the x-y profile, extruded in z. */
      /* wall-aware slant: h-walls (front/rear) profile in x, extrude in z
         at plane cz; v-walls (sideL/sideR) profile in z, extrude in x at
         plane cz. u0/u1 are the wall-run coords, tA/tB the roofline heights. */
      function pushSlantWall(wallN, u0, u1, y0, yTa, yTb, plane, sz) {
        if (wallN === 'front' || wallN === 'rear') { pushSlant(u0, u1, y0, yTa, yTb, plane, sz); return; }
        /* v-wall: build the trapezoid in the z-y plane, extrude along x */
        if (u1 - u0 < 0.015 || Math.max(yTa, yTb) - y0 < 0.015) return;
        var x0w = plane - sz / 2, x1w = plane + sz / 2;
        var A = [u0, y0], B = [u1, y0], C = [u1, Math.max(y0 + 0.002, yTb)], D = [u0, Math.max(y0 + 0.002, yTa)];
        function q(p1, p2, p3, p4) {
          pos.push(p1[0], p1[1], p1[2], p2[0], p2[1], p2[2], p3[0], p3[1], p3[2]);
          pos.push(p1[0], p1[1], p1[2], p3[0], p3[1], p3[2], p4[0], p4[1], p4[2]);
        }
        /* points are (x, y, z): here the run u maps to z, plane maps to x */
        var Af = [x1w, A[1], A[0]], Bf = [x1w, B[1], B[0]], Cf = [x1w, C[1], C[0]], Df = [x1w, D[1], D[0]];
        var Ab = [x0w, A[1], A[0]], Bb = [x0w, B[1], B[0]], Cb = [x0w, C[1], C[0]], Db = [x0w, D[1], D[0]];
        q(Af, Bf, Cf, Df); q(Bb, Ab, Db, Cb);
        q(Ab, Bb, Bf, Af); q(Df, Cf, Cb, Db);
        q(Ab, Af, Df, Db); q(Bf, Bb, Cb, Cf);
      }
      /* the raw prism, no internal division */
      function pushPrism(x0, x1, y0, yTa, yTb, cz, sz) {
        if (x1 - x0 < 0.012 || Math.max(yTa, yTb) - y0 < 0.008) return;
        var z0 = cz - sz / 2, z1 = cz + sz / 2;
        var A = [x0, y0], B = [x1, y0], C = [x1, Math.max(y0 + 0.002, yTb)], D = [x0, Math.max(y0 + 0.002, yTa)];
        function q(p1, p2, p3, p4) {
          pos.push(p1[0], p1[1], p1[2], p2[0], p2[1], p2[2], p3[0], p3[1], p3[2]);
          pos.push(p1[0], p1[1], p1[2], p3[0], p3[1], p3[2], p4[0], p4[1], p4[2]);
        }
        var Af = [A[0], A[1], z1], Bf = [B[0], B[1], z1], Cf = [C[0], C[1], z1], Df = [D[0], D[1], z1];
        var Ab = [A[0], A[1], z0], Bb = [B[0], B[1], z0], Cb = [C[0], C[1], z0], Db = [D[0], D[1], z0];
        q(Af, Bf, Cf, Df); q(Bb, Ab, Db, Cb);
        q(Ab, Bb, Bf, Af); q(Df, Cf, Cb, Db);
        q(Ab, Af, Df, Db); q(Bf, Bb, Cb, Cf);
      }
      /* pushSlant now SLICES the sloped region into PLANK-tall courses with a
         GAP between them, so a wide rake (mono horizontal) or a tall narrow
         taper (gable) shows the same plank seams as the flat cladding. Each
         course is itself a little trapezoid, its own top riding the slope. */
      function pushSlant(x0, x1, y0, yTa, yTb, cz, sz, slice) {
        var yHi = Math.max(yTa, yTb);
        if (x1 - x0 < 0.012 || yHi - y0 < 0.008) return;
        if (!slice) { pushPrism(x0, x1, y0, yTa, yTb, cz, sz); return; }
        /* top edge as a line across x: y(x) = yTa + (yTb-yTa)*(x-x0)/(x1-x0) */
        var span = (x1 - x0) || 1;
        function topAt(x) { return yTa + (yTb - yTa) * (x - x0) / span; }
        var yc = y0;
        var guard = 0;
        while (yc < yHi - 0.006 && guard++ < 60) {
          var yc1 = Math.min(yHi, yc + PLANK);
          /* this course spans only the x-range where the slope top is ABOVE
             yc; clamp each course's own top to yc1 so it is one plank tall */
          var cxa = x0, cxb = x1;
          /* find where topAt crosses yc (the course's lower-left/right start) */
          if (yTa < yc && yTb >= yc && yTb !== yTa) cxa = x0 + (yc - yTa) / (yTb - yTa) * span;
          if (yTb < yc && yTa >= yc && yTb !== yTa) cxb = x0 + (yc - yTa) / (yTb - yTa) * span;
          var ta = Math.min(yc1, topAt(cxa)), tb = Math.min(yc1, topAt(cxb));
          if (Math.max(ta, tb) - yc > 0.006) pushPrism(cxa, cxb, yc, ta, tb, cz, sz);
          yc = yc1 + GAP;
        }
      }
      function pushBox(cx, cy, cz, sx, sy, sz) {
        var x0 = cx - sx / 2, x1 = cx + sx / 2, y0 = cy - sy / 2, y1 = cy + sy / 2, z0 = cz - sz / 2, z1 = cz + sz / 2;
        var f = [
          [x0, y0, z1, x1, y0, z1, x1, y1, z1, x0, y1, z1],
          [x1, y0, z0, x0, y0, z0, x0, y1, z0, x1, y1, z0],
          [x1, y0, z1, x1, y0, z0, x1, y1, z0, x1, y1, z1],
          [x0, y0, z0, x0, y0, z1, x0, y1, z1, x0, y1, z0],
          [x0, y1, z1, x1, y1, z1, x1, y1, z0, x0, y1, z0],
          [x0, y0, z0, x1, y0, z0, x1, y0, z1, x0, y0, z1]
        ];
        for (var q = 0; q < 6; q++) {
          var v = f[q];
          pos.push(v[0], v[1], v[2], v[3], v[4], v[5], v[6], v[7], v[8]);
          pos.push(v[0], v[1], v[2], v[6], v[7], v[8], v[9], v[10], v[11]);
        }
      }

      function wallBands(len, sideHoles, wallN, noRakeClip) {
        var out = [], yv = yB;
        var yTw = Math.max(cladTopAt(wallN, -len / 2), Math.max(cladTopAt(wallN, 0), cladTopAt(wallN, len / 2)));
        while (yv < yTw - 0.02) {
          var y1v = Math.min(yTw, yv + PLANK);
          var segs2 = [[-len / 2, len / 2]];
          for (var hb = 0; hb < sideHoles.length; hb++) {
            var hh2 = sideHoles[hb];
            var hy0b = PANEL_BASE + hh2.b0, hy1b = PANEL_BASE + hh2.b1;
            if (y1v <= hy0b || yv >= hy1b) continue;
            var nx2 = [];
            for (var sb = 0; sb < segs2.length; sb++) {
              var a3 = segs2[sb][0], b4 = segs2[sb][1];
              if (hh2.lo > a3 + 0.01) nx2.push([a3, Math.min(b4, hh2.lo)]);
              if (hh2.hi < b4 - 0.01) nx2.push([Math.max(a3, hh2.hi), b4]);
            }
            segs2 = nx2;
          }
          /* only the RAKED walls (mono front/rear, gable front/rear) shrink
             their runs above the cap -- and they clip by the row BOTTOM so
             the slant wedge can saw the top cleanly. The eaves walls
             (sideL/sideR) are LEVEL to their own cap and never enter here;
             the high mono wall is a full rectangle to its own (raised) cap.
             This is why the eaves showed zuby: they were being run-clipped
             as if raked. */
          var raked2 = (wallN === 'front' || wallN === 'rear');
          if (y1v > capF9 + 0.01 && raked2 && !noRakeClip) {
            var nx3 = [];
            for (var sc3 = 0; sc3 < segs2.length; sc3++) {
              var a5 = segs2[sc3][0], b5 = segs2[sc3][1];
              if (STATE.roofType === 'mono') {
                var uMin5 = _extW() * ((Math.max(yv, capF9) - capF9) / (riseF9 || 1)) - _extW() / 2;
                if (uMin5 < b5 - 0.03) nx3.push([Math.max(a5, uMin5 + 0.01), b5]);
              } else if (STATE.roofType === 'gable') {
                var uH5 = (_extW() / 2) * (1 - (Math.max(yv, capF9) - capF9) / (riseFG || 1));
                if (uH5 > 0.04) nx3.push([Math.max(a5, -uH5), Math.min(b5, uH5)]);
              }
            }
            segs2 = nx3;
          }
          out.push({ y0: yv, y1: y1v, segs: segs2 });
          yv += PLANK + GAP;
        }
        return out;
      }

      function wallStrips(len, sideHoles, plankW) {
        var bounds = [0, len], i2, h2;
        if (plankW == null) {
          for (i2 = 0; i2 < sideHoles.length; i2++) {
            h2 = sideHoles[i2];
            bounds.push(Math.max(0, h2.lo + len / 2), Math.min(len, h2.hi + len / 2));
          }
          bounds.sort(function (a, b) { return a - b; });
          var strips = [];
          for (i2 = 0; i2 < bounds.length - 1; i2++) {
            if (bounds[i2 + 1] - bounds[i2] > 0.005) strips.push([bounds[i2], bounds[i2 + 1]]);
          }
          return strips;
        }
        var out = [], u = 0;
        var g2 = (arguments.length > 3 && arguments[3] != null) ? arguments[3] : GAP;
        while (u < len - 0.01) {
          out.push([u, Math.min(len, u + plankW)]);
          u += plankW + g2;
        }
        return out;
      }
      function buildWall(len, sideHoles, place, wallN) {
        if (SHELTER_SCREEN_WALLS || !_sideFilled(wallN)) return;   /* shelter: the screen builder owns the wall face */
        if (pattern === 'wood2') {
          /* horizontal boards: rows climb to the highest roofline point of
             this wall; each row's run is clipped to where the wall still
             exists at that height (rake / gable) */
          var bands = wallBands(len, sideHoles, wallN, true);   /* noRakeClip: rows keep full width, the TOP EDGE alone is sawn */
          var hW2 = (wallN === 'front' || wallN === 'rear');
          var zP2 = (wallN === 'front') ? (hl + OFF) : (wallN === 'rear' ? -(hl + OFF) : 0);
          for (var bi2 = 0; bi2 < bands.length; bi2++) {
            var bd = bands[bi2];
            for (var bs = 0; bs < bd.segs.length; bs++) {
              var s0 = bd.segs[bs][0], s1 = bd.segs[bs][1];
              if (s1 - s0 < 0.03 || bd.y1 - bd.y0 < 0.02) continue;
              var tS0 = cladTopAt(wallN, s0), tS1 = cladTopAt(wallN, s1);
              /* the saw comes ONLY FROM ABOVE: everything is clamped into
                 the row's own band [y0, y1]. The previous cut let the wedge
                 base fall BELOW the row when the roofline dipped under it --
                 boards tapering to spikes from underneath, as the review's
                 screenshot showed. */
              /* ONLY THE TOP boards meet the rake. A row fully below the
                 cap is a plain rectangle (no sawing at all -- this is what
                 the earlier version got right and the last two rounds broke:
                 they slanted every row). A row the roofline crosses is drawn
                 as one slant-topped prism; a row entirely above is skipped. */
              /* CUT FROM THE ROOF SIDE, exactly like the verticals: the
                 board body runs from the row bottom up to the roofline, its
                 TOP edge sawn along cladTopAt. Body to the lower corner as a
                 rectangle, then a slant wedge finishes the top to the two
                 roofline heights -- never touched from below, never
                 run-clipped in u. Rows fully under the cap are plain
                 rectangles; rows fully above the roofline vanish. */
              var tLoR = Math.min(tS0, tS1), tHiR = Math.max(tS0, tS1);
              var rowTop = bd.y1 - GAP;   /* leave the seam gap above */
              if (bd.y0 >= tHiR - 0.003) {
                /* whole row above the roof -- nothing */
              } else if (rowTop <= tLoR + 0.001) {
                /* row entirely below the rake: a plain full-width plank */
                place((s0 + s1) / 2, (bd.y0 + rowTop) / 2, s1 - s0, rowTop - bd.y0);
              } else if (hW2) {
                /* the rake crosses this course. A normal plank runs full
                   width from bd.y0; its TOP is sawn along the roofline. Body
                   rectangle up to the LOWER roofline corner, then ONE wedge
                   finishing the top to the two corners (clamped to rowTop so
                   the seam to the course above is the horizontal GAP). No
                   whole-area fill, no board above the roof. */
                /* THIS COURSE IS ONE PLANK TALL. It exists only across the
                   x where the roofline is above its bottom (bd.y0); within
                   that, it is full height where roof >= its top (rowTop), and
                   sawn where the roof passes THROUGH the course. So each row
                   is its own PLANK-tall strip -- the wedge never runs from
                   the eaves up the whole slope (that was the monolithic
                   wedge). Seams between courses stay horizontal. */
                var slope9 = (tS1 - tS0);
                var xB = (Math.abs(slope9) > 1e-6) ? (s0 + (bd.y0 - tS0) / slope9 * (s1 - s0)) : s0;  /* roof == bd.y0 */
                var xT = (Math.abs(slope9) > 1e-6) ? (s0 + (rowTop - tS0) / slope9 * (s1 - s0)) : s0; /* roof == rowTop */
                var hiAtS1 = (tS1 >= tS0);   /* high side at s1 */
                /* full-height sub-span (roof >= rowTop) */
                var fL = hiAtS1 ? Math.max(s0, xT) : s0;
                var fR = hiAtS1 ? s1 : Math.min(s1, xT);
                if (fR - fL > 0.012) place((fL + fR) / 2, (bd.y0 + rowTop) / 2, fR - fL, rowTop - bd.y0);
                /* sawn sub-span (bd.y0 <= roof < rowTop) */
                var wL = hiAtS1 ? Math.max(s0, xB) : Math.max(s0, xT);
                var wR = hiAtS1 ? Math.min(s1, xT) : Math.min(s1, xB);
                if (wR - wL > 0.006) {
                  var wtl = Math.min(rowTop, Math.max(bd.y0, tS0 + slope9 * (wL - s0) / ((s1 - s0) || 1)));
                  var wtr = Math.min(rowTop, Math.max(bd.y0, tS0 + slope9 * (wR - s0) / ((s1 - s0) || 1)));
                  pushSlant(wL, wR, bd.y0, wtl, wtr, zP2, THK, false);
                }
              } else {
                var yBody2 = Math.max(bd.y0, tLoR);
                if (yBody2 - bd.y0 > 0.012) place((s0 + s1) / 2, (bd.y0 + yBody2) / 2, s1 - s0, yBody2 - bd.y0);
              }
            }
            /* FILL up to the window frame: a course that STRADDLES a hole's
               sill or head had the hole's whole width removed for the course's
               full height -- restore the strip that is actually BELOW the sill
               or ABOVE the head, so the cladding reaches the frame (Marek). */
            var rowTopF = bd.y1 - GAP;
            for (var hf = 0; hf < sideHoles.length; hf++) {
              var HHf = sideHoles[hf];
              if (HHf.hi - HHf.lo < 0.02) continue;
              var hy0f = PANEL_BASE + HHf.b0, hy1f = PANEL_BASE + HHf.b1;
              if (bd.y0 < hy0f - 0.002 && rowTopF > hy0f + 0.002) place((HHf.lo + HHf.hi) / 2, (bd.y0 + hy0f) / 2, HHf.hi - HHf.lo, hy0f - bd.y0);
              if (rowTopF > hy1f + 0.002 && bd.y0 < hy1f - 0.002) place((HHf.lo + HHf.hi) / 2, (hy1f + rowTopF) / 2, HHf.hi - HHf.lo, rowTopF - hy1f);
            }
          }
          return;
        }
        var plankW = (pattern === 'panel') ? null : (pattern === 'bond' ? 0.27 : PLANK);
        var battenGap = (pattern === 'bond') ? 0.004 : GAP;
        var strips0 = wallStrips(len, sideHoles, plankW, battenGap);
        var hWall = (wallN === 'front' || wallN === 'rear');
        /* SPLIT wide boards in u at every hole edge they straddle, so each
           piece is either fully beside the opening (full height) or fully
           over it (clipped in y to sill/head). A board must reach the frame,
           even if it means cutting it -- the review's rule for wide boards. */
        var strips = [];
        for (var sp0 = 0; sp0 < strips0.length; sp0++) {
          var brk = [strips0[sp0][0], strips0[sp0][1]];
          for (var hb0 = 0; hb0 < sideHoles.length; hb0++) {
            var eL = sideHoles[hb0].lo + len / 2, eR = sideHoles[hb0].hi + len / 2;
            [eL, eR].forEach(function (e2) {
              if (e2 > strips0[sp0][0] + 0.004 && e2 < strips0[sp0][1] - 0.004) brk.push(e2);
            });
          }
          brk.sort(function (a2, b2) { return a2 - b2; });
          for (var bk0 = 0; bk0 + 1 < brk.length; bk0++) {
            if (brk[bk0 + 1] - brk[bk0] > 0.004) strips.push([brk[bk0], brk[bk0 + 1]]);
          }
        }
        for (var si2 = 0; si2 < strips.length; si2++) {
          var u0 = strips[si2][0], u1 = strips[si2][1];
          var cu = (u0 + u1) / 2 - len / 2;
          var cuA = u0 - len / 2, cuB = u1 - len / 2;
          /* the plank body runs to the LOWER roofline corner; the sawn
             wedge above it comes as a slant prism, so the top edge is one
             straight rake, not a staircase of plank-width steps */
          var tA = cladTopAt(wallN, cuA), tB = cladTopAt(wallN, cuB);
          var tLo = Math.min(tA, tB);
          var segs = [[yB, tLo]];
          for (var hi2 = 0; hi2 < sideHoles.length; hi2++) {
            var hh = sideHoles[hi2];
            if (u1 - len / 2 <= hh.lo || u0 - len / 2 >= hh.hi) continue;
            var hy0 = PANEL_BASE + hh.b0, hy1 = PANEL_BASE + hh.b1;
            var next = [];
            for (var sg2 = 0; sg2 < segs.length; sg2++) {
              var a2 = segs[sg2][0], b2 = segs[sg2][1];
              if (hy0 > a2 + 0.01) next.push([a2, Math.min(b2, hy0)]);
              if (hy1 < b2 - 0.01) next.push([Math.max(a2, hy1), b2]);
            }
            segs = next;
          }
          for (var sg3 = 0; sg3 < segs.length; sg3++) {
            var ya = segs[sg3][0], yb = segs[sg3][1];
            if (yb - ya < 0.02) continue;
            place(cu, (ya + yb) / 2, u1 - u0, yb - ya);
          }
          /* the wedge belongs to whichever wall RISES above the cap -- for
             a mono that is front/rear, for a gable the two ENDS (front/rear
             here too, since the ridge runs along the length). But the plane
             it sits on differs: h-walls extrude in z, v-walls in x. Gate on
             the actual rise, not the wall name, so no rake is ever missed. */
          var risesHere = Math.max(tA, tB) - tLo > 0.005;
          if (risesHere) {
            var zPl = (wallN === 'front') ? (hl + OFF) : (wallN === 'rear') ? -(hl + OFF) : (wallN === 'sideR') ? (hw + OFF) : -(hw + OFF);
            pushSlantWall(wallN, cuA, cuB, tLo - 0.001, tA, tB, zPl, THK);
          }
        }
      }
      /* THE BOARDS ARE CUT TO FIT -- corners, roofline, opening edges; the
         budget counts square metres, not whole boards, so the model owes the
         same honesty. Front/rear runs WRAP the corners (past the side
         boards' outer faces); side runs stop 1 mm before the front planes.
         And every board tops out at the ROOFLINE for its position: mono
         rakes the ends and lifts the whole high side, the gable peaks. */
      var tanF9 = Math.tan(clamp(STATE.roofPitch || 0, 0, 45) * Math.PI / 180);
      var riseF9 = (STATE.roofType === 'mono') ? _extW() * tanF9 : 0;
      var riseFG = (STATE.roofType === 'gable') ? (_extW() / 2) * tanF9 : 0;
      var capF9 = t.H - 0.09 - 0.001;
      function cladTopAt(wallN, u) {
        if (STATE.roofType === 'mono') {
          if (wallN === 'sideL') return capF9;
          if (wallN === 'sideR') return capF9 + riseF9 - 0.004;
          return capF9 + riseF9 * ((u + _extW() / 2) / _extW()) - 0.004;
        }
        if (STATE.roofType === 'gable' && (wallN === 'front' || wallN === 'rear') && pattern !== 'wood2') {
          /* VERTICAL cladding: buildWall's own planks run from the ground to
             the RAFTER TOP as ONE piece, holding the wall grid -- no separate
             gable set, no cap-level seam (Marek's rule: one profile ground to
             roof). rafter top = cap + gable-profile rise + kTop. */
          var _kTopC = (global.SaunaRoofFrame ? global.SaunaRoofFrame.RAFTER.h : 0.16) / Math.cos(clamp(STATE.roofPitch || 0, 0, 45) * Math.PI / 180);
          return capF9 + Math.max(0, riseFG * (1 - Math.abs(u) / (_extW() / 2))) + _kTopC;
        }
        /* gable front/rear HORIZONTAL (wood2): the wall stops at the last full
           course line AT/BELOW the cap (the grid the courses ride), so the
           gable triangle continues the SAME rhythm with no partial course or
           overlap at the cap (Marek: profiles must line up + equal gap). */
        if (STATE.roofType === 'gable' && (wallN === 'front' || wallN === 'rear') && pattern === 'wood2') {
          return yB + Math.max(0, Math.floor((capF9 - yB) / (PLANK || 1))) * (PLANK || 1);
        }
        return capF9;
      }
      var hExt = OFF + THK / 2 + 0.002;             /* wrap past the side boards */
      var lenH = _extW() + 2 * hExt;
      var lenV = t.L + 2 * (OFF - THK / 2 - 0.001); /* stop shy of the front planes */
      buildWall(lenV, holes.sideL, function (cu, cy, w, h) { pushBox(-hw - OFF, cy, cu, THK, h, w); }, 'sideL');
      buildWall(lenV, holes.sideR, function (cu, cy, w, h) { pushBox(hw + OFF, cy, cu, THK, h, w); }, 'sideR');
      buildWall(lenH, holes.rear, function (cu, cy, w, h) { pushBox(cu, cy, -hl - OFF, w, h, THK); }, 'rear');
      buildWall(lenH, holes.front, function (cu, cy, w, h) { pushBox(cu, cy, hl + OFF, w, h, THK); }, 'front');
      /* THE GABLE TRIANGLES, built explicitly. The strip machinery clads
         the walls to the cap cleanly, but the two triangular ends above the
         cap were never getting boards -- so here they are, on their own:
         vertical planks filling each end wall (the ones running along x,
         i.e. front & rear), every plank rising from the cap to the roof
         slope, the pair of slopes peaking at the ridge over x=0. */
      /* SHELTER: the screens clad their own gable triangle to the ridge
         (v31), so this facade triangle would lay a SECOND set of boards over
         them on its own PLANK/GAP grid -- the doubled, unevenly spaced slats
         in the gable Marek reported. */
      /* BETWEEN THE RAFTERS: the eaves walls (sideL/sideR -- both roof
         types run their rafters across the width) carry the cladding all
         the way UP: short boards fill each bay between rafter tails, from
         the local cap line to the roof underside, 1 mm clear of every
         rafter flank. The review's exact ask: natahnout obklad az nahoru. */
      try {
        var RFI = global.SaunaRoofFrame;
        var layF = RFI && RFI.layout ? RFI.layout() : null;
        if (layF && layF.rafters && layF.rafters.length) {
          var kTopF = 0.16 / Math.cos(Math.atan(tanF9 || 0.0001));
          var rzs = [];
          for (var rf9 = 0; rf9 < layF.rafters.length; rf9++) {
            var RR9 = layF.rafters[rf9];
            if (RR9.mi !== mi) continue;
            if (STATE.roofType === 'mono' && RR9.side !== 0) continue;
            if (STATE.roofType === 'gable' && RR9.side === 0) continue;
            if (rzs.indexOf(RR9.z) < 0) rzs.push(RR9.z);
          }
          rzs.sort(function (a9, b9) { return a9 - b9; });
          [['sideL', -1], ['sideR', 1]].forEach(function (SD) {
            var baseY9 = cladTopAt(SD[0], 0) - 0.002;
            var xPl9 = SD[1] * (hw + OFF);
            for (var gz9 = 0; gz9 + 1 < rzs.length; gz9++) {
              var za9 = rzs[gz9] + 0.031, zb9 = rzs[gz9 + 1] - 0.031;
              if (zb9 - za9 < 0.05) continue;
/* shed: no cladding between rafter tails */
            }
          });
        }
      } catch (eBR9) {}
      /* CORNER TRIMS: the front runs wrap the corners, and from an angle
         that lap reads as boards-on-boards. The trade dresses it with a
         vertical corner board on each edge -- so does the model: two 60 mm
         trims per corner, one on each face, proud of the planks by 1 mm,
         full height to the roofline. */
      (function cornerTrims() {
        /* SHELTER: no corner trim boards. They are a clad-cabin detail (they
           cover the cladding wrap joint) -- on a shelter they stood at the
           corners looking like posts, wearing the cladding colour. */
        if (SHELTER_SCREEN_WALLS) return;
        var trW = 0.06, trT = 0.02;
        var cxT = hw + OFF + THK / 2 + 0.001 + trT / 2;
        var czT = hl + OFF + THK / 2 + 0.001 + trT / 2;
        [[-1, -1], [1, -1], [-1, 1], [1, 1]].forEach(function (cn) {
          var topZ = cladTopAt(cn[0] < 0 ? 'sideL' : 'sideR', 0);
          var topX = cladTopAt(cn[1] < 0 ? 'rear' : 'front', cn[0] * hw);
          var hT = Math.max(topZ, topX) - yB;
          /* face on the front/rear plane */
          pushBox(cn[0] * (hw + OFF - trW / 2 + THK / 2), yB + hT / 2, cn[1] * czT, trW, hT, trT);
          /* face on the side plane */
          pushBox(cn[0] * cxT, yB + hT / 2, cn[1] * (hl + OFF - trW / 2 + THK / 2), trT, hT, trW);
        });
      })();
      /* SHELTER: NO corner posts here. These four 60x60 verticals are a
         clad-cabin detail drawn in the FACADE material (hence their slat
         colour in the stage view) and they stood right beside the real
         structural corner posts. They sit OUTSIDE cornerTrims and outside the
         bond block, which is why the earlier gates missed them (Marek: ty
         rohove listy tam porad jsou). */
      var g = new T.BufferGeometry();
      g.setAttribute('position', new T.Float32BufferAttribute(pos, 3));
      g.computeVertexNormals();
      var m = new T.Mesh(g, mat);
      m.userData.viz = 'layer_wall_clad';   /* W5 -- the planks are a LAYER now */
      global.SaunaRenderer._cladW = global.SaunaRenderer._cladW || { n: 0, above: 0 };
      global.SaunaRenderer._cladW.n++;
      grp.add(m);
      if (!covered) {
        var roof = new T.Mesh(new T.BoxGeometry(_extW() + 2 * OFF + 0.04, 0.06, t.L + 2 * OFF + 0.04), this.materials.membrane);
        roof.position.set(0, yT + 0.03, 0);
        roof.userData.viz = 'facade';
        grp.add(roof);
      }
    },

    _buildRailRun: function (grp, edges, yBase, RH) {
      var T = global.THREE;
      if (!this.materials.rail) {
        this.materials.rail = new T.MeshStandardMaterial({ color: 0x4a4d52, roughness: 0.6, metalness: 0.55 });
      }
      if (!this.materials.glass) {
        this.materials.glass = new T.MeshStandardMaterial({
          color: 0xcfe0dc, roughness: 0.06, metalness: 0.0,
          transparent: true, opacity: 0.28
        });
      }
      var fig = _railFigures(edges);
      for (var pi = 0; pi < fig.posts.length; pi++) {
        var post = new T.Mesh(new T.BoxGeometry(0.04, RH, 0.04), this.materials.rail);
        post.position.set(fig.posts[pi][0], yBase + RH / 2, fig.posts[pi][1]);
        post.userData.viz = 'railing';
        grp.add(post);
      }
      for (var ei = 0; ei < edges.length; ei++) {
        var e = edges[ei];
        var len = Math.hypot(e[2] - e[0], e[3] - e[1]);
        if (len < 0.15) continue;
        var horiz = Math.abs(e[3] - e[1]) < 1e-6;
        var mx = (e[0] + e[2]) / 2, mz = (e[1] + e[3]) / 2;
        var hand = new T.Mesh(
          horiz ? new T.BoxGeometry(len + 0.04, 0.04, 0.05) : new T.BoxGeometry(0.05, 0.04, len + 0.04),
          this.materials.rail
        );
        hand.position.set(mx, yBase + RH - 0.02, mz);
        hand.userData.viz = 'railing';
        grp.add(hand);
        var gH = RH - 0.12;
        var glass = new T.Mesh(
          horiz ? new T.BoxGeometry(len - 0.05, gH, 0.012) : new T.BoxGeometry(0.012, gH, len - 0.05),
          this.materials.glass
        );
        glass.position.set(mx, yBase + 0.06 + gH / 2, mz);
        glass.userData.viz = 'railing';
        grp.add(glass);
      }
      return fig;
    },

    _PIDCOL: [0x2E7BEF, 0xE53935, 0xFFC107, 0x43A047, 0x8E24AA, 0x00BCD4, 0xF4511E, 0xD81B60, 0x00897B, 0xC0CA33, 0x3949AB, 0xF06292],
    _stageMat: function (key) {
      var T = global.THREE;
      if (!this._stageMats) this._stageMats = {};
      if (!this._stageSeq) this._stageSeq = {};
      if (this._stageSeq[key] == null) this._stageSeq[key] = Object.keys(this._stageSeq).length;
      if (!this._stageMats[key]) {
        this._stageMats[key] = new T.MeshLambertMaterial({ color: this._PIDCOL[this._stageSeq[key] % this._PIDCOL.length], transparent: true, opacity: 1 });
      }
      return this._stageMats[key];
    },
    _woodHex: function () {
      var T = global.THREE;
      if (!this.materials.timber) this.materials.timber = new T.MeshStandardMaterial({ color: 0xb59a63, roughness: 0.85, metalness: 0.02 });
      return this.materials.timber.color.getHex();
    },
    _pidMat: function (pid, fixedColor) {
      var T = global.THREE;
      if (!pid) return this._pTimber || (this._pTimber = new T.MeshLambertMaterial({ color: 0xC9A06A }));
      if (!this._panelMats) this._panelMats = {};
      if (!this._panelSeq) this._panelSeq = {};
      if (fixedColor != null && this._panelFix !== fixedColor) { this._panelMats = {}; this._panelFix = fixedColor; }   /* colour changed -> rebuild, the cache used to outlive it */
      if (!this._panelMats[pid]) {
        if (this._panelSeq[pid] == null) this._panelSeq[pid] = Object.keys(this._panelSeq).length;
        /* fixedColor: the member still registers under its pid -- tap
           isolation keeps working -- but wears one uniform colour. The roof
           uses this: one timber brown for every truss, individuality on tap,
           not on sight. */
        this._panelMats[pid] = (fixedColor != null)
          /* MATCH THE OTHER TIMBER: same standard shading as materials.timber,
             not Lambert -- the same hex under Lambert reads dark brown, which
             is why the rafters and collars looked unlike every other member. */
          ? new T.MeshStandardMaterial({ color: fixedColor, roughness: 0.85, metalness: 0.02, transparent: true, opacity: 1 })
          : new T.MeshLambertMaterial({
              color: this._PIDCOL[this._panelSeq[pid] % this._PIDCOL.length],
              transparent: true, opacity: 1 });
      }
      return this._panelMats[pid];
    },

    _buildFrames: function (grp, mi, t) {
      var T = global.THREE;
      var ir = _innerRect(t);
      var hx = ir.hx, hz = ir.hz;
      var meMi = mi;
      /* the floor frame, from SaunaFloor.layout(): rim on the bearers, joists
         between the rims, deck on top -- everything strictly BELOW PANEL_BASE,
         so the walls above own their band alone. viz 'floorframe' so the eye
         cycler can undress down to it later (K6). */
      /* the wall frames, in the cavity where they physically live: outside
         the lining, inside the cladding. Invisible through solid walls --
         which is true of the building too -- and glimpsed through glazing.
         viz 'wallframe' so the K6 eye can undress to them. */
      try {
        var WLY = global.SaunaWalls.layout();
        if (SHELTER_SCREEN_WALLS) { WLY.studs = []; if (WLY.headers) WLY.headers = []; if (WLY.sills) WLY.sills = []; if (WLY.kings) WLY.kings = []; if (WLY.jacks) WLY.jacks = []; }
        var SW2 = global.SaunaWalls.STUD;
        var yS0 = PANEL_BASE + SW2.w;
        var inset = (INNER_MARGIN + PANEL_INSET) / 2;
        /* plates: a bottom and a top on every wall, the pieces the studs
           stand between -- the frame was drawn as studs alone, which is a
           fence, not a wall */
        var wallGeo = {
          rear:  { px: 0, pz: -(hz + INNER_MARGIN) + inset, horiz: true },
          front: { px: 0, pz: (hz + INNER_MARGIN) - inset, horiz: true },
          sideL: { px: -(hx + INNER_MARGIN) + inset, pz: 0, horiz: false },
          sideR: { px: (hx + INNER_MARGIN) - inset, pz: 0, horiz: false }
        };
        /* each panel wears its own tint in the frame view -- muted timber
           hues, keyed by panel index, so the stage view doubles as the
           assembly sheet: this piece, that piece, in this order */
        var self2 = this;
        /* default view: wall frame wears ONE timber colour (no per-pid rainbow
           -- that is for the stage views only). pid still registers so tap
           isolation keeps working. */
        function pMat(pid) { return self2._pidMat(pid, self2._woodHex()); }
        for (var wp = 0; wp < WLY.plates.length; wp++) {
          var Pl = WLY.plates[wp];
          if (Pl.mi !== meMi) continue;
          var wg = wallGeo[Pl.wall]; if (!wg) continue;
          if (SHELTER_NO_FLOOR) continue;   /* no floor structure -> no sole plate (it would float at PANEL_BASE around the perimeter) */
          var pm2 = new T.Mesh(wg.horiz
            ? new T.BoxGeometry(Pl.len, SW2.w, SW2.d)
            : new T.BoxGeometry(SW2.d, SW2.w, Pl.len), pMat(Pl.pid));
          if (Pl.kind !== 'bottom') continue;   /* the cap is the only top rail now */
          var pyP = PANEL_BASE + SW2.w / 2;
          pm2.position.set(wg.horiz ? (Pl.u || 0) : wg.px, pyP, wg.horiz ? wg.pz : (Pl.u || 0));
          pm2.userData.viz = 'wallframe';
          pm2.userData.pid = Pl.pid;
          pm2.userData.prof = 'plate';
          grp.add(pm2);
        }
        /* the continuous cap plate: one piece per wall, over every joint,
           and it is what the rafters actually land on */
        for (var wc2 = 0; wc2 < WLY.caps.length; wc2++) {
          var Cp = WLY.caps[wc2];
          if (Cp.mi !== meMi) continue;
          var wgC = wallGeo[Cp.wall]; if (!wgC) continue;
          /* SHELTER: this cap IS the head beam carrying the rafters, so it
             wears the POST profile (Marek: 60x160 was too weak for it). It
             thickens DOWNWARD -- the top face, and with it the whole roof,
             stays exactly where it was. */
          var capH2 = SHELTER_FIXED_ROOF ? _postProfile().w : SW2.w;
          var capD2 = SHELTER_FIXED_ROOF ? _postProfile().w : SW2.d;
          var capTop2 = PANEL_BASE + SW2.w * 2 + (_wallClearH(t) - 2 * SW2.w);
          var cm2 = new T.Mesh(wgC.horiz
            ? new T.BoxGeometry(Cp.len, capH2, capD2)
            : new T.BoxGeometry(capD2, capH2, Cp.len), this.materials.timber);
          cm2.position.set(wgC.horiz ? (Cp.u || 0) : wgC.px,
            capTop2 - capH2 / 2 + (Cp.dy || 0),
            wgC.horiz ? wgC.pz : (Cp.u || 0));
          if (Cp.rake) cm2.rotation.z = (Cp.wall === 'front' || Cp.wall === 'rear') ? Cp.rake : 0;
          cm2.userData.viz = 'wallframe';
          cm2.userData.prof = 'cap';
          grp.add(cm2);
        }
        for (var wh2 = 0; wh2 < WLY.headers.length; wh2++) {
          var Hd = WLY.headers[wh2];
          if (Hd.mi !== meMi) continue;
          var wg2 = wallGeo[Hd.wall]; if (!wg2) continue;
          /* FLAT, wider face down -- every horizontal in the panel lies the
             same way: sole plate, sill, this head, top plate. The head sits
             IMMEDIATELY above the opening frame; the top plate is the second
             horizontal above it, and cripples fill between the two. No deep
             on-edge header mid-wall. */
          var hm2 = new T.Mesh(wg2.horiz
            ? new T.BoxGeometry(Hd.len, SW2.w, SW2.d)
            : new T.BoxGeometry(SW2.d, SW2.w, Hd.len), pMat(Hd.pid));
          hm2.position.set(wg2.horiz ? Hd.u : wg2.px,
            PANEL_BASE + SW2.w + Hd.y + SW2.w / 2,
            wg2.horiz ? wg2.pz : Hd.u);
          hm2.userData.viz = 'wallframe';
          hm2.userData.pid = Hd.pid;
          hm2.userData.prof = 'header';
          grp.add(hm2);
        }
        /* W2: THE WALL WOOL. One field per stud bay, the floor discipline
           standing up: bounded by the studs' side faces (1 mm), by the
           plates top and bottom (1 mm), punched around every opening --
           a band below the sill, a band above the head, full height where
           the bay clears the hole. Friction fit; the budget already prices
           wallsA and wool takes no fasteners. Raked mono rise zones stay
           open -- wool fills to the base plate line. */
        try {
          if (!this.materials.floorWool) this.materials.floorWool = new T.MeshStandardMaterial({ color: 0xd8d8d2, roughness: 0.96, metalness: 0.0 });
          var w2y0 = PANEL_BASE + SW2.w + 0.001;
          var w2y1 = PANEL_BASE + SW2.w + (_wallClearH(t) - 2 * SW2.w) - 0.001;
          var self8 = this;
          var wgAll = { rear: wallGeo.rear, front: wallGeo.front, sideL: wallGeo.sideL, sideR: wallGeo.sideR };
          for (var wk8 in wgAll) {
            var wg8 = wgAll[wk8];
            var stu8 = [];
            for (var su8 = 0; su8 < WLY.studs.length; su8++) {
              var S8 = WLY.studs[su8];
              if (S8.mi !== meMi || S8.wall !== wk8) continue;
              if (S8.role === 'cripple') continue;
              stu8.push(S8.u);
            }
            stu8.sort(function (q8, r8) { return q8 - r8; });
            /* openings on this wall in wall-axis coords (headers carry u+len) */
            var hole8 = [];
            for (var oh8 = 0; oh8 < WLY.headers.length; oh8++) {
              var H8 = WLY.headers[oh8];
              if (H8.mi !== meMi || H8.wall !== wk8) continue;
              var ho0 = H8.u - H8.len / 2 + 0.06, ho1 = H8.u + H8.len / 2 - 0.06;
              var hTop = PANEL_BASE + SW2.w + H8.y - 0.001;
              var hBot = w2y0;
              for (var sl8 = 0; sl8 < WLY.sills.length; sl8++) {
                var SL8 = WLY.sills[sl8];
                if (SL8.mi !== meMi || SL8.wall !== wk8) continue;
                if (Math.abs(SL8.u - H8.u) < 0.3) hBot = PANEL_BASE + SW2.w + SL8.y + 0.001;
              }
              hole8.push([ho0, ho1, hBot, hTop]);
            }
            function wool8(u0w, u1w, ya, yb) {
              if (SHED_BARE) return;   /* non-insulated: empty bays */
              if (u1w - u0w < 0.03 || yb - ya < 0.03) return;
              var wm8 = (wg8.horiz)
                ? new T.Mesh(new T.BoxGeometry(u1w - u0w, yb - ya, 0.158), self8.materials.floorWool)
                : new T.Mesh(new T.BoxGeometry(0.158, yb - ya, u1w - u0w), self8.materials.floorWool);
              if (wg8.horiz) wm8.position.set((u0w + u1w) / 2, (ya + yb) / 2, wg8.pz);
              else wm8.position.set(wg8.px, (ya + yb) / 2, (u0w + u1w) / 2);
              wm8.userData.viz = 'layer_wall_wool';
              grp.add(wm8);
            }
            for (var bp8 = 0; bp8 + 1 < stu8.length; bp8++) {
              var ba8 = stu8[bp8] + 0.031, bb8 = stu8[bp8 + 1] - 0.031;
              if (bb8 - ba8 < 0.03) continue;
              /* clip the bay against every hole on the wall */
              var cuts8 = [];
              for (var hc8 = 0; hc8 < hole8.length; hc8++) {
                var HO = hole8[hc8];
                var ov0 = Math.max(ba8, HO[0]), ov1 = Math.min(bb8, HO[1]);
                if (ov1 > ov0 + 0.01) cuts8.push([ov0, ov1, HO[2], HO[3]]);
              }
              if (!cuts8.length) { wool8(ba8, bb8, w2y0, w2y1); continue; }
              cuts8.sort(function (m8, n8) { return m8[0] - n8[0]; });
              var cu8 = ba8;
              for (var ck8 = 0; ck8 < cuts8.length; ck8++) {
                var CT = cuts8[ck8];
                wool8(cu8, CT[0], w2y0, w2y1);
                wool8(CT[0], CT[1], w2y0, CT[2] - 0.0);
                wool8(CT[0], CT[1], CT[3] + 0.0, w2y1);
                cu8 = Math.max(cu8, CT[1]);
              }
              wool8(cu8, bb8, w2y0, w2y1);
            }
          }
        } catch (eW2) {}
        /* W3: THE WIND BARRIER -- INS00002 over the studs' OUTER faces,
           and it covers the WHOLE BUILDING, as the review insisted: not
           just to the cap, but up the mono rise wedges and the gable
           triangles to the underside of the roof plane. Openings are
           punched through (bands below sill, above head). Same green film
           as under the floor; the envelope closes. */
        /* W4: THE FACADE SUBFRAME on the membrane. Vertical FVF00301 battens
           ride every stud line (they pin the film; the vent gap breathes up
           along them), cut around the openings. With a VERTICAL cladding
           (wood1/bond) a second layer of HORIZONTAL counter-battens @600
           lands on them -- the boards' fixing lines; with wood2 the
           horizontal boards nail straight to the vertical battens and the
           second layer does not exist. Exactly what the exterior toggle
           says, in the section, in the budget, and here. */
        try {
          if (!this.materials.partTimber) this.materials.partTimber = new T.MeshStandardMaterial({ color: 0xb59a63, roughness: 0.85, metalness: 0.02 });
          var vertClad = (STATE.facadePattern !== 'wood2');
          var bOut1 = 0.0425 + 0.004 + 0.001 + 0.015;   /* vertical battens center off half */
          var bOut2 = bOut1 + 0.015 + 0.001 + 0.015;    /* horizontal counters center */
          var bY0 = PANEL_BASE + 0.001, bY1 = (t.H - 0.09) - 0.001;
          /* THE SUBFRAME CLIMBS TO THE ROOF, like the membrane under it --
             the review's screenshot showed green above bare of laths. Each
             wall answers 'how high at position u': mono rakes the front and
             rear across the width and lifts the whole high wall; the gable
             peaks its two ends at the ridge. */
          var tanB4 = Math.tan(clamp(STATE.roofPitch || 0, 0, 45) * Math.PI / 180);
          var riseB4 = (STATE.roofType === 'mono') ? _extW() * tanB4 : 0;
          var riseG4 = (STATE.roofType === 'gable') ? (_extW() / 2) * tanB4 : 0;
          function wallTopAt(WB0, u) {
            if (STATE.roofType === 'mono') {
              if (WB0.wall === 'sideL') return bY1;
              if (WB0.wall === 'sideR') return bY1 + riseB4 - 0.002;
              return bY1 + riseB4 * ((u + _extW() / 2) / _extW()) - 0.002;
            }
            if (STATE.roofType === 'gable') {
              if (WB0.wall === 'front' || WB0.wall === 'rear') {
                return bY1 + Math.max(0, riseG4 * (1 - Math.abs(u) / (_extW() / 2))) - 0.002;
              }
            }
            return bY1;
          }
          var selfB = this;
          /* SHELTER: the screens carry their OWN subframe (bottom rail, girts
             for vertical boards, nailers for horizontal ones). This batten
             pass is the clad stud wall's subframe -- it ran on all four walls
             and dressed the whole building in battens the moment the boards
             were switched to vertical (Marek). */
          for (var wb0 = 0; wb0 < (SHELTER_SCREEN_WALLS ? 0 : wallsM.length); wb0++) {
            var WB = wallsM[wb0];
            /* ONE BATTEN PER WALL MEMBER, over that member's own span --
               the review's rule made literal: cripples above headers and
               below sills carry their short battens, kings and jacks their
               tall ones, mono-raked studs their risen ones. A batten only
               where wood backs it; the openings need no cutting because no
               member crosses an opening. */
            var stB = [];
            for (var sb0 = 0; sb0 < WLY.studs.length; sb0++) {
              var SB = WLY.studs[sb0];
              if (!_sideFilled(SB.wall)) continue;
              if (SB.mi !== meMi || SB.wall !== WB.wall) continue;
              stB.push({ u: SB.u, y0: PANEL_BASE + SW2.w + (SB.y0 || 0), len: SB.len });
            }
            /* opening holes reused from the membrane pass */
            var holesB = [];
            for (var ohB = 0; ohB < WLY.headers.length; ohB++) {
              var HB = WLY.headers[ohB];
              if (HB.mi !== meMi || HB.wall !== WB.wall) continue;
              var hbB = bY0 + 0.06, htB = PANEL_BASE + SW2.w + HB.y - 0.001;
              for (var smB = 0; smB < WLY.sills.length; smB++) {
                var SLB = WLY.sills[smB];
                if (SLB.mi !== meMi || SLB.wall !== WB.wall) continue;
                if (Math.abs(SLB.u - HB.u) < 0.3) hbB = PANEL_BASE + SW2.w + SLB.y + 0.001;
              }
              holesB.push([HB.u - HB.len / 2 + 0.06, HB.u + HB.len / 2 - 0.06, hbB, htB]);
            }
            function batV(uB, ya, yb) {
              if (yb - ya < 0.05) return;
              var vb0 = (WB.o === 'h')
                ? new T.Mesh(new T.BoxGeometry(0.05, yb - ya, 0.030), selfB.materials.partTimber)
                : new T.Mesh(new T.BoxGeometry(0.030, yb - ya, 0.05), selfB.materials.partTimber);
              if (WB.o === 'h') vb0.position.set(uB, (ya + yb) / 2, (WB.pz < 0 ? -1 : 1) * (Math.abs(WB.pz) - 0.0425 + bOut1));
              else vb0.position.set((WB.px < 0 ? -1 : 1) * (Math.abs(WB.px) - 0.0425 + bOut1), (ya + yb) / 2, uB);
              /* the line above unpacks to: wall outer half + bOut1 */
              vb0.userData.viz = 'layer_wall_batten';
              grp.add(vb0);
            }
            for (var vb9 = 0; vb9 < stB.length; vb9++) {
              var MB9 = stB[vb9];
              batV(MB9.u, MB9.y0 + 0.001, MB9.y0 + MB9.len - 0.001);
              /* the gable end has no wall members above the cap; extend the
                 batten to the roofline there so the closure stays clothed */
              if (STATE.roofType === 'gable' && (WB.wall === 'front' || WB.wall === 'rear')) {
                var gt9 = wallTopAt(WB, MB9.u);
                if (gt9 > bY1 + 0.06 && MB9.y0 + MB9.len > bY1 - 0.06) batV(MB9.u, bY1 + 0.001, gt9);
              }
            }
            if (vertClad) {
              /* horizontal counters @600, holes punched along the run; rows
                 continue ABOVE the cap wherever the wall still exists at
                 that height -- the span shrinks with the rake or the gable */
              var rowTop9 = Math.max(wallTopAt(WB, -WB.segHalf), Math.max(wallTopAt(WB, 0), wallTopAt(WB, WB.segHalf)));
              for (var hy9 = bY0 + 0.3; hy9 < rowTop9 - 0.05; hy9 += 0.6) {
                var segH = [[-WB.segHalf + 0.002, WB.segHalf - 0.002]];
                if (hy9 > bY1) {
                  if (STATE.roofType === 'mono' && (WB.wall === 'front' || WB.wall === 'rear')) {
                    var uMin9 = _extW() * ((hy9 - bY1) / riseB4) - _extW() / 2;
                    segH = [[Math.max(-WB.segHalf + 0.002, uMin9 + 0.02), WB.segHalf - 0.002]];
                  } else if (STATE.roofType === 'gable' && (WB.wall === 'front' || WB.wall === 'rear')) {
                    var uHalf9 = (_extW() / 2) * (1 - (hy9 - bY1) / riseG4);
                    if (uHalf9 < 0.05) continue;
                    segH = [[-uHalf9 + 0.02, uHalf9 - 0.02]];
                  } else if (!(STATE.roofType === 'mono' && WB.wall === 'sideR')) continue;
                }
                for (var hc0 = 0; hc0 < holesB.length; hc0++) {
                  var HH0 = holesB[hc0];
                  if (hy9 < HH0[2] - 0.02 || hy9 > HH0[3] + 0.02) continue;
                  var nx0 = [];
                  for (var sg0 = 0; sg0 < segH.length; sg0++) {
                    var a0 = segH[sg0][0], b0 = segH[sg0][1];
                    if (HH0[0] > a0 + 0.01) nx0.push([a0, Math.min(b0, HH0[0] - 0.001)]);
                    if (HH0[1] < b0 - 0.01) nx0.push([Math.max(a0, HH0[1] + 0.001), b0]);
                  }
                  segH = nx0;
                }
                for (var sg1 = 0; sg1 < segH.length; sg1++) {
                  var ua0 = segH[sg1][0], ub0 = segH[sg1][1];
                  if (ub0 - ua0 < 0.05) continue;
                  var hb0 = (WB.o === 'h')
                    ? new T.Mesh(new T.BoxGeometry(ub0 - ua0, 0.05, 0.030), selfB.materials.partTimber)
                    : new T.Mesh(new T.BoxGeometry(0.030, 0.05, ub0 - ua0), selfB.materials.partTimber);
                  if (WB.o === 'h') hb0.position.set((ua0 + ub0) / 2, hy9, (WB.pz < 0 ? -1 : 1) * (Math.abs(WB.pz) - 0.0425 + bOut2));
                  else hb0.position.set((WB.px < 0 ? -1 : 1) * (Math.abs(WB.px) - 0.0425 + bOut2), hy9, (ua0 + ub0) / 2);
                  hb0.userData.viz = 'layer_wall_batten';
                  grp.add(hb0);
                }
              }
            }
          }
        } catch (eW4) {}
        /* W1: THE INTERIOR FACES of the exterior walls, room by room. A
           sauna segment wears the hot-room stack growing inward from the
           studs -- foil, horizontal battens, grooved T&G; a dry segment
           wears one sheet of moisture-resistant gypsum, the same skin its
           partitions have. Openings punch true holes: strips between them,
           bands below the sill and above the head. Everything 1 mm off
           everything, as always. */
        try {
          var FT9 = global.SaunaPartitions;
          var irF = _innerRect(t);
          var y0F = FLOOR_TOP + 0.011;
          var linYd = ((t.H - 0.09) - 0.001 - 0.16) - 0.001 - 0.0095;
          /* the interior face must run UP TO THE CAP (t.H-0.09), covering the
             full stud height -- it used to stop at linYd (the ceiling-lining
             level, 0.16 below the cap), leaving bare studs visible through a
             window above the ceiling line (Marek: obklad nejde do vrchu na
             exterierove stene; partitions reach their own ceiling so they are
             fine). */
          var capTopF = (t.H - 0.09) - 0.001;
          var topDry = capTopF;
          var topSau = capTopF;
          if (!this.materials.partGypsum) this.materials.partGypsum = new T.MeshStandardMaterial({ color: 0xf1efe9, roughness: 0.92, metalness: 0.0 });
          /* DEBUG (Marek): sauna T&G lining ORANGE to confirm it covers the
             whole sauna everywhere -- revert to 0xe8cfa8 wood after his OK. */
          if (!this.materials.partLining) this.materials.partLining = new T.MeshStandardMaterial({ color: 0xe0cfa0, roughness: 0.8, metalness: 0.02 });
          if (!this.materials.partGroove) this.materials.partGroove = new T.MeshStandardMaterial({ color: 0xc4a377, roughness: 0.9, metalness: 0.0 });
          if (!this.materials.partFoil) this.materials.partFoil = new T.MeshStandardMaterial({ color: 0xbfc6cc, roughness: 0.35, metalness: 0.55 });
          if (!this.materials.partTimber) this.materials.partTimber = new T.MeshStandardMaterial({ color: 0xb59a63, roughness: 0.85, metalness: 0.02 });
          var self9 = this;
          var wallsDef = [
            { wall: 'rear',  o: 'h', bound: -irF.hz, inFace: -(t.L / 2) + 0.1175, sgn: 1,  segHalf: _extW() / 2 - 0.16, mir: true },
            { wall: 'front', o: 'h', bound: irF.hz,  inFace: (t.L / 2) - 0.1175,  sgn: -1, segHalf: _extW() / 2 - 0.16, mir: false },
            { wall: 'sideL', o: 'v', bound: -irF.hx, inFace: -(_extW() / 2) + 0.1175, sgn: 1,  segHalf: t.L / 2 - 0.16, mir: false },
            { wall: 'sideR', o: 'v', bound: irF.hx,  inFace: (_extW() / 2) - 0.1175,  sgn: -1, segHalf: t.L / 2 - 0.16, mir: true }
          ];
          function faceRect(WD, isSau, u0, u1, y0r, y1r, depthOff, thick, mat, viz9) {
            if (u1 - u0 < 0.03 || y1r - y0r < 0.03) return;
            var cF = WD.inFace + WD.sgn * depthOff;
            var m9 = (WD.o === 'h')
              ? new T.Mesh(new T.BoxGeometry(u1 - u0, y1r - y0r, thick), mat)
              : new T.Mesh(new T.BoxGeometry(thick, y1r - y0r, u1 - u0), mat);
            if (WD.o === 'h') m9.position.set((u0 + u1) / 2, (y0r + y1r) / 2, cF);
            else m9.position.set(cF, (y0r + y1r) / 2, (u0 + u1) / 2);
            m9.userData.viz = viz9;
            grp.add(m9);
          }
          function faceStack(WD, isSau, u0, u1, y0r, y1r, withBattens) {
            if (SHED_BARE) return;   /* bare frame: no interior sheet */
            if (isSau) {
              faceRect(WD, 1, u0, u1, y0r, y1r, 0.002, 0.002, self9.materials.partFoil, 'layer_wall_foil');
              if (withBattens) for (var by9 = y0r + 0.2; by9 < y1r - 0.1; by9 += 0.4) {
                faceRect(WD, 1, u0 + 0.002, u1 - 0.002, by9 - 0.015, by9 + 0.015, 0.0185, 0.030, self9.materials.partTimber, 'layer_wall_face');
              }
              faceRect(WD, 1, u0, u1, y0r, y1r, 0.0415, 0.015, self9.materials.partLining, 'layer_wall_face');
              for (var gu9 = u0 + 0.1; gu9 < u1 - 0.01; gu9 += 0.1) {
                faceRect(WD, 1, gu9 - 0.002, gu9 + 0.002, y0r + 0.002, y1r - 0.002, 0.0495, 0.001, self9.materials.partGroove, 'layer_wall_face');
              }
            } else {
              /* dry side: one moisture-resistant gypsum sheet, pushed inboard
                 to a clean visible plane in FRONT of the studs (like the sauna
                 lining at 0.0415) -- it used to sit at 0.00725, nearly coplanar
                 with the stud inner face, so it was buried behind the bay wool
                 and read as bare frame through a window (Marek: mimo saunu chybi
                 sheet material). */
              faceRect(WD, 0, u0, u1, y0r, y1r, 0.0435, 0.0125, self9.materials.partGypsum, 'layer_wall_face');
            }
          }
          for (var wd9 = 0; wd9 < wallsDef.length; wd9++) {
            var WD = wallsDef[wd9];
            /* room segments along this wall */
            var segs9 = [];
            var rms9 = FT9 && FT9._rooms ? FT9._rooms(meMi) : [];
            if (rms9 && rms9.length) {
              for (var rs9 = 0; rs9 < rms9.length; rs9++) {
                for (var rr9 = 0; rr9 < rms9[rs9].rects.length; rr9++) {
                  var RB9 = rms9[rs9].rects[rr9];
                  var touch = (WD.o === 'h')
                    ? Math.abs(((WD.bound < 0) ? RB9[1] : RB9[3]) - WD.bound) < 0.12
                    : Math.abs(((WD.bound < 0) ? RB9[0] : RB9[2]) - WD.bound) < 0.12;
                  if (!touch) continue;
                  var a9 = (WD.o === 'h') ? RB9[0] : RB9[1], b9 = (WD.o === 'h') ? RB9[2] : RB9[3];
                  /* the rect's own n lied on the review's floorplan (magenta
                     in dry rooms) -- so ask the floorplan POINTWISE instead,
                     the exact oracle the partitions use: sample the segment's
                     midpoint 150 mm inside the wall and let _roomOf answer. */
                  var smid9 = (a9 + b9) / 2;
                  var sin9 = (WD.bound < 0) ? 0.15 : -0.15;
                  var spx9 = (WD.o === 'h') ? smid9 : WD.bound + sin9;
                  var spz9 = (WD.o === 'h') ? WD.bound + sin9 : smid9;
                  segs9.push([a9 + 0.001, b9 - 0.001, !!(FT9._isSaunaAt && FT9._isSaunaAt(meMi, spx9, spz9))]);
                }
              }
            }
            if (!segs9.length) segs9.push([-WD.segHalf, WD.segHalf, true]);
            /* GUARANTEE FULL COVERAGE: the room rects only line the stretches a
               room touches. After removing a partition a rect may not reach the
               wall, leaving a bare stretch where the frame + wool show through a
               window (Marek). Fill every gap between the sorted segments (and
               the ends) with a fallback face. The gap's type follows the oracle
               at its own midpoint, defaulting to sauna. */
            segs9.sort(function (p1, p2) { return p1[0] - p2[0]; });
            var covered = [], cursor9 = -WD.segHalf;
            for (var cg9 = 0; cg9 < segs9.length; cg9++) {
              var GA = segs9[cg9][0], GB = segs9[cg9][1];
              if (GA - cursor9 > 0.02) {
                var gm9 = (cursor9 + GA) / 2;
                var gpx9 = (WD.o === 'h') ? gm9 : (WD.bound + ((WD.bound < 0) ? 0.15 : -0.15));
                var gpz9 = (WD.o === 'h') ? (WD.bound + ((WD.bound < 0) ? 0.15 : -0.15)) : gm9;
                covered.push([cursor9, GA - 0.001, !!(FT9 && FT9._isSaunaAt && FT9._isSaunaAt(meMi, gpx9, gpz9))]);
              }
              covered.push(segs9[cg9]);
              cursor9 = Math.max(cursor9, GB);
            }
            if (WD.segHalf - cursor9 > 0.02) {
              var em9 = (cursor9 + WD.segHalf) / 2;
              var epx9 = (WD.o === 'h') ? em9 : (WD.bound + ((WD.bound < 0) ? 0.15 : -0.15));
              var epz9 = (WD.o === 'h') ? (WD.bound + ((WD.bound < 0) ? 0.15 : -0.15)) : em9;
              covered.push([cursor9, WD.segHalf, !!(FT9 && FT9._isSaunaAt && FT9._isSaunaAt(meMi, epx9, epz9))]);
            }
            segs9 = covered;
            /* openings on this wall, mapped to the wall axis */
            var opsW = [];
            var oAll = STATE.openings || [];
            for (var ow9 = 0; ow9 < oAll.length; ow9++) {
              var OP9 = oAll[ow9];
              if (OP9.mi !== meMi || OP9.wall !== WD.wall) continue;
              var ctr9 = WD.mir ? (WD.segHalf - OP9.off) : (OP9.off - WD.segHalf);
              opsW.push([ctr9 - OP9.w / 2, ctr9 + OP9.w / 2, y0F + (OP9.sill || 0), y0F + (OP9.sill || 0) + OP9.h]);
            }
            opsW.sort(function (q1, q2) { return q1[0] - q2[0]; });
            for (var sg9 = 0; sg9 < segs9.length; sg9++) {
              var SA = segs9[sg9][0], SB = segs9[sg9][1], isS9 = segs9[sg9][2];
              var topY9 = isS9 ? topSau : topDry;
              var cur9 = SA;
              for (var oq9 = 0; oq9 < opsW.length; oq9++) {
                var OA = Math.max(SA, opsW[oq9][0]), OB = Math.min(SB, opsW[oq9][1]);
                if (OB <= OA) continue;
                faceStack(WD, isS9, cur9, OA - 0.001, y0F, topY9, true);
                var oy0 = opsW[oq9][2], oy1 = Math.min(opsW[oq9][3], topY9 - 0.02);
                if (oy0 - y0F > 0.05) faceStack(WD, isS9, OA, OB, y0F, oy0 - 0.001, false);
                if (topY9 - oy1 > 0.05) faceStack(WD, isS9, OA, OB, oy1 + 0.001, topY9, false);
                cur9 = Math.max(cur9, OB + 0.001);
              }
              faceStack(WD, isS9, cur9, SB, y0F, topY9, true);
            }
          }
        } catch (eW1) {}
        for (var ws2 = 0; ws2 < WLY.sills.length; ws2++) {
          var Si = WLY.sills[ws2];
          if (Si.mi !== meMi) continue;
          var wgS = wallGeo[Si.wall]; if (!wgS) continue;
          var sil = new T.Mesh(wgS.horiz
            ? new T.BoxGeometry(Si.len, SW2.w, SW2.d)
            : new T.BoxGeometry(SW2.d, SW2.w, Si.len), pMat(Si.pid));
          sil.position.set(wgS.horiz ? Si.u : wgS.px,
            PANEL_BASE + SW2.w + Si.y - SW2.w / 2,
            wgS.horiz ? wgS.pz : Si.u);
          sil.userData.viz = 'wallframe';
          sil.userData.pid = Si.pid;
          sil.userData.prof = 'sill';
          grp.add(sil);
        }
        for (var wf = 0; wf < WLY.studs.length; wf++) {
          var St = WLY.studs[wf];
          if (!_sideFilled(St.wall)) continue;
          if (St.mi !== meMi) continue;
          var sm = new T.Mesh(new T.BoxGeometry(
            (St.wall === 'rear' || St.wall === 'front') ? SW2.w : SW2.d,
            St.len,
            (St.wall === 'rear' || St.wall === 'front') ? SW2.d : SW2.w), pMat(St.pid));
          var px2 = 0, pz2 = 0;
          if (St.wall === 'rear') { px2 = St.u; pz2 = -(hz + INNER_MARGIN) + inset; }
          else if (St.wall === 'front') { px2 = St.u; pz2 = (hz + INNER_MARGIN) - inset; }
          else if (St.wall === 'sideL') { pz2 = St.u; px2 = -(hx + INNER_MARGIN) + inset; }
          else { pz2 = St.u; px2 = (hx + INNER_MARGIN) - inset; }
          sm.position.set(px2, yS0 + (St.y0 || 0) + St.len / 2, pz2);
          sm.userData.viz = 'wallframe';
          sm.userData.pid = St.pid;
          sm.userData.prof = 'stud';
          grp.add(sm);
        }
      } catch (eWF) {}
    },


    _buildCuts: function (grp, mi, t) {
      var T = global.THREE;
      if (!this.materials.primer) {
        this.materials.primer = new T.MeshStandardMaterial({ color: 0x7d3b2d, roughness: 0.62, metalness: 0.35 });
      }
      if (!this.materials.shaftDark) {
        this.materials.shaftDark = new T.MeshStandardMaterial({ color: 0x17181a, roughness: 0.95, metalness: 0.05 });
      }
      if (!this.materials.tread) {
        this.materials.tread = new T.MeshStandardMaterial({ color: 0xb08d57, roughness: 0.72, metalness: 0.03 });
      }
    },

    _buildFloorGuard: function (grp, hole) {
      this._buildRailRun(grp, _guardEdgesFor(hole), FLOOR_TOP + 0.03, 1.0);
    },



    _openingViz: 'opening3d',


    /* Timber sauna cabin: deck, corner posts, flat clad walls and a gable roof
       whose pitch the user sets in STEP 1. Wall openings are cut the same way
       the corrugated shell used to do it -- _corrPanel with zero depth gives a
       flat panel and keeps the hole handling we already trust. */
    _buildCabin: function (typeKey, colorHex, wallHoles, inkColor, roofHoles, floorHoles) {
      var T = global.THREE;
      var t = _saunaType();
      var L = t.L, W = _extW(), H = t.H;
      var hl = L / 2, hw = W / 2;
      var grp = new T.Group();

      if (!this.materials.timber) {
        this.materials.timber = new T.MeshStandardMaterial({ color: 0x9c7248, roughness: 0.78, metalness: 0.02 });
      }
      if (!this.materials.timberDark) {
        this.materials.timberDark = new T.MeshStandardMaterial({ color: 0x7d5a38, roughness: 0.82, metalness: 0.02 });
      }
      if (!this.materials.roofSheet) {
        this.materials.roofSheet = new T.MeshStandardMaterial({ color: 0x3b3f45, roughness: 0.55, metalness: 0.35, side: T.DoubleSide });
      }
      var wood = this.materials.timber;
      var woodD = this.materials.timberDark;

      /* deck slab: a SKIN. Untagged it survived the strip-down and z-fought
         the floor frame's own OSB -- the glitch in the photo. */
      /* SHELTER: no deck slab -- there is no floor at all (posts + roof). */

      /* corner posts: LOOK elements from the clad cabin, not frame members --
         under the mono rise they ended mid-wall and read as mystery stubs.
         Tagged as skin; the frame's own panel end studs own the corners. */
      var corners = [[-1, -1], [1, -1], [-1, 1], [1, 1]];
      /* SHELTER: no decorative look posts -- the real structural posts (with
         their own footings) are the only posts (Marek: jen sloupy drzici strechu). */

      /* wall panels -- flat (depth 0) so openings keep working */
      var wh = wallHoles || {};
      var panelH = H - FLOOR_TOP;
      var panelY = FLOOR_TOP + panelH / 2;
      var sideLen = L - 2 * POST;
      var BOARD = 0.14;

      /* SHELTER: no flat skin wall panels -- they showed as an opaque white
         face from the inside on filled AND empty sides (single-sided planes,
         drawn for all four walls regardless of the fill). The shelter's look
         is the screen boards + rails from _buildScreens and the open posts. */

      /* The roof is its own thing to show or hide. It came free with the cabin,
         which is why a plan step opened with a roof over the plan -- you cannot
         draw a partition through a roof you are looking at from above. */
      if (_shows('roof', true)) {
        if (STATE.roofType === 'gable') this._buildGableRoof(grp, L, W, H, roofHoles);
        else this._buildMonoRoof(grp, L, W, H, roofHoles);
      }

      if (floorHoles && floorHoles.length) {
        for (var ghi = 0; ghi < floorHoles.length; ghi++) this._buildFloorGuard(grp, floorHoles[ghi]);
      }
      return grp;
    },

    /* Mono-pitch roof. One plane falling across the width: low at -x (left
       wall), high at +x (right wall). Same overhang and thickness as the gable
       so the two types stay swappable. Needs a right-triangle infill at each
       end plus a rectangular strip closing the high wall, because unlike the
       gable both eaves sit at different heights. */
    _buildMonoRoof: function (grp, L, W, H, roofHoles) {
      var T = global.THREE;
      var pitch = clamp(STATE.roofPitch || 0, 0, 45);
      var ov = STATE.roofOver;
      var rl = L + ov.z0 + ov.z1;
      var cz = (ov.z1 - ov.z0) / 2;
      var tanP = Math.tan(pitch * Math.PI / 180);
      /* Same pivot rule as the gable, one plane: through the LOW wall top at
         (-W/2, H) and the HIGH wall top at (+W/2, H + W tan). Rise is measured
         over the wall span; the overhang extends the plane past both plates
         along the same slope -- below H on the low side, above the high wall on
         the high side. It used to be measured over the roof rectangle with the
         low eave pinned at H, so the plane cleared both plates by ov x tan and
         dragging the roof rect floated the roof off the building. */
      var wallRise = W * tanP;
      var ang = Math.atan2(wallRise, W);
      var THK = 0.06;

      if (pitch < 0.5) {
        var rw0 = W + ov.x0 + ov.x1;
        var flat = new T.Mesh(new T.BoxGeometry(rw0, THK, rl), this.materials.roofSheet);
        flat.position.set((ov.x1 - ov.x0) / 2, H + THK / 2, cz);
        flat.userData.viz = 'roof';
        grp.add(flat);
        return;
      }

      var run = W + ov.x0 + ov.x1;
      var slope = run / Math.cos(ang);
      var pane = new T.Mesh(new T.BoxGeometry(slope, THK, rl), this.materials.roofSheet);
      /* centre of the extended run, on the plane through the plates */
      var cxm = (ov.x1 - ov.x0) / 2;
      pane.position.set(cxm, (H - 0.09) + (W / 2 + cxm) * tanP + THK / 2, cz);
      pane.rotation.z = ang;
      pane.userData.viz = 'roof';
      grp.add(pane);
      /* P3, mono: the same seat rule, two plates, one rafter -- underside
         through the low plate top AND the high plate top, body above both */
      try {
        var RFm = global.SaunaRoofFrame, RDm = RFm.RAFTER;
        var laym = RFm.layout();
        var liftm = (RDm.h / 2) / Math.cos(ang);
        var meMim = (typeof this._curMi === 'number') ? this._curMi : 0;
        for (var rmf = 0; rmf < laym.rafters.length; rmf++) {
          var Rm = laym.rafters[rmf];
          if (Rm.mi !== meMim || Rm.side !== 0) continue;
          var rb = new T.Mesh(new T.BoxGeometry(Rm.len, RDm.h, RDm.w), this.materials.timber);
          rb.position.set(cxm, (H - 0.09) + (W / 2 + cxm) * tanP + liftm, Rm.z);
          rb.rotation.z = ang;
          rb.userData.viz = 'roofframe';
          grp.add(rb);
        }
        /* R1: the hydro foil, first skin over the bones. A 2 mm dark film
           parallel to the roof plane, 1 mm above the rafters' top faces,
           the whole rectangle including the overhangs -- stapled down, the
           counter-battens and the rest come above it. */
        if (!this.materials.roofHydro) this.materials.roofHydro = new T.MeshStandardMaterial({ color: 0x36514b, roughness: 0.6, metalness: 0.08 });
        var hyd = new T.Mesh(new T.BoxGeometry(slope - 0.004, 0.002, rl - 0.004), this.materials.roofHydro);
        hyd.position.set(cxm, (H - 0.09) + (W / 2 + cxm) * tanP + 2 * liftm + 0.002, cz);
        hyd.rotation.z = ang;
        hyd.userData.viz = 'layer_roof_hydro';
        grp.add(hyd);
        /* R2 REVISED: no PIR -- the thermal envelope closed at the ceiling
           wool, so the roof stays a COLD VENTED one. Counter-battens 30x50
           run ALONG each rafter over the foil (they pin it down and open
           the vent gap), and the covering rides on them. */
        for (var cb9 = 0; cb9 < laym.rafters.length; cb9++) {
          var Rcb = laym.rafters[cb9];
          if (Rcb.mi !== meMim || Rcb.side !== 0) continue;
          var cbb = new T.Mesh(new T.BoxGeometry(Rcb.len - 0.004, 0.030, 0.05), this.materials.partTimber || this.materials.timber);
          cbb.position.set(cxm, (H - 0.09) + (W / 2 + cxm) * tanP + 2 * liftm + 0.004 + 0.015, Rcb.z);
          cbb.rotation.z = ang;
          cbb.userData.viz = 'layer_roof_batten';
          grp.add(cbb);
        }
        /* R2b: BATTENS ACROSS -- the review asked "nevadi ze late nejsou
           kontra?" and it would: sheet covering needs level fixing lines,
           counter-battens alone give none. Rows @500 up the slope, riding
           the counter-battens, and the covering climbs 31 mm onto them. */
        for (var xb9 = -slope / 2 + 0.15; xb9 < slope / 2 - 0.05; xb9 += 0.5) {
          var cxb = new T.Mesh(new T.BoxGeometry(0.05, 0.030, rl - 0.004), this.materials.partTimber || this.materials.timber);
          var xw9 = cxm + xb9 * Math.cos(ang);
          cxb.position.set(xw9, (H - 0.09) + (W / 2 + xw9) * tanP + 2 * liftm + 0.004 + 0.030 + 0.001 + 0.015, cz);
          cxb.rotation.z = ang;
          cxb.userData.viz = 'layer_roof_batten';
          grp.add(cxb);
        }
        /* R3: the covering on the CROSS battens, 1 mm of air, 20 mm drip
           edges proud all round */
        var rtp = new T.Mesh(new T.BoxGeometry(slope + 0.120, 0.030, rl + 0.120), this._roofTopMat());
        rtp.position.set(cxm, (H - 0.09) + (W / 2 + cxm) * tanP + 2 * liftm + 0.004 + 0.030 + 0.001 + 0.030 + 0.001 + 0.015, cz);
        rtp.rotation.z = ang;
        rtp.userData.viz = 'layer_roof_top';
        grp.add(rtp);
        /* the ledger ring first: the frame the joists hang in */
        for (var lr9 = 0; lr9 < laym.ceilRing.length; lr9++) {
          var LR = laym.ceilRing[lr9];
          if (LR.mi !== meMim) continue;
          var lrb = (LR.o === 'v')
            ? new T.Mesh(new T.BoxGeometry(0.06, RDm.h, LR.len), this.materials.timber)
            : new T.Mesh(new T.BoxGeometry(LR.len, RDm.h, 0.06), this.materials.timber);
          /* the ring HANGS from the bearing line: its TOP face is flush with
             the cap plate's top (1 mm shy), the body below -- so the rafters
             pass clear above it on every side, including the low one where
             they sit directly on the cap */
          lrb.position.set(LR.o === 'v' ? LR.x : 0, LR.y - 0.001 - RDm.h / 2, LR.o === 'v' ? 0 : LR.z);
          lrb.userData.viz = 'roofframe';
          grp.add(lrb);
        }
        /* S4: THE CEILING WOOL. Batts between the joists, exactly the floor
           discipline: one field per bay, bounded by the joists' side faces
           (1 mm), by the ledger ring's inner faces (1 mm), by the joists'
           top plane above and -- over the sauna -- by the FOIL below (2 mm
           of air to it; over dry rooms, 2 mm to the lining). Friction fit:
           the budget says so explicitly, no fasteners exist for wool. */
        try {
          if (!this.materials.floorWool) this.materials.floorWool = new T.MeshStandardMaterial({ color: 0xd8d8d2, roughness: 0.96, metalness: 0.0 });
          var mcz = [];
          for (var wc9 = 0; wc9 < laym.chords.length; wc9++) {
            if (laym.chords[wc9].mi === meMim && laym.chords[wc9].mono) mcz.push(laym.chords[wc9].z);
          }
          mcz.sort(function (a9, b9) { return a9 - b9; });
          if (mcz.length) {
            var ledIn9 = 0.1785;
            var wzEdges = [-(L / 2 - ledIn9) + 0.001];
            for (var we9 = 0; we9 < mcz.length; we9++) {
              wzEdges.push(mcz[we9] - RDm.w / 2 - 0.001);
              wzEdges.push(mcz[we9] + RDm.w / 2 + 0.001);
            }
            wzEdges.push((L / 2 - ledIn9) - 0.001);
            var mcW9 = (laym.chords.filter(function (c9) { return c9.mono; })[0] || { len: W - 0.36 }).len;
            var jCy = ((H - 0.09) - 0.001) - RDm.h / 2;
            for (var wb9 = 0; wb9 + 1 < wzEdges.length; wb9 += 2) {
              var wz0 = wzEdges[wb9], wz1 = wzEdges[wb9 + 1];
              if (wz1 - wz0 < 0.03) continue;
              if (SHED_BARE) continue;   /* non-insulated: no ceiling wool */
              var cwB = new T.Mesh(new T.BoxGeometry(mcW9 - 0.002, RDm.h - 0.002, wz1 - wz0), this.materials.floorWool);
              cwB.position.set(0, jCy, (wz0 + wz1) / 2);
              cwB.userData.viz = 'layer_ceil_wool';
              grp.add(cwB);
            }
          }
        } catch (eCW9) {}
        /* the flat mono ceiling joists, from the layout: level at the
           bearing line, 1 mm above the low cap, hanger-hung ends clear of
           both walls */
        for (var mcj = 0; mcj < laym.chords.length; mcj++) {
          var MC = laym.chords[mcj];
          if (MC.mi !== meMim || !MC.mono) continue;
          var mcb = new T.Mesh(new T.BoxGeometry(MC.len - 0.004, RDm.h, RDm.w), this.materials.timber);
          mcb.position.set(0, MC.y - 0.001 - RDm.h / 2, MC.z);   /* joists flush-top with the ring */
          mcb.userData.viz = 'roofframe';
          grp.add(mcb);
        }
      } catch (eRFm) {}

      for (var g = -1; g <= 1; g += 2) {
        var tri = new T.Shape();
        tri.moveTo(-W / 2, 0);
        tri.lineTo(W / 2, 0);
        tri.lineTo(W / 2, wallRise);
        tri.lineTo(-W / 2, 0);
        var geo = new T.ExtrudeGeometry(tri, { depth: 0.04, bevelEnabled: false });
        var m = new T.Mesh(geo, this.materials.timber);
        m.position.set(0, H, g * (L / 2 - 0.02));
        m.userData.viz = 'roof';
        grp.add(m);
      }

      var strip = new T.Mesh(new T.BoxGeometry(0.04, wallRise, L), this.materials.timber);
      strip.position.set(W / 2 - 0.02, H + wallRise / 2, 0);
      strip.userData.viz = 'roof';
      grp.add(strip);
    },

    /* Gable roof. rise comes from STATE.roofPitch (degrees), the ridge runs
       along the long axis, and the eaves overhang on every side.

       The rafter pivots on the WALL PLATE. That one sentence is the whole
       geometry, and it is what the previous version got backwards: it measured
       the rise over the roof rectangle (walls + overhang) and pinned the eaves
       at wall-top height -- so the plane only touched the walls when the
       overhang was zero, and every millimetre of overhang lifted it off the
       plate by ov x tan(pitch). Drag the roof rect out and the roof visibly
       took off from the building while the ridge climbed: "the roof jumps off
       the structure, the junction does not move, the roof stretches".

       Correct: the plane passes THROUGH the wall top at x = +-W/2, the ridge
       stands over the wall centre at H + (W/2) tan -- the wall span alone
       decides the apex -- and the overhang continues the same rafter DOWN the
       slope, so the eave drops BELOW wall height by ov x tan. Extend the
       overhang and the eave reaches further and lower; the line where roof
       meets wall does not move, because that line is the building. */
    _buildGableRoof: function (grp, L, W, H, roofHoles) {
      var T = global.THREE;
      var pitch = clamp(STATE.roofPitch || 0, 0, 45);
      var ov = STATE.roofOver;
      var rl = L + ov.z0 + ov.z1;
      var cz = (ov.z1 - ov.z0) / 2;
      var tanP = Math.tan(pitch * Math.PI / 180);
      var rise = (W / 2) * tanP;                        /* wall span only */
      var ang = Math.atan2(rise, W / 2);
      var THK = 0.06;

      if (pitch < 0.5) {
        var rw0 = W + ov.x0 + ov.x1;
        var flat = new T.Mesh(new T.BoxGeometry(rw0, THK, rl), this.materials.roofSheet);
        flat.position.set((ov.x1 - ov.x0) / 2, H + THK / 2, cz);
        flat.userData.viz = 'roof';
        grp.add(flat);
        return;
      }

      /* each side is its own rafter: run = half the wall span plus that side's
         overhang, eave at H - ov tan, ridge at H + rise, so the plane passes
         through the plate at exactly H. The two panes differ in length when the
         overhangs differ -- which is what an asymmetric overhang IS. */
      for (var s = -1; s <= 1; s += 2) {
        var ovS = (s < 0) ? ov.x0 : ov.x1;
        var run = W / 2 + ovS;
        var slopeS = run / Math.cos(ang);
        var pane = new T.Mesh(new T.BoxGeometry(slopeS, THK, rl), this.materials.roofSheet);
        pane.position.set(s * run / 2, H + ((W / 2 - ovS) / 2) * tanP + THK / 2, cz);
        pane.rotation.z = -s * ang;
        pane.userData.viz = 'roof';
        grp.add(pane);
      }
      /* the frame under the sheet: same line, dropped by half sheet + half
         rafter along the normal. viz 'roofframe' for the K6 eye. */
      try {
        /* P3: the rafter SITS ON the plate. It used to hang under the sheet
           line -- axis dropped by half sheet plus half rafter -- so inside the
           wall face its body dipped BELOW wall top, into the plate band, into
           window heads. The seat cut (birdsmouth) means the opposite: the
           rafter's UNDERSIDE passes through plate top at the wall line and the
           whole body rides above it. So the axis is LIFTED off the H-line by
           half the rafter depth along the slope. The overhang tail's underside
           then follows the same line down past the wall -- which is the soffit
           -- and nothing structural ever enters the band the P1 clamp keeps
           the glazing out of. The notch itself is not carved (that is CSG for
           another day); the seat GEOMETRY -- what touches what, where -- is
           now true. */
        var RF = global.SaunaRoofFrame, RD = RF.RAFTER;
        var lay = RF.layout();
        var lift = (RD.h / 2) / Math.cos(ang);
        var meMi2 = (typeof this._curMi === 'number') ? this._curMi : 0;
        /* EVERY MEMBER CUT TO WHAT IT MEETS. The boxes are gone: each roof
           member is now its true SIDE PROFILE, drawn in the section plane and
           extruded one member wide -- so the cuts are real geometry, not
           squares pretending.

           The rafter: a parallelogram between its underside line and its top
           line, with BOTH ends vertical -- a plumb cut at the ridge, seating
           the pair exactly against the ridge board's faces, and a plumb cut
           at the tail, so the fascia meets a flat vertical end. */
        var RIDGE_W = 0.06, RIDGE_H = 0.20;
        var kTop = RD.h / Math.cos(ang);                 /* vertical thickness of the rafter */
        /* R0, the BEARING LINE, measured at last instead of assumed: sole
           plate 60 + studs + cap 60 puts the cap's TOP FACE at t.H - 0.09.
           The v181 guess of -0.03 left the whole roof frame hovering 60 mm
           above the wood; the ceiling frame measured the truth in v204 and
           now the rafters come down to the same plane. One constant, every
           frame member derives from it. */
        var BEAR = H - 0.09;
        function yUnder(x) { return BEAR + (W / 2 - Math.abs(x)) * tanP; }
        function profileMesh(pts, mat) {
          var sh = new T.Shape();
          sh.moveTo(pts[0][0], pts[0][1]);
          for (var pi2 = 1; pi2 < pts.length; pi2++) sh.lineTo(pts[pi2][0], pts[pi2][1]);
          sh.lineTo(pts[0][0], pts[0][1]);
          return new T.Mesh(new T.ExtrudeGeometry(sh, { depth: RD.w, bevelEnabled: false }), mat);
        }
        /* the rafter's LENGTH RULE: its tail must run past the ceiling
           joist's outer corner, always -- so the eave edge is drawn by the
           rafter BELOW the joist's underside, never by the joist's end cut.
           With a generous overhang this costs nothing; with a stingy one the
           tail grows the little extra it needs. */
        var jCorner = xOnTopPlane(BEAR);
        for (var rf = 0; rf < lay.rafters.length; rf++) {
          var Rr = lay.rafters[rf];
          if (Rr.mi !== meMi2) continue;
          if (Rr.side === 0) continue;
          var ovR = (Rr.side < 0) ? ov.x0 : ov.x1;
          var xTail = Rr.side * (W / 2 + ovR), xRidge = Rr.side * (RIDGE_W / 2);   /* SHELTER: tail ends at the fixed drip edge */
          var rmP = profileMesh([
            [xRidge, yUnder(xRidge)], [xTail, yUnder(xTail)],
            [xTail, yUnder(xTail) + kTop], [xRidge, yUnder(xRidge) + kTop]
          ], this._pidMat(Rr.pid, this._woodHex()));
          rmP.position.z = Rr.z - RD.w / 2;
          rmP.userData.viz = 'roofframe';
          rmP.userData.pid = Rr.pid;
          rmP.userData.prof = 'rafter';
          grp.add(rmP);
        }
        /* R1, gable: the hydro foil per roof side -- a 2 mm film parallel to
           each rafter plane, 1 mm above the top faces (yUnder + kTop),
           ridge to tails, the whole length. */
        try {
          if (!this.materials.roofHydro) this.materials.roofHydro = new T.MeshStandardMaterial({ color: 0x36514b, roughness: 0.6, metalness: 0.08 });
          var rlG = (lay.fascias && lay.fascias.length) ? lay.fascias[0].len : (lay.ridge && lay.ridge.length ? lay.ridge[0].len : 0);
          var czG = (ov.z1 - ov.z0) / 2;
          if (rlG > 0.1) for (var hs = -1; hs <= 1; hs += 2) {
            var ovH = (hs < 0) ? ov.x0 : ov.x1;
            var xT9 = W / 2 + ovH, xR9 = RIDGE_W / 2;   /* SHELTER: covering ends at the fixed drip edge */
            var slp9 = (xT9 - xR9) / Math.cos(ang);
            var xc9 = hs * (xR9 + xT9) / 2;
            var hyd9 = new T.Mesh(new T.BoxGeometry(slp9 - 0.004, 0.002, rlG - 0.004), this.materials.roofHydro);
            hyd9.position.set(xc9, yUnder(xc9) + kTop + 0.002, czG);
            hyd9.rotation.z = -hs * ang;
            hyd9.userData.viz = 'layer_roof_hydro';
            grp.add(hyd9);
            /* R2 REVISED per side: counter-battens along the rafters over
               the foil -- the cold roof's vent gap; no PIR (the ceiling
               wool is the thermal envelope) */
            for (var cb8 = 0; cb8 < lay.rafters.length; cb8++) {
              var Rc8 = lay.rafters[cb8];
              if (Rc8.mi !== meMi2 || Rc8.side !== hs) continue;
              var slC = (xT9 - xR9) / Math.cos(ang);
              var cb8m = new T.Mesh(new T.BoxGeometry(slC - 0.004, 0.030, 0.05), this.materials.partTimber || this.materials.timber);
              cb8m.position.set(xc9, yUnder(xc9) + kTop + 0.004 + 0.015, Rc8.z);
              cb8m.rotation.z = -hs * ang;
              cb8m.userData.viz = 'layer_roof_batten';
              grp.add(cb8m);
            }
            /* R2b per side: level battens @500 across the counter-battens */
            for (var xb8 = -slp9 / 2 + 0.15; xb8 < slp9 / 2 - 0.05; xb8 += 0.5) {
              var cxb8 = new T.Mesh(new T.BoxGeometry(0.05, 0.030, rlG - 0.004), this.materials.partTimber || this.materials.timber);
              var xw8 = xc9 + hs * xb8 * Math.cos(ang);
              cxb8.position.set(xw8, yUnder(xw8) + kTop + 0.004 + 0.030 + 0.001 + 0.015, czG);
              cxb8.rotation.z = -hs * ang;
              cxb8.userData.viz = 'layer_roof_batten';
              grp.add(cxb8);
            }
            /* R3 per side: the covering on the CROSS battens, drip edges proud */
            var rtp9 = new T.Mesh(new T.BoxGeometry(slp9 + 0.120, 0.030, rlG + 0.120), this._roofTopMat());
            rtp9.position.set(xc9, yUnder(xc9) + kTop + 0.004 + 0.030 + 0.001 + 0.030 + 0.001 + 0.015, czG);
            rtp9.rotation.z = -hs * ang;
            rtp9.userData.viz = 'layer_roof_top';
            grp.add(rtp9);
          }
        } catch (eRH9) {}
        /* the collar and the ceiling joist obey one rule: the end face is
           SAWN PARALLEL TO THE ROOF and runs until it lands on the rafter's
           FAR (top) plane -- nailed across the flanks, nothing short, nothing
           square. x on that plane, at height y:  x = W/2 - (y - H - kTop)/tan */
        function xOnTopPlane(y) { return W / 2 - (y - BEAR - kTop) / tanP; }
        for (var cl = 0; cl < lay.collars.length; cl++) {
          var C = lay.collars[cl];
          if (C.mi !== meMi2) continue;
          var cy0 = C.y - 0.12, cy1 = C.y;
          var cx0 = xOnTopPlane(cy0), cx1 = xOnTopPlane(cy1);
          var cmP = profileMesh([[-cx0, cy0], [cx0, cy0], [cx1, cy1], [-cx1, cy1]],
            this._pidMat(C.pid, this._woodHex()));
          /* ONE SYSTEM, the common one: ties are NAILED TO THE FLANK. The
             collar was sitting on the rafter's axis -- inside the wood --
             while the joist sat on the side. Both now lap the same face of
             their rafter, the way a framer actually nails them. */
          cmP.position.z = C.z + RD.w / 2;
          cmP.userData.viz = 'roofframe';
          cmP.userData.pid = C.pid;
          cmP.userData.prof = 'collar';
          grp.add(cmP);
        }
        for (var ch2 = 0; ch2 < lay.chords.length; ch2++) {
          var Ch = lay.chords[ch2];
          if (Ch.mi !== meMi2) continue;
          if (Ch.mono) continue;   /* mono joists render flat in the mono block */
          /* the bottom rail obeys the SAME rule as the top one: both end
             corners lie in the rafter's far (top) plane, the end face sawn
             parallel to the roof. And it is RAISED by the rafter's vertical
             thickness -- which is not an arbitrary nudge: at exactly that
             lift the joist's bottom corner lands on the OUTER WALL FACE, so
             the piece runs plane-to-plane without a stub, and the rafter
             tail below it reads as a longer side overhang. One rule, both
             rails, one saw angle. */
          /* back DOWN: the joist RESTS ON the wall panels' top profile --
             bearing, not floating. The ends keep the plane rule. */
          var jy0 = Ch.y, jy1 = jy0 + RD.h;
          /* SHELTER: joist ends TRIMMED PLUMB at the roof edge -- with the
             fixed 100 mm drip there is no room for the sawn-parallel run past
             the wall (nothing may poke past the roof). */
          var xLimJ = W / 2 + Math.min(ov.x0 || 0, ov.x1 || 0) - 0.03;
          var jx0 = Math.min(xLimJ, xOnTopPlane(jy0)), jx1 = Math.min(xLimJ, xOnTopPlane(jy1));
          var chP = profileMesh([[-jx0, jy0], [jx0, jy0], [jx1, jy1], [-jx1, jy1]],
            this._pidMat(Ch.pid, this._woodHex()));
          chP.position.z = Ch.z + RD.w / 2;
          chP.userData.viz = 'roofframe';
          chP.userData.pid = Ch.pid;
          chP.userData.prof = 'chord';
          grp.add(chP);
        }
        if (lay.ridge.length) {
          /* the board's top edge meets the rafters' top edge where the plumb
             cuts land -- the pair and the board close at one highest line */
          var ridgeTopY = H + (W / 2 - RIDGE_W / 2) * tanP + RD.h / Math.cos(ang);
          var rg = new T.Mesh(new T.BoxGeometry(RIDGE_W, RIDGE_H, lay.ridge[0].len), this.materials.timber);
          rg.position.set(0, ridgeTopY - RIDGE_H / 2, cz);
          rg.userData.viz = 'roofframe';
          grp.add(rg);
        }
        for (var ff = 0; ff < lay.fascias.length; ff++) {
          var Fa = lay.fascias[ff];
          if (Fa.mi !== meMi2) continue;
          /* the fascia follows the TAILS, not the layout's overhang number:
             v134 grew short tails past the joist corner and the board on that
             side stayed at the old plane -- buried behind the new ends. One
             tail rule, one board rule: the fascia hangs on the plumb faces
             wherever they actually ended up, covering their full height. */
          var ovF = (Fa.side < 0) ? ov.x0 : ov.x1;
          var xT = W / 2 + ovF;   /* SHELTER: fascia at the fixed drip edge */
          var yT2 = yUnder(xT);
          var fb = new T.Mesh(new T.BoxGeometry(0.025, kTop + 0.03, Fa.len), this.materials.timber);
          fb.position.set(Fa.side * (xT + 0.0125), yT2 + kTop / 2, cz);
          fb.userData.viz = 'roofframe';
          grp.add(fb);
        }
      } catch (eRF) {}

      /* (removed) the old gable infill triangle -- a solid brown plane behind
         the gable cladding. Now the gable is fully clad with planks and the
         wind barrier W3 closes it separately, so this plane only showed
         THROUGH the joints between narrow planks (Marek: a solid surface over
         the seams, something extra). The cladding is the finish here. */
    },

    build: function () {
      var eng = global.SaunaEngine;
      var T = global.THREE;
      if (!eng || !eng.group || !T) return;
      this._ensure();

      function disposeDeep(o) {
        if (o.geometry) o.geometry.dispose();
        for (var j = 0; j < o.children.length; j++) disposeDeep(o.children[j]);
      }
      while (eng.group.children.length) disposeDeep(eng.group.children.pop());
      this._panelMats = {}; this._panelSeq = {}; this._nanSkipped = 0;


      var mi;
      for (mi = 0; mi < STATE.modules.length; mi++) {
        var mod = STATE.modules[mi];
        this._curMi = mi;   /* the floor frame filters by module DURING the build,
                               and userData.moduleIdx is only stamped after it */
        var c = this._buildCabin(mod.type, _colorOf(mod).hex, _holesFor(mi), _colorOf(mod).ink,
          _cutRoofHolesFor(mi, _typeOf(mod)), _cutFloorHolesFor(mi));
        c.position.set(mod.x || 0, _yFor(mi, 0), mod.z || 0);
        c.rotation.y = mod.rot || 0;
        c.userData.moduleIdx = mi;
        this._buildCuts(c, mi, _typeOf(mod));
        /* the frame has no business in the LAYOUT plan: seen from above the
           extended cap plates read as bars sticking past the building. The
           plan is rooms and openings; the frame belongs to 3D and to the
           frame stage of the eye. */
        if ((STATE.mode | 0) !== 3) this._buildFrames(c, mi, _typeOf(mod));
        if ((STATE.mode | 0) !== 3) { this._buildOpenPosts(c, mi, _typeOf(mod)); this._buildScreens(c, mi, _typeOf(mod)); }
        /* W5 planks are a LAYER of the wall now, not a mode-5 decoration --
           they build in every mode (the review could not see them because
           this call sat behind the lining gate); the frame strip below does
           not touch layer_wall_clad, only the old 'facade' extras. */
        if (STATE.envelope === 'exterior') {
          this._buildFacade(c, mi, _typeOf(mod));
          this._buildGutters(c, mi, _typeOf(mod));
          this._buildRoofFlashing(c, mi, _typeOf(mod));
        }
        /* SHELTER: switching a side on is THE decision here, so the "+" discs
           stay up by default -- in the EXTERIOR step and once the wizard is
           closed alike (Marek: nechme je defaultne zaple i bez editace). */
        /* THE PLUS DISCS ONLY IN EXTERIOR (Marek): switching a side on is that
           step's decision, and out of it they are clutter standing on the
           ground under the model */
        if (_editable('look')) _ModCtl.decorateFill(c, mi, _typeOf(mod));
        /* THE PLAN IS THE DRAWING. In LAYOUT the wall is the overlay band and
           the opening is its symbol -- but the 3D wall skins and the windows'
           own frames, seen from straight above, were printing themselves over
           the thin lines and the breaks. In mode 3 the third dimension of the
           perimeter steps aside: skins, glazing and opening bodies hide, the
           band and its interruptions carry the whole story. Everything comes
           back with the next rebuild out of the step. */
        /* THE 2D VIEW IS A PLAN. Looking straight down at a finished building
           you see a roof, which is exactly what a plan must not show -- so the
           same strip the FLOORPLAN step uses now runs for ANY 2D view, not just
           inside that step (Marek: the roof must not cover the view). Cheaper
           and more predictable than a clipping plane, and it reuses the viz
           tags the build already carries. */
        var planHide = (STATE.mode | 0) === 3 || STATE.view === '2d';
        if (planHide || FRAME_ONLY) {
          c.traverse(function (o3) {
            var v3 = o3.userData && o3.userData.viz;
            var isSkin = (v3 === 'facade' || v3 === 'front' || v3 === 'rear' ||
                          v3 === 'sideL' || v3 === 'sideR' || v3 === 'railing' || v3 === 'roof' ||
                          v3 === 'floorskin' || v3 === 'cornerpost' || v3 === 'plinth');
            /* the plan additionally hides the openings' 3D bodies; the
               frame-only strip keeps them -- they are placed products, not
               layers, and the frame needs them to prove its kings */
            /* THE PLAN IS INTERIOR + POSTS. Strip the whole roof (frame and its
               covering/gutter/flashing layers) and the foundation ghosts, so
               seen from above the drawing reads clean and nothing hangs over the
               fitout. The support posts (layer_roof_post) stay. */
            var planStrip = planHide && (
              v3 === 'roofframe' || v3 === 'foundation' ||
              v3 === 'layer_roof_flash' || v3 === 'layer_roof_gutter' ||
              v3 === 'layer_roof_hydro' || v3 === 'layer_roof_batten' || v3 === 'layer_roof_top' || v3 === 'layer_roof_header');
            if (isSkin || planStrip || (planHide && v3 === 'opening3d')) o3.visible = false;
          });
        }
        eng.group.add(c);
      }

      /* No small sample beside the cabin. The section is the ASSEMBLY step and
         nothing else -- that branch above returns before we get here. Leaving a
         mode-5 route in meant a saved showDetail could put a wall sample next to
         the finished building on a page nobody had asked to edit. */


      var box = new T.Box3().setFromObject(eng.group);
      if (!box.isEmpty()) {
        var size = box.getSize(new T.Vector3());
        var ctr = box.getCenter(new T.Vector3());
        /* THE SHADOW IS WHY THE BUILDING WAS SMALL, AND IT IS THE ONLY
           DIFFERENCE AGAINST THE SHED.

           It was drawn 1.55x the model wide and 1.35x long AND put in the
           group -- and fitCameraToFootprint frames the group. So the camera
           has been framing the shadow all along and the building came out at
           two thirds of the frame, whatever distance was computed. The shed
           has no ground in its group at all, which is why the plain stage fit
           looks right there and never did here (Marek: vem to z shed).

           Two changes, either of which would do, both cheap: it is the size of
           the model now, not half again as big, and it goes in the SCENE, not
           the group. The scene is not emptied by a rebuild, so the previous
           one is taken out and its geometry freed. */
        var sh = new T.Mesh(
          new T.PlaneGeometry(size.x, size.z),
          new T.MeshBasicMaterial({ map: this._shadow(), transparent: true, depthWrite: false })
        );
        sh.rotation.x = -Math.PI / 2;
        sh.position.set(ctr.x, 0.005, ctr.z);
        sh.renderOrder = -1;
        sh.userData.arSkip = true;
        try {
          if (this._shadowMesh && this._shadowMesh.parent) this._shadowMesh.parent.remove(this._shadowMesh);
          if (this._shadowMesh && this._shadowMesh.geometry) this._shadowMesh.geometry.dispose();
        } catch (eSh) {}
        this._shadowMesh = sh;
        (eng.scene || eng.group).add(sh);
        _log('shadow ' + Math.round(size.x * 100) / 100 + ' x ' + Math.round(size.z * 100) / 100 +
             ' m into the ' + (eng.scene ? 'scene' : 'group') + ' -- the group stays the building');
      }

      var person = new T.Group();
      var bx = (STATE.bodyX != null) ? STATE.bodyX : _defaultBodyX();
      var bz = (STATE.bodyZ != null) ? STATE.bodyZ : _defaultBodyZ();
      person.position.set(bx, 0, bz);
      person.userData.partType = 'person';
      person.userData.basePos = person.position.clone();
      person.visible = (STATE.bodyVisible === true);
      eng.group.add(person);

      if (_personScene) {
        if (_personAttachLogCount < 3) { _personAttachLogCount++; _pdbg('attaching cached person'); }
        person.add(_personScene);
      } else if (STATE.bodyVisible) {
        _loadPersonScene().then(function (scene) {
          var bg = global.SaunaEngine && global.SaunaEngine.group;
          if (!bg) return;
          for (var pi = 0; pi < bg.children.length; pi++) {
            var pc = bg.children[pi];
            if (pc.userData && pc.userData.partType === 'person' && pc.children.length === 0) {
              pc.add(scene);
              _pdbg('attached person on async resolve');
              break;
            }
          }
        }).catch(function () {});
      }

      STATE.buildStep = 0;
      if (global.SaunaViz) global.SaunaViz.apply();
    }
  };

  var PERSON_GLB_URL = 'https://cdn.shopify.com/3d/models/a185bdffeca46290/human.glb';
  var _personScene = null;
  var _personLoading = null;
  var _personAttachLogCount = 0;

  function _pdbg(msg) {
    if (global.Configurator && global.Configurator.log && global.Configurator.log.log) {
      global.Configurator.log.log('[container-person] ' + msg);
    }
  }

  function _getGLTFLoader() {
    return new Promise(function (resolve, reject) {
      var T = global.THREE;
      if (T && T.GLTFLoader) { resolve(T.GLTFLoader); return; }
      if (global.GLTFLoader) { resolve(global.GLTFLoader); return; }
      if (!T) { reject(new Error('THREE not on window')); return; }

      var rev = T.REVISION || '160';
      var script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/three@0.' + rev + '.0/examples/js/loaders/GLTFLoader.js';
      script.onload = function () {
        if (T.GLTFLoader)      { resolve(T.GLTFLoader); return; }
        if (global.GLTFLoader) { resolve(global.GLTFLoader); return; }
        tryESM();
      };
      script.onerror = function () { tryESM(); };
      document.head.appendChild(script);

      function tryESM() {
        var url = 'https://esm.sh/three@0.' + rev + '/examples/jsm/loaders/GLTFLoader.js';
        try {
          import(url).then(function (mod) {
            if (mod && mod.GLTFLoader) resolve(mod.GLTFLoader);
            else reject(new Error('no GLTFLoader in esm.sh module'));
          }).catch(reject);
        } catch (e) { reject(e); }
      }
    });
  }

  function _loadPersonScene() {
    if (_personScene) return Promise.resolve(_personScene);
    if (_personLoading) return _personLoading;

    _personLoading = _getGLTFLoader().then(function (Loader) {
      return new Promise(function (resolve, reject) {
        var T = global.THREE;
        _pdbg('GLTFLoader ready, loading GLB');
        var loader = new Loader();
        loader.load(PERSON_GLB_URL, function (gltf) {
          var scene = gltf.scene;
          var bbox = new T.Box3().setFromObject(scene);
          var size = bbox.getSize(new T.Vector3());
          if (size.y > 0) scene.scale.setScalar(1.75 / size.y);
          var bbox2 = new T.Box3().setFromObject(scene);
          var center = bbox2.getCenter(new T.Vector3());
          scene.position.set(-center.x, -bbox2.min.y, -center.z);
          var mat = new T.MeshStandardMaterial({
            color: 0x5a5f66, transparent: true, opacity: 0.40,
            depthWrite: false, roughness: 1.0,
            emissive: 0x000000, emissiveIntensity: 0
          });
          scene.traverse(function (child) {
            if (child.isMesh) {
              child.material = mat;
              child.castShadow = false;
              child.receiveShadow = false;
            }
          });
          _personScene = scene;
          _pdbg('GLB loaded + fitted');
          resolve(scene);
        }, null, function (err) {
          _pdbg('GLB load failed: ' + (err && err.message ? err.message : err));
          reject(err);
        });
      });
    }).catch(function (err) {
      _personLoading = null;
      throw err;
    });

    return _personLoading;
  }

  function _defaultBodyX() { return _extW() / 2 + 0.60; }
  function _defaultBodyZ() {
    var mod = STATE.modules[0];
    var t = _saunaType();
    return (mod.z || 0) + t.L / 2 - 0.35;
  }

  /* K6: the eye undresses the building in build order, backwards. Six stages,
     each one a real moment on site:

       0 FULL BUILD      everything
       1 OPEN ROOF       roof covering off -- the old stage 1
       2 SECTION         side wall off too -- the old stage 2
       3 STRUCTURE       frames only: foundations, floor frame, wall frames,
                         roof frame, partitions as frames -- the naked timber
                         building K1-K4 built, finally visible
       4 SHELL           foundations + floor frame + wall frames (roof frame off)
       5 FOUNDATIONS     just the ground: heads, bearers, floor frame

     The frame stages work by INVERSION: everything that is not frame goes,
     which is why every structural mesh since K2 carries a viz tag ending in
     'frame'. Adding a member to any layout makes it appear here for free. */
  /* The eye, cut to what is needed now: the finished building, and the frame
     it stands on. Stage 1 is the WHOLE structural story in one view --
     foundation heads and timber bearers (untagged, so always visible), floor
     frame, wall frames complete with plates and headers, roof frame. The
     intermediate undressing stages went; they can come back when there is a
     reason for them, and the tags they ran on are all still in place. */
  global.SaunaViz = {
    STAGES: 3,
    init: function () { this._update(); },
    cycle: function () {
      STATE.vizStage = (STATE.vizStage === 2) ? 0 : 2;   /* shelter: only Full <-> profiles */
      /* Coming HOME is a rebuild, not a flood-fill. Stage 0 used to set
         visible=true on every tagged mesh -- which is not the base view, it
         is everything at once: linings outside their step, ghosts, pieces
         other systems had deliberately hidden. The only authority on what the
         base view shows is the build itself, so returning to it means asking
         the build to happen again. The frame stage stays a pure filter on
         top. */
      this._selPid = null;
      try { this._applyGhost(); } catch (eG) {}
      if ((STATE.vizStage || 0) === 0) { rebuild(); }
      else { this.apply(); }
      this._update();
      savePref();
    },
    _FRAME: { floorframe: 1, wallframe: 1, roofframe: 1, partframe: 1 },
    _WOOD2: { floorframe: 1, wallframe: 1, roofframe: 1, partframe: 1, ceilframe: 1, pergola: 1, layer_wall_batten: 1, layer_roof_batten: 1, layer_ceil_batten: 1, layer_roof_header: 1, layer_roof_post: 1 },
    apply: function () {
      var eng = global.SaunaEngine; if (!eng || !eng.group) return;
      var stage = STATE.vizStage || 0;
      if (stage === 0) return;   /* the build already IS the base view */
      var FR = this._FRAME, W2 = this._WOOD2;
      var lib = global.SaunaRenderer;
      if (lib) { lib._stageMats = {}; lib._stageSeq = {}; }
      eng.group.traverse(function (o) {
        if (!o.isMesh && !o.isLine && !o.isSprite && !o.isPoints) return;
        var k = o.userData && o.userData.viz;
        var show = (stage === 2) ? !!(k && (W2[k] || (k === 'foundation' && (o.userData && o.userData.prof) === 'bearer'))) : !!(k && FR[k]);
        o.visible = show;
        /* STAGE 1 = colour by PANEL (each wall panel its own colour, every
           other system one colour). STAGE 2 = colour by PROFILE TYPE (same
           timber section = same colour), so at a glance the customer sees
           where each profile is used. Cladding and all skin are already
           filtered out above -- only the frame is shown. */
        if (show && o.isMesh && lib && lib._stageMat) {
          var gid;
          if (stage === 2) {
            var sA = o.userData && o.userData.sect;
            var g = o.geometry && o.geometry.parameters;
            if (sA && sA.length === 2) { gid = 'sect:' + Math.round(sA[0] * 1000) + 'x' + Math.round(sA[1] * 1000); }
            else if (g && (g.width || g.height || g.depth)) { var d3 = [g.width || 0, g.height || 0, g.depth || 0].sort(function (a, b) { return a - b; }); gid = 'sect:' + Math.round(d3[0] * 1000) + 'x' + Math.round(d3[1] * 1000); }
            else { gid = 'prof:' + ((o.userData && o.userData.prof) || k); }
          } else {
            gid = (k === 'wallframe' && o.userData && o.userData.pid) ? ('pan:' + o.userData.pid) : ('sys:' + k);
          }
          o.material = lib._stageMat(gid);
        }
      });
    },
    _update: function () {
      var btn = $('cf-btn-viz'), num = $('cf-stat-viz');
      if (!btn) return;
      var stage = STATE.vizStage || 0;
      btn.classList.toggle('active', stage !== 0);
      btn.setAttribute('aria-pressed', stage !== 0 ? 'true' : 'false');
      if (num) num.textContent = String(stage === 0 ? 1 : 2);
      btn.title = 'Visualisation: ' + (stage === 0 ? 'Full build' : stage === 1 ? 'Frame - panels' : 'Frame - profiles') + ' (click to inspect)';
    },
    /* Tap a panel, and the rest step back. The per-panel material registry
       makes this one loop: the tapped pid keeps opacity 1, every other drops
       to a ghost; tap it again (or tap air) and the sheet comes back.
       Selection is a look, not a state -- it does not save. */
    _selPid: null,
    tapAt: function (clientX, clientY) {
      if ((STATE.vizStage || 0) !== 1) return false;
      var eng = global.SaunaEngine, T = global.THREE;
      if (!eng || !eng.camera || !eng.renderer || !T || !T.Raycaster) return false;
      var el2 = eng.renderer.domElement, r = el2.getBoundingClientRect();
      var nx = ((clientX - r.left) / r.width) * 2 - 1;
      var ny = -((clientY - r.top) / r.height) * 2 + 1;
      var rc = this._rc || (this._rc = new T.Raycaster());
      rc.setFromCamera({ x: nx, y: ny }, eng.camera);
      var cands = [];
      eng.group.traverse(function (o) { if (o.isMesh && o.visible && o.userData && o.userData.pid) cands.push(o); });
      var hits = rc.intersectObjects(cands, false);
      var pid = hits.length ? hits[0].object.userData.pid : null;
      this._selPid = (pid && pid !== this._selPid) ? pid : null;
      this._applyGhost();
      return true;
    },
    _applyGhost: function () {
      var reg = (global.SaunaRenderer && global.SaunaRenderer._panelMats) || {};
      var sel = this._selPid;
      for (var k in reg) {
        reg[k].opacity = (!sel || k === sel) ? 1 : 0.10;
        reg[k].depthWrite = (!sel || k === sel);
      }
    }
  };

  var _BoQDebug = {
    _el: null,
    enabled: function () {
      try { return global.localStorage.getItem('cf_boqdbg') === '1'; } catch (e) { return false; }
    },
    _ensure: function () {
      if (this._el) return this._el;
      var root = $('cf-root') || document.body;
      if (!$('cf-boq-dbg-style')) {
        var st = document.createElement('style');
        st.id = 'cf-boq-dbg-style';
        st.textContent =
          '#cf-boq-dbg{position:absolute;top:56px;right:12px;z-index:40;width:300px;max-height:60vh;overflow-y:auto;background:rgba(28,29,31,0.94);color:#eee;border-radius:12px;padding:10px 12px;font:500 11px/1.4 Montserrat,monospace;box-shadow:0 8px 24px rgba(0,0,0,0.4);-webkit-overflow-scrolling:touch;}' +
          '#cf-boq-dbg h4{margin:0 0 6px;font:700 12px Montserrat,sans-serif;color:#c79a4a;letter-spacing:0.04em;display:flex;justify-content:space-between;align-items:center;}' +
          '#cf-boq-dbg .cf-boq-x{cursor:pointer;color:#888;font-size:14px;}' +
          '#cf-boq-dbg table{width:100%;border-collapse:collapse;}' +
          '#cf-boq-dbg td{padding:2px 3px;border-bottom:1px solid rgba(255,255,255,0.08);vertical-align:top;}' +
          '#cf-boq-dbg td.c{color:#9fd3ff;font-family:monospace;white-space:nowrap;}' +
          '#cf-boq-dbg td.q{text-align:right;color:#c5e1a5;white-space:nowrap;}' +
          '#cf-boq-dbg .cf-boq-cat{color:#c79a4a;font-weight:700;padding-top:6px;text-transform:uppercase;font-size:10px;letter-spacing:0.05em;}' +
          '#cf-boq-dbg .cf-boq-empty{color:#888;padding:8px 0;}';
        document.head.appendChild(st);
      }
      var el = document.createElement('div');
      el.id = 'cf-boq-dbg';
      root.appendChild(el);
      this._el = el;
      return el;
    },
    refresh: function (data) {
      if (!this.enabled()) { if (this._el) this._el.style.display = 'none'; return; }
      var el = this._ensure();
      el.style.display = 'block';
      var rows = (data && data.rows) || [];
      var html = '<h4>BoQ DEBUG (' + rows.length + ')<span class="cf-boq-x" data-boq-close>\u2715</span></h4>';
      if (!rows.length) {
        html += '<div class="cf-boq-empty">No rows yet.</div>';
      } else {
        var byCat = {};
        for (var i = 0; i < rows.length; i++) {
          var c = rows[i].category || 'other';
          (byCat[c] = byCat[c] || []).push(rows[i]);
        }
        html += '<table>';
        var cats = Object.keys(byCat).sort();
        for (var ci = 0; ci < cats.length; ci++) {
          html += '<tr><td colspan="3" class="cf-boq-cat">' + cats[ci] + '</td></tr>';
          var list = byCat[cats[ci]];
          for (var j = 0; j < list.length; j++) {
            var r = list[j];
            html += '<tr><td class="c">' + r.code + '</td><td>' + r.label +
              (r.note ? ' <span style="color:#888">(' + r.note + ')</span>' : '') +
              '</td><td class="q">' + r.qty + ' ' + r.unit + '</td></tr>';
          }
        }
        html += '</table>';
      }
      el.innerHTML = html;
      var x = el.querySelector('[data-boq-close]');
      if (x) x.addEventListener('click', function () {
        try { global.localStorage.setItem('cf_boqdbg', '0'); } catch (e) {}
        el.style.display = 'none';
      });
    }
  };

  global.SaunaBoQ = {
    /* The sauna's slice of the central catalog: every code this file is allowed
       to emit, with the English name and the pi-note baked in from the CSV.

       Two jobs. It is the fallback when the central sheet is not on the page --
       so a caption is never a code and never a guess. And it is the fence:
       compute() may not emit a code that is not in this table, which is what
       "never invent BoQ codes" looks like as a data structure instead of a
       memory. The names are the catalog's own, verbatim, pi and all.

       Localisation is NOT stored here. The budget snippet on the same page
       already fetches the central sheet with all twenty languages in it; this
       file resolves through that (see _catName) and only lands here when the
       sheet is absent. One source of translations, not a second copy ageing in
       a JS file. */
    CAT: {
      KVR10015: { en: 'SAUNA INTERIOR WOOD CLADDING  T&G \u03C0', nM: '100 x 15', nI: '5/8In. \u00D7 4In.', u: 'M2' },
      KVR03050: { en: 'SAUNA BATTENS (SUBFRAME LATHS) \u03C0', nM: '30 x 50', nI: '1In. \u00D7 2In.', u: 'M' },
      KVR02890: { en: 'SAUNA BENCHES SLATS \u03C0', nM: '28 x 90', nI: '1In. \u00D7 4In.', u: 'M' },
      FFW00004: { en: 'SAUNA ALUMINUM FOIL VAPOR BARRIER', nM: '', nI: '', u: 'M2' },
      FEC00033: { en: 'ALUMINIUM FOIL TAPE \u03C0', nM: '50mm \u00D7 50m', nI: '2In. \u00D7 165ft', u: 'PCS' },
      KVH60160: { en: 'DIMENSIONAL LUMBER \u03C0', nM: '60 \u00D7 160', nI: '2In. \u00D7 6In.', u: 'M' },
      KVH10060: { en: 'DIMENSIONAL LUMBER \u03C0', nM: '60 x 100', nI: '2In. x 4In.', u: 'M' },
      KVH60200: { en: 'DIMENSIONAL LUMBER \u03C0', nM: '60 \u00D7 200', nI: '2In. \u00D7 8In.', u: 'M' },
      INS01601: { en: 'MINERAL WOOL INSULATION \u03C0', nM: '160', nI: '6In.', u: 'M2' },
      INS00601: { en: 'MINERAL WOOL INSULATION \u03C0', nM: '60', nI: '2In.', u: 'M2' },
      INS02001: { en: 'MINERAL WOOL INSULATION \u03C0', nM: '200', nI: '8In.', u: 'M2' },
      INS00002: { en: 'BREATHABLE FACADE MEMBRANE \u03C0', nM: '0.5mm \u00D7 1500mm', nI: '20mil. \u00D7 5ft', u: 'M2' },
      FVF00301: { en: 'HORIZONTAL TIMBER BATTEN \u03C0', nM: '30 \u00D7 50', nI: '1In. \u00D7 3In.', u: 'M' },
      ROV00100: { en: 'EAVES VENTILATION STRIP \u03C0', nM: '100', nI: '4In.', u: 'M' },
      FVW01351: { en: 'WOOD FACADE CLADDING \u03C0', nM: '15', nI: '5/8In.', u: 'M2' },
      FFC00005: { en: 'VINYL FLOORING (LVF)', nM: '', nI: '', u: 'M2' },
      MFP00001: { en: 'PENETRATING WOOD SEALER', nM: '', nI: '', u: 'M2' },
      MFP00002: { en: 'EXPOSED WOOD COATING', nM: '', nI: '', u: 'M2' },
      MFP00003: { en: 'SAUNA WOOD OIL', nM: '', nI: '', u: 'M2' },
      FFF00003: { en: 'SAUNA DUCKBOARD FLOOR GRATE \u03C0', nM: '600mm \u00D7 600mm', nI: '24In. \u00D7 24In.', u: 'M2' },
      FAS45070: { en: 'STAINLESS STEEL SCREW (A2) \u03C0', nM: '4.5 \u00D7 70', nI: '#9 \u00D7 2-3/4"', u: 'PCS' },
      OSB25001: { en: 'OSB BOARD \u03C0', nM: '15', nI: '5/8In.', u: 'M2' },
      ROR00002: { en: 'ROOF HYDROFOIL', nM: '', nI: '', u: 'M2' },
      ROD00201: { en: 'DRIP EDGE ROOF FLASHING', nM: '200mm', nI: '8In.', u: 'M' },
      ROR00006: { en: 'GABLE FLASHING', nM: '', nI: '', u: 'M' },
      ROR00009: { en: 'RIDGE CAP FLASHING \u03C0', nM: '250mm', nI: '10In.', u: 'M' },
      ROR00007: { en: 'RIDGE VENTILATION STRIP', nM: '', nI: '', u: 'M' },
      ROG01251: { en: 'RAINWATER GUTTER \u03C0', nM: '\u00F8125', nI: '5In.', u: 'M' },
      ROA01251: { en: 'GUTTER BRACKET \u03C0', nM: '\u00F8125', nI: '5In.', u: 'PCS' },
      ROG00001: { en: 'GUTTER END CAP \u03C0', nM: '\u00F8125', nI: '5In.', u: 'PCS' },
      ROG00005: { en: 'GUTTER HOPPER \u03C0', nM: '\u00F8125/\u00F880', nI: '5In./3In.', u: 'PCS' },
      ROG01252: { en: 'RAINWATER DOWNSPOUT \u03C0', nM: '\u00F880', nI: '3In.', u: 'M' },
      ROG00011: { en: 'DOWNSPOUT BRACKET \u03C0', nM: '\u00F880', nI: '3In.', u: 'PCS' },
      ROG00006: { en: 'DOWNSPOUT ELBOW \u03C0', nM: '\u00F880', nI: '3In.', u: 'PCS' },
      ROG00009: { en: 'DOWNSPOUT OUTLET ELBOW \u03C0', nM: '\u00F880', nI: '3In.', u: 'PCS' },
      ODI07001: { en: 'SAUNA DOOR \u03C0 RH', nM: '750', nI: '28In.', u: 'PCS' },
      ODI08003: { en: 'INTERIOR DOOR \u03C0 LH', nM: '800', nI: '30In.', u: 'PCS' },
      ODI08004: { en: 'INTERIOR DOOR \u03C0 RH', nM: '800', nI: '30In.', u: 'PCS' },
      ODD00001: { en: 'DOOR HANDLE SET', nM: '', nI: '', u: 'PCS' },
      ODD00006: { en: 'SAUNA DOOR HANDLE SET', nM: '', nI: '', u: 'PCS' },
      ODD00002: { en: 'EXTERIOR SECURITY DOOR HANDLE SET', nM: '', nI: '', u: 'PCS' },
      ODD00004: { en: 'GARAGE DOOR OPENER', nM: '', nI: '', u: 'PCS' },
      ODD00005: { en: 'DOOR HINGES', nM: '', nI: '', u: 'PCS' },
      FMC00034: { en: 'SAUNA HEATER', nM: '500', nI: '20In.', u: 'PCS' },
      FMC00035: { en: 'SAUNA WALL THERMOMETER', nM: '', nI: '', u: 'PCS' },
      FMC00036: { en: 'SAUNA STONES', nM: '', nI: '', u: 'PCS' },
      FMC00037: { en: 'SAUNA CONTROL UNIT', nM: '', nI: '', u: 'PCS' },
      FMC00038: { en: 'SAUNA TEMPERATURE SENSOR', nM: '', nI: '', u: 'PCS' },
      FMC00039: { en: 'SAUNA HEATER GUARD \u03C0', nM: '700mm \u00D7 700mm', nI: '28In. \u00D7 28In.', u: 'PCS' },
      EEW00002: { en: 'SAUNA LUMINAIRE IP65 \u03C0', nM: '\u00D8100 mm', nI: '4In.', u: 'PCS' },
      EEE00002: { en: 'SILICONE HIGH-TEMPERATURE CABLE \u03C0', nM: '5 \u00D7 2.5 mm', nI: '5 \u00D7 AWG14', u: 'PCS' },
      VMG10201: { en: 'SAUNA AIR INLET VENT \u03C0', nM: '', nI: '', u: 'PCS' },
      VMG10202: { en: 'SAUNA AIR OUTLET VENT WITH SLIDER \u03C0', nM: '', nI: '', u: 'PCS' },
      FAS80200: { en: 'WASHER HEAD SCREW \u03C0', nM: '8 \u00D7 200', nI: '5/16" \u00D7 8"', u: 'PCS' },
      FGR00004: { en: 'LOAD BEARING GROUND SCREW \u03C0', nM: '1500', nI: '60In.', u: 'PCS' },
      FGR00005: { en: 'LOAD BEARING GROUND SCREW \u03C0', nM: '2000', nI: '79In.', u: 'PCS' },
      FCO00009: { en: 'PRECAST CONCRETE FOOTINGS \u03C0', nM: '400mm \u00D7 400mm', nI: '16In. \u00D7 16In.', u: 'PCS' },
      FCO00010: { en: 'PRECAST CONCRETE FOOTINGS \u03C0', nM: '600mm \u00D7 600mm', nI: '24In. \u00D7 24In.', u: 'PCS' },
      KVR14619: { en: 'PLANED T&G TIMBER PLANK \u03C0', nM: '146 x 19', nI: '', u: 'M2' },
      /* the rest of what compute() already emitted -- foundations, pergolas,
         fence leftovers -- generated from the same CSV, so the fence covers the
         whole file and not just the sauna family */
      EES00001: { en: 'STANDARD SOCKET 230V', nM: '', nI: '', u: 'PCS' },
      EET00001: { en: 'SKIRTING TRUNKING WITH SOCKETS', nM: '100 \u00d7 50', nI: '4in \u00d7 2in', u: 'M' },
      FAS00225: { en: 'SEALING SCREWS \u03c0', nM: '25', nI: '1"', u: 'PCS' },
      FAS55050: { en: 'STAINLESS STEEL SCREW (A2) \u03c0', nM: '5.5 \u00d7 50', nI: '#12 \u00d7 2"', u: 'PCS' },
      FAS60100: { en: 'WOOD SCREW \u03c0', nM: '6 \u00d7 100', nI: '#14 \u00d7 4"', u: 'PCS' },
      FAS80180: { en: 'WOOD SCREW \u03c0', nM: '8 \u00d7 180', nI: '#16 \u00d7 7"', u: 'PCS' },
      FAS60120: { en: 'WOOD SCREW \u03c0', nM: '6 \u00d7 120', nI: '#14 \u00d7 5"', u: 'PCS' },
      FAS80260: { en: 'WASHER HEAD SCREW \u03c0', nM: '8 \u00d7 260', nI: '#16 \u00d7 10"', u: 'PCS' },
      FAS80320: { en: 'WASHER HEAD SCREW \u03c0', nM: '8 \u00d7 320', nI: '#16 \u00d7 12.5"', u: 'PCS' },
      /* THE CONNECTIONS THAT WERE NEVER BILLED (Marek: musime vykazovat i
         spojovaci material). Every joint the model draws has a row now; these
         are the codes those rows needed that the slice did not carry. */
      FAS38038: { en: 'STRUCTURAL CONNECTOR NAIL \u03c0', nM: '3.8 x 38', nI: '1 1/2In.', u: 'PCS' },
      FAS80160: { en: 'WOOD SCREW \u03c0', nM: '8 \u00d7 160', nI: '#16 \u00d7 6 1/4In.', u: 'PCS' },
      FAS60080: { en: 'WOOD SCREW \u03c0', nM: '6 \u00d7 80', nI: '#12 \u00d7 3In.', u: 'PCS' },
      FAS00318: { en: 'ROOFING NAILS \u03c0', nM: '18', nI: '3/4In.', u: 'PCS' },
      FEC00038: { en: 'POLYURETHANE CONSTRUCTION SEALANT', nM: '290ml', nI: '10oz', u: 'PCS' },
      /* FIVE ROWS THAT WERE BEING REFUSED. _push fences every code against
         this slice and drops the unknown ones with a line in the log -- the
         right fence, but these five are real catalogue rows the bill already
         asked for: the terrace's footings and DECKING, the corner trims and
         both window sills never reached the budget. Found by checking every
         add() against the slice (Marek: vsechno co je v modelu do vykazu). */
      FCO00008: { en: 'PRECAST CONCRETE FOOTINGS \u03c0', nM: '', nI: '', u: 'PCS' },
      FVW02660: { en: 'WOOD CORNER TRIM PROFILE \u03c0', nM: '60 x 20', nI: '3In. x 3/4In.', u: 'M' },
      OWW00001: { en: 'INTERIOR WINDOW SILL', nM: '', nI: '', u: 'M' },
      OWW00002: { en: 'EXTERIOR WINDOW SILL', nM: '', nI: '', u: 'M' },
      TST00002: { en: 'DECKING BOARDS \u03c0', nM: '25 x 145', nI: '1In. x 6In.', u: 'M2' },
      FAS80100: { en: 'WOOD SCREW \u03c0', nM: '8 \u00d7 100', nI: '#16 \u00d7 4"', u: 'PCS' },
      FAS70000: { en: 'NAILS \u03c0', nM: '70', nI: '2.75"', u: 'PCS' },
      FAS50060: { en: 'WOOD SCREW \u03c0', nM: '5 \u00d7 60', nI: '#10 \u00d7 2.5"', u: 'PCS' },
      WDD24002: { en: 'MOISTURE-RESISTANT GYPSUM BOARD', nM: '2400 \u00d7 1200 \u00d7 12.5', nI: '', u: 'M2' },
      WDS00001: { en: 'DRYWALL SCREWS', nM: '', nI: '', u: 'PCS' },
      WDF00008: { en: 'DRYWALL TAPE', nM: '', nI: '', u: 'M' },
      WDF00009: { en: 'DRYWALL JOINT COMPOUND', nM: '', nI: '', u: 'KG' },
      FAS63000: { en: 'STAPLES', nM: '63', nI: '2.5"', u: 'PCS' },
      FAT00012: { en: 'HOLD DOWN STRAP \u03c0', nM: '40 \u00d7 400 \u00d7 2', nI: '', u: 'PCS' },
      FAT00009: { en: 'JOIST HANGER \u03c0', nM: '60 \u00d7 160', nI: '', u: 'PCS' },
      FAT00010: { en: 'JOIST HANGER \u03c0', nM: '60 \u00d7 200', nI: '', u: 'PCS' },
      FAS80080: { en: 'WASHER HEAD SCREW \u03c0', nM: '8 \u00d7 80', nI: '5/16" \u00d7 3-1/2"', u: 'PCS' },
      FAT00005: { en: 'REINFORCED ANGLE BRACKET', nM: '', nI: '', u: 'PCS' },
      FCO00001: { en: 'GRAVEL BASE  \u03c0', nM: '16-32', nI: '3/4In.', u: 'M3' },
      FCO00003: { en: 'REINFORCING STEEL BAR \u03c0', nM: '\u00d814', nI: '5/8In.', u: 'M' },
      FCO00004: { en: 'POST BASE ANCHOR U TYPE', nM: '', nI: '', u: 'PCS' },
      FCO00005: { en: 'THREADED ANCHOR ROD \u03c0', nM: 'M12', nI: '1/2In.', u: 'M' },
      FEC00008: { en: 'STEEL SUPPORT POST FOR GABION \u03c0', nM: '60 \u00d7 60 \u00d7 2 700', nI: '2In. \u00d7 2In. \u00d7 108In.', u: 'PCS' },
      FEC00015: { en: 'STEEL FENCE PANEL', nM: '', nI: '', u: 'PCS' },
      FEC00032: { en: 'CHEMICAL ANCHOR', nM: '', nI: '', u: 'PCS' },
      FEC00002: { en: 'GRAVEL BASE', nM: '', nI: '', u: 'M3' },
      FEC00003: { en: 'GEOTEXTILE', nM: '', nI: '', u: 'M2' },
      FEE00001: { en: 'EXCAVATIONS', nM: '', nI: '', u: 'M3' },
      FFM00001: { en: 'CAST-IN-PLACE CONCRETE C20/25', nM: '', nI: '', u: 'M3' },
      FFP01001: { en: 'ADJUSTABLE POST BASE \u03c0', nM: '110', nI: '4In.', u: 'PCS' },
      FFP01002: { en: 'H-POST ANCHOR \u03c0', nM: '160', nI: '8In.', u: 'PCS' },
      FGR00001: { en: 'LOAD BEARING GROUND SCREW \u03c0', nM: '1200', nI: '48In.', u: 'PCS' },
      FGR00002: { en: 'U HEAD POST ADAPTER', nM: '', nI: '', u: 'PCS' },
      FVC00101: { en: 'CORTEN STEEL FACADE PANEL', nM: '1000 \u00d7 2000 \u00d7 1.5', nI: '40in \u00d7 80in \u00d7 1/16in', u: 'M2' },
      KVH08060: { en: 'DIMENSIONAL LUMBER \u03c0', nM: '80 x 60', nI: '3In.  x 2In.', u: 'M' },
      KVH10100: { en: 'DIMENSIONAL LUMBER \u03c0', nM: '100 \u00d7 100', nI: '4In. \u00d7 4In.', u: 'M' },
      KVH10160: { en: 'DIMENSIONAL LUMBER \u03c0', nM: '100 \u00d7 160', nI: '4In. \u00d7 6In.', u: 'M' },
      KVH40060: { en: 'TIMBER BATTEN \u03c0', nM: '40 \u00d7 60', nI: '2In. \u00d7 2In.', u: 'M' },
      KVH60120: { en: 'DIMENSIONAL LUMBER \u03c0', nM: '60 \u00d7 120', nI: '2In. \u00d7 5In.', u: 'M' },
      KVL20040: { en: 'TIMBER BATTEN \u03c0', nM: '20 \u00d7 40', nI: '1In. \u00d7 2In.', u: 'M' },
      ODP00001: { en: 'PU FOAM', nM: '', nI: '', u: 'PCS' },
      /* the drawer's own openings: every id the configurator can place is in
         the fence, so a placed window becomes a BoQ row instead of a refusal.
         Names mirror the sheet; the sheet's localized columns win at runtime. */
      WIND0101: { en: 'Window 500x500', nM: '500 x 500 mm', nI: '24In. x 24In.' },
      WIND0102: { en: 'Window 500x750', nM: '500 x 750 mm', nI: '24In. x 32In.' },
      WIND0103: { en: 'Window 500x1000', nM: '500 x 1000 mm', nI: '24In. x 40In.' },
      WIND0201: { en: 'Window 750x500', nM: '750 x 500 mm', nI: '32In. x 24In.' },
      WIND0202: { en: 'Window 750x750', nM: '750 x 750 mm', nI: '32In. x 32In.' },
      WIND0203: { en: 'Window 750x1000', nM: '750 x 1000 mm', nI: '32In. x 40In.' },
      WIND0301: { en: 'Window 1000x500', nM: '1000 x 500 mm', nI: '40In. x 24In.' },
      WIND0302: { en: 'Window 1000x750', nM: '1000 x 750 mm', nI: '40In. x 32In.' },
      WIND0303: { en: 'Window 1000x1000', nM: '1000 x 1000 mm', nI: '40In. x 40In.' },
      EXTE0101: { en: 'Exterior door 900', nM: '900 x 2100 mm', nI: '36In. x 84In.' },
      ODE18002: { en: 'Double-wing exterior door 1500', nM: '1500 x 2080 mm', nI: '60In. x 82In.' },
      ODG48001: { en: 'Garage door', nM: 'up to 4800 x 2080 mm', nI: 'up to 189In. x 82In.' },
      PFG00001: { en: 'GEOTEXTILE', nM: '', nI: '', u: 'M2' },
      ROP12001: { en: 'PIR INSULATION BOARD ALU \u03c0', nM: '100', nI: '4In.', u: 'M2' },
      ROP20001: { en: 'TAPERED ROOF INSULATION WEDGES', nM: 'avg 100mm, tapered', nI: 'avg 4in, tapered', u: 'M2' },
      ROR00001: { en: 'ROOF TILES / COVERING', nM: '', nI: '', u: 'M2' },
      SBC00002: { en: 'FLOOR TILES', nM: '', nI: '', u: 'M2' },
      SSS00005: { en: 'GALVANIZED STEEL RHS \u03c0', nM: '140 x 80 x 5', nI: '5 1/2In. \u00d7 3 1/8In. \u00d7 3/16In.', u: 'M' },
      TST00001: { en: 'DECKING BOARDS \u03c0', nM: '25 x 145', nI: '1In. x 6In.', u: 'M' }
    },
    /* The budget snippet's own translation contract, verbatim: absolute column
       offsets per language, NAME at offset+40, and pi in a name replaced by the
       dimension note in column 17 (metric) or 18 (imperial). Kept identical so
       the Assembly caption, the budget row and the PDF cannot spell one item
       three ways. */
    _LANG_OFF: { EN: 0, AR: 28, CS: 29, DE: 30, EL: 31, ES: 32, FI: 33, FR: 34, HE: 35, HU: 36, IT: 37, JA: 38, KO: 39, NL: 40, NO: 41, PL: 42, 'PT-PT': 43, RO: 44, SV: 45, TR: 46, 'ZH-CN': 47 },
    /* THE wall, as data. One list, two consumers: _buildDetail draws it layer
       by layer, compute() prices it row by row -- so the section on screen and
       the budget under it are the same object read twice, and "assembly must
       pair with BoQ" stops being a discipline and becomes a property. Add a
       layer here and it appears in both; nowhere else to add one.

       d = drawn thickness (m), mm = the number on the label, col/film/ribs are
       drawing hints, area tells compute() what to multiply: 'lin' walls+ceiling,
       'wall' walls only. qtyF scales the area (laps). unit overrides M2. */
    WALL_CORE: [
      { code: 'KVR10015', d: 0.015, mm: 15, col: 0xe8cfa8, area: 'lin', note: 'hot room lining, walls + ceiling' },
      { code: 'KVR03050', d: 0.030, mm: 30, col: 0xc9a06a, ribs: true, area: 'lin', unit: 'M', perM: true, note: 'lining subframe @600, forms the air gap', open: true },
      { code: 'FFW00004', d: 0.006, mm: 0.04, col: 0xbfc6cc, film: true, area: 'lin', qtyF: 1.1, note: 'warm side, 10 pct laps' },
      { code: 'INS01601', d: 0.160, mm: 160, col: 0xd8d8d2, ribs: true, ribCol: 0x8a6b43, area: 'lin', note: 'full depth between studs' }
    ],
    /* K8 grew a fourth member: the four walls a sauna BUILDING actually has.
       SAUNA/EXTERIOR is the priced envelope (CORE + EXT). SAUNA/INTERIOR is
       the same sauna core -- foil, battens, sauna lining -- finished with a
       plain interior face instead of weather layers: the wall between the
       hot room and the changing room. Then the ordinary partition, and the
       ordinary exterior wall. Four icons, one panel, one grammar. */
    WALL_INT_FACE: [
      { code: 'KVR10015', d: 0.015, mm: 15, col: 0xe8cfa8, area: 'wall', note: 'interior face' }
    ],
    /* TWO partitions, because the building has two kinds of neighbours.
       Between dry rooms: gypsum both faces, 125 mm. Between the SAUNA and
       anything else: the sauna face carries the full hot-room detail --
       T&G, battens for the air gap, alu vapour barrier -- exactly like the
       ceiling and the exterior sauna walls; the far face stays gypsum.
       The model and the budget pick per FACE by room adjacency. */
    WALL_PART: [
      { code: 'WDD24002', d: 0.0125, mm: 12.5, col: 0xf1efe9, area: 'wall', note: 'moisture-resistant gypsum, face A' },
      { code: 'KVH10060', code2: 'INS00601', mm2: 60, d: 0.100, mm: 100, col: 0xEDE9E1, ribs: true, ribCol: 0x8a6b43, area: 'wall', note: 'studs @600, wool between' },
      { code: 'WDD24002', d: 0.0125, mm: 12.5, col: 0xf1efe9, area: 'wall', note: 'moisture-resistant gypsum, face B' }
    ],
    /* shed partition: bare load-bearing studs, boards on ONE face only */
    WALL_PART_SHED: [
      { code: 'KVH10060', d: 0.100, mm: 100, col: 0xEDE9E1, ribs: true, sparse: true, ribCol: 0x8a6b43, area: 'wall', open: true, note: 'load-bearing studs @600, open bays (uninsulated)' },
      { code: 'FVW01351', d: 0.020, mm: 20, col: null, area: 'wall', note: 'cladding, one side' }
    ],
    WALL_PART_SAUNA: [
      { code: 'KVR10015', d: 0.015, mm: 15, col: 0xe8cfa8, area: 'wall', note: 'sauna lining, hot side' },
      { code: 'KVR03050', d: 0.030, mm: 30, col: 0xc9a06a, ribs: true, sparse: true, horiz: true, area: 'wall', open: true, note: 'battens ACROSS the studs, foil protection gap' },
      { code: 'FFW00004', d: 0.006, mm: 0.2, col: 0xbfc6cc, film: true, area: 'wall', note: 'alu vapor barrier' },
      { code: 'KVH10060', code2: 'INS00601', mm2: 60, d: 0.100, mm: 100, col: 0xEDE9E1, ribs: true, ribCol: 0x8a6b43, area: 'wall', note: 'studs @600, wool between' },
      { code: 'WDD24002', d: 0.0125, mm: 12.5, col: 0xf1efe9, area: 'wall', note: 'gypsum, dry side' }
    ],
    /* three more build-ups, spoken in fence codes only. FLOOR reads top down
       -- the walking layer first, the way you meet it; CEILING reads from the
       room up; ROOF from the rafters out, exactly the warm-roof recipe the
       BoQ already prices. */
    FLOOR_ASM: [
      { code: 'FFC00005', d: 0.006, mm: 6, col: 0x8d8f93, area: 'floor', note: 'vinyl flooring LVF' },
      { code: 'OSB25001', d: 0.015, mm: 15, col: 0xd9c08a, area: 'floor', note: 'deck, sheet says 15' },
      { code: 'INS02001', code2: 'KVH60200', d: 0.200, mm: 200, col: 0xd8d8d2, ribs: true, ribCol: 0x8a6b43, area: 'floor', note: 'wool between 60x200 joists' },
      { code: 'INS00002', d: 0.006, mm: 0.5, col: 0x2f4f3a, film: true, area: 'floor', note: 'breathable windproof membrane under the joists -- vapour dries down, wind stays out; the plinth board keeps closing the void at the PERIMETER, in foundations, where it always was' }
    ],
    CEIL_ASM: [
      { code: 'KVR14619', d: 0.019, mm: 19, col: 0xead9b8, area: 'ceiling', note: 'interior T&G, room side' },
      { code: 'FFW00004', d: 0.006, mm: 0.2, col: 0xbfc6cc, film: true, area: 'ceiling', note: 'vapor barrier' },
      { code: 'INS01601', code2: 'KVH60160', d: 0.160, mm: 160, col: 0xd8d8d2, ribs: true, ribCol: 0x8a6b43, area: 'ceiling', note: 'wool between 60x160 joists' }
    ],
    CEIL_SAUNA_ASM: [
      { code: 'KVR10015', d: 0.015, mm: 15, col: 0xe8cfa8, area: 'ceiling', note: 'sauna lining, room side' },
      { code: 'KVR03050', d: 0.030, mm: 30, col: 0xc9a06a, ribs: true, sparse: true, horiz: true, area: 'ceiling', note: 'battens ACROSS the joists -- they carry the lining and shield the foil; the common ceiling nails its T&G straight to the joists, so it needs none', open: true },
      { code: 'FFW00004', d: 0.006, mm: 0.2, col: 0xbfc6cc, film: true, area: 'ceiling', note: 'alu vapor barrier' },
      { code: 'INS01601', code2: 'KVH60160', d: 0.160, mm: 160, col: 0xd8d8d2, ribs: true, ribCol: 0x8a6b43, area: 'ceiling', note: 'wool between joists' }
    ],
    /* no wedges anywhere: the rafters ARE the slope on both roof types, the
       PIR lies parallel to the plane, and the budget stopped charging for a
       flat-roof detail this building never had */
    /* COLD VENTED ROOF -- the review struck the PIR: the thermal envelope
       closes at the INSULATED CEILING (wool between the ceiling joists), so
       insulating the roof plane too was double work, and over-rafter PIR
       doubly so. What remains is the honest cold roof: rafters, hydro foil,
       counter-battens for the vent gap, covering. */
    ROOF_ASM: [
      { code: 'KVH60160', d: 0.160, mm: 160, col: 0xc9a06a, ribs: true, sparse: true, ribCol: 0x8a6b43, area: 'roof', note: 'rafters, air between (cold roof)', open: true },
      { code: 'ROR00002', d: 0.006, mm: 0.5, col: 0x2f4f3a, film: true, area: 'roof', note: 'hydrofoil on the rafters' },
      { code: 'KVR03050', d: 0.030, mm: 30, col: 0xc9a06a, ribs: true, sparse: true, area: 'roof', open: true, note: 'counter-battens along the rafters, vent gap' },
      { code: 'KVR03050', d: 0.030, mm: 30, col: 0xc9a06a, ribs: true, sparse: true, horiz: true, area: 'roof', open: true, note: 'battens ACROSS @500, the covering\u2019s fixing lines' },
      { code: 'ROR00001', d: 0.030, mm: 30, col: 0x4a4d52, area: 'roof', note: 'covering' }
    ],
    WALL_STD: [
      { code: 'WDD24002', d: 0.0125, mm: 12.5, col: 0xf1efe9, area: 'wall', note: 'moisture-resistant gypsum (dry rooms)' },
      { code: 'FFW00004', d: 0.006, mm: 0.2, col: 0xbfc6cc, film: true, area: 'wall', note: 'vapor barrier' },
      { code: 'INS01601', d: 0.160, mm: 160, col: 0xd8d8d2, ribs: true, ribCol: 0x8a6b43, area: 'wall', note: 'wool between studs' },
      { code: 'INS00002', d: 0.006, mm: 0.5, col: 0x2f4f3a, film: true, area: 'wall', note: 'wind barrier' },
      { code: 'FVF00301', d: 0.030, mm: 30, col: 0xc9a06a, ribs: true, sparse: true, area: 'wall', unit: 'M', perM: true, note: 'ventilated cavity', horiz: true, open: true },
      { code: 'FVW01351', d: 0.020, mm: 20, col: null, area: 'wall', note: 'cladding' }
    ],
    /* THE SUBFRAME FOLLOWS THE CLADDING, as decreed: vertical battens sit
       on the wall studs ALWAYS (they pin the membrane and the vent gap
       breathes upward along them); a VERTICAL cladding (wood1/bond) then
       needs one more layer of HORIZONTAL counter-battens as its fixing
       lines; a HORIZONTAL cladding (wood2) nails straight to the vertical
       battens. Both the section and the budget switch with the exterior
       toggle. */
    WALL_EXT_V: [
      { code: 'INS00002', d: 0.006, mm: 0.5, col: 0x2f4f3a, film: true, area: 'wall', qtyF: 1.1, note: 'cold side, wind barrier' },
      { code: 'FVF00301', d: 0.030, mm: 30, col: 0xc9a06a, ribs: true, sparse: true, area: 'wall', unit: 'M', perM: true, note: 'vertical battens on the studs, vent gap', open: true },
      { code: 'FVF00301', d: 0.030, mm: 30, col: 0xc9a06a, ribs: true, sparse: true, horiz: true, area: 'wall', unit: 'M', mF: 1.7, note: 'counter-battens ACROSS @600, fixing lines for vertical cladding', open: true },
      { code: 'FVW01351', d: 0.020, mm: 20, col: null, area: 'wall', note: 'cladding, vertical boards' }
    ],
    WALL_EXT_H: [
      { code: 'INS00002', d: 0.006, mm: 0.5, col: 0x2f4f3a, film: true, area: 'wall', qtyF: 1.1, note: 'cold side, wind barrier' },
      { code: 'FVF00301', d: 0.030, mm: 30, col: 0xc9a06a, ribs: true, sparse: true, area: 'wall', unit: 'M', perM: true, note: 'vertical battens on the studs, vent gap', open: true },
      { code: 'FVW01351', d: 0.020, mm: 20, col: null, area: 'wall', note: 'cladding, horizontal boards' }
    ],
    get WALL_EXT() { return (STATE.facadePattern === 'wood2') ? this.WALL_EXT_H : this.WALL_EXT_V; },
    /* shed exterior wall: open (uninsulated) studs, then the same weather layers
       the exterior envelope wears -- wind barrier, battens, cladding. No gypsum,
       no foil, no wool: bare frame inside, boards outside. */
    get WALL_SHED() {
      return [{ code: 'KVH60160', d: 0.160, mm: 160, col: 0xEDE9E1, ribs: true, sparse: true, ribCol: 0x8a6b43, area: 'wall', open: true, note: 'studs @600, open bays (uninsulated)' }].concat(this.WALL_EXT.filter(function (L) { return L.code !== 'INS00002'; }));
    },

    _sheetRow: function (code) {
      var pd = global.productData;
      if (!pd || !pd.length) return null;
      for (var i = 0; i < pd.length; i++) {
        var r = pd[i];
        if (!r || r[0] !== code) continue;
        /* Trust, verified. The wrong Czech names on the assembly can only come
           from a row that does not belong to the code -- an inserted sheet
           column, a shifted export, a variant row overwriting the wrong index.
           A row is only believed if it still looks like the row it claims to
           be: the ID matches AND the English name cell is a non-empty string.
           Anything else falls through to the baked catalog, which is at worst
           English and at least true. */
        if (typeof r[3] === 'string' && r[3].length > 1) return r;
        _log('catalog: row for ' + code + ' malformed (col3=' + r[3] + '), using fallback');
        return null;
      }
      return null;
    },
    /* The plan label asks the CATALOG, not a generic word. Every window in
       the sheet has an ID, a per-language name with a pi placeholder, and the
       dimension strings that replace it -- so the label becomes: the
       localized product name (pi stripped) on line one, the dimension in the
       CURRENT unit system on line two. The opening is matched by its metric
       dimensions against the OPENINGS rows, +-5 mm, which is exactly how a
       person reads that sheet. */
    /* a code's LOCALIZED NAME from the central sheet, pi stripped -- the one
       naming authority for everything the plan writes. Returns null when the
       sheet has no such row, so callers can fall back without lying. */
    sheetName: function (code) {
      var rs = this._sheetRow(code);
      if (!rs) return null;
      var loc = ((global.SAUNA_BOOT && global.SAUNA_BOOT.locale) || 'en').toUpperCase();
      var off = this._LANG_OFF[loc];
      var nm = (loc !== 'EN' && off != null && rs[off + 40]) ? rs[off + 40] : rs[3];
      if (typeof nm !== 'string' || nm.length < 2) return null;
      return String(nm).replace('\u03C0', '').replace(/\s+/g, ' ').trim();
    },
    opCatalog: function (op) {
      var pd = global.productData;
      if (!pd || !pd.length || !op) return null;
      var A = Math.round(op.w * 1000), B = Math.round(op.h * 1000);
      var loc = ((global.SAUNA_BOOT && global.SAUNA_BOOT.locale) || 'en').toUpperCase();
      var off = this._LANG_OFF[loc];
      /* SKU FIRST. The sheet already reserved rows for the configurator's own
         item ids -- WIND0101.., EXTE.., SLID.., FREN.. -- so an opening that
         knows its sku reads its OWN row: localized name, and the dimension
         string once the sheet fills it (until then, the computed size). Only
         a sku-less or unknown opening falls back to dimension matching. */
      if (op.sku) {
        var rs = this._sheetRow(op.sku);
        if (rs) {
          var nmS = (loc !== 'EN' && off != null && rs[off + 40]) ? rs[off + 40] : rs[3];
          if (typeof nmS === 'string' && nmS.length > 1) {
            var dimS = (STATE.isMetric ? rs[17] : rs[18]) || '';
            if (!dimS || /^x?$/.test(String(dimS).trim())) {
              dimS = STATE.isMetric ? (A + ' x ' + B + ' mm')
                                    : (Math.round(op.w / IN) + 'In. x ' + Math.round(op.h / IN) + 'In.');
            }
            return { id: rs[0], name: String(nmS).replace('\u03C0', '').replace(/\s+/g, ' ').trim(), dim: String(dimS).trim() };
          }
        }
      }
      for (var i = 0; i < pd.length; i++) {
        var r = pd[i];
        if (!r || r[1] !== 'OPENINGS') continue;
        var rA = parseInt(r[4], 10), rB = parseInt(r[5], 10);
        if (!isFinite(rA) || !isFinite(rB)) continue;
        if (Math.abs(rA - A) > 5 || Math.abs(rB - B) > 5) continue;
        var nm = (loc !== 'EN' && off != null && r[off + 40]) ? r[off + 40] : r[3];
        if (typeof nm !== 'string' || nm.length < 2) continue;
        var dim = (STATE.isMetric ? r[17] : r[18]) || '';
        return { id: r[0], name: String(nm).replace('\u03C0', '').replace(/\s+/g, ' ').trim(), dim: String(dim).trim() };
      }
      /* NO LOCALES: even a sku-less legacy opening is named by the SHEET --
         the generic window or door row carries the localized word, and the
         dimensions are the opening's own. The id stays null so the BoQ never
         mistakes a generic label for a real product match. */
      var genCode = (op.kind === 'door' || op.kind === 'hs' || op.kind === 'doorhg') ? 'EXTE0101' : 'WIND0101';
      var gn = this.sheetName(genCode);
      if (gn) {
        var dimG = STATE.isMetric ? (A + ' x ' + B + ' mm')
                                  : (Math.round(op.w / IN) + 'In. x ' + Math.round(op.h / IN) + 'In.');
        /* strip the generic row's own size words if any digits leaked in */
        gn = gn.replace(/[0-9]+\s*x\s*[0-9]+/g, '').replace(/\s+/g, ' ').trim();
        return { id: null, name: gn, dim: dimG };
      }
      return null;
    },
    catName: function (code, fallback) {
      var loc = ((global.SAUNA_BOOT && global.SAUNA_BOOT.locale) || 'en').toUpperCase();
      if (loc === 'CS') loc = 'CS'; /* Shopify says cs; the sheet column is CS */
      var row = this._sheetRow(code), name = null, note = '';
      if (row) {
        var off = this._LANG_OFF[loc];
        name = (loc !== 'EN' && off != null && row[off + 40]) ? row[off + 40] : row[3];
        note = (STATE.isMetric ? row[17] : row[18]) || '';
      }
      if (!name) {
        var c = this.CAT[code];
        if (c) { name = c.en; note = STATE.isMetric ? c.nM : c.nI; }
      }
      if (!name) return fallback || code;
      return String(name).replace('\u03C0', note).replace(/\s+/g, ' ').trim();
    },
    _push: function (map, rows, code, label, category, qty, unit, note) {
      if (!code || qty <= 0) return;
      /* the fence: a code outside the sauna's catalog slice does not become a
         row, it becomes a loud snapshot line -- inventing codes is how the
         container home's ghosts got into the sauna's budget in the first place */
      if (!this.CAT[code]) { _log('BoQ REFUSED unknown code ' + code + ' (' + label + ')'); return; }
      var q = Math.round(qty * 1000) / 1000;
      var nm = this.catName(code, label);
      rows.push({ code: code, label: nm, category: category, qty: q, unit: unit, note: note || '' });
      if (!map[code]) map[code] = { code: code, qty: 0, unit: unit };
      map[code].qty = Math.round((map[code].qty + q) * 1000) / 1000;
    },
    compute: function () {
      var map = {}, rows = [];
      var self = this;
      function add(code, label, cat, qty, unit, note) { self._push(map, rows, code, label, cat, qty, unit, note); }

      /* No line for "the cabin". SAU00001 was invented at fork time and exists
         in no catalog, so this row pointed at nothing and still reached the
         customer's budget. A building is the sum of its parts; the parts are
         below. Found by saunatest, not by looking at the screen. */

      var envType = STATE.envelope || 'exterior';
      var wallsA = 0, ceilA = 0, floorA = 0, perimIn = 0;
      for (var emi = 0; emi < STATE.modules.length; emi++) {
        var emod = STATE.modules[emi];
        var et = _typeOf(emod);
        var eH = _wallClearH(et);
        var eper = 2 * (et.intL + et.intW);
        var efper = (_sideFilled('front') ? et.intW : 0) + (_sideFilled('rear') ? et.intW : 0) + (_sideFilled('sideL') ? et.intL : 0) + (_sideFilled('sideR') ? et.intL : 0);
        var eArea = efper * (eH + (SHELTER_SCREEN_WALLS ? (PANEL_BASE - global.SaunaWalls.STUD.w + _foundLift() - 0.05) : 0));   /* screens run grade to cap */
        if (STATE.fillType === 'slats') eArea *= (0.06 / (0.06 + Math.max(0, Math.min(0.3, (STATE.slatGap != null ? STATE.slatGap : 0.04)))));
        wallsA += eArea;
        ceilA += et.intL * et.intW;
        floorA += et.intL * et.intW;
        perimIn += eper;
      }
      var opA = 0;
      var opsE = STATE.openings || [];
      for (var eoi = 0; eoi < opsE.length; eoi++) {
        var eo = opsE[eoi];
        if (eo.w > 0 && eo.h > 0) opA += eo.w * eo.h;
      }
      var passA = 0, passN = 0, passTrim = 0;
      wallsA = Math.max(0, wallsA - opA - passA);
      var r2e = function (v) { return Math.round(v * 100) / 100; };
      if (STATE.modules.length) {
        /* Sauna envelope. What stood here was a container conversion: spray PU
           "onto corrugation", gypsum lining, a hydrofoil "onto steel, outside"
           and brackets screwed "into container frame". A timber cabin has no
           steel and a hot room is not lined with plasterboard.

           Rows follow the build-up drawn in SaunaRenderer._buildDetail, layer
           for layer, so the section and the budget cannot disagree:

             T&G lining -> battens + gap -> ALU foil -> wool in studs
             -> breathable membrane -> cavity -> cladding

           Direction matters. The aluminium foil closes the warm side and the
           membrane stays open on the cold side; two closed layers would trap
           the wool and rot the frame. That is why the old ROV00001 PE foil
           cannot simply be swapped for FFW00004 -- they sit on opposite faces.

           Still area x coefficient, not derived from a frame. Screws and
           connectors need stud positions, which do not exist yet. This stops
           the lying; the counting comes with the structural model. */
        var linA = wallsA + ceilA;
        var studBm = Math.ceil(perimIn / 0.6) * (STATE.footprint.h + 0.3);
        /* the battens MIRROR the wall members one to one (v236), so their
           metres are not an estimate any more -- they ARE the stud metres,
           summed here from the layout (stM lives in another scope) */
        var battBm = 0;
        try {
          var WLBb = global.SaunaWalls.layout();
          for (var bb9 = 0; bb9 < WLBb.studs.length; bb9++) battBm += WLBb.studs[bb9].len;
        } catch (eBBm) { battBm = Math.ceil(perimIn / 0.6) * (STATE.footprint.h + 0.3); }

        /* The envelope rows come off the SAME table _buildDetail draws. One
           layer added there is one row added here; they cannot disagree,
           because there is no "they". Codes not in the table (studs, tape,
           screws) follow -- they are inside or between layers, not layers. */
        function layerRows(list, wallA2, linA2) {
          for (var lr = 0; lr < list.length; lr++) {
            var Ly = list[lr];
            var A2 = (Ly.area === 'lin') ? linA2 : wallA2;

            if (Ly.perM) { /* batten metres: not billed on a screen wall */ }
            else if (Ly.mF) add(Ly.code, Ly.code, 'envelope', r2e(A2 * Ly.mF), 'M', Ly.note);
            else add(Ly.code, Ly.code, 'envelope', r2e(A2 * (Ly.qtyF || 1)), Ly.unit || 'M2', Ly.note);
          }
        }
        /* ROOM-AWARE INTERIOR. The old line billed the sauna core over the
           WHOLE interior -- every dry room paid for T&G, battens and foil it
           does not have, and the ceiling lining was in the budget twice.
           Now the floorplan is asked: sauna rooms (type 0) get the hot-room
           set on their exterior-wall faces and their ceiling; dry rooms get
           plain T&G interior finish; the wool fills ALL walls and the WHOLE
           ceiling exactly once. */
        var saunaA = 0, saunaPerimExt = 0;
        saunaA = Math.min(saunaA, ceilA);
        var wallHrm = _wallClearH(_saunaType());
        var saunaWallsA = Math.min(wallsA, saunaPerimExt * wallHrm);
        var saunaLin = saunaWallsA + saunaA;
        var dryLin = Math.max(0, (wallsA - saunaWallsA)) + Math.max(0, ceilA - saunaA);
        /* dry rooms split (the review's call): WALLS in moisture-resistant
           gypsum -- the same skin their partitions wear -- while the dry
           CEILING keeps its approved T&G planks. */
        var dryWallsA9 = Math.max(0, wallsA - saunaWallsA);
        var dryCeilA9 = Math.max(0, ceilA - saunaA);
        var RFB = global.SaunaRoofFrame.layout();
        if (RFB.rafters.length) {
          var rfM = 0;
          for (var rb = 0; rb < RFB.rafters.length; rb++) rfM += RFB.rafters[rb].len;
          add('KVH60160', 'Rafter 60\u00D7160 C24', 'structure', r2e(rfM),
            'M', RFB.rafters.length + ' pcs, birdsmouth on the plate');
          if (RFB.ridge.length) {
            add('KVH60200', 'Ridge board 60\u00D7200 C24', 'structure', r2e(RFB.ridge[0].len), 'M', 'ridge board');
            /* every rafter is toe-screwed into the ridge from both flanks */
            add('FAS80160', 'Wood screw 8\u00D7160', 'fasteners', RFB.rafters.length * 2, 'PCS', 'rafter to ridge board, 2 toe-screws per rafter');
          }
          if (RFB.chords.length) {
            var chM = 0;
            for (var cq = 0; cq < RFB.chords.length; cq++) chM += RFB.chords[cq].len;
            add('KVH60160', 'Ceiling joist 60\u00D7160 C24', 'structure', r2e(chM), 'M', RFB.chords.length + ' pcs at plate level (gable: ties the rafter pairs; mono: hanger-hung)');
            var monoCh = 0;
            for (var cq2 = 0; cq2 < RFB.chords.length; cq2++) if (RFB.chords[cq2].mono) monoCh++;
            if (monoCh) add('FAT00009', 'Joist hanger 60\u00D7160', 'fasteners', monoCh * 2, 'PCS', 'mono ceiling joists onto the ledger ring, both ends');
            if (monoCh) add('FAS70000', 'Nails 70', 'fasteners', monoCh * 2 * 6, 'PCS', 'joist hanger nailing, 6 per hanger');
            if (RFB.ceilRing && RFB.ceilRing.length) {
              var lrM = 0;
              for (var lr2 = 0; lr2 < RFB.ceilRing.length; lr2++) lrM += RFB.ceilRing[lr2].len;
              add('KVH60160', 'Ceiling ledger 60\u00D7160', 'structure', r2e(lrM), 'M', 'perimeter ring screwed to the wall studs at plate level');
              add('FAS80180', 'Wood screw 8\u00D7180', 'fasteners', Math.ceil(lrM / 0.6) * 2, 'PCS', 'ledger to studs, 2 per stud crossing');
            }
          }
          if (RFB.fascias.length) {
            var faM = 0;
            for (var fb2 = 0; fb2 < RFB.fascias.length; fb2++) faM += RFB.fascias[fb2].len;
            add('KVH60120', 'Fascia board', 'structure', r2e(faM), 'M', 'joins the rafter tails at both eaves');
            /* screwed to every rafter tail it crosses, two per tail */
            add('FAS60100', 'Wood screw 6\u00D7100', 'fasteners', RFB.rafters.length * 2 * Math.max(1, RFB.fascias.length), 'PCS', 'fascia to the rafter tails, 2 per tail');
          }
          if (RFB.collars.length) {
            var clM = 0;
            for (var cb = 0; cb < RFB.collars.length; cb++) clM += RFB.collars[cb].len;
            add('KVH60120', 'Collar tie 60\u00D7120 C24', 'structure', r2e(clM), 'M', RFB.collars.length + ' pcs, upper third');
          }
        }
        /* K5: fasteners counted per JOINT, from the same layouts the members
           come from. A joint is a fact about two pieces meeting; multiplying
           an area by a coefficient never knew where the pieces were. Every
           count below names its joint in the note, so the site can check the
           box count against the drawing. */
        if (RFB.rafters.length) {
          add('FAT00005', 'Reinforced angle bracket', 'fasteners', RFB.rafters.length, 'PCS', 'rafter seat on the plate, 1 per birdsmouth');
          /* a bracket takes connector nails, not the stainless cladding screw
             it was billed with: 10 per reinforced bracket */
          add('FAS38038', 'Structural connector nail 3.8\u00D738', 'fasteners', RFB.rafters.length * 10, 'PCS', 'rafter seat brackets, 10 per bracket');
          if (RFB.collars.length) add('FAS80080', 'Washer head screw 8\u00D780', 'fasteners', RFB.collars.length * 4, 'PCS', 'collar tie to rafter, 2 each end');
        }
        var WLB = global.SaunaWalls.layout();
        if (SHELTER_SCREEN_WALLS) { WLB.studs = []; if (WLB.headers) WLB.headers = []; if (WLB.sills) WLB.sills = []; if (WLB.kings) WLB.kings = []; if (WLB.jacks) WLB.jacks = []; }
        var stM = 0, stN = 0, plM = 0, hdM = 0;
        for (var wb = 0; wb < WLB.studs.length; wb++) { if (!_sideFilled(WLB.studs[wb].wall)) continue; stM += WLB.studs[wb].len; stN++; }
        for (var wb2 = 0; wb2 < WLB.plates.length; wb2++) plM += WLB.plates[wb2].len;
        for (var wb3 = 0; wb3 < WLB.headers.length; wb3++) hdM += WLB.headers[wb3].len * 2;
        if (stN > 0) add('KVH60160', 'Wall stud 60\u00D7160 C24', 'structure', r2e(stM), 'M', stN + ' studs incl. kings, jacks and cripples');
        if (WLB.sills.length) {
          var slM = 0;
          for (var sb = 0; sb < WLB.sills.length; sb++) slM += WLB.sills[sb].len;
          add('KVH60160', 'Window sill plate 60\u00D7160', 'structure', r2e(slM), 'M', WLB.sills.length + ' pcs between the jacks');
        }
        var cpM = 0;
        for (var cb2 = 0; cb2 < WLB.caps.length; cb2++) cpM += WLB.caps[cb2].len;
        if (SHELTER_FIXED_ROOF) { var _cpp = _postProfile(); add(_cpp.code, 'Head beam ' + _cpp.label + ' C24', 'structure', r2e(cpM), 'M', 'continuous beam on the posts, carries the rafters'); }
        else add('KVH60160', 'Cap plate 60\u00D7160 C24', 'structure', r2e(cpM), 'M', 'continuous over panel joints, carries the rafters');
        (function () {
          var _sf9 = _sideFilled, _et9 = _typeOf(STATE.modules[0]);
          function _rN4(sp) { return Math.max(0, Math.ceil(Math.abs(sp) / 4.0) - 1); }
          function _rNs(w9, sp) { return (!_sf9(w9) && (w9 === 'front' || w9 === 'rear')) ? (Math.abs(sp) > 6.0 ? 1 : 0) : _rN4(sp); }
          var _pN = 4;   /* corner posts, always */
          var _bN = 0;   /* braces: open corners + open-side intermediates, 2 each */
          _bN += 8;   /* two corner braces per wall, clad or open */
          _pN += _rNs('front', _et9.intW); _bN += _rNs('front', _et9.intW) * 2;
          _pN += _rNs('rear', _et9.intW); _bN += _rNs('rear', _et9.intW) * 2;
          _pN += _rNs('sideL', _et9.intL); _bN += _rNs('sideL', _et9.intL) * 2;
          _pN += _rNs('sideR', _et9.intL); _bN += _rNs('sideR', _et9.intL) * 2;
          if (_pN > 0) { var _pp = _postProfile(); add(_pp.code, 'Timber post ' + _pp.label + ' C24', 'structure', r2e(_pN * Math.max(0.2, _wallClearH(_et9) - 2 * global.SaunaWalls.STUD.w)), 'M', _pN + ' open-side support posts'); }
          if (_bN > 0) { var _bpp = _postProfile(); add(_bpp.code, 'Timber brace ' + _bpp.label + ' C24', 'structure', r2e(_bN * 0.85), 'M', _bN + ' knee braces on the open-side posts, along the wall'); add('FAS80180', 'Wood screw 8\u00D7180', 'fasteners', _bN * 4, 'PCS', 'open-side brace fixings, 2 massive screws per joint'); }
          if (_pN > 0) {
            if (STATE.foundation === 'pads') {
              add('FEE00001', 'Excavations', 'foundation', r2e(_pN * PAD_GEO_W * PAD_GEO_W * (PAD_H - PAD_PROUD + PAD_GRAVEL_H)), 'M3', 'pad pits');
              add('FEC00003', 'Geotextile', 'foundation', r2e(_pN * PAD_GEO_W * PAD_GEO_W), 'M2', 'separation layer at the pit bottom');
              add('FEC00002', 'Gravel base', 'foundation', r2e(_pN * PAD_GRAVEL_W * PAD_GRAVEL_W * PAD_GRAVEL_H), 'M3', 'compacted macadam bed under the pads');
              add('FFM00001', 'Cast-in-place concrete C20/25', 'foundation', r2e(_pN * PAD_W * PAD_W * PAD_H), 'M3', 'open-side post pads');
              add('FFP01001', 'Adjustable post base \u03c0', 'foundation', _pN, 'PCS', 'post-to-pad brackets');
            } else if (STATE.foundation === 'piers') {
              add('FFP01002', 'H-post anchor \u03c0', 'foundation', _pN, 'PCS', 'open-side post anchors');
            } else {
              add('FGR00001', 'Ground screw 1200', 'foundation', _pN, 'PCS', 'open-side post footings, no floor -- founded individually');
              add('FGR00002', 'U-head post adapter', 'foundation', _pN, 'PCS', 'post to ground screw');
            }
            /* THE POST IS FIXED IN ITS FOOT. A U-head holds the post between two
               flanges and is screwed through both; an adjustable base has four
               holes; the H-anchor two bolts through the post. None of that was
               a row, so every post stood in its bracket loose on paper. */
            if (STATE.foundation === 'pads')        add('FAS80100', 'Wood screw 8\u00D7100', 'fasteners', _pN * 4, 'PCS', 'post to adjustable base, 4 per base');
            else if (STATE.foundation === 'piers')  add('FAS80100', 'Wood screw 8\u00D7100', 'fasteners', _pN * 2, 'PCS', 'post to H-anchor, 2 through-screws per post');
            else                                    add('FAS80100', 'Wood screw 8\u00D7100', 'fasteners', _pN * 2, 'PCS', 'post to U-head, one through each flange');
            /* THE POST CARRIES THE BEAM. Two reinforced angle brackets per post
               head, one each side, nailed with connector nails -- the bracket
               is what the catalogue has for a post-to-beam joint, and a
               connector nail is what a bracket is nailed with. */
            add('FAT00005', 'Reinforced angle bracket', 'fasteners', _pN * 2, 'PCS', 'post head to beam, 2 per post');
            add('FAS38038', 'Structural connector nail 3.8\u00D738', 'fasteners', _pN * 2 * 10, 'PCS', 'post head brackets, 10 per bracket');
          }
          if (SHELTER_SCREEN_WALLS) {
            var _railM = 0, _nailM = 0, _scrJ = 0;
            var _H9 = Math.max(0.2, _wallClearH(_et9) - 2 * global.SaunaWalls.STUD.w) + PANEL_BASE + global.SaunaWalls.STUD.w + _foundLift() - 0.05;
            ['front', 'rear', 'sideL', 'sideR'].forEach(function (w9) {
              if (!_sf9(w9)) return;
              var sp9 = (w9 === 'front' || w9 === 'rear') ? _et9.intW : _et9.intL;
              var stN9 = _rN4(sp9) + 2;
              _railM += sp9; _scrJ += stN9;
              if (STATE.facadePattern !== 'wood2') {
                var nG9 = Math.max(0, Math.ceil((_H9 - 0.06) / 0.75) - 1);
                _railM += nG9 * sp9; _scrJ += nG9 * stN9;
              } else {
                var bays9 = stN9 - 1;
                for (var b9 = 0; b9 < bays9; b9++) {
                  var nn9 = Math.max(0, Math.ceil((sp9 / bays9) / 1.25) - 1);
                  _nailM += nn9 * _H9; _scrJ += nn9 * 2;
                }
              }
            });
            if (_railM > 0) add('KVH08060', 'Screen rail 80\u00D760 C24', 'structure', r2e(_railM), 'M', 'bottom rail + girts carrying the screen boards');
            if (_nailM > 0) add('KVH08060', 'Screen nailer 80\u00D760 C24', 'structure', r2e(_nailM), 'M', 'vertical nailers for horizontal boards, bays split to max 1.25 m');
            if (_scrJ > 0) add('FAS80180', 'Wood screw 8\u00D7180', 'fasteners', _scrJ * 2, 'PCS', 'screen rails and nailers, 2 per joint');
          }
        })();
        /* WALL FRAME FASTENING -- computed, never modeled, as decreed.
           Joint schedule (carpentry practice, review may re-spec any line):
           - stud ends: 2 screws 8x180 per joint, two joints per stud
           - headers: 6 screws 8x180 each (through kings + top)
           - jack-to-king stitching: one 6x120 per 400 mm of jack
           - sill plates: 4 screws 6x120 each
           - sole anchoring: washer-head 8x260 through sole + OSB into the
             floor frame, one per 600 mm of wall
           - cap corner laps: 4 washer-head 8x200 per corner
           - hold-down straps at the corners: FAT00012, 2 per corner,
             nailed with 8 nails 70 each
           - panel-to-panel end studs: one 8x100 per 500 mm of height */
        try {
          var fstStudJoints = 0, fstJack120 = 0, fstHdr = 0, fstSill120 = 0;
          for (var fw = 0; fw < WLB.studs.length; fw++) {
            var FSt = WLB.studs[fw];
            fstStudJoints += 4;
            if (FSt.role === 'jack') fstJack120 += Math.ceil(FSt.len / 0.4);
          }
          fstHdr = WLB.headers.length * 6;
          fstSill120 = WLB.sills.length * 4;
          var wallRun = 0;
          for (var fp2 = 0; fp2 < WLB.plates.length; fp2++) wallRun += WLB.plates[fp2].len;
          var soleAnch = Math.ceil(wallRun / 0.6);
          var corners4 = 4 * ((STATE.modules && STATE.modules.length) || 1);
          var pj = Math.max(0, WLB.panels.length - 4);   /* joints ~ panels minus one per wall */
          var hPan = _wallClearH(_saunaType());
          var pjScrews = pj * Math.ceil(hPan / 0.5);
          add('FAS80180', 'Wood screw 8\u00D7180', 'fasteners', fstStudJoints + fstHdr, 'PCS', 'stud ends 2+2 each, headers 6 each');
          add('FAS60120', 'Wood screw 6\u00D7120', 'fasteners', fstJack120 + fstSill120, 'PCS', 'jack-king stitching @400 + sill plates');
          add('FAS80200', 'Washer head screw 8\u00D7200', 'fasteners', corners4 * 4, 'PCS', 'cap plate corner laps, 4 per corner');
          add('FAT00012', 'Hold down strap', 'fasteners', corners4 * 2, 'PCS', 'wall corners, 2 per corner');
          add('FAS70000', 'Nails 70', 'fasteners', corners4 * 2 * 8, 'PCS', 'hold down strap nailing, 8 per strap');
          if (pjScrews) add('FAS80100', 'Wood screw 8\u00D7100', 'fasteners', pjScrews, 'PCS', 'panel end-stud pairs @500 height');
        } catch (eWF2) {}

        if (hdM > 0) add('KVH60160', 'Opening head profile, flat', 'structure', r2e(hdM / 2), 'M', 'directly above ' + WLB.headers.length + ' opening frames');
        var plBotM = 0;
        for (var pb2 = 0; pb2 < WLB.plates.length; pb2++) if (WLB.plates[pb2].kind === 'bottom') plBotM += WLB.plates[pb2].len;

        if (envType !== 'interior') {
          layerRows(SHED_BARE ? self.WALL_EXT.filter(function (L) { return L.code !== 'INS00002'; }) : self.WALL_EXT, wallsA, linA);
          /* the wind barrier covers the WHOLE building: add the closure
             above the cap (mono wedges + high wall / gable triangles) and
             its staples -- the wallsA row above covers only the rectangles */
          var tanW4 = Math.tan((STATE.roofPitch || 0) * Math.PI / 180);
          var closA = 0;
          if (SHELTER_SCREEN_WALLS) {
            /* SHELTER: only a FILLED gable side is clad above the cap (screen
               boards to the ridge + the GLAP lap, v31); mono has no above-cap
               cladding and there is never a membrane. Slat gaps reduce it. */
            if (STATE.roofType === 'gable') {
              var gS4 = (_sideFilled('front') ? 1 : 0) + (_sideFilled('rear') ? 1 : 0);
              var GL5 = ((global.SaunaRoofFrame && global.SaunaRoofFrame.RAFTER) ? global.SaunaRoofFrame.RAFTER.h : 0.16) / Math.cos(Math.atan(tanW4 || 0.0001));
              closA = gS4 * (_extW() * ((_extW() / 2) * tanW4) / 2 + _extW() * GL5);
              if (STATE.fillType === 'slats') closA *= (0.06 / (0.06 + Math.max(0, Math.min(0.3, (STATE.slatGap != null ? STATE.slatGap : 0.04)))));
            }
          } else if (STATE.roofType === 'mono') {
            var riseB = _extW() * tanW4;
            /* two raked trapezoids (W*rise/2 each) + the high wall rectangle */
            closA = _extW() * riseB + STATE.footprint.l * riseB;
          } else if (STATE.roofType === 'gable') {
            /* two end triangles to the ridge */
            closA = _extW() * ((_extW() / 2) * tanW4) / 2 * 2;
          }
          if (closA > 0.05) add('FVW01351', 'Wood facade cladding', 'envelope', r2e(closA * 1.05), 'M2', 'closure above the cap (rakes / gables), boards cut to the roofline');
          var trimM = 8 * (STATE.footprint.h - 0.05);
          add('FAS45070', 'Stainless screw A2 4.5\u00D770', 'fasteners', Math.ceil((wallsA + closA) * 12), 'PCS', 'facade cladding to the battens, 12/m2');
          var roofExpA = 0;
          for (var rmi = 0; rmi < STATE.modules.length; rmi++) {
            var rmod = STATE.modules[rmi];
            var rt2 = _typeOf(rmod);
            roofExpA += rt2.intL * rt2.intW * (1 - _coverFrac(rmi));
          }
          /* ROR00001 is ROOF TILES / COVERING in the catalog; the waterproof
             layer this row means is ROR00002. Right label on the wrong code is
             the worst kind of wrong -- it reads correctly all the way to the
             merchant's order. */
          add('ROR00002', 'Roof hydrofoil', 'envelope', r2e(roofExpA * 1.05), 'M2', 'cold vented roof -- thermal envelope closes at the ceiling wool');
          add('FAS63000', 'Staples 63', 'fasteners', Math.ceil(roofExpA * 10), 'PCS', 'hydrofoil to the rafters, ~10/m2');
          add('KVR03050', 'Counter-battens 30\u00D750', 'structure', r2e(roofExpA * 1.7), 'M', 'along the rafters over the foil, vent gap');
          add('FAS50060', 'Wood screw 5\u00D760', 'fasteners', Math.ceil(roofExpA * 1.7 / 0.4), 'PCS', 'counter-battens through the foil into rafters @400');
          add('KVR03050', 'Roof battens 30\u00D750', 'structure', r2e(roofExpA * 2.0), 'M', 'ACROSS on the counter-battens @500, the covering\u2019s fixing lines');
          add('FAS50060', 'Wood screw 5\u00D760', 'fasteners', Math.ceil(roofExpA * 2.0 / 0.6), 'PCS', 'cross battens to counter-battens at the crossings');
          /* THE COVERING THE ROOF IS DRAWN WITH, NOT ALWAYS THE SAME ROW. This
             billed ROR00001 whatever the roof showed -- and called it a sheet,
             while the catalogue row is ROOF TILES / COVERING, the shingle. */
          var covB = _covDef();
          add(covB.code, covB.def, 'envelope', r2e(roofExpA * 1.05), 'M2', 'top layer, ' + covB.key + ', 5% cut waste');
          /* A SHEET IS SCREWED, A SHINGLE IS NAILED. Sealing screws with EPDM
             washers through a metal sheet, six to the square metre; a shingle
             strip takes four roofing nails, and there are about seven strips
             to the square metre. */
          if (covB.key === 'shingle') add('FAS00318', 'Roofing nails 18', 'fasteners', Math.ceil(roofExpA * 30), 'PCS', 'shingle strips, 4 nails each, ~30/m2');
          else add('FAS00225', 'Sealing screws 25', 'fasteners', Math.ceil(roofExpA * 6), 'PCS', 'roofing to the stack, EPDM washers, 6/m2');
          /* ROOF EDGE FLASHING, step 1: the metal that finishes the roof
             perimeter. The roof plane overhangs the walls by roofOver on
             every side; its edges are the covered footprint plus those
             overhangs. Which edge is an EAVE (drip), a RAKE/gable, or the
             RIDGE depends on the roof type -- mono rises across the width so
             its low & high sides are eaves and the two side edges are rakes;
             a gable ridges along the length so the long sides are eaves and
             the two ends are the raking gables meeting at a ridge. */
          var ovR = STATE.roofOver || { x0: 0.18, x1: 0.18, z0: 0.18, z1: 0.18 };
          /* the roof edge is held at the covering floor (v309): effective
             x-overhang = Math.max(ov, kTop/tanP+0.12). z keeps its own value. */
          var _cosP = Math.cos(clamp(STATE.roofPitch || 0, 0, 45) * Math.PI / 180);
          var _tanP = Math.tan(clamp(STATE.roofPitch || 0, 0, 45) * Math.PI / 180);
          var _kTop = 0.16 / (_cosP || 1);
          var _eaveFloor = _kTop / (_tanP || 1) + 0.12;
          var _exX0 = (ovR.x0 || 0), _exX1 = (ovR.x1 || 0);   /* SHELTER: BoQ at the fixed roof edge */
          var edgeW = _extW() + _exX0 + _exX1;                  /* roof width incl. floored overhang */
          var edgeL = STATE.footprint.l + (ovR.z0 || 0) + (ovR.z1 || 0); /* roof length incl. overhang */
          var pitchR = clamp(STATE.roofPitch || 0, 0, 45) * Math.PI / 180;
          var slopeF = 1 / Math.cos(pitchR);   /* raked edges are longer by 1/cos */
          if (STATE.roofType === 'mono') {
            /* eaves: low + high side, both run along the width (x). Drip edge
               only where water leaves -- the LOW eave. The high side gets a
               drip too (wind-driven), keep both. Rakes: the two side edges,
               raking along the slope, take gable flashing. No ridge. */
            add('ROD00201', 'Drip edge roof flashing', 'envelope', r2e(edgeW * 2), 'M', 'eaves, low and high side');
            add('ROR00006', 'Gable flashing', 'envelope', r2e(edgeL * slopeF * 2), 'M', 'the two raking side edges');
          } else if (STATE.roofType === 'gable') {
            /* eaves: the two long sides (drip to the gutters). Rakes: the two
               gable ends, each a pair of slopes -> gable flashing along both.
               Ridge: one run along the length + a ventilation strip under it. */
            add('ROD00201', 'Drip edge roof flashing', 'envelope', r2e(edgeL * 2), 'M', 'the two eaves');
            add('ROR00006', 'Gable flashing', 'envelope', r2e(edgeW * slopeF * 2), 'M', 'the four raking gable edges');
            /* A SHINGLE ROOF HAS SHINGLE CAPS, as on the ADU: the ridge is
               capped with the same asphalt -- cap pieces 333 long lapped every
               143 mm, i.e. 0.33 m2 of shingle per metre of ridge -- not with
               the metal profile (Marek). The vent strip beneath stays. */
            if (_covDef().key === 'shingle') {
              add('ROR00001', 'Asphalt shingle', 'envelope', r2e(edgeL * 0.333 * 1.05), 'M2', 'ridge cap shingles, 333 long lapped at 143, 5% waste');
              add('FAS00318', 'Roofing nails 18', 'fasteners', Math.ceil(edgeL / 0.143) * 2, 'PCS', 'ridge cap shingles, 2 nails each');
            } else {
              add('ROR00009', 'Ridge cap flashing \u03C0', 'envelope', r2e(edgeL), 'M', 'sheet-roof ridge, closes the two planes');
            }
            add('ROR00007', 'Ridge ventilation strip', 'envelope', r2e(edgeL), 'M', 'under the ridge cap, cold roof breathes');
          }
          var flashM = (STATE.roofType === 'mono')
            ? (edgeW * 2 + edgeL * slopeF * 2)
            : (edgeL * 2 + edgeW * slopeF * 2 + edgeL);
          add('FAS45070', 'Stainless screw A2 4.5\u00D770', 'fasteners', Math.ceil(flashM / 0.3), 'PCS', 'edge flashings to the battens/OSB @300');
        /* every lap and every screw head gets a bead: one cartridge does
           about twelve metres of edge */
        add('FEC00038', 'PU construction sealant 290ml', 'consumables', Math.max(1, Math.ceil(flashM / 12)), 'PCS', 'flashing laps and screw heads, 1 cartridge per ~12 m');
          /* GUTTERS + DOWNSPOUTS, step 2. Water is collected at the EAVES.
             Mono sheds to ONE side (the low eave) -> one gutter run along the
             width, one downspout. Gable sheds to BOTH long eaves -> two runs,
             two downspouts. Each run: the gutter itself, brackets @600, an end
             cap at each end, and a hopper feeding the downspout. Each
             downspout: pipe from the eave to the ground, brackets @1500, an
             elbow pair to clear the roof overhang at the top and an outlet
             elbow at the bottom. Auto by pitch, as agreed. */
          var eaveRun = (STATE.roofType === 'mono') ? edgeW : edgeL;   /* gutter length per eave */
          var nEave = (STATE.roofType === 'mono') ? 1 : 2;             /* how many eaves drain */
          var dropH = STATE.footprint.h + 0.35;                        /* eave-to-ground drop */
          /* ROOF SUPPORT POSTS for deep overhangs (> 0.5 m): 4x4 timber, a
             pair per deep eave, founded like the cabin. */
          var deepEaves = 0;
          var edgeInsetH = STATE.footprint.h + 0.3;
          if ((ovR.x0 || 0) > OVERHANG_POST) deepEaves++;
          if ((ovR.x1 || 0) > OVERHANG_POST) deepEaves++;
          var deepGables = 0;
          if ((ovR.z0 || 0) > OVERHANG_POST) deepGables++;
          if ((ovR.z1 || 0) > OVERHANG_POST) deepGables++;
          if (deepGables > 0) {
            /* two gable posts per deep gable, minus the corners already
               carried by a side-eave post; no top header (the plate ties). */
            var sideDeepCount = ((ovR.x0 || 0) > OVERHANG_POST ? 1 : 0) + ((ovR.x1 || 0) > OVERHANG_POST ? 1 : 0);
            var nGP = Math.max(0, deepGables * 2 - deepGables * sideDeepCount);
            if (nGP > 0) add('KVH10100', 'Timber post 100\u00D7100', 'structure', r2e(nGP * edgeInsetH), 'M', 'posts under the projecting gable');
            if (nGP > 0) add('KVH10100', 'Timber brace 100\u00D7100', 'structure', r2e(nGP * 0.85), 'M', 'diagonal knee braces along the plate, gable posts');
            if (nGP > 0) add('FAS80180', 'Wood screw 8\u00D7180', 'fasteners', nGP * 4, 'PCS', 'gable brace fixings, 2 screws per joint');
            if (nGP > 0) {
              if (STATE.foundation === 'screws') {
                add('FGR00001', 'Ground screw 1200', 'foundation', nGP, 'PCS', 'gable post footings');
                add('FGR00002', 'U-head post adapter', 'foundation', nGP, 'PCS', 'post sockets');
                add('FAS80100', 'Wood screw 8\u00D7100', 'fasteners', nGP * 2, 'PCS', 'gable post to U-head, one through each flange');
              } else if (STATE.foundation === 'pads') {
                add('FFM00001', 'Cast-in-place concrete C20/25', 'foundation', r2e(nGP * 0.09), 'M3', 'gable post pads');
                add('FFP01001', 'Adjustable post base \u03c0', 'foundation', nGP, 'PCS', 'post sockets');
              } else {
                add('FFP01002', 'H-post anchor \u03c0', 'foundation', nGP, 'PCS', 'gable post anchors');
              }
            }
          }
          if (deepEaves > 0) {
            var nPosts = deepEaves * 2;
            /* 4x4 posts -> the catalog's 100x100 timber post (KVH10100), the
               same one the pergola uses; founded EXACTLY like the cabin --
               screws get a ground screw + U-head, pads get concrete + a post
               base bracket, piers get the H-anchor. Only real codes. */
            add('KVH10100', 'Timber post 100\u00D7100', 'structure', r2e(nPosts * edgeInsetH), 'M', 'posts under the deep overhang');
            add('KVH10100', 'Timber post 100\u00D7100', 'structure', r2e(deepEaves * (STATE.footprint.l + (ovR.z0 || 0) + (ovR.z1 || 0))), 'M', 'eave headers tying the post pairs, carrying the overhang');
            add('KVH10100', 'Timber brace 100\u00D7100', 'structure', r2e(nPosts * 0.85), 'M', 'diagonal knee braces along the eave, one per post');
            add('FAS80180', 'Wood screw 8\u00D7180', 'fasteners', nPosts * 4, 'PCS', 'brace fixings, 2 massive screws per joint (2 joints per brace)');
            if (STATE.foundation === 'screws') {
              add('FGR00001', 'Ground screw 1200', 'foundation', nPosts, 'PCS', 'post footings, as the cabin');
              add('FGR00002', 'U-head post adapter', 'foundation', nPosts, 'PCS', 'post to ground screw');
              add('FAS80100', 'Wood screw 8\u00D7100', 'fasteners', nPosts * 2, 'PCS', 'eave post to U-head, one through each flange');
            } else if (STATE.foundation === 'pads') {
              add('FFM00001', 'Cast-in-place concrete C20/25', 'foundation', r2e(nPosts * 0.09), 'M3', 'post pads');
              add('FFP01001', 'Adjustable post base \u03c0', 'foundation', nPosts, 'PCS', 'post-to-pad brackets');
            } else {
              add('FFP01002', 'H-post anchor \u03c0', 'foundation', nPosts, 'PCS', 'post-to-pier anchors');
            }
          }
          if (eaveRun > 0.3 && nEave > 0) {
            add('ROG01251', 'Rainwater gutter \u03C0', 'envelope', r2e(eaveRun * nEave), 'M', (nEave === 2) ? 'both eaves' : 'the low eave');
            add('ROA01251', 'Gutter bracket \u03C0', 'fasteners', Math.ceil(eaveRun / 0.6) * nEave, 'PCS', 'gutter hangers @600');
            add('FAS50060', 'Wood screw 5\u00D760', 'fasteners', Math.ceil(eaveRun / 0.6) * nEave * 2, 'PCS', 'gutter brackets to the fascia, 2 each');
            add('ROG00001', 'Gutter end cap \u03C0', 'envelope', 2 * nEave, 'PCS', 'one at each end of every run');
            add('ROG00005', 'Gutter hopper \u03C0', 'envelope', nEave, 'PCS', 'gutter to downspout outlet');
            add('ROG01252', 'Rainwater downspout \u03C0', 'envelope', r2e(dropH * nEave), 'M', 'eave to ground');
            add('FAS60080', 'Wood screw 6\u00D780', 'fasteners', Math.ceil(dropH / 1.5 + 1) * nEave * 2, 'PCS', 'downspout brackets to the post, 2 each');
            add('ROG00011', 'Downspout bracket \u03C0', 'fasteners', Math.ceil(dropH / 1.5 + 1) * nEave, 'PCS', 'downspout to the facade @1500');
            add('ROG00006', 'Downspout elbow \u03C0', 'envelope', 2 * nEave, 'PCS', 'offset pair at the top, clears the overhang');
            add('ROG00009', 'Downspout outlet elbow \u03C0', 'envelope', nEave, 'PCS', 'discharge at the base');
          }
          var facAOut = 0;
          for (var fmi = 0; fmi < STATE.modules.length; fmi++) {
            var fmod = STATE.modules[fmi];
            var ftt = _typeOf(fmod);
            facAOut += 2 * (ftt.L + _extW()) * (ftt.H + 0.05);
          }
          facAOut = Math.max(0, facAOut - opA);
          if (STATE.facadeColor === 'corten') {
            add('FVC00101', 'Corten facade panel', 'facade', r2e(facAOut), 'M2');
            add('FAS00225', 'Sealing screws 25', 'fasteners', Math.round(facAOut * 6), 'PCS', 'corten panel fixing');
          } else {
            /* the cladding M2 row itself now comes from WALL_EXT above; what
               stays here is the finish system that depends on the pattern */
            add('MFP00001', 'Penetrating wood sealer', 'facade', r2e(facAOut), 'M2');
            add('MFP00002', 'Exposed wood coating', 'facade', r2e(facAOut), 'M2', STATE.facadeColor === 'shou' ? 'shou sugi ban finish' : 'finish coat');
            add('FAS45070', 'Stainless screw A2 4.5\u00D770', 'fasteners', Math.round(facAOut * 12), 'PCS', 'cladding fixing');
            if (STATE.facadePattern === 'bond') {
              add('KVL20040', 'Timber batten 20\u00D740', 'facade', r2e(facAOut * 3.65), 'M', 'cover battens');
            }
          }
        }
        /* Floor. The container laid a floating wood floor over the box's own
           plywood deck -- a sauna has neither. Joists carry the insulation, the
           vapour barrier closes the warm side as it does in the walls, and the
           finish is a lift-out duckboard: bare feet never touch a hot or wet
           surface, and it comes out to dry. */
        /* the cut list: N pieces x their length, from the same layout the
           drawing stands on. A merchant reads this row and knows what to cut. */

        /* Bathroom and kitchen came out with the container home's room types:
           toilet, bath, shower, ceramic tiling, PEX runs and a fitted kitchen,
           all keyed off a room being tagged 'bath' or 'kitchen'. A garden
           building has no room types. Sauna wet fittings (shower, floor drain)
           come back later as their own thing, driven by buildingType. */

        /* PARTITIONS -- from the drawing, not a coefficient. Each wall's real
           resolved length times the real clear height: studs at 600, 60 mm wool
           between them, T&G on both faces because every face in this building
           is a sauna face. This is the first block where the budget line and
           the thing on screen are the same object, which is the whole point of
           modelling: drag the wall shorter and the row shrinks with it. */

        /* SAUNA EQUIPMENT -- one per building, because these are what make the
           building a sauna: the heater and its stones, the control loop, the
           guard, the two vents that keep the loyly moving, the IP65 light on
           silicone cable, and oil for the wood. Benches are NOT here: they are
           not modelled yet, and a quantity with no geometry behind it is the
           habit this file is being cured of. They arrive with the INSIDE step. */

        var roomPer = 0, sockets = 0;
        if (roomPer > 0) {
          sockets = Math.max(2, Math.round(roomPer / 2));
          add('EET00001', 'Skirting trunking with sockets', 'envelope', r2e(roomPer), 'M', 'room perimeters');
          add('EES00001', 'Socket 230V', 'envelope', sockets, 'PCS');
        }
      }

      if (passN > 0) {
        add('SSS00005', 'Steel channel UPE160', 'structure', r2e(passTrim), 'M', 'passage opening trim');
        add('FEC00032', 'Chemical anchor', 'fasteners', passN * 8, 'PCS', 'passage frame');
      }

      var pergs = STATE.pergolas || [];
      var pv = STATE.pergolaVariant || 'wood_slat';
      var pAlu = pv.indexOf('alu') === 0;
      var pFloor = (pv === 'wood_deck') ? 'deck' : (pv === 'alu_tile' ? 'tile' : 'none');
      var pPostBm = 0, pBeamBm = 0, pSlatBm = 0, pDeckA = 0, pTileA = 0, pPostCount = 0, pFloorA = 0;
      for (var pgi = 0; pgi < pergs.length; pgi++) {
        var pg = pergs[pgi];
        var H = 2.5;
        var nx = 2 + (pg.w > 3.2 ? 1 : 0), nz = 2 + (pg.l > 3.2 ? 1 : 0);
        var np = nx * 2 + (nz - 2) * 2;
        if (pg.attach) np = Math.ceil(np / 2) + 1;
        pPostCount += np;
        pPostBm += np * H;
        pBeamBm += 2 * (pg.w + pg.l);
        var along = Math.min(pg.w, pg.l), across = Math.max(pg.w, pg.l);
        var nSlat = Math.floor(across / 0.17);
        pSlatBm += nSlat * along;
        pFloorA += pg.w * pg.l;
        if (pFloor === 'deck') pDeckA += pg.w * pg.l;
        if (pFloor === 'tile') pTileA += pg.w * pg.l;
      }
      if (pergs.length) {
        if (pAlu) {
          add('KVH10100', 'Aluminium post 100\u00D7100', 'pergola', r2e(pPostBm), 'M', 'posts (alu profile)');
          add('KVH10160', 'Aluminium beam 100\u00D7160', 'pergola', r2e(pBeamBm), 'M', 'perimeter beams');
          add('KVH60120', 'Aluminium slat 60\u00D7120', 'pergola', r2e(pSlatBm), 'M', 'roof slats');
        } else {
          add('KVH10100', 'Timber post 100\u00D7100', 'pergola', r2e(pPostBm), 'M', 'posts');
          add('KVH10160', 'Timber beam 100\u00D7160', 'pergola', r2e(pBeamBm), 'M', 'perimeter beams');
          add('KVH60120', 'Timber slat 60\u00D7120', 'pergola', r2e(pSlatBm), 'M', 'roof slats');
          add('MFP00001', 'Penetrating wood sealer', 'pergola', r2e((pPostBm + pBeamBm + pSlatBm) * 0.08), 'M2', 'timber protection');
        }
        add('FFP01001', 'Adjustable post base', 'pergola', pPostCount, 'PCS', 'post footings');
        add('FGR00001', 'Ground screw 1200', 'pergola', pPostCount, 'PCS', 'post anchoring');
        add('FAT00005', 'Reinforced angle bracket', 'fasteners', pPostCount * 2 + pergs.length * 4, 'PCS', 'pergola connections');
        add('FEC00032', 'Chemical anchor', 'fasteners', pPostCount, 'PCS', 'post base to footing');
        if (pFloor === 'deck') {
          add('TST00001', 'Decking boards', 'pergola', r2e(pDeckA * 7.4), 'M', 'pergola floor decking');
          add('KVH40060', 'Timber batten 40\u00D760', 'pergola', r2e(pDeckA * 2.2), 'M', 'deck sub-frame');
          add('FAS55050', 'Stainless screw A2 5.5\u00D750', 'fasteners', Math.round(pDeckA * 20), 'PCS', 'decking fixing');
        } else if (pFloor === 'tile') {
          add('FFP01002', 'Adjustable pedestal', 'pergola', Math.ceil(pTileA / 0.36), 'PCS', 'pedestals @600');
          add('SBC00002', 'Floor tiles', 'pergola', r2e(pTileA * 1.05), 'M2', 'pergola paving 2 cm');
        }
      }

      var opN = (STATE.openings || []).length;
      if (opN > 0) {
        add('ODP00001', 'PU foam', 'fasteners', Math.ceil(opN * 0.75), 'PCS', 'opening perimeter seal');
        /* EVERY PLACED OPENING IS A LINE. The exterior windows and doors used
           to vanish into a foam count; now each one reports itself by its own
           sku -- the same id the drawer sold and the sheet translates. A
           legacy opening without a sku stays silent rather than inventing a
           code the fence would refuse. */
        var sillIntM = 0, sillExtM = 0, trimM = 0, trimN = 0;
        for (var ob2 = 0; ob2 < (STATE.openings || []).length; ob2++) {
          var opB = STATE.openings[ob2];
          if (!opB || !opB.sku) continue;
          var szB = Math.round(opB.w * 1000) + 'x' + Math.round(opB.h * 1000);
          add(opB.sku, 'Opening ' + szB, 'openings', 1, 'PCS', opB.wall + ' ' + szB + ' mm');
          /* A WINDOW (sill >= 30 mm) gets an interior and an exterior sill,
             each window-width long. A DOOR (sill < 30 mm) sits on the floor --
             no sill board. Every opening gets the corner trim profile around
             the EXTERNAL REVEAL: two jambs + the head (the bottom is the sill
             board on a window / the threshold on a door). Trim per opening =
             2*h + w. */
          var isDoorB = ((opB.sill || 0) < 0.03);
          if (!isDoorB) { sillIntM += opB.w; sillExtM += opB.w; }
          else if (opB.sku === 'ODG48001') { add('ODD00004', 'Garage door opener', 'openings', 1, 'PCS', 'motorized sectional'); }
          else if (opB.sku === 'ODE18002') { add('ODD00002', 'Exterior door handle set', 'openings', 1, 'PCS', 'double-wing'); add('ODD00005', 'Door hinges', 'openings', 6, 'PCS', 'double-wing, 3 per leaf'); }
          else { add('ODD00002', 'Exterior door handle set', 'openings', 1, 'PCS', 'lock + handle'); add('ODD00005', 'Door hinges', 'openings', 4, 'PCS', 'per exterior door'); }
          trimM += 2 * opB.h + opB.w;
          trimN += 1;
        }
        if (sillIntM > 0) add('OWW00001', 'Interior window sill', 'openings', +sillIntM.toFixed(2), 'M', 'all windows');
        if (sillExtM > 0) add('OWW00002', 'Exterior window sill', 'openings', +sillExtM.toFixed(2), 'M', 'all windows');
        if (trimM > 0) {
          add('FVW02660', 'Wood corner trim profile', 'facade', +trimM.toFixed(2), 'M', 'opening reveals + door cover strips');
          add('FAS45070', 'Stainless screw', 'fasteners', Math.max(trimN * 4, Math.ceil(trimM / 0.30)), 'PCS', 'corner trim @300');
        }
        add('FAS60100', 'Wood screw 6\u00D7100', 'fasteners', opN * 8, 'PCS', 'frame fixing');
      }

      var terrA = 0, terrRail = 0, railPosts = 0, railBays = 0, railInfill = 0;
      for (var tmi = 0; tmi < STATE.modules.length; tmi++) {
        var tin = (STATE.terraceTops && STATE.terraceTops[tmi]) ? _terraceForModule(tmi) : null;
        if (tin) {
          terrA += tin.area;
          terrRail += tin.railLen || 0;
          var tf = _railFigures(tin.rails);
          railPosts += tf.postN; railBays += tf.bays; railInfill += tf.infillA;
        }
        if ((STATE.modules[tmi].lvl | 0) >= 1) {
          var ghs = _cutFloorHolesFor(tmi);
          for (var ghi2 = 0; ghi2 < ghs.length; ghi2++) {
            var gf = _railFigures(_guardEdgesFor(ghs[ghi2]));
            railPosts += gf.postN; railBays += gf.bays; railInfill += gf.infillA;
          }
        }
      }
      if (railPosts > 0) {
        add('FEC00008', 'Steel post 60\u00D760', 'railing', railPosts, 'PCS', 'railing posts \u2014 swap to RAILING POST when added');
        add('FEC00015', 'Steel fence panel', 'railing', railBays, 'PCS', 'infill ' + (Math.round(railInfill * 100) / 100) + ' m\u00B2 \u2014 swap to RAILING INFILL (M2) when added');
        add('FAS80080', 'Washer head screw 8\u00D780', 'fasteners', railPosts * 2, 'PCS', 'post anchoring');
      }
      var placeTerr = STATE.terraces || [];
      var ptA = 0, ptPosts = 0, ptRailEdges = [];
      for (var pti = 0; pti < placeTerr.length; pti++) {
        var pt = placeTerr[pti];
        ptA += pt.w * pt.l;
        var att2 = _terraceAttach(pt);
        ptPosts += att2 ? 2 : 4;
        var pedges = [
          { k: 'zmin', e: [-pt.w / 2, -pt.l / 2, pt.w / 2, -pt.l / 2] },
          { k: 'zmax', e: [-pt.w / 2, pt.l / 2, pt.w / 2, pt.l / 2] },
          { k: 'xmin', e: [-pt.w / 2, -pt.l / 2, -pt.w / 2, pt.l / 2] },
          { k: 'xmax', e: [pt.w / 2, -pt.l / 2, pt.w / 2, pt.l / 2] }
        ];
        for (var pe = 0; pe < pedges.length; pe++) {
          if (att2 && att2.side === pedges[pe].k) continue;
          ptRailEdges.push(pedges[pe].e);
        }
      }
      var terrTotalA = terrA + ptA;
      if (terrTotalA > 0) {
        if (STATE.terraceSurface === 'pavers') {
          add('FFP01002', 'Adjustable pedestal', 'terrace', Math.ceil(terrTotalA / 0.36), 'PCS', 'pedestals @600');
          add('SBC00002', 'Floor tiles', 'terrace', r2e(terrTotalA * 1.05), 'M2', 'terrace paving 2 cm');
        } else {
          add('FAS55050', 'Stainless screw A2 5.5\u00D750', 'fasteners', Math.round(terrTotalA * 20), 'PCS', 'deck fixing');
          add('FVW01351', 'Wood boards', 'terrace', Math.round(terrTotalA * 1.05 * 100) / 100, 'M2', 'terrace decking');
        }
      }
      if (placeTerr.length) {
        add('KVH08060', 'Impregnated lumber 80\u00D760', 'terrace', r2e(ptA * 1.6), 'M', 'terrace joists + posts');
        add('FFP01001', 'Adjustable post base', 'terrace', ptPosts, 'PCS', 'terrace footings');
        add('FGR00001', 'Ground screw 1200', 'terrace', ptPosts, 'PCS', 'terrace post anchoring');
        var ptRailFig = _railFigures(ptRailEdges);
        if (ptRailFig.postN > 0) {
          add('FEC00008', 'Steel post 60\u00D760', 'railing', ptRailFig.postN, 'PCS', 'terrace railing posts');
          add('FEC00015', 'Steel fence panel', 'railing', ptRailFig.bays, 'PCS', 'terrace infill ' + ptRailFig.infillA + ' m\u00B2');
        }
      }

      /* NEW footprint terrace (STATE.terraceOn + terraceOver): a wrap-around deck
         on every side whose reach >= TERR_MIN, plus the shared corner. Foundation
         is the SAME type as the cabin, KVH10160 bearers continue the cabin grid,
         TST00002 decking on top, stainless fixings. Mirrors _buildTerraceDeck. */
      if (STATE.terraceOn) {
        var tMod = STATE.modules[0];
        if (tMod) {
          var tT = _typeOf(tMod), tov = STATE.terraceOver;
          var thw = _extW() / 2, thz = tT.L / 2;
          var tsZ1 = tov.z1 >= TERR_MIN, tsZ0 = tov.z0 >= TERR_MIN, tsX1 = tov.x1 >= TERR_MIN, tsX0 = tov.x0 >= TERR_MIN;
          var tRects = [];
          if (tsZ1) tRects.push([2 * thw, tov.z1]);
          if (tsZ0) tRects.push([2 * thw, tov.z0]);
          if (tsX1) tRects.push([tov.x1, 2 * thz]);
          if (tsX0) tRects.push([tov.x0, 2 * thz]);
          if (tsZ1 && tsX1) tRects.push([tov.x1, tov.z1]);
          if (tsZ1 && tsX0) tRects.push([tov.x0, tov.z1]);
          if (tsZ0 && tsX1) tRects.push([tov.x1, tov.z0]);
          if (tsZ0 && tsX0) tRects.push([tov.x0, tov.z0]);
          if (tRects.length) {
            var tKind = STATE.foundation || 'screws';
            var tlay = global.SaunaFoundation.layout();
            var tPts = 0, tBearM = 0, tDeckA = 0, tri, tlp, tlb;
            for (tlp = 0; tlp < tlay.points.length; tlp++) if (tlay.points[tlp].terr) tPts++;
            for (tlb = 0; tlb < tlay.bearers.length; tlb++) tBearM += (tlay.bearers[tlb].terrLen || 0);
            for (tri = 0; tri < tRects.length; tri++) tDeckA += tRects[tri][0] * tRects[tri][1];
            if (tKind === 'pads') {
              add('FCO00009', 'Precast footing 400\u00D7400', 'terrace', tPts, 'PCS', 'terrace footings, as the cabin');
              add('FCO00004', 'Post base anchor U type', 'terrace', tPts, 'PCS', 'terrace bearer seats');
            } else if (tKind === 'piers') {
              add('FCO00008', 'Precast footing 280\u00D7220', 'terrace', tPts, 'PCS', 'terrace footings, as the cabin');
              add('FCO00004', 'Post base anchor U type', 'terrace', tPts, 'PCS', 'terrace bearer seats');
            } else {
              add('FGR00001', 'Ground screw 1200\u00D776', 'terrace', tPts, 'PCS', 'terrace footings, as the cabin');
              add('FGR00002', 'U-head post adapter', 'terrace', tPts, 'PCS', 'terrace bearer seats');
            }
            add('KVH10160', 'Dimensional lumber 100\u00D7160', 'terrace', r2e(tBearM), 'M', 'terrace bearers, continue the cabin grid');
            add('FAS55050', 'Stainless screw A2 5.5\u00D750', 'fasteners', tPts * 4, 'PCS', 'terrace bearers to footings');
            add('TST00002', 'Decking boards 25\u00D7145', 'terrace', r2e(tDeckA * 1.05), 'M2', 'terrace decking, 5% cut waste');
            add('FAS45070', 'Stainless screw A2 4.5\u00D770', 'fasteners', Math.ceil(tDeckA * 22), 'PCS', 'terrace decking @ ~22/m2');
          }
        }
      }

      var stack = false;
      var pts = 0, railLen = 0, railCount = 0;
      for (var fi = 0; fi < STATE.modules.length; fi++) {
        var fm = STATE.modules[fi];
        if ((fm.lvl | 0) !== 0) continue;
        var ft = _typeOf(fm);
        /* counted below, from the layout -- this loop only established which
           modules stand on the ground */
      }
      var lay = global.SaunaFoundation.layout();
      for (var lp = 0; lp < lay.points.length; lp++) if (!lay.points[lp].terr) pts++;
      railCount = lay.bearers.length;
      for (var lb = 0; lb < lay.bearers.length; lb++) railLen += (lay.bearers[lb].len - (lay.bearers[lb].terrLen || 0));

      var bm = {};
      for (var bk in map) bm[bk] = map[bk].qty;
      return { byMaterial: bm, rows: rows };
    },
    publish: function () {
      var b = this.compute();
      try { _BoQDebug.refresh(b); } catch (e) {}

      var M_TO_FT = 3.2808, M2_TO_FT2 = 10.7639, M3_TO_FT3 = 35.3147;
      function r2(n) { return Math.round(n * 100) / 100; }
      var unitOf = {};
      for (var ui = 0; ui < b.rows.length; ui++) {
        unitOf[b.rows[ui].code] = String(b.rows[ui].unit || '').toUpperCase();
      }
      var csv = [[], []];
      Object.keys(b.byMaterial).forEach(function (id) {
        var qty = b.byMaterial[id], u = unitOf[id], metric, imp;
        if (u === 'M')       { metric = r2(qty); imp = r2(qty * M_TO_FT); }
        else if (u === 'M2') { metric = r2(qty); imp = r2(qty * M2_TO_FT2); }
        else if (u === 'M3') { metric = r2(qty); imp = r2(qty * M3_TO_FT3); }
        else                 { metric = Math.round(qty); imp = Math.round(qty); }
        csv.push([id, metric, imp]);
      });
      if (global.ConfiguratorBudgetBridge && global.ConfiguratorBudgetBridge.publishVariantData) {
        global.ConfiguratorBudgetBridge.publishVariantData(b.byMaterial);
        global.ConfiguratorBoQ = global.ConfiguratorBoQ || {};
        global.ConfiguratorBoQ.variantData = csv;
        global.ConfiguratorBoQ.source = 'sauna';
      } else {
        global.ConfiguratorBoQ = { source: 'sauna', variantData: csv, byMaterial: b.byMaterial };
      }
      clearTimeout(this._t);
      this._t = setTimeout(function () {
        if (typeof global.loadVariantCSV === 'function') { try { global.loadVariantCSV(); } catch (e) {} }
      }, 150);
    }
  };

  function _pdfSvgToPng(svg, w, h, scale) {
    scale = scale || 2;
    var doc = global.document;
    return new Promise(function (resolve) {
      try {
        if (svg.indexOf('xmlns="http://www.w3.org/2000/svg"') === -1)
          svg = svg.replace('<svg ', '<svg xmlns="http://www.w3.org/2000/svg" ');
        if (w && h && svg.indexOf('width=') === -1)
          svg = svg.replace('<svg ', '<svg width="' + w + '" height="' + h + '" ');
        var URLo = global.URL || global.webkitURL;
        var blob = new global.Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
        var url = URLo.createObjectURL(blob);
        var img = new global.Image();
        img.onload = function () {
          try {
            var cw = (w || img.naturalWidth) * scale, ch = (h || img.naturalHeight) * scale;
            var cv = doc.createElement('canvas'); cv.width = cw; cv.height = ch;
            var cx = cv.getContext('2d');
            cx.fillStyle = '#ffffff'; cx.fillRect(0, 0, cw, ch);
            cx.drawImage(img, 0, 0, cw, ch);
            try { URLo.revokeObjectURL(url); } catch (e) {}
            resolve(cv.toDataURL('image/png'));
          } catch (e) { resolve(null); }
        };
        img.onerror = function () { try { URLo.revokeObjectURL(url); } catch (e) {} resolve(null); };
        img.src = url;
      } catch (e) { resolve(null); }
    });
  }

  function _pdfFmtLen(m) {
    if (STATE.isMetric) return Math.round(m * 1000).toLocaleString() + ' mm';
    var ftT = m * M_TO_FT, ft = Math.floor(ftT), inch = Math.round((ftT - ft) * 12);
    if (inch === 12) { ft++; inch = 0; }
    return ft + '\u2032' + (inch ? ' ' + inch + '\u2033' : '');
  }

  /* The documentation leader: the same anchor and the same dragged offset the
     screen uses, drawn in ink. What you arranged on the tablet IS the drawing
     that goes to site -- drag a label clear of a dimension chain on screen and
     the PDF honours it, because there is one offset and both read it. */
  function _pdfLeader(ctx, ax, ay, lx, ly, text) {
    ctx.save();
    ctx.strokeStyle = '#b91c1c';
    ctx.lineWidth = 1.1;
    ctx.beginPath();
    ctx.moveTo(ax, ay);
    ctx.lineTo(lx, ly);
    ctx.stroke();
    ctx.fillStyle = '#b91c1c';
    ctx.beginPath();
    ctx.arc(ax, ay, 2.2, 0, Math.PI * 2);
    ctx.fill();
    ctx.font = '700 11px Montserrat, Arial, sans-serif';
    var tw = ctx.measureText(text).width;
    ctx.fillStyle = 'rgba(255,255,255,0.92)';
    ctx.fillRect(lx - tw / 2 - 4, ly - 9, tw + 8, 18);
    ctx.fillStyle = '#111827';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, lx, ly);
    ctx.restore();
  }


  /* ---------- DIMENSIONS (koty) ----------------------------------------
     Style lifted from the BBQ configurator so every LEVA product dimensions
     the same way: a grey 1.3 px dimension line with solid arrow heads, pale
     leader lines back to what is being measured, and the value in a white
     chip with a hairline border and 12 px semibold text. Vertical runs get
     the chip and text rotated so neighbouring koty do not collide.

     Drawn in SCREEN pixels, so the same routine serves the on-canvas 2D plan
     and the PDF page -- one look, one source. */
  var DIM_COL = '#6b6b6b', DIM_LEAD = '#c9c4ba', DIM_TXT = '#1f1f1f', DIM_CHIP = '#e2ddd2';
  function _dimArrow(ctx, x, y, dx, dy) {
    var L = 8, W = 4, n = Math.sqrt(dx * dx + dy * dy) || 1;
    dx /= n; dy /= n;
    var px = -dy, py = dx;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + dx * L + px * W, y + dy * L + py * W);
    ctx.lineTo(x + dx * L - px * W, y + dy * L - py * W);
    ctx.closePath();
    ctx.fillStyle = DIM_COL;
    ctx.fill();
  }
  function _dimChip(ctx, cx, cy, text, vert) {
    ctx.save();
    ctx.translate(cx, cy);
    if (vert) ctx.rotate(-Math.PI / 2);
    var bw = text.length * 7.2 + 12;
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = DIM_CHIP;
    ctx.lineWidth = 1;
    var r = 4, x0 = -bw / 2, y0 = -9, w = bw, h = 18;
    ctx.beginPath();
    ctx.moveTo(x0 + r, y0);
    ctx.lineTo(x0 + w - r, y0); ctx.quadraticCurveTo(x0 + w, y0, x0 + w, y0 + r);
    ctx.lineTo(x0 + w, y0 + h - r); ctx.quadraticCurveTo(x0 + w, y0 + h, x0 + w - r, y0 + h);
    ctx.lineTo(x0 + r, y0 + h); ctx.quadraticCurveTo(x0, y0 + h, x0, y0 + h - r);
    ctx.lineTo(x0, y0 + r); ctx.quadraticCurveTo(x0, y0, x0 + r, y0);
    ctx.closePath();
    ctx.fill(); ctx.stroke();
    ctx.fillStyle = DIM_TXT;
    ctx.font = '600 12px system-ui,-apple-system,sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, 0, 0);
    ctx.restore();
  }
  /* p1/p2 are the points being measured; ox/oy pushes the dimension line off
     the object, and the leaders run back from the object to the line. */
  function _dimLine(ctx, p1, p2, ox, oy, text, vert) {
    var a = { x: p1.x + ox, y: p1.y + oy }, b = { x: p2.x + ox, y: p2.y + oy };
    ctx.save();
    ctx.strokeStyle = DIM_LEAD; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(p1.x, p1.y); ctx.lineTo(a.x, a.y);
    ctx.moveTo(p2.x, p2.y); ctx.lineTo(b.x, b.y); ctx.stroke();
    ctx.strokeStyle = DIM_COL; ctx.lineWidth = 1.3;
    ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
    _dimArrow(ctx, a.x, a.y, b.x - a.x, b.y - a.y);
    _dimArrow(ctx, b.x, b.y, a.x - b.x, a.y - b.y);
    ctx.restore();
    _dimChip(ctx, (a.x + b.x) / 2, (a.y + b.y) / 2, text, vert);
  }
  /* One source for the kota geometry: the rings are drawn in SCREEN pixels, so
     the PDF page has to reserve exactly as much room as the outermost ring
     needs. Deriving the page margin from these instead of a guessed number is
     what keeps the last dimension chain on the sheet. */
  var PLAN_RINGS = 5, PLAN_CHIP = 30;
  /* the ring spacing follows the SCALE of the drawing: fixed pixels read fine
     on screen but threw the koty far out on a PDF sheet drawn at a different
     ppm, and the outermost chain ran off the page (Marek). */
  function _planStep(ppm) { return Math.max(16, Math.min(30, (ppm || 60) * 0.28)); }
  function _planMargin(ppm) { var st = _planStep(ppm); return st * (PLAN_RINGS + 1) + PLAN_CHIP; }

  /* ---------- "+ PDF" SNAPSHOTS ----------------------------------------
     The exporter prints anything handed to it in view3d as a FULL-BLEED page
     right after the cover, so the lib's job is only to collect them. The
     control is an overlay chip inside the viewport: tap = capture the current
     viewport, long-press = clear the queue. Works in 2D and 3D alike.

     (Restored: a region replace while reworking the koty swallowed this whole
     block, which left the view3d payload referring to a name that no
     longer existed -- every PDF export died with "_pdfShots is not defined".) */
  var _pdfShots = [], _snapEl = null, _snapBadge = null;
  function _ensureSnapBtn() {
    if (_snapEl) return _snapEl;
    var eng = global.SaunaEngine;
    var cv = eng && eng.renderer && eng.renderer.domElement;
    var host = cv && cv.parentElement;
    if (!host) return null;
    try { if (getComputedStyle(host).position === 'static') host.style.position = 'relative'; } catch (e) {}
    var b = document.createElement('button');
    b.id = 'cf-snap-overlay';
    b.type = 'button';
    b.style.cssText = 'position:absolute;top:14px;right:86px;z-index:40;display:none;' +
      'padding:9px 15px;border:0;border-radius:999px;background:#D2A878;color:#fff;' +
      'font:800 13px/1 system-ui,-apple-system,sans-serif;letter-spacing:.4px;' +
      'box-shadow:0 2px 8px rgba(0,0,0,.25);cursor:pointer;-webkit-tap-highlight-color:transparent';
    b.appendChild(document.createTextNode(_t('snap_btn', '+ PDF')));
    var badge = document.createElement('span');
    badge.style.cssText = 'margin-left:8px;background:#fff;color:#8a5a2a;border-radius:999px;' +
      'padding:2px 7px;font-weight:800;display:none';
    b.appendChild(badge);
    var lt = null, fired = false, lastTouch = 0;
    function down() { fired = false; lt = setTimeout(function () { fired = true; clearPdfSnapshots(); }, 600); }
    function up(e, isTouch) {
      if (lt) { clearTimeout(lt); lt = null; }
      if (isTouch) lastTouch = Date.now();
      else if (Date.now() - lastTouch < 700) return;
      if (fired) { e.preventDefault(); return; }
      addPdfSnapshot();
    }
    b.addEventListener('touchstart', down, { passive: true });
    b.addEventListener('touchend', function (e) { up(e, true); });
    b.addEventListener('mousedown', function () { if (Date.now() - lastTouch > 700) down(); });
    b.addEventListener('mouseup', function (e) { up(e, false); });
    b.addEventListener('click', function (e) { e.preventDefault(); });
    host.appendChild(b);
    _snapEl = b; _snapBadge = badge;
    return b;
  }
  function _updateSnapBtn() {
    var on = (STATE.view === '2d' || STATE.view === '3d');
    var b = _ensureSnapBtn();
    if (!b) { if (on) setTimeout(_updateSnapBtn, 400); return; }
    b.style.display = on ? '' : 'none';
  }
  function _updateSnapBadge() {
    if (!_snapBadge) return;
    _snapBadge.textContent = String(_pdfShots.length);
    _snapBadge.style.display = _pdfShots.length ? '' : 'none';
  }
  function addPdfSnapshot() {
    var eng = global.SaunaEngine;
    if (!eng || !eng.renderer || !eng.renderer.domElement) { _log('snapshot: engine not ready'); return null; }
    if (_pdfShots.length >= 6) { _log('snapshot: queue full (6) -- long-press to clear'); return null; }
    try {
      var gl = eng.renderer.domElement;
      try { eng.renderer.render(eng.scene, eng.camera); } catch (eR) {}
      var k = Math.min(1, 1800 / Math.max(1, gl.width));
      var c = document.createElement('canvas');
      c.width = Math.max(1, Math.round(gl.width * k));
      c.height = Math.max(1, Math.round(gl.height * k));
      var x = c.getContext('2d');
      x.fillStyle = '#f5f5f5';
      x.fillRect(0, 0, c.width, c.height);
      x.drawImage(gl, 0, 0, c.width, c.height);
      try {
        var ov = $('cf-plan2d');
        if (ov && ov.style.display !== 'none' && ov.width) x.drawImage(ov, 0, 0, c.width, c.height);
      } catch (eO) {}
      var url = c.toDataURL('image/png');
      if (!url || url.length < 2000) { _log('snapshot: empty readback'); return null; }
      _pdfShots.push(url);
      _updateSnapBadge();
      _log('snapshot ' + _pdfShots.length + '/6 captured');
      return url;
    } catch (e) { _log('snapshot ERR: ' + (e && e.message ? e.message : e)); return null; }
  }
  function clearPdfSnapshots() { _pdfShots = []; _updateSnapBadge(); _log('snapshots cleared'); }

  /* Rooms wear the SAME colours as the floorplan step (Marek): the palette is
     the shared floorplan module's _VIEW_PALETTE, indexed by room order, so the
     plain 2D view, the wizard step and the PDF all read as one drawing. */
  var PLAN_ROOM_COLS = ['#EF9A9A', '#90CAF9', '#A5D6A7', '#FFCC80', '#CE93D8', '#80DEEA', '#BCAAA4',
                        '#F48FB1', '#C5E1A5', '#FFAB91', '#9FA8DA', '#FFF59D', '#B0BEC5', '#E6EE9C'];
  function _planRoomFill(i) { return PLAN_ROOM_COLS[i % PLAN_ROOM_COLS.length] + '66'; }
  /* Koty are laid out in RINGS, nearest to furthest: the opening chains sit on
     the wall, the roof overhang (the line the posts stand on) next, then the
     overall building, and the overall terrace and roof furthest out -- the
     main dimensions somebody reads first. Chain segments alternate between two
     offsets, because a run of short piers on ONE line turns into a row of
     overlapping chips. */
  function _planDims(ctx, mi, ppm, ox, oy) {
    try {
      var mod = STATE.modules[mi]; if (!mod) return;
      var hx = _fpW(mod) / 2, hz = _fpL(mod) / 2;
      var mx = (mod.x || 0), mz = (mod.z || 0);
      var X0 = ox + (mx - hx) * ppm, X1 = ox + (mx + hx) * ppm;
      var Z0 = oy + (mz - hz) * ppm, Z1 = oy + (mz + hz) * ppm;
      var GAP = _planStep(ppm), RING = GAP;
      var tv = (STATE.terraceOn && STATE.terraceOver) ? STATE.terraceOver : { x0: 0, x1: 0, z0: 0, z1: 0 };
      var rv = STATE.roofOver || { x0: 0, x1: 0, z0: 0, z1: 0 };
      /* EVERY ring starts OUTSIDE THE WHOLE PLAN (Marek): not just past the
         wall, but past whatever that side actually carries -- the deck, the
         roof overhang and the posts standing under it. A deck only exists
         where its reach makes TERR_MIN, so a shorter value must not push the
         koty out for a deck that was never built. */
      var TM = (typeof TERR_MIN === 'number') ? TERR_MIN : 1;
      function _terr(v) { return (STATE.terraceOn && (v || 0) >= TM) ? v : 0; }
      var reach = {
        rear: Math.max(_terr(tv.z0), (rv.z0 || 0)) * ppm,
        front: Math.max(_terr(tv.z1), (rv.z1 || 0)) * ppm,
        sideL: Math.max(_terr(tv.x0), (rv.x0 || 0)) * ppm,
        sideR: Math.max(_terr(tv.x1), (rv.x1 || 0)) * ppm
      };
      /* ring 1 -- the opening chain, on the wall it belongs to */
      var segs = _modWallSegs(mi), ops = (STATE.openings || []).filter(function (o) { return o.mi === mi; });
      for (var si = 0; si < segs.length; si++) {
        var seg = segs[si];
        var mine = ops.filter(function (o) { return o.wall === seg.wall; })
                      .sort(function (a, b) { return a.off - b.off; });
        if (!mine.length) continue;
        var horiz = Math.abs(seg.dir.x) > Math.abs(seg.dir.z);
        var out = (seg.wall === 'front' || seg.wall === 'sideR') ? 1 : -1;
        var base = (reach[seg.wall] || 0) + GAP;
        var marks = [0], k, q;
        for (k = 0; k < mine.length; k++) { marks.push(mine[k].off - mine[k].w / 2); marks.push(mine[k].off + mine[k].w / 2); }
        marks.push(seg.len);
        for (q = 0; q + 1 < marks.length; q++) {
          var d = marks[q + 1] - marks[q];
          if (d < 0.02) continue;
          var stag = base + ((q % 2) ? 22 : 0);   /* alternate so neighbouring chips clear each other */
          var offc = horiz ? { x: 0, y: out * stag } : { x: out * stag, y: 0 };
          var p1 = { x: ox + (seg.x0 + seg.dir.x * marks[q]) * ppm, y: oy + (seg.z0 + seg.dir.z * marks[q]) * ppm };
          var p2 = { x: ox + (seg.x0 + seg.dir.x * marks[q + 1]) * ppm, y: oy + (seg.z0 + seg.dir.z * marks[q + 1]) * ppm };
          _dimLine(ctx, p1, p2, offc.x, offc.y, fmtLen(d), !horiz);
        }
      }
      /* ring 2 -- roof overhang: the reach past each wall, which is where the
         support posts stand, so setting them out needs this number */
      var r2 = GAP + RING * 2;
      if ((rv.z1 || 0) > 0.05) _dimLine(ctx, { x: X1, y: Z1 }, { x: X1, y: Z1 + rv.z1 * ppm }, reach.sideR + r2, 0, fmtLen(rv.z1), true);
      if ((rv.z0 || 0) > 0.05) _dimLine(ctx, { x: X1, y: Z0 - rv.z0 * ppm }, { x: X1, y: Z0 }, reach.sideR + r2, 0, fmtLen(rv.z0), true);
      if ((rv.x1 || 0) > 0.05) _dimLine(ctx, { x: X1, y: Z0 }, { x: X1 + rv.x1 * ppm, y: Z0 }, 0, -(reach.rear + r2), fmtLen(rv.x1), false);
      if ((rv.x0 || 0) > 0.05) _dimLine(ctx, { x: X0 - rv.x0 * ppm, y: Z0 }, { x: X0, y: Z0 }, 0, -(reach.rear + r2), fmtLen(rv.x0), false);
      /* ring 3 -- the building itself */
      var r3 = GAP + RING * 3;
      _dimLine(ctx, { x: X0, y: Z1 }, { x: X1, y: Z1 }, 0, reach.front + r3, fmtLen(_fpW(mod)), false);
      _dimLine(ctx, { x: X0, y: Z0 }, { x: X0, y: Z1 }, -(reach.sideL + r3), 0, fmtLen(_fpL(mod)), true);
      /* ring 4 -- the OVERALL figures, furthest out: roof, and the terrace */
      var r4 = GAP + RING * 4, r5 = GAP + RING * 5;
      var roofW = _fpW(mod) + (rv.x0 || 0) + (rv.x1 || 0), roofL = _fpL(mod) + (rv.z0 || 0) + (rv.z1 || 0);
      if (roofW > _fpW(mod) + 0.05 || roofL > _fpL(mod) + 0.05) {
        _dimLine(ctx, { x: X0 - (rv.x0 || 0) * ppm, y: Z1 }, { x: X1 + (rv.x1 || 0) * ppm, y: Z1 }, 0, reach.front + r4, fmtLen(roofW), false);
        _dimLine(ctx, { x: X0, y: Z0 - (rv.z0 || 0) * ppm }, { x: X0, y: Z1 + (rv.z1 || 0) * ppm }, -(reach.sideL + r4), 0, fmtLen(roofL), true);
      }
      if (STATE.terraceOn) {
        var terW = _fpW(mod) + (tv.x0 || 0) + (tv.x1 || 0), terL = _fpL(mod) + (tv.z0 || 0) + (tv.z1 || 0);
        if (terW > _fpW(mod) + 0.05 || terL > _fpL(mod) + 0.05) {
          _dimLine(ctx, { x: X0 - (tv.x0 || 0) * ppm, y: Z1 }, { x: X1 + (tv.x1 || 0) * ppm, y: Z1 }, 0, reach.front + r5, fmtLen(terW), false);
          _dimLine(ctx, { x: X0, y: Z0 - (tv.z0 || 0) * ppm }, { x: X0, y: Z1 + (tv.z1 || 0) * ppm }, -(reach.sideL + r5), 0, fmtLen(terL), true);
        }
      }
    } catch (e) { _log('planDims: ' + (e && e.message ? e.message : e)); }
  }

  function _pdfDrawFloor(ctx, lvl, ppm, ox, oy) {
    var P = global.SaunaPartitions;
    var WALL = 0.09;
    for (var mi = 0; mi < STATE.modules.length; mi++) {
      var mod = STATE.modules[mi];
      if ((mod.lvl | 0) !== lvl) continue;
      var t = _typeOf(mod);
      var hx = _fpW(mod) / 2, hz = _fpL(mod) / 2;
      var x0 = ox + ((mod.x || 0) - hx) * ppm, x1 = ox + ((mod.x || 0) + hx) * ppm;
      var z0 = oy + ((mod.z || 0) - hz) * ppm, z1 = oy + ((mod.z || 0) + hz) * ppm;

      if (P) {
        var rooms = P._rooms(mi);
        for (var r = 0; r < rooms.length; r++) {
          /* one neutral fill: rooms are no longer typed */
          ctx.fillStyle = _planRoomFill(r);
          for (var ri = 0; ri < rooms[r].rects.length; ri++) {
            var R = rooms[r].rects[ri];
            var w2 = P._l2w(mi, (R[0] + R[2]) / 2, (R[1] + R[3]) / 2);
            var rw = (R[2] - R[0]) * ppm, rh = (R[3] - R[1]) * ppm;
            var q = _quarter(mod);
            if (q & 1) { var tt2 = rw; rw = rh; rh = tt2; }
            ctx.fillRect(ox + w2[0] * ppm - rw / 2, oy + w2[1] * ppm - rh / 2, rw, rh);
          }
        }
      }

      ctx.strokeStyle = '#1f2937';
      ctx.lineWidth = WALL * ppm;
      var ihx = _extW() / 2 - 0.02, ihz = t.L / 2 - 0.02;
      var corners = [[-ihx, -ihz], [ihx, -ihz], [ihx, ihz], [-ihx, ihz]];
      ctx.beginPath();
      for (var cc = 0; cc < 4; cc++) {
        var wcp = P ? P._l2w(mi, corners[cc][0], corners[cc][1]) : [corners[cc][0] + (mod.x || 0), corners[cc][1] + (mod.z || 0)];
        var pxc = ox + wcp[0] * ppm, pzc = oy + wcp[1] * ppm;
        if (cc === 0) ctx.moveTo(pxc, pzc); else ctx.lineTo(pxc, pzc);
      }
      ctx.closePath();
      ctx.stroke();

      var segs = _modWallSegs(mi);
      var ops = STATE.openings || [];
      for (var oi = 0; oi < ops.length; oi++) {
        var op = ops[oi];
        if (op.mi !== mi) continue;
        var seg = null;
        for (var si = 0; si < segs.length; si++) if (segs[si].wall === op.wall) { seg = segs[si]; break; }
        if (!seg) continue;
        var cxw = seg.x0 + seg.dir.x * op.off, czw = seg.z0 + seg.dir.z * op.off;
        var px = ox + cxw * ppm, pz = oy + czw * ppm;
        var along = op.w * ppm, gap = WALL * ppm + 2;
        ctx.save();
        ctx.translate(px, pz);
        if (seg.dir.z !== 0) ctx.rotate(Math.PI / 2);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(-along / 2, -gap / 2, along, gap);
        ctx.strokeStyle = '#1f2937';
        ctx.lineWidth = 1.4;
        /* WHAT IT IS, not how high it sits. A sill test calls every
           floor-level or full-height window a door -- so a full or half
           window came out with a leaf and a swing arc (Marek). The record
           carries its own kind; only the door family swings. */
        var _k = op.kind || '';
        /* 'glazed' is NOT a door: FULL0001 / HALF0001 are the full- and
           half-wall GLAZING panels, fixed lights that happen to start at the
           floor -- they were getting a leaf and a swing arc (Marek). Only a
           leaf that actually swings is drawn as a door. */
        var isDoor = (_k === 'door' || _k === 'doorhg' || _k === 'french') ||
                     (!_k && (op.sill || 0) < 0.03);
        /* the BREAK first: the thin symbol was drawn OVER the dark wall band
           of the top view and drowned in it. An opening in plan is drawn the
           way every architect draws it: the poche is INTERRUPTED -- a clear
           gap the width of the opening -- and only then the thin lines: a
           double hairline for glazing, the leaf and its arc for a door. */
        ctx.fillStyle = '#f5f5f5';
        ctx.fillRect(-along / 2, -gap / 2 - 1, along, gap + 2);
        if (isDoor) {
          ctx.beginPath();
          ctx.moveTo(-along / 2, gap / 2 - 1);
          ctx.lineTo(-along / 2 + along, gap / 2 - 1 - along);
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(-along / 2, gap / 2 - 1, along, -Math.PI / 2, 0);
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(-along / 2, -gap / 2); ctx.lineTo(-along / 2, gap / 2);
          ctx.moveTo(along / 2, -gap / 2); ctx.lineTo(along / 2, gap / 2);
          ctx.stroke();
        } else {
          ctx.beginPath();
          ctx.moveTo(-along / 2, -2); ctx.lineTo(along / 2, -2);
          ctx.moveTo(-along / 2, 2); ctx.lineTo(along / 2, 2);
          ctx.moveTo(-along / 2, -gap / 2); ctx.lineTo(-along / 2, gap / 2);
          ctx.moveTo(along / 2, -gap / 2); ctx.lineTo(along / 2, gap / 2);
          ctx.stroke();
        }
        ctx.restore();
        /* the caption, at its dragged offset -- op.lbl is the SAME field the
           plan drag writes, so screen and paper agree by construction */
        if (P) {
          var oa = P._openingAnchor(op);
          if (oa) {
            var oL = op.lbl || ((seg.dir.z !== 0) ? { x: (op.wall === 'sideL' ? -0.75 : 0.75), z: 0 } : { x: 0, z: (op.wall === 'rear' ? -0.75 : 0.75) });
            var aW = P._l2w(mi, oa[0], oa[1]);
            var lW = P._l2w(mi, oa[0] + oL.x, oa[1] + oL.z);
            var kTxt = ((op.sill || 0) < 0.03) ? _t('doors', 'DOOR') : _t('windows', 'WINDOW');
            _pdfLeader(ctx, ox + aW[0] * ppm, oy + aW[1] * ppm, ox + lW[0] * ppm, oy + lW[1] * ppm,
              kTxt + ' ' + _pdfFmtLen(op.w));
          }
        }
      }

      if (P) {
        var parts = STATE.partitions || [];
        for (var pi = 0; pi < parts.length; pi++) {
          var p = parts[pi];
          if (p.mi !== mi) continue;
          var se = P._resolve(p, t);
          var dops = [];
          var dls = STATE.doors || [];
          for (var di = 0; di < dls.length; di++) {
            var dd = dls[di];
            if (dd.mi !== mi || dd.o !== p.o || Math.abs(dd.c - p.c) > 1e-3) continue;
            dops.push([dd.u - (dd.w || 0.8) / 2, dd.u + (dd.w || 0.8) / 2]);
          }
          dops.sort(function (a, b) { return a[0] - b[0]; });
          var cur = se[0];
          ctx.strokeStyle = '#374151';
          ctx.lineWidth = Math.max(2, 0.10 * ppm);
          function seg2(a, b) {
            if (b - a < 0.02) return;
            var pa = P._l2w(mi, p.o === 'h' ? a : p.c, p.o === 'h' ? p.c : a);
            var pb = P._l2w(mi, p.o === 'h' ? b : p.c, p.o === 'h' ? p.c : b);
            ctx.beginPath();
            ctx.moveTo(ox + pa[0] * ppm, oy + pa[1] * ppm);
            ctx.lineTo(ox + pb[0] * ppm, oy + pb[1] * ppm);
            ctx.stroke();
          }
          for (var doi = 0; doi < dops.length; doi++) { seg2(cur, dops[doi][0]); cur = Math.max(cur, dops[doi][1]); }
          seg2(cur, se[1]);
        }
        /* interior door captions, same grammar as the openings: anchor at the
           gap centre on the wall, label at the offset the finger left */
        var dls2 = STATE.doors || [];
        for (var dj2 = 0; dj2 < dls2.length; dj2++) {
          var dd2 = dls2[dj2];
          if (dd2.mi !== mi) continue;
          var dcc = (dd2.o === 'v') ? [dd2.c, dd2.u] : [dd2.u, dd2.c];
          var dof = dd2.lbl || ((dd2.o === 'v') ? { x: -0.55, z: 0 } : { x: 0, z: -0.55 });
          var aW2 = P._l2w(mi, dcc[0], dcc[1]);
          var lW2 = P._l2w(mi, dcc[0] + dof.x, dcc[1] + dof.z);
          _pdfLeader(ctx, ox + aW2[0] * ppm, oy + aW2[1] * ppm, ox + lW2[0] * ppm, oy + lW2[1] * ppm,
            _pdfFmtLen(dd2.w || 0.7));
        }

        var rooms2 = P._roomsNamed(mi);
        var qM = _quarter(mod);
        function toPx(lx2, lz2) {
          var w3 = P._l2w(mi, lx2, lz2);
          return [ox + w3[0] * ppm, oy + w3[1] * ppm];
        }
        for (var r2 = 0; r2 < rooms2.length; r2++) {
          var lp2 = rooms2[r2].lp || [rooms2[r2].cx, rooms2[r2].cz];
          var wc = P._l2w(mi, lp2[0], lp2[1]);
          var lx = ox + wc[0] * ppm, ly = oy + wc[1] * ppm;

          var bx0 = Infinity, bx1 = -Infinity, bz0 = Infinity, bz1 = -Infinity;
          for (var rb = 0; rb < rooms2[r2].rects.length; rb++) {
            var RB = rooms2[r2].rects[rb];
            bx0 = Math.min(bx0, RB[0]); bx1 = Math.max(bx1, RB[2]);
            bz0 = Math.min(bz0, RB[1]); bz1 = Math.max(bz1, RB[3]);
          }
          ctx.fillStyle = '#111827';
          ctx.textAlign = 'center';
          /* ti2 was the other half of the same fault as the 3D room label: a
             bare name left behind when room types went, unguarded, so the PDF
             threw here rather than printing a plan. The name comes off the
             room now, exactly as it does on screen -- one source, so the
             drawing the customer downloads says what the drawing they made
             said. */
          if (rooms2[r2].name) {
            ctx.font = '700 ' + Math.max(11, ppm * 0.16) + 'px Montserrat, Arial';
            ctx.fillText(rooms2[r2].name, lx, ly - ppm * 0.10);
          }
          ctx.font = '600 ' + Math.max(10, ppm * 0.14) + 'px Montserrat, Arial';
          ctx.fillText(fmtArea(rooms2[r2].area), lx, ly + ppm * 0.14);
        }

      }

      /* GHOST OUTLINES so the koty have something to point at: the roof edge
         (footprint + overhang) and, where there is one, the terrace edge --
         drawn as a light dashed rectangle exactly like a site drawing shows
         what is above and around the plan. Without them the outer dimension
         chains measured thin air (Marek). */
      try {
        var _rv = STATE.roofOver || { x0: 0, x1: 0, z0: 0, z1: 0 };
        var _tv = (STATE.terraceOn && STATE.terraceOver) ? STATE.terraceOver : null;
        var _hx = _fpW(mod) / 2, _hz = _fpL(mod) / 2, _mx = (mod.x || 0), _mz = (mod.z || 0);
        function ghost(o0, o1, o2, o3, col) {
          ctx.save();
          ctx.strokeStyle = col;
          ctx.lineWidth = 1;
          ctx.setLineDash([7, 5]);
          ctx.strokeRect(ox + (_mx - _hx - o0) * ppm, oy + (_mz - _hz - o2) * ppm,
                         (2 * _hx + o0 + o1) * ppm, (2 * _hz + o2 + o3) * ppm);
          ctx.restore();
        }
        if ((_rv.x0 || _rv.x1 || _rv.z0 || _rv.z1)) ghost(_rv.x0 || 0, _rv.x1 || 0, _rv.z0 || 0, _rv.z1 || 0, '#9aa0a6');
        if (_tv) ghost(_tv.x0 || 0, _tv.x1 || 0, _tv.z0 || 0, _tv.z1 || 0, '#c2b39a');
      } catch (eGh) {}
      _planDims(ctx, mi, ppm, ox, oy);
    }
  }





  function _pdfDrawPlan() {
    var doc = global.document;
    var Wp = 1400, Hp = 990, SC = 2;
    var lvls = [0];
    for (var i = 0; i < STATE.modules.length; i++) if ((STATE.modules[i].lvl | 0) >= 1) { lvls = [0, 1]; break; }
    var minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
    for (var m = 0; m < STATE.modules.length; m++) {
      var md = STATE.modules[m];
      minX = Math.min(minX, (md.x || 0) - _fpW(md) / 2); maxX = Math.max(maxX, (md.x || 0) + _fpW(md) / 2);
      minZ = Math.min(minZ, (md.z || 0) - _fpL(md) / 2); maxZ = Math.max(maxZ, (md.z || 0) + _fpL(md) / 2);
    }
    if (!isFinite(minX)) return Promise.resolve(null);
    /* the drawing is wider than the building: the deck reaches past the walls
       and the koty are set out past the deck, so the page has to frame all of
       it or the outermost dimension chain falls off the sheet */
    if (STATE.terraceOn && STATE.terraceOver) {
      var _tv = STATE.terraceOver;
      minX -= (_tv.x0 || 0); maxX += (_tv.x1 || 0);
      minZ -= (_tv.z0 || 0); maxZ += (_tv.z1 || 0);
    }
    var cv = doc.createElement('canvas');
    cv.width = Wp * SC; cv.height = Hp * SC;
    var ctx = cv.getContext('2d');
    if (!ctx) return Promise.resolve(null);
    ctx.scale(SC, SC);
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, Wp, Hp);
    var n = lvls.length;
    var cellW = Wp / n;
    var spanX = maxX - minX, spanZ = maxZ - minZ;
    /* the koty live OUTSIDE the outline (overall + the opening chains a
       step further out), so the plan needs a wider margin than the raw
       footprint or the dimension chips get cropped at the sheet edge */
    /* the drawing is the building PLUS every ring of koty around it, so the
       usable area is the sheet less that margin on each side (plus the strip
       the floor label sits in at the bottom) */
    var _p0 = Math.max(6, Math.min((cellW - 300) / spanX, (Hp - 320) / spanZ));
    var _M = _planMargin(_p0);
    var ppm = Math.max(6, Math.min((cellW - 2 * _M) / spanX, (Hp - 2 * _M - 60) / spanZ));   /* two floors share the width, so keep a floor under the scale */
    for (var li = 0; li < n; li++) {
      var cx0 = cellW * li + cellW / 2;
      var ox = cx0 - (minX + spanX / 2) * ppm;
      var oy = Hp / 2 + 20 - (minZ + spanZ / 2) * ppm;
      _pdfDrawFloor(ctx, lvls[li], ppm, ox, oy);
      ctx.fillStyle = '#111827';
      ctx.font = '800 20px Montserrat, Arial';
      ctx.textAlign = 'center';
      ctx.fillText(lvls[li] === 0 ? 'GROUND FLOOR' : 'UPPER FLOOR', cx0, Hp - 34);
    }
    if (STATE.climateZone > 0) {
      ctx.font = '600 14px Montserrat, Arial';
      ctx.textAlign = 'left';
      ctx.fillStyle = '#4b5563';
      ctx.fillText('Climate zone (frost depth): ' + STATE.climateZone.toFixed(2) + ' m', 24, Hp - 14);
    }
    var out = null;
    try { out = cv.toDataURL('image/png'); } catch (e) { out = null; }
    return Promise.resolve(out);
  }

  function _pdfElevation() {
    var T = global.THREE, eng = global.SaunaEngine;
    if (!T || !global.SVGRenderer || !eng || !eng.group) return Promise.resolve(null);
    var P = global.SaunaPartitions;
    var skipRoots = [];
    if (P && P._grp) skipRoots.push(P._grp);
    function skipped(o) {
      for (var n2 = o; n2; n2 = n2.parent) {
        for (var k = 0; k < skipRoots.length; k++) if (n2 === skipRoots[k]) return true;
        if (n2.userData && (n2.userData.arSkip || n2.userData.found)) return true;
      }
      return false;
    }
    var bbMin = null, bbMax = null;
    var edgeMat = new T.LineBasicMaterial({ color: 0x1a1a1a });
    var fillMat = new T.MeshBasicMaterial({ color: 0xffffff, polygonOffset: true, polygonOffsetFactor: 1, polygonOffsetUnits: 1 });
    var flat = new T.Scene();
    flat.background = new T.Color(0xffffff);
    eng.group.updateMatrixWorld(true);
    eng.group.traverse(function (o) {
      if (!o.isMesh || !o.geometry || !o.visible || o.isSprite) return;
      if (o.material && o.material.depthTest === false) return;
      if (skipped(o)) return;
      if (!o.geometry.boundingBox) o.geometry.computeBoundingBox();
      var bb = o.geometry.boundingBox.clone().applyMatrix4(o.matrixWorld);
      if (!isFinite(bb.min.x) || !isFinite(bb.max.x)) return;
      if (!bbMin) { bbMin = bb.min.clone(); bbMax = bb.max.clone(); }
      else { bbMin.min(bb.min); bbMax.max(bb.max); }
      var fill = new T.Mesh(o.geometry, fillMat);
      fill.matrixAutoUpdate = false; fill.matrix.copy(o.matrixWorld);
      flat.add(fill);
      try {
        var edges = new T.EdgesGeometry(o.geometry, 20);
        var lines = new T.LineSegments(edges, edgeMat);
        lines.matrixAutoUpdate = false; lines.matrix.copy(o.matrixWorld);
        flat.add(lines);
      } catch (e) {}
    });
    if (!bbMin) return Promise.resolve(null);
    var cX = (bbMin.x + bbMax.x) / 2, cY = (bbMin.y + bbMax.y) / 2, cZ = (bbMin.z + bbMax.z) / 2;
    var vL = Math.max(0.5, bbMax.z - bbMin.z), vH = Math.max(0.5, bbMax.y - bbMin.y);
    var Wp = 1400, Hp = 990, aspect = Wp / Hp, MM = 1.28;
    var halfH = (vH / 2) * MM, halfW = (vL / 2) * MM;
    if (halfW / halfH > aspect) halfH = halfW / aspect; else halfW = halfH * aspect;
    var cam = new T.OrthographicCamera(-halfW, halfW, halfH, -halfH, 0.01, 400);
    cam.up.set(0, 1, 0);
    cam.position.set(cX + 80, cY, cZ);
    cam.lookAt(cX, cY, cZ);
    cam.updateProjectionMatrix();
    cam.updateMatrixWorld(true);
    var svg = null;
    try {
      var svgR = new global.SVGRenderer();
      if (svgR.setQuality) svgR.setQuality('high');
      svgR.setSize(Wp, Hp, false);
      svgR.render(flat, cam);
      svg = new global.XMLSerializer().serializeToString(svgR.domElement);
    } catch (e) { svg = null; }
    if (!svg) return Promise.resolve(null);
    return _pdfSvgToPng(svg, Wp, Hp, 2);
  }

  function _containerBuildBoq(bridge) {
    var out = [], b;
    try { b = global.SaunaBoQ.compute(); } catch (e) { return out; }
    /* CONSTRUCTION ORDER for the document (Marek: roughly, not to the letter).
       compute() hands back the detailed rows WITH their category, so the PDF
       can read top to bottom the way the thing gets built -- ground first, then
       the frame, the envelope over it, the skin, the openings cut into it, the
       deck and railings last -- instead of the alphabet by code, which put the
       roof covering next to the anchors. Fasteners and consumables run through
       every trade, so they close the list rather than interrupting it. */
    var _catOf = {};
    try {
      var _rws = b.rows || [];
      for (var _ri = 0; _ri < _rws.length; _ri++) {
        if (_rws[_ri] && _rws[_ri].code && !_catOf[_rws[_ri].code]) _catOf[_rws[_ri].code] = _rws[_ri].category || '';
      }
    } catch (eCat) {}
    var _BUILD_ORDER = { foundation: 1, structure: 2, envelope: 3, facade: 4, openings: 5,
                         terrace: 6, railing: 7, equipment: 8, fitout: 8, pergola: 9,
                         fasteners: 10, consumables: 11 };
    function _buildRank(code) {
      var r = _BUILD_ORDER[_catOf[code] || ''];
      return (r == null) ? 9.5 : r;   /* anything uncategorised sits just before the fixings */
    }
    if (!b || !b.byMaterial) return out;
    Object.keys(b.byMaterial).forEach(function (code) {
      var qty = b.byMaterial[code];
      if (qty == null) return;
      var item = bridge && bridge.getBoqItem ? bridge.getBoqItem(code) : null;
      var localUnit = item ? (STATE.isMetric ? item.unitMetric : item.unitImperial) : null;
      var displayQty = qty;
      if (item && !STATE.isMetric) {
        var lbl = String(item.unitMetric || '').trim().toUpperCase();
        if (lbl === 'M') displayQty = qty * 3.28084;
        else if (lbl === 'M2') displayQty = qty * 10.7639;
        else if (lbl === 'M3') displayQty = qty * (String(item.unitImperial || '').trim().toUpperCase() === 'YD3' ? 1.30795 : 35.3147);
      }
      var up = bridge && bridge.getUserPrice ? bridge.getUserPrice(code) : null;
      var unitPrice = up && typeof up.price === 'number' ? up.price : null;
      out.push({
        code: code,
        name: (item && item.name) || code,
        qty: displayQty,
        unit: localUnit || (STATE.isMetric ? 'm' : 'ft'),
        unitPrice: unitPrice,
        total: (unitPrice != null) ? unitPrice * displayQty : null
      });
    });
    out.sort(function (a, b2) {
      var ra = _buildRank(a.code), rb = _buildRank(b2.code);
      if (ra !== rb) return ra - rb;
      return a.code < b2.code ? -1 : 1;   /* inside a trade, keep it predictable */
    });
    return out;
  }

  function _containerBuildTotals(boqRows) {
    function idText(id) {
      var el = global.document && document.getElementById(id);
      if (!el || !el.textContent) return '';
      var t2 = el.textContent.trim();
      return (!t2 || t2 === '\u2014' || t2 === '-') ? '' : t2;
    }
    var cur = (global.SAUNA_BOOT && global.SAUNA_BOOT.currency) ||
      (global.Shopify && global.Shopify.currency && global.Shopify.currency.active) || 'USD';
    var fmt = (typeof global.formatPrice === 'function')
      ? global.formatPrice
      : function (n2) { return Math.round(+n2 || 0).toLocaleString() + ' ' + cur; };
    var totalText = idText('cf-bw-total');
    var materialText = idText('cf-bw-material');
    var execText = idText('cf-bw-execution');
    var timeText = idText('cf-bw-time');
    if (!materialText) {
      var s2 = 0;
      boqRows.forEach(function (r2) { if (r2.total != null) s2 += r2.total; });
      if (s2) materialText = fmt(s2);
    }
    if (!totalText && materialText) totalText = materialText;
    return {
      texts: { total: totalText || '', material: materialText || '', execution: execText || '', time: timeText || '', capacity: '' },
      materials: { count: 0, value: 0 },
      execution: { count: 0, value: 0, hours: 0 },
      total: 0,
      currency: cur
    };
  }

  function _containerExportPdf() {
    var BOOT = global.SAUNA_BOOT || {};
    var bridge = global.ConfiguratorBudgetBridge || null;
    if (!global.ConfiguratorPdfExporter || !global.ConfiguratorPdfExporter.export) {
      _log('PDF exporter not loaded yet');
      try { alert('PDF library not yet loaded \u2014 try again in a moment.'); } catch (e) {}
      return Promise.resolve();
    }
    var btn = global.document && document.getElementById('cf-btn-pdf');
    if (btn) btn.classList.add('active');
    var boq = _containerBuildBoq(bridge);
    var totals = _containerBuildTotals(boq);
    var mi2 = Infinity, ma2 = -Infinity, mz0 = Infinity, mz1 = -Infinity, maxLvl = 0, hMax = 0;
    for (var i2 = 0; i2 < STATE.modules.length; i2++) {
      var md2 = STATE.modules[i2];
      mi2 = Math.min(mi2, (md2.x || 0) - _fpW(md2) / 2); ma2 = Math.max(ma2, (md2.x || 0) + _fpW(md2) / 2);
      mz0 = Math.min(mz0, (md2.z || 0) - _fpL(md2) / 2); mz1 = Math.max(mz1, (md2.z || 0) + _fpL(md2) / 2);
      maxLvl = Math.max(maxLvl, md2.lvl | 0);
      hMax = Math.max(hMax, _typeOf(md2).H);
    }
    var dims = { L: Math.max(0.01, mz1 - mz0), W: Math.max(0.01, ma2 - mi2), H: _foundLift() + (maxLvl + 1) * hMax };
    return Promise.all([_pdfDrawPlan()]).then(function (caps) {
      var planPng = caps[0], elevPng = null;   /* the PDF is the PLAN, nothing else (Marek) */
      _log('pdf export: plan=' + !!planPng + ' elevation=' + !!elevPng + ' boq=' + boq.length);
      try {
        global.ConfiguratorPdfExporter.export({
          productTitle: BOOT.productTitle || 'Sauna',
          customerName: BOOT.customerName || null,
          coverImage: BOOT.coverImage || BOOT.productImage || null,
          shareUrl: (global.location && global.location.href ? global.location.href.split('#')[0] : ''),
          totals: totals,
          basePlan: planPng ? { imageDataUrl: planPng } : null,
          baseCrossSection: elevPng ? { imageDataUrl: elevPng } : null,
          baseAssembly: null,
          view3d: _pdfShots.map(function (u, i) { return { label: 'View ' + (i + 1), imageDataUrl: u }; }),
          view2d: {},
          rackDims: dims,
          instances: [],
          boqNames: {},
          cutList: [],
          boq: boq,
          meta: {
            locale: BOOT.locale || 'en',
            productHandle: BOOT.productHandle || 'sauna',
            date: new Date(),
            i18n: {
              pageOrthosTitle: 'Floor plans',
              pageIsoTitle: 'Elevation',
              pageBoqTitle: 'Bill of quantities',
              continued: 'continued',
              totalsTitle: 'Project totals',
              materialsLabel: 'Materials',
              executionLabel: 'Execution',
              grandTotalLabel: 'Grand total',
              orthoFront: 'Floor plan',
              orthoSide: 'Side elevation',
              boq: {
                headerCode: 'Code',
                headerName: 'Name',
                headerQty: 'Quantity',
                headerUnitPrice: 'Unit price',
                headerTotal: 'Total',
                grandTotal: 'Grand total'
              }
            }
          }
        });
      } catch (e) {
        _log('PDF export failed: ' + (e && e.message ? e.message : e));
        try { alert('PDF export failed: ' + (e && e.message ? e.message : e)); } catch (e2) {}
      } finally {
        if (btn) btn.classList.remove('active');
      }
    });
  }

  var PDF_BTN_ACTIVE = '#111111';
  function _initPdfButtonState() {
    var doc = global.document;
    if (!doc) return;
    var btn = doc.getElementById('cf-btn-pdf');
    if (!btn) { setTimeout(_initPdfButtonState, 600); return; }
    if (btn.getAttribute('data-cf-container-pdf')) return;
    btn.setAttribute('data-cf-container-pdf', '1');

    if (!doc.getElementById('cf-pdf-btn-style')) {
      var st = doc.createElement('style');
      st.id = 'cf-pdf-btn-style';
      st.textContent =
        '#cf-btn-pdf.cf-pdf-loading{opacity:.4;pointer-events:none;cursor:default;filter:grayscale(1);}' +
        '#cf-btn-pdf.cf-pdf-ready{background:' + PDF_BTN_ACTIVE + ' !important;border-color:' + PDF_BTN_ACTIVE + ' !important;color:#fff !important;}' +
        '#cf-btn-pdf.cf-pdf-ready > *{filter:brightness(0) invert(1) !important;}';
      doc.head.appendChild(st);
    }

    var bridge = global.ConfiguratorBudgetBridge || null;
    function isReady() { return !(bridge && typeof bridge.isReady === 'function') || bridge.isReady(); }
    function promote() { btn.classList.remove('cf-pdf-loading'); btn.classList.add('cf-pdf-ready'); }

    btn.addEventListener('click', function (ev) {
      ev.preventDefault();
      var X = global.ConfiguratorPdfExporter;
      if (X && X.socialGate) {
        X.socialGate({ key: _pdfKey() }).then(function () {
          _containerExportPdf().catch(function (e) { _log('[exportPdf] ' + (e && e.message ? e.message : e)); });
        });
      } else {
        _containerExportPdf().catch(function (e) { _log('[exportPdf] ' + (e && e.message ? e.message : e)); });
      }
    });

    if (isReady()) { promote(); return; }
    btn.classList.add('cf-pdf-loading');
    if (bridge && typeof bridge.onReady === 'function') { try { bridge.onReady(promote); } catch (e) {} }
    var waited = 0;
    var iv = global.setInterval(function () {
      if (isReady()) { global.clearInterval(iv); promote(); }
      else if ((waited += 300) >= 30000) { global.clearInterval(iv); promote(); }
    }, 300);
  }

  var _Banner = {
    _els: null, _bound: false,
    init: function () {
      var self = this, tries = 0;
      (function attempt() {
        tries++;
        self._bind();
        if (!self._bound && tries < 12) { setTimeout(attempt, 500); return; }
        self.sync();
      })();
    },
    _bind: function () {
      if (this._bound) return;
      var vn = $('cf-stat-variant-name');
      if (!vn) return;
      var vItem = $('cf-info-variant');
      if (vItem) vItem.title = 'Mode \u2014 tap to switch';
      var pItem = $('cf-info-preset');
      if (pItem) { pItem.title = 'Usable floor area'; pItem.classList.remove('cf-info-item--cycle'); pItem.style.cursor = 'default'; }
      if (vItem && !$('cf-info-count') && vItem.parentNode) {
        var osep = document.createElement('div');
        osep.className = 'cf-info-sep';
        osep.id = 'cf-openings-sep';
        var oitem = document.createElement('div');
        oitem.className = 'cf-info-item cf-info-item--cycle';
        oitem.id = 'cf-info-openings';
        oitem.title = 'Windows & doors';
        oitem.innerHTML = '<img src="https://cdn.shopify.com/s/files/1/0803/9815/6119/files/window.png?v=1774790669" alt="Windows &amp; doors" style="width:22px;height:22px;display:block;object-fit:contain;pointer-events:none;">';
        vItem.parentNode.insertBefore(osep, vItem.nextSibling);
        vItem.parentNode.insertBefore(oitem, osep.nextSibling);

        var sep = document.createElement('div');
        sep.className = 'cf-info-sep';
        sep.id = 'cf-count-sep';
        var item = document.createElement('div');
        item.className = 'cf-info-item cf-info-item--cycle';
        item.id = 'cf-info-count';
        item.title = 'Containers \u2014 tap to switch 1-4';
        var val = document.createElement('span');
        val.className = 'cf-info-value';
        val.id = 'cf-stat-count-name';
        item.appendChild(val);
        oitem.parentNode.insertBefore(sep, oitem.nextSibling);
        oitem.parentNode.insertBefore(item, sep.nextSibling);

        var esep = document.createElement('div');
        esep.className = 'cf-info-sep';
        esep.id = 'cf-env-sep';
        var eitem = document.createElement('div');
        eitem.className = 'cf-info-item cf-info-item--cycle';
        eitem.id = 'cf-info-env';
        eitem.title = 'Insulation type \u2014 tap to switch';
        var eval2 = document.createElement('span');
        eval2.className = 'cf-info-value';
        eval2.id = 'cf-stat-env-name';
        eitem.appendChild(eval2);
        item.parentNode.insertBefore(esep, item.nextSibling);
        item.parentNode.insertBefore(eitem, esep.nextSibling);

        var tssep = document.createElement('div');
        tssep.className = 'cf-info-sep';
        tssep.id = 'cf-terr-sep';
        var tsitem = document.createElement('div');
        tsitem.className = 'cf-info-item cf-info-item--cycle';
        tsitem.id = 'cf-info-terr';
        tsitem.title = 'Terrace surface \u2014 tap to switch';
        var tsval = document.createElement('span');
        tsval.className = 'cf-info-value';
        tsval.id = 'cf-stat-terr-name';
        tsitem.appendChild(tsval);
        item.parentNode.insertBefore(tssep, item.nextSibling);
        item.parentNode.insertBefore(tsitem, tssep.nextSibling);

        var pgsep = document.createElement('div');
        pgsep.className = 'cf-info-sep';
        pgsep.id = 'cf-perg-sep';
        var pgitem = document.createElement('div');
        pgitem.className = 'cf-info-item cf-info-item--cycle';
        pgitem.id = 'cf-info-perg';
        pgitem.title = 'Pergola type \u2014 tap to switch';
        var pgval = document.createElement('span');
        pgval.className = 'cf-info-value';
        pgval.id = 'cf-stat-perg-name';
        pgitem.appendChild(pgval);
        item.parentNode.insertBefore(pgsep, item.nextSibling);
        item.parentNode.insertBefore(pgitem, pgsep.nextSibling);

        var fasep = document.createElement('div');
        fasep.className = 'cf-info-sep';
        fasep.id = 'cf-fac-sep';
        var faitem = document.createElement('div');
        faitem.className = 'cf-info-item cf-info-item--cycle';
        faitem.id = 'cf-info-fac';
        faitem.title = 'Facade finish \u2014 tap to switch';
        var faval = document.createElement('span');
        faval.className = 'cf-info-value';
        faval.id = 'cf-stat-fac-name';
        faitem.appendChild(faval);
        item.parentNode.insertBefore(fasep, item.nextSibling);
        item.parentNode.insertBefore(faitem, fasep.nextSibling);

        var dsep = document.createElement('div');
        dsep.className = 'cf-info-sep';
        dsep.id = 'cf-detail-sep';
        var ditem = document.createElement('div');
        ditem.className = 'cf-info-item cf-info-item--cycle';
        ditem.id = 'cf-info-detail';
        ditem.title = 'Assembly section detail \u2014 tap to toggle';
        var dval = document.createElement('span');
        dval.className = 'cf-info-value';
        dval.id = 'cf-stat-detail-name';
        ditem.appendChild(dval);
        item.parentNode.insertBefore(dsep, item.nextSibling);
        item.parentNode.insertBefore(ditem, dsep.nextSibling);

        var fsep = document.createElement('div');
        fsep.className = 'cf-info-sep';
        fsep.id = 'cf-found-sep';
        var fitem = document.createElement('div');
        fitem.className = 'cf-info-item cf-info-item--cycle';
        fitem.id = 'cf-info-found';
        fitem.title = 'Foundation type \u2014 tap to switch';
        var fval = document.createElement('span');
        fval.className = 'cf-info-value';
        fval.id = 'cf-stat-found-name';
        fitem.appendChild(fval);
        item.parentNode.insertBefore(fsep, item.nextSibling);
        item.parentNode.insertBefore(fitem, fsep.nextSibling);

        var rsep = document.createElement('div');
        rsep.className = 'cf-info-sep';
        rsep.id = 'cf-roof-sep';
        var ritem = document.createElement('div');
        ritem.className = 'cf-info-item cf-info-item--cycle';
        ritem.id = 'cf-info-roof';
        ritem.title = 'Roof type \u2014 tap to switch';
        var rlbl = document.createElement('span');
        rlbl.className = 'cf-info-label';
        rlbl.id = 'cf-stat-roof-name';
        var rval = document.createElement('span');
        rval.className = 'cf-info-value';
        rval.innerHTML = '<span id="cf-stat-pitch-val">18</span>' +
                         '<span class="cf-info-unit-inline" id="cf-stat-pitch-unit">\u00B0</span>';
        ritem.appendChild(rlbl);
        ritem.appendChild(rval);
        item.parentNode.insertBefore(rsep, item.nextSibling);
        item.parentNode.insertBefore(ritem, rsep.nextSibling);
      }
      this._els = { mode: vn, count: $('cf-stat-count-name'), found: $('cf-stat-found-name'), roof: $('cf-stat-roof-name'), env: $('cf-stat-env-name'), fac: $('cf-stat-fac-name'), perg: $('cf-stat-perg-name'), terr: $('cf-stat-terr-name'), detail: $('cf-stat-detail-name'), area: $('cf-stat-preset-name') };
      function hook(id, fn) {
        var el = $(id);
        if (!el || el.getAttribute('data-cf-container-bound')) return;
        el.setAttribute('data-cf-container-bound', '1');
        el.style.cursor = 'pointer';
        el.addEventListener('click', function (ev) { ev.preventDefault(); ev.stopPropagation(); fn(); });
      }
      hook('cf-info-variant', nextMode);
      hook('cf-info-count', nextCount);
      hook('cf-info-found', nextFoundation);
      hook('cf-info-roof', nextRoofType);
      hook('cf-info-env', nextEnvelope);
      hook('cf-info-fac', nextFacade);
      hook('cf-info-terr', toggleTerraceMode);
      this._bound = true;
    },
    sync: function () {
      if (!this._bound || !this._els) return;
      var T = (global.SAUNA_BOOT && global.SAUNA_BOOT.i18n) || {};
      function tt(k, f) {
        var v = T[k];
        if (typeof v !== 'string' || !v || /translation missing/i.test(v)) return f;
        return _deent(v);
      }
      var md = MODES[clamp((STATE.mode | 0) - 1, 0, MODES.length - 1)];
      if (this._els.mode) {
        this._els.mode.textContent = tt('container_step', 'STEP') + ' ' + (STATE.mode | 0);
        var sub = $('cf-mode-sub');
        if (!sub && this._els.mode.parentNode) {
          sub = document.createElement('div');
          sub.id = 'cf-mode-sub';
          sub.style.cssText = 'font:600 10px Montserrat,sans-serif;letter-spacing:0.06em;text-transform:uppercase;opacity:0.72;margin-top:1px;white-space:nowrap;';
          this._els.mode.parentNode.appendChild(sub);
        }
        if (sub) sub.textContent = _deent(tt(md.i18n, md.def));
      }
      if (this._els.count) this._els.count.textContent = STATE.modules.length + '\u00D7';
      var cItem = $('cf-info-count'), cSep = $('cf-count-sep');
      var showCount = ((STATE.mode | 0) === 1) && MAX_MODULES > 1;   /* single module enforced (MAX_MODULES === 1) -> the count cycler never shows; shared machinery stays for configs that allow more */
      if (cItem) cItem.style.display = showCount ? '' : 'none';
      if (cSep) cSep.style.display = showCount ? '' : 'none';
      var oItem = $('cf-info-openings'), oSep = $('cf-openings-sep');
      var showOp = (STATE.mode | 0) === 4;
      if (oItem) oItem.style.display = showOp ? '' : 'none';
      if (oSep) oSep.style.display = showOp ? '' : 'none';
      var fItem = $('cf-info-found'), fSep = $('cf-found-sep');
      var showF = (STATE.mode | 0) === 2;
      if (fItem) fItem.style.display = showF ? '' : 'none';
      if (fSep) fSep.style.display = showF ? '' : 'none';
      var rItem = $('cf-info-roof'), rSep = $('cf-roof-sep');
      var showR = (STATE.mode | 0) === 1;
      if (rItem) rItem.style.display = showR ? '' : 'none';
      if (rSep) rSep.style.display = showR ? '' : 'none';
      var eItem = $('cf-info-env'), eSep = $('cf-env-sep');
      var showE = (STATE.mode | 0) === 5;
      if (eItem) eItem.style.display = showE ? '' : 'none';
      if (eSep) eSep.style.display = showE ? '' : 'none';
      if (this._els.env) {
        this._els.env.textContent = tt(STATE.envelope === 'exterior' ? 'sauna_env_2' : 'sauna_env_1',
          STATE.envelope === 'exterior' ? 'Exterior insulation' : 'Interior insulation');
      }
      var dItem = $('cf-info-detail'), dSep = $('cf-detail-sep');
      var showD = (STATE.mode | 0) === 5;
      if (dItem) dItem.style.display = showD ? '' : 'none';
      if (dSep) dSep.style.display = showD ? '' : 'none';
      if (this._els.detail) {
        this._els.detail.textContent = tt('pdf_page_cross_section', 'Section') + (STATE.showDetail ? ' \u2713' : '');
      }
      var tsItem = $('cf-info-terr'), tsSep = $('cf-terr-sep');
      var showTs = _sitePergActive();
      if (tsItem) tsItem.style.display = showTs ? '' : 'none';
      if (tsSep) tsSep.style.display = showTs ? '' : 'none';
      if (this._els.terr) {
        this._els.terr.textContent = 'TERRACE \u00B7 ' + (STATE.terraceOn ? 'ON' : 'OFF');
      }
      var pgItem = $('cf-info-perg'), pgSep = $('cf-perg-sep');
      var showPg = _sitePergActive();
      if (pgItem) pgItem.style.display = showPg ? '' : 'none';
      if (pgSep) pgSep.style.display = showPg ? '' : 'none';
      if (this._els.perg) {
        this._els.perg.textContent = 'PERGOLA \u00B7 ' + (STATE.pergolaOn ? 'ON' : 'OFF');
      }
      var faItem = $('cf-info-fac'), faSep = $('cf-fac-sep');
      var showFa = (STATE.mode | 0) === 5 && STATE.envelope === 'exterior';
      if (faItem) faItem.style.display = showFa ? '' : 'none';
      if (faSep) faSep.style.display = showFa ? '' : 'none';
      if (this._els.fac) {
        var fd = _facadePatternDef();
        this._els.fac.textContent = tt(fd.i18n, fd.def);
      }
      if (this._els.found) {
        var fLbl = (STATE.foundation === 'piers') ? { k: 'sauna_found_2', d: 'Precast blocks' }
                 : (STATE.foundation === 'pads') ? { k: 'sauna_found_3', d: 'Cast pads' }
                 : { k: 'sauna_found_1', d: 'Screws' };
        this._els.found.textContent = tt(fLbl.k, fLbl.d);
      }
      if (this._els.roof) {
        /* both labels are the same length so the column keeps its width */
        this._els.roof.textContent = (STATE.roofType === 'gable')
          ? tt('shelters_roof_gable', 'GABLE ROOF')
          : tt('shelters_roof_mono', 'MONO PITCH');
      }
      if (this._els.area) this._els.area.textContent = fmtArea(totalFloorArea());
    }
  };

  function refreshUI() {
    /* the icon row is rebuilt from state on every wizard refresh; without this
       a tile could stay grey after the state under it changed by another
       route (Marek: dlazdice se ne vzdy prepnou) */
    try { if (_wiz && _wiz.isOpen && _wiz.isOpen() && _wiz.refresh) _wiz.refresh(); } catch (eWR) {}
    var t = _type();
    var elL = $('cf-stat-l'), elW = $('cf-stat-w'), elH = $('cf-stat-h');
    if (elL) elL.textContent = statVal(t.L);
    if (elW) elW.textContent = statVal(_extW());
    if (elH) elH.textContent = statVal(t.H);
    var unitEl = $('cf-stat-size-unit');
    if (unitEl) unitEl.textContent = STATE.isMetric ? 'm' : 'ft';
    _Banner.sync();
  }

  function rebuild() {
    /* the cut is on the renderer, so new meshes are still cut -- but the
       stencil caps and the double-sided materials are per mesh, so they are
       put back on whatever the rebuild just made */
    try { if (_cutTool && _cutTool.on()) setTimeout(function () { try { _cutTool.set({ rebuild: true }); } catch (e) {} }, 0); } catch (e0) {}
    if (SHELTER_FIXED_ROOF) { STATE.roofOver = { x0: 0.15, x1: 0.15, z0: 0.10, z1: 0.10 }; }   /* 150 mm over the eave edge (Marek), 100 mm at the gable ends */
    global.SaunaRenderer.build();
    /* the bodies are new now: a walk in progress recollects them and stays
       on its step */
    try { _asmRebuilt(); } catch (eA) {}
    try { if (global.SaunaPlan2D && STATE.view === '2d') global.SaunaPlan2D.refresh(); } catch (eP3) {}
    global.SaunaBoQ.publish();
    _ModHandles.refresh();
    refreshUI();
    /* shelter-v119 as core-v706 (Marek: model vzdy pri zmenach fit to canvas;
       Shelters staci) */
    try { _shFitAfter('the building changed'); } catch (eFA) {}
  }
  /* ALWAYS FRAMED AFTER A CHANGE (shelter-v119, as core-v706 on the house
     line): after a full rebuild whose building (_safeBox) is more than 3 %
     bigger, smaller or moved than when it was last framed, _fit3D frames it
     again -- from the angle the visitor left the camera at (it keeps the
     direction). A colour or an opening leaves the camera alone. Never under a
     finger: framed 250 ms after the last one lifts. Not in the plan, not
     during the assembly walk. */
  var _shFitB = null, _shPtrs = {}, _shPend = '';
  try {
    document.addEventListener('pointerdown', function (e) { if (e.isPrimary) _shPtrs = {}; _shPtrs[e.pointerId] = 1; }, { capture: true, passive: true });
    var _shUp = function (e) {
      delete _shPtrs[e.pointerId];
      if (!_shPend || Object.keys(_shPtrs).length) return;
      var w = _shPend; _shPend = '';
      setTimeout(function () { try { _shFitAfter(w + ', after the finger lifted'); } catch (eF) {} }, 250);
    };
    document.addEventListener('pointerup', _shUp, { capture: true, passive: true });
    document.addEventListener('pointercancel', _shUp, { capture: true, passive: true });
  } catch (eSp) {}
  function _shFitAfter(why) {
    if (Object.keys(_shPtrs).length) { _shPend = why; return; }
    if (STATE.view !== '3d' || !_shFitB) return;
    try { if (_asm2 && _asm2.on) return; } catch (eA) {}
    var T = global.THREE, box = _safeBox();
    if (!T || !box || box.isEmpty()) return;
    var sp = box.getBoundingSphere(new T.Sphere()), L = _shFitB;
    if (!(sp.radius > 0) || !(L.r > 0)) return;
    if (Math.abs(sp.radius - L.r) / L.r < 0.03 && sp.center.distanceTo(L.c) / L.r < 0.03) return;
    var was = L.r;
    if (_fit3D()) _log('frame    framed again -- ' + why + ': radius ' + was.toFixed(2) + ' -> ' + sp.radius.toFixed(2) + ' m, from the visitor\'s own angle (shelter-v119)');
  }

  /* Live rebuild for pointer drags. Two things make the full rebuild too heavy
     to run per pointermove: it publishes the whole BoQ (a full recompute plus a
     broadcast), and pointermove fires faster than we can draw. So coalesce into
     one rebuild per animation frame and leave the BoQ alone until the gesture
     ends -- nobody reads the budget mid-drag anyway. Call _dragCommit() on
     pointerup to flush. */
  var _dragRaf = 0;
  function rebuildLive() {
    if (_dragRaf) return;
    _dragRaf = global.requestAnimationFrame(function () {
      _dragRaf = 0;
      global.SaunaRenderer.build();
      _ModHandles.refresh();
      refreshUI();
    });
  }
  function _dragCommit() {
    /* the building just changed size: the framing follows it */
    if (_dragRaf) { global.cancelAnimationFrame(_dragRaf); _dragRaf = 0; }
    rebuild();
  }
  function _dragRafPending() { return !!_dragRaf; }

  function selectVariant(n, silent) {
    STATE.variant = Math.max(1, Math.min(VARIANT_KEYS.length, n | 0));
    var sel = clamp(STATE.selected | 0, 0, STATE.modules.length - 1);
    STATE.modules[sel].type = VARIANT_KEYS[STATE.variant - 1];
    rebuild();
    _fitCamera();
    if (!silent) savePref();
    _log('variant -> ' + STATE.variant + ' (module ' + sel + ' = ' + STATE.modules[sel].type + ')');
  }
  function nextVariant() { selectVariant((STATE.variant % VARIANT_KEYS.length) + 1); }
  function previousVariant() { var n = STATE.variant - 1; if (n < 1) n = VARIANT_KEYS.length; selectVariant(n); }

  function selectPreset(n, silent) {
    var mod = STATE.modules[clamp(STATE.selected | 0, 0, STATE.modules.length - 1)];
    mod.color = Math.max(1, Math.min(PRESET_COUNT, n | 0));
    rebuild();
    if (!silent) savePref();
    _log('color -> ' + _colorOf(mod).key);
  }
  function nextPreset() { cycleColor(clamp(STATE.selected | 0, 0, STATE.modules.length - 1)); }
  function previousPreset() { var mod = STATE.modules[STATE.selected]; var n = (mod.color | 0) - 1; if (n < 1) n = PRESET_COUNT; selectPreset(n); }

  function toggleUnits() {
    STATE.isMetric = !STATE.isMetric;
    /* Switching units REBUILDS the model onto the nearest legal model of the
       other system -- footprint to the new module, height to the new sheet
       stop, opening edges re-snapped, doors re-clamped. Canonical storage is
       metres throughout, and every snap runs FROM the canonical value, so
       flipping back and forth cannot walk the building. The change is a real
       change: the person sees dimensions move, not silently drift later. */
    try {
      STATE.footprint.w = clamp(_snapDim(STATE.footprint.w), FOOTPRINT.minW, FOOTPRINT.maxW);
      STATE.footprint.l = clamp(_snapDim(STATE.footprint.l), FOOTPRINT.minL, FOOTPRINT.maxL);
      STATE.footprint.h = _snapH(STATE.footprint.h);
      var opsU = STATE.openings || [];
      for (var iu = 0; iu < opsU.length; iu++) {
        var gU = _GRID() / 2, oU = opsU[iu];
        oU.off = Math.round((oU.off - oU.w / 2) / gU) * gU + oU.w / 2;
      }
      _reclampOpenings();
    } catch (eTU) {}
    rebuild();
    savePref();
    _log('units -> ' + (STATE.isMetric ? 'metric' : 'imperial'));    try { _syncPitchSlider(); } catch (eU) {}    try { _rebuildGrid(); } catch (eGU) {}
  }

  function reset() {
    STATE.modules = [{ type: '20', x: 0, z: 0, rot: 0, lvl: 0, color: 1 }];
    STATE.selected = 0;
    STATE.variant = 1; STATE.vizStage = 0;
    STATE.mode = 1; STATE.foundation = 'screws'; STATE.openings = []; STATE.partitions = []; STATE.doors = []; STATE.roomNames = []; STATE.envelope = 'exterior'; STATE.facadePattern = 'wood1'; STATE.facadeColor = 'larch'; STATE.colorView = 0; STATE.wallFill = { front: false, rear: false, sideL: false, sideR: false }; STATE.fillType = 'boards'; STATE.slatGap = 0.04; STATE.showDetail = false;
    STATE.bodyX = null; STATE.bodyZ = null;
    _ModHandles.refresh();
    rebuild(); _fitCamera(); savePref();
  }

  var PREF_KEY = 'cfg_sauna_v1';
  function _configPayload() {
    return {
      modules: STATE.modules, selected: STATE.selected, variant: STATE.variant,
      mode: STATE.mode, foundation: STATE.foundation, openings: STATE.openings, partitions: STATE.partitions, doors: STATE.doors,
      roomNames: STATE.roomNames,
      envelope: STATE.envelope,
      footprint: STATE.footprint, roofPitch: STATE.roofPitch, roofType: STATE.roofType, roofOver: STATE.roofOver,
      facadePattern: STATE.facadePattern, facadeColor: STATE.facadeColor, colorView: STATE.colorView,
      covering: STATE.covering,
      wallFill: STATE.wallFill, fillType: STATE.fillType, slatGap: STATE.slatGap,
      climateZone: STATE.climateZone || 0,
      pergolas: STATE.pergolas, pergolaVariant: STATE.pergolaVariant, pergolaOn: !!STATE.pergolaOn, terraceSurface: STATE.terraceSurface,
      terraceMode: !!STATE.terraceMode, terraceTops: STATE.terraceTops, terraceOn: !!STATE.terraceOn, terraceOver: STATE.terraceOver,
      vizStage: STATE.vizStage, bodyVisible: STATE.bodyVisible, scenery: STATE.scenery || 'map',
      bodyX: STATE.bodyX, bodyZ: STATE.bodyZ, isMetric: STATE.isMetric
    };
  }

  var LASTCFG_KEY = PREF_KEY + '__last';
  function _bootCustomerId() {
    try { return (global.SAUNA_BOOT && global.SAUNA_BOOT.customerId) || null; } catch (e) { return null; }
  }
  function _bootVariantId() {
    try {
      var id = new URL(location.href).searchParams.get('variant');
      if (!id) { var inp = document.querySelector('form[action*="/cart/add"] [name="id"]'); if (inp) id = inp.value; }
      return id || '';
    } catch (e) { return ''; }
  }
  function _lastCfgReadMap() {
    try { var m = JSON.parse(global.localStorage.getItem(LASTCFG_KEY) || '{}'); return (m && typeof m === 'object') ? m : {}; }
    catch (e) { return {}; }
  }
  function _lastCfgKey() { return String(_bootCustomerId()) + '__' + String(_bootVariantId()); }
  function _lastCfgGet() {
    if (!_bootCustomerId()) return null;
    var rec = _lastCfgReadMap()[_lastCfgKey()];
    return (rec && typeof rec === 'object') ? rec : null;
  }
  function _lastCfgWrite(cfgId) {
    var cid = _bootCustomerId();
    if (!cid) return;
    try {
      var map = _lastCfgReadMap();
      var k = _lastCfgKey();
      var prev = map[k] || {};
      map[k] = { cfg: (cfgId != null) ? cfgId : (prev.cfg || null), ts: Date.now(), payload: _configPayload() };
      var keys = Object.keys(map);
      if (keys.length > 40) {
        keys.sort(function (a, b) { return (map[b].ts || 0) - (map[a].ts || 0); });
        var trimmed = {}; keys.slice(0, 40).forEach(function (kk) { trimmed[kk] = map[kk]; });
        map = trimmed;
      }
      global.localStorage.setItem(LASTCFG_KEY, JSON.stringify(map));
    } catch (e) {}
  }


  /* ---------- CLOUD CONFIG (Firestore) --------------------------------
     The share button hands on whatever is in the address bar, so the way to
     make it share a CONFIGURATION rather than a product page is to keep the
     address bar honest: every settled change is written to Firestore and the
     URL carries its ?cfg= id. Open that link anywhere and the model comes
     back -- which is also what makes shared budgets and hardcoded presets
     work, since a preset is just a saved id.

     Deliberately self-contained: it must not depend on the budget snippet or
     any share module being on the page. Uses the firebase compat SDK when
     window.db exists, otherwise the Firestore REST API with the public web
     key. Doc: { type, ts, payload:<JSON string> } in leva_configs/{id}. */
  var _CFG_FS = {
    projectId: 'leva-791c5',
    apiKey: 'AIzaSyBoS6u5DLBF7ZYnB6egP9uvKLJ4lFHkNtI',
    col: 'leva_configs',
    type: 'shelter'
  };
  var _cfgId = null, _cfgTimer = 0, _cfgLastSaved = null, _cfgBusy = false;
  function _cfgNewId() { return Date.now().toString(36) + Math.random().toString(36).slice(2, 8); }
  function _cfgUrl(id) { return location.origin + location.pathname + '?cfg=' + id; }
  function _cfgRestBase() {
    return 'https://firestore.googleapis.com/v1/projects/' + _CFG_FS.projectId +
           '/databases/(default)/documents/' + _CFG_FS.col;
  }
  function _fsDecode(v) {
    if (!v || typeof v !== 'object') return null;
    if ('stringValue' in v) return v.stringValue;
    if ('integerValue' in v) return +v.integerValue;
    if ('doubleValue' in v) return +v.doubleValue;
    if ('booleanValue' in v) return !!v.booleanValue;
    return null;
  }
  function _cfgSaveDirect(payloadObj) {
    var id = _cfgNewId();
    var body = { type: _CFG_FS.type, ts: Date.now(), payload: JSON.stringify(payloadObj) };
    if (global.db && global.db.collection) {
      return global.db.collection(_CFG_FS.col).doc(id).set(body).then(function () { return id; });
    }
    if (!global.fetch) return Promise.reject(new Error('no transport'));
    return global.fetch(_cfgRestBase() + '?documentId=' + id + '&key=' + _CFG_FS.apiKey, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fields: {
        type: { stringValue: body.type },
        ts: { integerValue: String(body.ts) },
        payload: { stringValue: body.payload }
      } })
    }).then(function (r) {
      if (!r.ok) throw new Error('save ' + r.status);
      return id;
    });
  }
  function _cfgLoadDirect(id) {
    if (global.db && global.db.collection) {
      return global.db.collection(_CFG_FS.col).doc(id).get().then(function (d) {
        var v = d && d.exists ? d.data() : null;
        return (v && v.payload) ? JSON.parse(v.payload) : null;
      });
    }
    if (!global.fetch) return Promise.reject(new Error('no transport'));
    return global.fetch(_cfgRestBase() + '/' + encodeURIComponent(id) + '?key=' + _CFG_FS.apiKey)
      .then(function (r) { if (!r.ok) throw new Error('load ' + r.status); return r.json(); })
      .then(function (doc) {
        var p = doc && doc.fields ? _fsDecode(doc.fields.payload) : null;
        return p ? JSON.parse(p) : null;
      });
  }
  function _cfgPutUrl(id) {
    try {
      if (!global.history || !history.replaceState) return;
      var u = new URL(location.href);
      u.searchParams.set('cfg', id);
      history.replaceState(null, '', u.pathname + u.search + u.hash);
    } catch (e) {}
  }
  /* debounced: one write per settled edit, never mid-drag, and never a write
     that would store what is already stored */
  function _cfgTouch() {
    try {
      if (_cfgTimer) { clearTimeout(_cfgTimer); _cfgTimer = 0; }
      _cfgTimer = setTimeout(function () {
        _cfgTimer = 0;
        if (_cfgBusy) return;
        var payload, now;
        try { payload = _configPayload(); now = JSON.stringify(payload); } catch (e) { return; }
        if (now === _cfgLastSaved) return;
        _cfgBusy = true;
        _cfgSaveDirect(payload).then(function (id) {
          _cfgBusy = false; _cfgId = id; _cfgLastSaved = now;
          _cfgPutUrl(id);
          try { _lastCfgWrite(id); } catch (eW) {}
          _log('cfg saved -> ' + id);
        }, function (err) {
          _cfgBusy = false;
          _log('cfg save failed: ' + (err && err.message ? err.message : err));
        });
      }, 1500);
    } catch (e) {}
  }
  /* THE SHELTER A FIRST VISIT OPENS ON.

     Not a set of constants: the whole cloud config comes down with it -- the
     footprint, the roof, which sides are filled and with what, the colours.
     The same mechanism a share link uses, just with an id baked in, so it can
     be changed by saving a new one and pasting the id here (Marek).

     ORDER MATTERS. A ?cfg= in the address always wins; after that whatever the
     visitor built last, restored by loadPref; and only when there is neither
     does this open. Somebody who has been here before never sees it, and
     nobody's work is ever overwritten by it. THE ID MUST STAY IN THE CLOUD
     STORE: it is read on every first visit. */
  var DEFAULT_CFG = 'mu37lnm48ayo1l';
  var _prefRestored = false;

  function _cfgBootApply() {
    var m = /[?&]cfg=([^&]+)/.exec(location.search || '');
    if (!m) {
      /* nothing in the address: the default, unless this visitor has a model
         of their own already */
      if (_prefRestored || !DEFAULT_CFG) return;
      _cfgLoadDirect(DEFAULT_CFG).then(function (payload) {
        if (!payload) { _log('default cfg ' + DEFAULT_CFG + ' -- not in the store'); return; }
        _applyConfig(payload);
        _clampPitch();
        rebuild();
        try { _syncPitchSlider(); } catch (eS0) {}
        try { _fitCamera(); } catch (eF0) {}
        _log('default cfg applied <- ' + DEFAULT_CFG + ' (the visitor had nothing of their own)');
      }, function (err) { _log('default cfg load failed: ' + ((err && err.message) || err)); });
      return;
    }
    var id = decodeURIComponent(m[1]);
    _cfgLoadDirect(id).then(function (payload) {
      if (!payload) return;
      _cfgId = id;
      try { _cfgLastSaved = JSON.stringify(payload); } catch (e) {}
      _applyConfig(payload);
      _clampPitch();
      rebuild();
      try { _syncPitchSlider(); } catch (eS) {}
      _log('cfg applied <- ' + id);
    }, function (err) { _log('cfg load failed: ' + (err && err.message ? err.message : err)); });
  }

  /* Undo. savePref is the commit point -- it runs once a change is settled
     (a drag released, a cycler tapped, a slider let go), never mid-gesture. So
     the stack gets one entry per thing the user actually did, rather than one
     per frame of a drag.

     It holds the state BEFORE each change, which is what undo has to restore.
     Comparing serialised payloads means a save that changed nothing adds
     nothing -- otherwise the button would need pressing five times to walk back
     one edit.

     The stack lives for the session only. Nothing on disk: an undo history that
     outlives the tab is a promise about someone else's yesterday. When it runs
     out, reset() -- the end of the road is the beginning. */
  var UNDO_MAX = 60;
  var _undo = [];
  var _undoBtn = null;
  var _undoPrev = null;
  var _undoing = false;

  /* THE DESIGN, NOT THE VIEW (shelter-v118-pdfkey): the key the PDF is paid for, so the
     same design downloads again for free for 30 days (configurator-pdf.liquid
     pdf-pay-v4, worker leva-ai) */
  function _pdfKey() {
    try { var p = Object.assign({}, _configPayload()); ['vizStage', 'bodyVisible', 'scenery', 'colorView', 'selected'].forEach(function (k) { delete p[k]; }); return 'shelters|' + JSON.stringify(p); } catch (e) { return ''; }
  }
  function savePref() {
    if (_REF_HOLD > 0) return;   /* shelter-v120: REF builds its designs -- nothing written, no Undo, no ?cfg= */
    try {
      var now = JSON.stringify(_configPayload());
      if (!_undoing && _undoPrev !== null && _undoPrev !== now) {
        _undo.push(_undoPrev);
        if (_undo.length > UNDO_MAX) _undo.shift();
      }
      _undoPrev = now;
      global.localStorage.setItem(PREF_KEY, now);
    } catch (e) {}
    _lastCfgWrite();
    _cfgTouch();
    try { if (_undoBtn) _undoBtn.refresh(); } catch (eB) {}
  }

  function undo() {
    if (!_undo.length) {
      _dbg('undo -> stack empty, reset to defaults');
      reset();
      _undo = []; _undoPrev = null;
      try { _undoPrev = JSON.stringify(_configPayload()); } catch (e) {}
      return;
    }
    var prev = _undo.pop();
    _undoing = true;
    try {
      _applyConfig(JSON.parse(prev));
      _undoPrev = prev;
      _clampPitch();
      try { _syncPitchSlider(); } catch (eP) {}
      _ModHandles.refresh();
      rebuild();
      try { if (global.SaunaFootprint) global.SaunaFootprint.refresh(); } catch (eF) {}
      savePref();
    } catch (eU) {
      _noteError('undo: ' + (eU && eU.message ? eU.message : eU));
    }
    _undoing = false;
    try { if (_undoBtn) _undoBtn.refresh(); } catch (eB2) {}
    _dbg('undo -> ' + _undo.length + ' left');
  }
  function _applyConfig(d) {
    try {
      if (Array.isArray(d.modules) && d.modules.length) {
        STATE.modules = d.modules.filter(function (m) {
          return !!m;
        }).map(function (m) {
          return { type: m.type, x: +m.x || 0, z: +m.z || 0,
            rot: +m.rot || 0,
            lvl: clamp(m.lvl | 0, 0, MAX_LEVEL),
            color: clamp((m.color | 0) || (d.preset | 0) || 1, 1, PRESET_COUNT) };
        }).slice(0, MAX_MODULES);
        if (!STATE.modules.length) STATE.modules = [{ type: '20', x: 0, z: 0, rot: 0, lvl: 0, color: 1 }];
        STATE.modules[0].rot = 0;
        STATE.modules[0].lvl = 0;
      }
      if (typeof d.selected === 'number') STATE.selected = clamp(d.selected | 0, 0, STATE.modules.length - 1);
      var selKey = STATE.modules[STATE.selected].type;
      for (var vi = 0; vi < VARIANT_KEYS.length; vi++) if (VARIANT_KEYS[vi] === selKey) STATE.variant = vi + 1;
      if (d.mode >= 1 && d.mode <= 5) STATE.mode = d.mode | 0;
      if (['screws', 'pads'].indexOf(d.foundation) >= 0) STATE.foundation = d.foundation;
      /* Migration, not restoration. Every saved payload in the field carries
         envelope='interior' -- not because anyone chose it, but because it was
         the default the day they first opened the page, and a default that
         nobody was ever shown a switch for is not a decision. Honouring it
         meant the v89 fix changed nothing for exactly the people who reported
         the missing layers: their own project kept restoring the four-layer
         wall over the new default, forever. There is no interior-sauna product
         to protect, so there is nothing to restore: a garden sauna has the
         whole wall. When the interior variant becomes a real product, it gets
         a real switch, and only payloads written after that switch exists can
         mean it. */
      STATE.envelope = 'exterior';
      if (d.facadePattern === 'wood1' || d.facadePattern === 'wood2' || d.facadePattern === 'bond') STATE.facadePattern = d.facadePattern;
      if (SHELTER_SCREEN_WALLS && STATE.facadePattern === 'bond') STATE.facadePattern = 'wood2';   /* the bond stage is gone here */
      if (d.facadeColor === 'larch' || d.facadeColor === 'shou' || d.facadeColor === 'stain' || d.facadeColor === 'corten') STATE.facadeColor = d.facadeColor;
      /* colorView deliberately NOT restored from prefs: every load opens the natural-wood default view 0 (Marek); the palette button still cycles in-session */
      if (d.wallFill && typeof d.wallFill === 'object') { ['front', 'rear', 'sideL', 'sideR'].forEach(function (ws) { STATE.wallFill[ws] = (d.wallFill[ws] !== false); }); }
      if (d.fillType === 'boards' || d.fillType === 'slats') STATE.fillType = d.fillType;
      /* the covering is a product decision like the roof type, so it comes
         back; the colour overrides do NOT, by the same rule as colorView --
         every load opens on the natural-wood scheme (Marek) */
      if (d.covering === 'shingle' || d.covering === 'trapez' || d.covering === 'seam') STATE.covering = d.covering;
      if (typeof d.slatGap === 'number' && d.slatGap >= 0 && d.slatGap <= 0.3) STATE.slatGap = d.slatGap;

      if (!d.facadeColor && (d.facade === 'larch' || d.facade === 'shou' || d.facade === 'stain' || d.facade === 'corten')) STATE.facadeColor = d.facade;
      /* showDetail is not saved: it says which step of the editor you were on,
         not what you are building. Restoring it opened a section on a page the
         visitor had not asked to edit. */
      if (typeof d.climateZone === 'number') STATE.climateZone = d.climateZone;
      if (typeof d.mapLocked === 'boolean') STATE.mapLocked = d.mapLocked;
      if (Array.isArray(d.pergolas)) {
        STATE.pergolas = d.pergolas.filter(function (p) {
          return p && typeof p.x === 'number' && typeof p.z === 'number' && p.w > 0 && p.l > 0;
        }).map(function (p) {
          return { x: p.x, z: p.z, w: p.w, l: p.l, rot: p.rot || 0, attach: p.attach || null };
        });
      }
      if (['wood_slat', 'wood_deck', 'alu_slat', 'alu_tile'].indexOf(d.pergolaVariant) >= 0) STATE.pergolaVariant = d.pergolaVariant;
      if (typeof d.pergolaOn === 'boolean') STATE.pergolaOn = d.pergolaOn;
      STATE.terraceOn = false;   /* shelter has no terrace */
      if (d.terraceOver && typeof d.terraceOver === 'object') {
        var _tk = ['x0', 'x1', 'z0', 'z1'];
        for (var _ti = 0; _ti < 4; _ti++) if (typeof d.terraceOver[_tk[_ti]] === 'number') STATE.terraceOver[_tk[_ti]] = clamp(d.terraceOver[_tk[_ti]], TERR_LIM.min, TERR_LIM.max);
      }
      if (Array.isArray(d.partitions)) {
        STATE.partitions = d.partitions.filter(function (p) {
          return p && typeof p.mi === 'number' && p.mi < STATE.modules.length &&
            (p.o === 'v' || p.o === 'h') && typeof p.c === 'number';
        });
      }



      if (d.footprint && typeof d.footprint === 'object') {
        STATE.footprint = {
          w: clamp(+d.footprint.w || 7.0, FOOTPRINT.minW, FOOTPRINT.maxW),
          l: clamp(+d.footprint.l || 3.6, FOOTPRINT.minL, FOOTPRINT.maxL),
          /* saved heights predate the real floor: clamp lifts every legacy
             2.0-2.3 project to a height its own door fits under */
          h: clamp(+d.footprint.h || 2.9, FOOTPRINT.minH, FOOTPRINT.maxH)   /* snapped to a stop below, once units are known */
        };
      }
      if (typeof d.roofPitch === 'number') STATE.roofPitch = clamp(d.roofPitch, 0, 45);
      /* roofType is restored just below, so the per-type clamp runs after it */
      if (d.roofType === 'mono' || d.roofType === 'gable') STATE.roofType = d.roofType;
      _clampPitch();
      if (d.roofOver) {
        var _ro = STATE.roofOver, _rk = ['x0', 'x1', 'z0', 'z1'], _ri;
        for (_ri = 0; _ri < 4; _ri++) {
          if (typeof d.roofOver[_rk[_ri]] === 'number') {
            _ro[_rk[_ri]] = clamp(d.roofOver[_rk[_ri]], OVER_LIM.min, OVER_LIM.max);
          }
        }
      }

      if (Array.isArray(d.doors)) {
        STATE.doors = d.doors.filter(function (dd) {
          return dd && typeof dd.mi === 'number' && dd.mi < STATE.modules.length &&
            (dd.o === 'v' || dd.o === 'h') && typeof dd.c === 'number' && typeof dd.u === 'number';
        });
      }
      if (Array.isArray(d.roomNames)) {
        STATE.roomNames = d.roomNames.filter(function (rn) {
          return rn && typeof rn.mi === 'number' && rn.mi < STATE.modules.length &&
            typeof rn.x === 'number' && typeof rn.z === 'number' && typeof rn.n === 'number';
        });
      }
      if (Array.isArray(d.openings)) {
        STATE.openings = d.openings.filter(function (o) {
          return o && typeof o.mi === 'number' && o.mi < STATE.modules.length &&
            (o.wall === 'sideL' || o.wall === 'sideR' || o.wall === 'rear' || o.wall === 'front') &&
            typeof o.off === 'number' && o.w > 0 && o.h > 0;
        });
      }
      if (d.vizStage >= 0 && d.vizStage <= 2) STATE.vizStage = d.vizStage | 0;
      if (d.scenery === 'map' || d.scenery === 'meadow' || d.scenery === 'forest') STATE.scenery = d.scenery;
      if (typeof d.bodyVisible === 'boolean') STATE.bodyVisible = d.bodyVisible;
      if (typeof d.bodyX === 'number') STATE.bodyX = d.bodyX;
      if (typeof d.bodyZ === 'number') STATE.bodyZ = d.bodyZ;
      if (typeof d.isMetric === 'boolean') STATE.isMetric = d.isMetric;
    } catch (e) {}
  }

  /* Two stores, and which one is read depends on whether you are logged in.
     Signed in: the lastCfg map, keyed per customer. Signed out: PREF_KEY in
     localStorage -- and note the early return, so for a signed-in user
     cfg_sauna_v1 is written and NEVER read. Nine versions were spent watching a
     key that may not be the one in play. */
  function loadPref() {
    try {
      var rec = _lastCfgGet();
      if (rec && rec.payload) { _applyConfig(rec.payload); _prefRestored = true; return; }
      if (_bootCustomerId()) { _prefRestored = true; return; }
      var raw = global.localStorage.getItem(PREF_KEY);
      if (!raw) return;
      _applyConfig(JSON.parse(raw));
      _prefRestored = true;       /* so the default does not overwrite it */
    } catch (e) {}
  }

  var PANEL_BASE = 0.16 + 0.20 + 0.015;   /* bearer + joist + deck: walls on the deck */
  var INNER_MARGIN = 0.095;
  var FLOOR_TOP = PANEL_BASE;             /* the interior floor IS the deck top */
  function _panelH(t) { return t.H - PANEL_BASE - 0.09; }
  function _innerRect(t) {
    return { hx: _extW() / 2 - INNER_MARGIN, hz: t.L / 2 - INNER_MARGIN };
  }
  function _wallClearH(t) { return (t.H - 0.09) - FLOOR_TOP; }
  function _sideFilled(w) { return !(STATE.wallFill && STATE.wallFill[w] === false); }
  function _shelterArea() { var m = STATE.modules[0]; return m ? (_extW() * _typeOf(m).L) : 0; }
  function _postProfile() {
    return (_shelterArea() > 15)
      ? { w: 0.16, code: 'KVH16160', label: '160x160' }
      : { w: 0.10, code: 'KVH10100', label: '100x100' };
  }

  function _rotQ(px, pz, q) {
    var x = px, z = pz, t;
    for (var k = 0; k < q; k++) { t = x; x = z; z = -t; }
    return [x, z];
  }

  function _modWallSegs(mi) {
    var mod = STATE.modules[mi];
    if (!mod) return [];
    var t = _typeOf(mod);
    var rot = mod.rot || 0;
    var ca = Math.cos(rot), sa = Math.sin(rot);
    function rotp(px, pz) { return [px * ca - pz * sa, px * sa + pz * ca]; }
    var cx = mod.x || 0, cz = mod.z || 0;
    var y0 = _yFor(mi, 0);
    var hw = _extW() / 2, hl = t.L / 2;
    var sideLen = t.L - 2 * POST, endW = _extW() - 2 * POST;
    var lineX = hw - 0.073, lineZ = hl - 0.073;
    var defs = [
      { wall: 'sideL', p0: [-lineX, -sideLen / 2], dir: [0, 1], nrm: [-1, 0], len: sideLen },
      { wall: 'sideR', p0: [lineX, sideLen / 2], dir: [0, -1], nrm: [1, 0], len: sideLen },
      { wall: 'rear', p0: [endW / 2, -lineZ], dir: [-1, 0], nrm: [0, -1], len: endW },
      { wall: 'front', p0: [-endW / 2, lineZ], dir: [1, 0], nrm: [0, 1], len: endW }
    ];
    var out = [];
    for (var i = 0; i < defs.length; i++) {
      var d = defs[i];
      var p0 = rotp(d.p0[0], d.p0[1]);
      var dr = rotp(d.dir[0], d.dir[1]);
      var nr = rotp(d.nrm[0], d.nrm[1]);
      out.push({
        mi: mi, wall: d.wall, id: mi + ':' + d.wall,
        x0: cx + p0[0], z0: cz + p0[1],
        dir: { x: dr[0], z: dr[1] },
        nrm: { x: nr[0], z: nr[1] },
        len: d.len,
        baseY: y0 + PANEL_BASE,
        top: y0 + PANEL_BASE + _panelH(t)
      });
    }
    return out;
  }
  function _holesFor(mi) {
    var mod = STATE.modules[mi];
    var t = _typeOf(mod);
    var ph = _panelH(t);
    var out = { sideL: [], sideR: [], rear: [], front: [] };
    var ops = STATE.openings || [];
    for (var i = 0; i < ops.length; i++) {
      var o = ops[i];
      if (o.mi !== mi || !out[o.wall]) continue;
      var lo = o.off - o.w / 2, hi = o.off + o.w / 2;
      out[o.wall].push({ lo: lo, hi: hi, b0: (o.sill || 0), b1: (o.sill || 0) + o.h });
    }
    _passageHolesFor(mi, out);
    return out;
  }

  function _cutRoofHolesFor(mi, t) {
    var out = [];
    var Lr = t.L - 0.10, Wr = _extW() - 0.10;
    return out;
  }


  function _cutFloorHolesFor(mi) {
    var mod = STATE.modules[mi];
    if ((mod.lvl | 0) < 1) return [];
    if (!_isOrtho(mod)) return [];
    var q = _quarter(mod);
    var out = [];
    return out;
  }



  function _passageHolesFor(mi, out) {
  }

  function _wallTagFor(mod, dx, dz) {
    var q = _quarter(mod);
    var lx = dx, lz = dz, t;
    for (var k = 0; k < q; k++) { t = lx; lx = -lz; lz = t; }
    if (lx > 0.5) return 'sideR';
    if (lx < -0.5) return 'sideL';
    if (lz > 0.5) return 'door';
    return 'rear';
  }

  var _Ghost = {
    _list: [],
    apply: function (i, tag) {
      var bg = global.SaunaEngine && global.SaunaEngine.group;
      if (!bg) return;
      for (var k = 0; k < bg.children.length; k++) {
        var g = bg.children[k];
        if (!g.userData || g.userData.moduleIdx !== i) continue;
        var self = this;
        g.traverse(function (o) {
          if (!o.isMesh || !o.userData || o.userData.viz !== tag || o.userData._ghosted) return;
          o.userData._origMat = o.material;
          var gm = o.material.clone();
          gm.transparent = true;
          gm.opacity = 0.22;
          gm.depthWrite = false;
          o.material = gm;
          o.userData._ghosted = true;
          self._list.push(o);
        });
      }
    },
    clear: function () {
      for (var k = 0; k < this._list.length; k++) {
        var o = this._list[k];
        if (o.userData && o.userData._origMat) {
          try { o.material.dispose(); } catch (e) {}
          o.material = o.userData._origMat;
          delete o.userData._origMat;
          delete o.userData._ghosted;
        }
      }
      this._list = [];
    }
  };

  var _Guides = {
    _grp: null, _mat: null,

    _ensure: function () {
      var T = global.THREE;
      var eng = global.SaunaEngine;
      if (!eng || !eng.scene) return false;
      if (!this._grp) {
        this._grp = new T.Group();
        eng.scene.add(this._grp);
      }
      if (!this._mat) {
        this._mat = new T.MeshBasicMaterial({
          color: 0xc79a4a, transparent: true, opacity: 0.28,
          side: T.DoubleSide, depthWrite: false
        });
      }
      return true;
    },

    set: function (guides, i) {
      if (!this._ensure()) return;
      this.clear();
      var T = global.THREE;
      var self = STATE.modules[i];
      for (var g = 0; g < guides.length; g++) {
        var gd = guides[g];
        var m = STATE.modules[gd.j];
        if (!m) continue;
        var selfTop = _yFor(i, 0) + _typeOf(self).H;
        var otherTop = _yFor(gd.j, 0) + _typeOf(m).H;
        var h = Math.max(selfTop, otherTop) + 0.25;
        var a0, a1;
        if (gd.axis === 'x') {
          a0 = Math.min((self.z || 0) - _fpL(self) / 2, (m.z || 0) - _fpL(m) / 2) - 0.4;
          a1 = Math.max((self.z || 0) + _fpL(self) / 2, (m.z || 0) + _fpL(m) / 2) + 0.4;
          var pl = new T.Mesh(new T.PlaneGeometry(a1 - a0, h), this._mat);
          pl.rotation.y = Math.PI / 2;
          pl.position.set(gd.v, h / 2, (a0 + a1) / 2);
          pl.renderOrder = 4;
          this._grp.add(pl);
        } else {
          a0 = Math.min((self.x || 0) - _fpW(self) / 2, (m.x || 0) - _fpW(m) / 2) - 0.4;
          a1 = Math.max((self.x || 0) + _fpW(self) / 2, (m.x || 0) + _fpW(m) / 2) + 0.4;
          var pl2 = new T.Mesh(new T.PlaneGeometry(a1 - a0, h), this._mat);
          pl2.position.set((a0 + a1) / 2, h / 2, gd.v);
          pl2.renderOrder = 4;
          this._grp.add(pl2);
        }
      }
    },

    clear: function () {
      if (!this._grp) return;
      while (this._grp.children.length) {
        var c = this._grp.children.pop();
        this._grp.remove(c);
        if (c.geometry) c.geometry.dispose();
      }
    }
  };

  var _ModCtl = {
    _texCache: {},
    _bound: false,
    _drag: null,
    _gkey: '',

    _iconTex: function (kind, gold) {
      var key = kind + (gold ? '_g' : '');
      if (this._texCache[key]) return this._texCache[key];
      var T = global.THREE;
      var c = document.createElement('canvas');
      c.width = 256; c.height = 256;
      var ctx = c.getContext('2d');
      ctx.clearRect(0, 0, 256, 256);
      ctx.beginPath();
      ctx.arc(128, 128, 118, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(20,21,24,0.92)';
      ctx.fill();
      ctx.lineWidth = 12;
      ctx.strokeStyle = gold ? '#c79a4a' : '#ffffff';
      ctx.stroke();
      ctx.strokeStyle = '#ffffff';
      ctx.fillStyle = '#ffffff';
      ctx.lineWidth = 16;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      function arrow(x, y, dx, dy) {
        var s = 26;
        ctx.beginPath();
        ctx.moveTo(x - dy * s * 0.7 - dx * s, y + dx * s * 0.7 - dy * s);
        ctx.lineTo(x, y);
        ctx.lineTo(x + dy * s * 0.7 - dx * s, y - dx * s * 0.7 - dy * s);
        ctx.stroke();
      }
      if (kind === 'drag') {
        ctx.beginPath(); ctx.moveTo(128, 62); ctx.lineTo(128, 194); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(62, 128); ctx.lineTo(194, 128); ctx.stroke();
        arrow(128, 52, 0, -1); arrow(128, 204, 0, 1);
        arrow(52, 128, -1, 0); arrow(204, 128, 1, 0);
      } else if (kind === 'rot') {
        ctx.beginPath(); ctx.arc(128, 128, 56, -Math.PI * 0.4, Math.PI * 1.1); ctx.stroke();
        var a = -Math.PI * 0.4;
        arrow(128 + 56 * Math.cos(a), 128 + 56 * Math.sin(a), Math.sin(a) * -1, Math.cos(a) * -1);
      } else if (kind === 'color') {
        ctx.beginPath();
        ctx.moveTo(128, 54);
        ctx.bezierCurveTo(178, 122, 186, 152, 158, 182);
        ctx.bezierCurveTo(142, 198, 114, 198, 98, 182);
        ctx.bezierCurveTo(70, 152, 78, 122, 128, 54);
        ctx.fill();
      } else if (kind.indexOf('fill') === 0) {
        ctx.beginPath(); ctx.moveTo(128, 66); ctx.lineTo(128, 190); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(66, 128); ctx.lineTo(190, 128); ctx.stroke();
      } else {
        ctx.beginPath(); ctx.moveTo(76, 180); ctx.lineTo(180, 76); ctx.stroke();
        arrow(186, 70, 0.707, -0.707);
        arrow(70, 186, -0.707, 0.707);
      }
      var tex = new T.CanvasTexture(c);
      tex.colorSpace = T.SRGBColorSpace;
      tex.anisotropy = 4;
      this._texCache[key] = tex;
      return tex;
    },

    decorateFill: function (grp, mi, t) {
      /* the "+" GROUND buttons: one flat disc on the ground at the midpoint of
         each side, just outside the wall line. Tap toggles that side's wall
         fill (open <-> filled). */
      var T = global.THREE;
      var hw = _extW() / 2, hl = t.L / 2, off = 0.35;
      var gy = -_foundLift() + 0.03;
      var sd = [
        { s: 'front', x: 0, z: hl + off },
        { s: 'rear', x: 0, z: -(hl + off) },
        { s: 'sideL', x: -(hw + off), z: 0 },
        { s: 'sideR', x: (hw + off), z: 0 }
      ];
      for (var i = 0; i < sd.length; i++) {
        var disc = new T.Mesh(
          new T.CircleGeometry(0.30, 28),
          new T.MeshBasicMaterial({ map: this._iconTex('fill_' + sd[i].s, false), transparent: true, depthWrite: false })
        );
        disc.rotation.x = -Math.PI / 2;
        disc.position.set(sd[i].x, gy, sd[i].z);
        disc.renderOrder = 6;
        disc.userData.ctl = 'fill_' + sd[i].s;
        disc.userData.ctlModule = mi;
        grp.add(disc);
      }
    },

    _discs: function () {
      var out = [];
      var bg = global.SaunaEngine && global.SaunaEngine.group;
      if (!bg) return out;
      bg.traverse(function (o) { if (o.isMesh && o.userData && o.userData.ctl) out.push(o); });
      return out;
    },

    syncSelection: function () {
      var self = this;
      this._discs().forEach(function (d) {
        d.material.map = self._iconTex(d.userData.ctl, d.userData.ctlModule === STATE.selected);
        d.material.needsUpdate = true;
      });
    },

    _ray: null, _ndc: null, _plane: null, _hit: null,
    _pointerRay: function (clientX, clientY) {
      var T = global.THREE;
      var eng = global.SaunaEngine;
      if (!eng || !eng.camera || !eng.renderer) return null;
      var rect = eng.renderer.domElement.getBoundingClientRect();
      if (!this._ray) {
        this._ray = new T.Raycaster();
        this._ray.params.Line = { threshold: 0.0001 };
        this._ndc = new T.Vector2();
        this._plane = new T.Plane(new T.Vector3(0, 1, 0), 0);
        this._hit = new T.Vector3();
      }
      this._ndc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
      this._ndc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
      this._ray.setFromCamera(this._ndc, eng.camera);
      return this._ray;
    },
    _groundHit: function (clientX, clientY) {
      var ray = this._pointerRay(clientX, clientY);
      if (!ray) return null;
      return ray.ray.intersectPlane(this._plane, this._hit);
    },

    init: function () {
      if (this._bound) return;
      var eng = global.SaunaEngine;
      if (!eng || !eng.renderer) return;
      this._bound = true;
      var canvas = eng.renderer.domElement;
      var self = this;
      var pending = null;

      canvas.addEventListener('pointerdown', function (e) {
        if (STATE.view === '2d') return; 
        if (global.SaunaEngine.view === '2d') return;
        var ray = self._pointerRay(e.clientX, e.clientY);
        if (!ray) return;
        var hits = ray.intersectObjects(self._discs(), false);
        if (!hits.length) return;
        var disc = hits[0].object;
        var ctl = disc.userData.ctl;
        var idx = disc.userData.ctlModule;
        e.preventDefault();
        e.stopPropagation();
        if (eng.controls) eng.controls.enabled = false;
        if (ctl === 'drag' && idx > 0) {
          var p = self._groundHit(e.clientX, e.clientY);
          var mod = STATE.modules[idx];
          self._drag = {
            idx: idx, moved: false,
            offX: p ? (p.x - (mod.x || 0)) : 0,
            offZ: p ? (p.z - (mod.z || 0)) : 0
          };
          pending = null;
        } else {
          pending = { ctl: ctl, idx: idx, x: e.clientX, y: e.clientY };
        }
      }, true);

      global.addEventListener('pointermove', function (e) {
        if (self._drag) { self._onMoveDrag(e); return; }
        if (pending && (Math.abs(e.clientX - pending.x) > 10 || Math.abs(e.clientY - pending.y) > 10)) pending = null;
      }, true);

      global.addEventListener('pointerup', function () {
        var wasDrag = !!self._drag;
        if (wasDrag) {
          var i = self._drag.idx;
          var moved = self._drag.moved;
          self._drag = null;
          _Ghost.clear();
          _Guides.clear();
          self._gkey = '';
          _selectModule(i);
          if (moved) { _recenterLayout(); rebuild(); savePref(); }
        } else if (pending) {
          var p2 = pending; pending = null;
          _selectModule(p2.idx);
          if (p2.ctl === 'color') cycleColor(p2.idx);
          else if (p2.ctl && p2.ctl.indexOf('fill_') === 0) {
            var _fs = p2.ctl.slice(5);
            if (STATE.wallFill && STATE.wallFill[_fs] != null) { STATE.wallFill[_fs] = !STATE.wallFill[_fs]; rebuild(); savePref(); }
          }
        }
        if (eng.controls && !self._drag) eng.controls.enabled = true;
      }, true);
    },

    refresh: function () { this.syncSelection(); }
  };

  var OVER_LIM = { min: 0.10, max: 3.00 };   /* min 100 mm roof overhang; all eave pieces tie to this edge */
  var TERR_LIM = { min: 0, max: 4.00 };   /* terrace reach past each wall; 0 = none */
  var TERR_MIN = 1.0;                      /* a deck is generated on a side only where the reach is at least this */

  /* Roof outline in the module's local frame. Overhang is per side, so the roof
     rectangle is not concentric with the cabin once the sides differ. */
  function _RoofRect(w, l, ov) {
    return { x0: -(w / 2 + ov.x0), x1: w / 2 + ov.x1, z0: -(l / 2 + ov.z0), z1: l / 2 + ov.z1 };
  }

  /* Roof grips: same eight-point layout as the footprint, but each one edits an
     overhang rather than a dimension. side names which of roofOver it drives;
     corners drive two. */


  global.SaunaFootprint = {
    _h: null,

    /* Two rects, and they no longer come out together.

       The overhang rect belongs to ROOF: it is the roof, and ROOF is the step
       where the roof is on screen. It was here because SIZE was the only step
       with grips, which is a reason about the code, not about the building.

       The building rect belongs to LAYOUT, and it comes back movable:false.
       That flag is not cosmetic. handles() falls back to grabbing the whole
       rect when no grip is under the thumb, and it does that in the capture
       phase -- so with SIZE and PARTITIONS merged, every tap inside the plan
       would have been swallowed to slide the cabin sideways, and nothing in
       here could ever have seen a wall. The building is the project origin; it
       has nowhere to move to. Only the eight grips are grips. */
    _rects: function () {
      var m = STATE.modules[0];
      if (!m) return [];
      var f = STATE.footprint, x = m.x || 0, z = m.z || 0;
      var hw = f.w / 2, hl = f.l / 2;
      if (_editable('look') && STATE.terraceOn) {
        var tr = _RoofRect(f.w, f.l, STATE.terraceOver);
        return [{ key: 'terrace', style: 'secondary', snap: 0.05, movable: false,
                  x0: x + tr.x0, z0: z + tr.z0, x1: x + tr.x1, z1: z + tr.z1 }];
      }
      var rr = _RoofRect(f.w, f.l, STATE.roofOver);
      var roof = { key: 'roof', style: 'secondary', snap: 0.05, movable: false,
                   x0: x + rr.x0, z0: z + rr.z0, x1: x + rr.x1, z1: z + rr.z1 };
      var build = { key: 'build', style: 'primary', snap: 0.1, movable: false,
                    x0: x - hw, z0: z - hl, x1: x + hw, z1: z + hl };
      /* SHELTER: no roof footprint at all -- the roof is fixed to the posts,
         so the roof rect is never drawn and never draggable (dragging it used
         to spawn overhang posts that snapped back on drop). */
      if (SHELTER_FIXED_ROOF) return [build];
      if (!_wizOpen()) return [roof, build];        /* no wizard: the old STEP 1, untouched */
      return _editable('roof') ? [roof] : [build];
    },

    _apply: function (key, r) {
      var m = STATE.modules[0], f = STATE.footprint;
      if (!m) return;
      if (key === 'build') {
        /* keep the pinned edge exactly where it was: derive the centre from it
           rather than from the midpoint, so hitting a limit cannot slide the
           cabin sideways */
        if (r.x0 != null) {
          var pinLeft = Math.abs(r.x0 - (m.x - f.w / 2)) < Math.abs(r.x1 - (m.x + f.w / 2));
          f.w = clamp(_snapDim(r.x1 - r.x0), FOOTPRINT.minW, FOOTPRINT.maxW);
          m.x = pinLeft ? (r.x0 + f.w / 2) : (r.x1 - f.w / 2);
        }
        if (r.z0 != null) {
          var pinNear = Math.abs(r.z0 - (m.z - f.l / 2)) < Math.abs(r.z1 - (m.z + f.l / 2));
          f.l = clamp(_snapDim(r.z1 - r.z0), FOOTPRINT.minL, FOOTPRINT.maxL);
          m.z = pinNear ? (r.z0 + f.l / 2) : (r.z1 - f.l / 2);
        }
      } else if (key === 'terrace') {
        var tov = STATE.terraceOver, tmx = m.x || 0, tmz = m.z || 0;
        if (r.x0 != null) {
          tov.x0 = clamp((tmx - f.w / 2) - r.x0, TERR_LIM.min, TERR_LIM.max);
          tov.x1 = clamp(r.x1 - (tmx + f.w / 2), TERR_LIM.min, TERR_LIM.max);
        }
        if (r.z0 != null) {
          tov.z0 = clamp((tmz - f.l / 2) - r.z0, TERR_LIM.min, TERR_LIM.max);
          tov.z1 = clamp(r.z1 - (tmz + f.l / 2), TERR_LIM.min, TERR_LIM.max);
        }
        rebuildLive();
        return;
      } else {
        /* overhang is the gap between a roof edge and the wall it covers */
        if (SHELTER_FIXED_ROOF) return;   /* SHELTER: the overhang is fixed, no rect may edit it */
        var ov = STATE.roofOver, mx = m.x || 0, mz = m.z || 0;
        if (r.x0 != null) {
          ov.x0 = clamp((mx - f.w / 2) - r.x0, OVER_LIM.min, OVER_LIM.max);
          ov.x1 = clamp(r.x1 - (mx + f.w / 2), OVER_LIM.min, OVER_LIM.max);
        }
        if (r.z0 != null) {
          ov.z0 = clamp((mz - f.l / 2) - r.z0, OVER_LIM.min, OVER_LIM.max);
          ov.z1 = clamp(r.z1 - (mz + f.l / 2), OVER_LIM.min, OVER_LIM.max);
        }
      }
      _clampPitch();
      try { _syncPitchSlider(); } catch (eS) {}
      /* SIZE used to run on a step of its own, so nothing else was on screen
         and nothing else had to be told. Now the walls are: shrink the shell
         under a partition and the partition has to come with it, or it ends up
         standing outside the building it divides. */
      try {
      } catch (eP) {}
      rebuildLive();
    },

    _dims: function (key) {
      var m = STATE.modules[0];
      if (!m) return [];
      var f = STATE.footprint, x = m.x || 0, z = m.z || 0;
      var met = !!STATE.isMetric, F = global.ConfiguratorUI.fmtDim;
      if (key === 'build') {
        return [
          { text: F(f.w, met), x: x, z: z - f.l / 2 - 0.4 },
          { text: F(f.l, met), x: x + f.w / 2 + 0.4, z: z }
        ];
      }
      var rr = _RoofRect(f.w, f.l, STATE.roofOver);
      if (key === 'terrace') {
        var trr = _RoofRect(f.w, f.l, STATE.terraceOver);
        return [
          { text: F(STATE.terraceOver.x1, met), x: x + trr.x1 + 0.3, z: z },
          { text: F(STATE.terraceOver.z1, met), x: x, z: z + trr.z1 + 0.3 }
        ];
      }
      return [
        { text: F(STATE.roofOver.x1, met), x: x + rr.x1 + 0.3, z: z },
        { text: F(STATE.roofOver.z0, met), x: x, z: z + rr.z0 - 0.3 }
      ];
    },

    bind: function () {
      if (this._h) return;
      if (!global.ConfiguratorUI || !global.ConfiguratorUI.handles) {
        _noteError('ConfiguratorUI.handles missing -- asset not loaded?');
        return;
      }
      var self = this;
      this._h = global.ConfiguratorUI.handles({
        id: 'sauna',
        y: 0.04,
        /* Handles belong to SIZE and nowhere else: left up through the wizard
           they invite dragging the footprint while you meant to place a window,
           and the ghost covers the very thing the later steps are about. Shut,
           there is nothing to grab -- the view is just the building. */
        active: function () { return _editable('layout') || _editable('roof') || (_editable('look') && STATE.terraceOn); },
        rects: function () { return self._rects(); },
        apply: function (key, r) { self._apply(key, r); },
        dims: function (key) { return self._dims(key); },
        /* THE DIMENSION IS THE THING BEING DRAGGED, so it is sized on screen
           and not in the world: absent, the kit drew a world-sized sprite that
           on a shelter this size was a speck nobody could read (Marek: koty
           nejsou videt). The same fraction the house line uses -- a 22 px
           pill on a 728 px canvas -- so the two rails read alike. */
        dimScreen: 22 / 728,
        onCommit: function () {
          _dragCommit();
          savePref();
          _dbg('footprint -> w=' + STATE.footprint.w.toFixed(2) +
               ' l=' + STATE.footprint.l.toFixed(2) +
               ' over=' + STATE.roofOver.x0.toFixed(2) + '/' + STATE.roofOver.x1.toFixed(2) +
               '/' + STATE.roofOver.z0.toFixed(2) + '/' + STATE.roofOver.z1.toFixed(2));
        }
      });
      if (this._h) this._h.bind();
    },

    refresh: function () { if (this._h) this._h.refresh(); }
  };

  var _ModHandles = _ModCtl;


  /* The wizard's path. MODES stays as the expert view -- the numbered steps
     someone who knows what they want can jump between. This is the other
     door: same model, asked in the order a person actually decides.

     Order is not the build order. Openings come BEFORE the interior because
     you cannot place a bench until you know where the window is -- nobody
     sits facing glass in a hot room, and the heater needs a wall without an
     opening. The old STEP 3/4 had it backwards.

     mode is what each step drives; the lib already gates every control on it,
     so the wizard is a second way of setting the same number. */

  /* =========================================================================
     ASSEMBLY -- THE ADU'S WALK, ON THE SHELTER'S BODIES.

     The same thing the house line does, done the same way (Marek: assembly
     musime udelat stejne jako mame v ADU). Step 1 is the exploded view; from
     step 2 every item flies in from the direction it was exploded to -- the
     roof and everything above from above, the walls from outside their own
     face, the footings from below -- and lands on the piece before it. The
     name over the button is the CENTRAL BoQ's, resolved through the budget
     bridge, so it reads in the page's language and with the same words the
     budget uses; the locale key is the fallback for an item with no code.

     WHAT A STEP IS HERE. The house line tags every body with a catalogue
     code as it is built. This product's builders tag bodies with a viz key
     (userData.viz) -- foundation, wallframe, roofframe, the roof's layers --
     so the steps are those keys, in the order below, each pointed at the
     code that names it. The old SaunaBuild that played a canned sequence is
     deleted; this replaces it whole.
     ========================================================================= */
  var ASM_STEPS = [
    { viz: 'foundation',        dir: 'bottom', code: function () { return (STATE.foundation === 'pads') ? 'FCO00012' : 'FGR00001'; }, i18n: 'aframe_foundation', def: 'Foundation' },
    { viz: 'wallframe',         dir: 'side',   code: 'KVH10100', i18n: 'posts',            def: 'Posts' },
    { viz: 'roofframe',         dir: 'top',    code: 'KVH60160', i18n: 'roof_framing',     def: 'Roof framing' },
    { viz: 'layer_roof_hydro',  dir: 'top',    code: 'ROR00002', i18n: 'roof_underlay',    def: 'Roof underlay' },
    { viz: 'layer_roof_batten', dir: 'top',    code: 'KVR03050', i18n: 'roof_battens',     def: 'Roof battens' },
    { viz: 'roof',              dir: 'top',    code: '',         i18n: 'roof',             def: 'Roof' },
    { viz: 'layer_roof_top',    dir: 'top',    code: function () { return _covDef().code; }, i18n: 'roof_covering', def: 'Roof covering' },
    { viz: 'layer_roof_flash',  dir: 'top',    code: 'ROD00201', i18n: 'roof',             def: 'Roof' },
    { viz: 'layer_roof_gutter', dir: 'side',   code: 'ROG01251', i18n: 'gutters',          def: 'Gutters' },
    { viz: 'layer_wall_batten', dir: 'side',   code: 'KVH08060', i18n: 'layer_vented_battens', def: 'Screen rails' },
    { viz: 'layer_wall_clad',   dir: 'side',   code: 'FVW01351', i18n: 'sauna_ui_cladding', def: 'Cladding' },
    { viz: 'facade',            dir: 'side',   code: 'FVW01351', i18n: 'sauna_ui_cladding', def: 'Cladding' },
    { viz: 'railing',           dir: 'side',   code: 'FEC00008', i18n: 'fence',            def: 'Fence' }
  ];
  var _asm2 = { on: false, k: 0, steps: [], anim: null, flying: null };
  var _asmNameFrom = '';

  function _asmRect() {
    var m = STATE.modules[0] || {}, f = STATE.footprint || {};
    var hx = _extW() / 2, hz = (f.l || 0) / 2, x = m.x || 0, z = m.z || 0;
    return { x0: x - hx, x1: x + hx, z0: z - hz, z1: z + hz };
  }
  function _asmCodeOf(s) { return (typeof s.code === 'function') ? (s.code() || '') : (s.code || ''); }

  /* THE NAME IS THE BUDGET'S OWN, DIMENSIONS INCLUDED (Marek). SaunaBoQ.catName
     is what the budget rows are labelled with: the central sheet's name in the
     page's language, with the item's dimension put in for the pi mark -- and
     the baked catalogue behind it when the sheet has not landed. The locale
     key only for a step that has no code at all. */
  function _asmName(code, i18n, def) {
    _asmNameFrom = 'fallback';
    try {
      var bq = global.SaunaBoQ;
      if (bq && bq.catName && code) {
        var nm = bq.catName(code, null);
        if (nm && nm !== code) { _asmNameFrom = 'boq ' + code; return nm; }
      }
    } catch (e) {}
    return _t(i18n, def);
  }
  function _asmStepLabel(s) { return s ? _asmName(_asmCodeOf(s), s.i18n, s.def) : ''; }

  /* collect: every mesh in the stage, grouped by its viz key, in the order
     of the table; keys the table does not know go last, in build order */
  function _asmCollect() {
    var eng = global.SaunaEngine; if (!eng || !eng.group) return [];
    var byViz = {}, order = [], i;
    /* ONLY WHAT IS ON SCREEN. The viz stage (the eye button) and the builders
       hide bodies on purpose -- this file says so itself, a few thousand lines
       up: visible=true on every tagged mesh is not the base view, it is what
       other systems had deliberately hidden. The first walk did exactly that
       and a slab nobody had ever seen appeared under the top beams (Marek).
       A hidden body, or one under a hidden parent, is not a step; and every
       body remembers how it was so the walk can put it back, not turn it on. */
    function shown(o) { var p = o; while (p) { if (p.visible === false) return false; p = p.parent; } return true; }
    eng.group.traverse(function (o) {
      if (!o.isMesh) return;
      var v = (o.userData && o.userData.viz) || '';
      if (!v || o.userData.ctl || o.userData.arSkip) return;
      if (!shown(o)) return;
      o.userData.__asmVis = true;
      if (!byViz[v]) { byViz[v] = []; order.push(v); }
      byViz[v].push(o);
    });
    var out = [], seen = {};
    for (i = 0; i < ASM_STEPS.length; i++) {
      var s = ASM_STEPS[i]; if (!byViz[s.viz]) continue;
      seen[s.viz] = true;
      out.push({ viz: s.viz, dir: s.dir, code: _asmCodeOf(s), i18n: s.i18n, def: s.def, meshes: byViz[s.viz] });
    }
    for (i = 0; i < order.length; i++) {
      if (seen[order[i]]) continue;
      out.push({ viz: order[i], dir: 'top', code: '', i18n: order[i], def: order[i], meshes: byViz[order[i]] });
    }
    return out;
  }

  function _asmBodyCtr(T, m, bx, c) {
    var p = m.userData.__asmPos || (m.userData.__asmPos = { x: m.position.x, y: m.position.y, z: m.position.z });
    if (!m.userData.__asmCtr) {
      try { bx.setFromObject(m); bx.getCenter(c); m.userData.__asmCtr = { x: c.x - (m.position.x - p.x), y: c.y - (m.position.y - p.y), z: c.z - (m.position.z - p.z) }; }
      catch (e2) { m.userData.__asmCtr = { x: p.x, y: p.y, z: p.z }; }
    }
    return m.userData.__asmCtr;
  }
  function _asmSideOf(q, B) {
    var dx0 = q.x - B.x0, dx1 = B.x1 - q.x, dz0 = q.z - B.z0, dz1 = B.z1 - q.z;
    var mn = Math.min(dx0, dx1, dz0, dz1);
    return mn === dx0 ? 'x0' : mn === dx1 ? 'x1' : mn === dz0 ? 'z0' : 'z1';
  }
  var SIDEV = { x0: { x: -1, z: 0 }, x1: { x: 1, z: 0 }, z0: { x: 0, z: -1 }, z1: { x: 0, z: 1 } };

  function _asmLand() {
    if (_asm2.anim) { try { global.cancelAnimationFrame(_asm2.anim); } catch (e) {} _asm2.anim = null; }
    var q, S = _asm2.flying || [];
    for (q = 0; q < S.length; q++) { var s = S[q]; s.m.position.set(s.x, s.y, s.z); }
    _asm2.flying = null;
  }

  /* THE EXPLODED VIEW stretches from the middle of the building: every body
     moves the way it flies in, further the further it stands from the middle
     along that way, with at least 200 mm between one step and the next going
     the same way -- so a roof comes apart layer by layer */
  var _explodeK = 0, _explodeShown = 0, EXPLODE_GAIN = 0.35;
  function _asmSpread() {
    var T = global.THREE, S = _asm2.steps, i, j, out = [];
    if (!T) return out;
    var B = _asmRect(), bx = new T.Box3(), c = new T.Vector3();
    var cx = (B.x0 + B.x1) / 2, cz = (B.z0 + B.z1) / 2, cy = 0.35;
    var MIN_GAP = 0.20, last = {};
    for (i = 0; i < S.length; i++) {
      var st = S[i], stepMax = {};
      for (j = 0; j < st.meshes.length; j++) {
        var m = st.meshes[j], q = _asmBodyCtr(T, m, bx, c), key, mag, v;
        if (st.dir === 'side') {
          var face = _asmSideOf(q, B), sd = SIDEV[face];
          key = 'side:' + face;
          mag = Math.abs(sd.x ? (q.x - cx) : (q.z - cz)) * EXPLODE_GAIN;
          mag = Math.max(mag, (last[key] || 0) + MIN_GAP);
          v = { x: sd.x * mag, y: 0, z: sd.z * mag };
        } else if (st.dir === 'bottom') {
          key = 'bottom';
          mag = Math.max(Math.max(0, cy - q.y) * EXPLODE_GAIN, (last[key] || 0) + MIN_GAP);
          v = { x: 0, y: -mag, z: 0 };
        } else {
          key = 'top';
          mag = Math.max(Math.max(0, q.y - cy) * EXPLODE_GAIN, (last[key] || 0) + MIN_GAP);
          v = { x: 0, y: mag, z: 0 };
        }
        stepMax[key] = Math.max(stepMax[key] || 0, mag);
        out.push({ m: m, p: m.userData.__asmPos, v: v });
      }
      var kq;
      for (kq in stepMax) if (stepMax.hasOwnProperty(kq)) last[kq] = Math.max(last[kq] || 0, stepMax[kq]);
    }
    return out;
  }
  function setExplode(k, animate) {
    _asmLand();
    _explodeK = Math.max(0, Math.min(4, Number(k) || 0));
    var from = _explodeShown, to = _explodeK, spread = _asmSpread();
    function put(kk) {
      var q;
      for (q = 0; q < spread.length; q++) { var s = spread[q]; s.m.position.set(s.p.x + s.v.x * kk, s.p.y + s.v.y * kk, s.p.z + s.v.z * kk); }
      _explodeShown = kk;
    }
    if (!animate) { put(to); return to; }
    var t0 = (global.performance && global.performance.now) ? global.performance.now() : Date.now(), DUR = 800;
    function tick() {
      var now = (global.performance && global.performance.now) ? global.performance.now() : Date.now();
      var u = Math.min(1, (now - t0) / DUR), e = 1 - Math.pow(1 - u, 3);
      put(from + (to - from) * e);
      if (u < 1) _asm2.anim = global.requestAnimationFrame(tick); else _asm2.anim = null;
    }
    _asm2.anim = global.requestAnimationFrame(tick);
    return to;
  }

  /* THE NAME SITS AT THE BUTTON, as on the ADU: the button's title is the
     step's name and the tooltip flashes for a moment after the tap */
  var _asmTipTimer = 0;
  function _asmPillShow(text) {
    var b = $('cf-btn-assembly'); if (!b) return;
    b.title = text || _t('assembly', 'Assembly');
    if (_asmTipTimer) { try { global.clearTimeout(_asmTipTimer); } catch (e) {} _asmTipTimer = 0; }
    if (text) {
      b.classList.add('show-tip');
      _asmTipTimer = global.setTimeout(function () { try { b.classList.remove('show-tip'); } catch (e2) {} }, 2500);
    } else b.classList.remove('show-tip');
  }
  function _asmCounter() {
    try { var el = $('cf-stat-assembly'); if (el) el.textContent = (_asm2.k > 0 ? _asm2.k : 0) + '/' + (_asm2.steps.length + 1); } catch (e) {}
  }

  /* k = 0 the whole building; 1 the exploded view; n >= 2 the items up to
     n - 1 in place and item n - 1 flying in from its own direction */
  function _asmApply(animate) {
    var T = global.THREE, S = _asm2.steps, k = _asm2.k, i, j;
    _asmLand();
    if (k <= 0) {
      for (i = 0; i < S.length; i++) for (j = 0; j < S[i].meshes.length; j++) S[i].meshes[j].visible = (S[i].meshes[j].userData.__asmVis !== false);
      if (_explodeK > 0) setExplode(0);
      _asmPillShow('');
      return;
    }
    if (k === 1) {
      for (i = 0; i < S.length; i++) for (j = 0; j < S[i].meshes.length; j++) S[i].meshes[j].visible = true;
      setExplode(2, true);
      _asmPillShow(_t('exploded_view', 'Exploded view'));
      return;
    }
    if (_explodeK > 0) setExplode(0);
    var n = k - 1;
    for (i = 0; i < S.length; i++) for (j = 0; j < S[i].meshes.length; j++) S[i].meshes[j].visible = (i < n);
    var step = S[n - 1]; if (!step) return;
    _asmPillShow(_asmStepLabel(step));
    if (!animate || !T) return;
    var B = _asmRect(), starts = [], bx = new T.Box3(), cc = new T.Vector3();
    for (j = 0; j < step.meshes.length; j++) {
      var m = step.meshes[j], p = m.userData.__asmPos || m.position, off = { x: 0, y: 0, z: 0 };
      if (step.dir === 'side') { var sv = SIDEV[_asmSideOf(_asmBodyCtr(T, m, bx, cc), B)]; off.x = sv.x * 2.5; off.z = sv.z * 2.5; }
      else if (step.dir === 'bottom') off.y = -3.0;
      else off.y = 3.0;
      starts.push({ m: m, x: p.x, y: p.y, z: p.z, ox: off.x, oy: off.y, oz: off.z });
      m.position.set(p.x + off.x, p.y + off.y, p.z + off.z);
    }
    _asm2.flying = starts;
    var t0 = (global.performance && global.performance.now) ? global.performance.now() : Date.now(), DUR = 700;
    function tick() {
      var now = (global.performance && global.performance.now) ? global.performance.now() : Date.now();
      var u = Math.min(1, (now - t0) / DUR), e = 1 - Math.pow(1 - u, 3), q;
      for (q = 0; q < starts.length; q++) { var s = starts[q]; s.m.position.set(s.x + s.ox * (1 - e), s.y + s.oy * (1 - e), s.z + s.oz * (1 - e)); }
      if (u < 1) _asm2.anim = global.requestAnimationFrame(tick); else { _asm2.anim = null; _asm2.flying = null; }
    }
    _asm2.anim = global.requestAnimationFrame(tick);
  }

  /* the rail button and the step: one tap forward, round to the whole
     building at the end; entering the step lands on the exploded view */
  function asmNext() {
    if (!_asm2.on) {
      /* the bodies must exist and be current before they are collected */
      try { rebuild(); } catch (eRb) {}
      _asm2.on = true;
      _asm2.steps = _asmCollect();
      _asmReport();
    }
    var N = _asm2.steps.length + 1;
    _asm2.k = (_asm2.k >= N) ? 0 : _asm2.k + 1;
    _asmApply(_asm2.k > 1);
    if (_asm2.k === 0) { _asm2.on = false; _asm2.steps = []; }
    _asmCounter();
    var s = _asm2.k > 1 ? _asm2.steps[_asm2.k - 2] : null, lb = s ? _asmStepLabel(s) : '';
    _log('assembly ' + (_asm2.k === 0 ? ('0/' + N + ' -- the whole building') : _asm2.k === 1 ? ('1/' + N + ' exploded view') :
         (_asm2.k + '/' + N + ' ' + lb + ' [' + _asmNameFrom + '] (' + s.meshes.length + ' bodies, ' + s.dir + ', ' + s.viz + ')')));
    return _asm2.k;
  }
  /* what the walk found, once, when it starts */
  function _asmReport() {
    var S = _asm2.steps, i, unknown = [], br = global.ConfiguratorBudgetBridge;
    for (i = 0; i < S.length; i++) {
      var it = (br && br.getBoqItem && S[i].code) ? br.getBoqItem(S[i].code) : null;
      if (S[i].code && !(it && it.name)) unknown.push(S[i].code + ' (' + S[i].viz + ')');
    }
    _log('assembly steps ' + S.length + ' | bridge ' + (br && br.getBoqItem ? 'present' : 'MISSING') +
         (unknown.length ? (' | codes the bridge does not know: ' + unknown.join(', ')) : ''));
    /* NUMBERED THE WAY THE BUTTON COUNTS: 1 is the exploded view, the items
       run from 2. A viz with no bodies is not a step at all, so the numbers
       move with what the shelter actually has -- which is why naming a step
       by its number only works off this list (Marek). */
    _log('assembly walk: 1 = exploded view');
    for (i = 0; i < S.length; i++) {
      _log('  ' + (i + 2) + '. ' + _asmStepLabel(S[i]) + '  [' + S[i].viz + ', ' + S[i].meshes.length + ' bodies, ' + S[i].dir + ']');
    }
    try {
      var T = global.THREE, bx = new T.Box3(), sz = new T.Vector3(), big = [];
      for (i = 0; i < S.length; i++) {
        var best = null, bestA = 0, j;
        for (j = 0; j < S[i].meshes.length; j++) {
          bx.setFromObject(S[i].meshes[j]); bx.getSize(sz);
          var a = sz.x * sz.z; if (a > bestA) { bestA = a; best = sz.clone(); }
        }
        if (best) big.push(S[i].viz + ' ' + Math.round(best.x * 100) / 100 + 'x' + Math.round(best.y * 100) / 100 + 'x' + Math.round(best.z * 100) / 100);
      }
      _log('assembly biggest body per step: ' + big.join(' | '));
    } catch (eB) {}
  }
  /* stop the walk from outside -- the building goes back together */
  function _asmLeave() {
    if (!_asm2.on) return;
    _asmLand();
    _asm2.k = 0;
    _asmApply(false);
    _asm2.on = false;
    _asm2.steps = [];
    _asmCounter();
  }
  /* a rebuild while walking: the bodies are new, so the steps are recollected
     and the walk resumes at the same step */
  function _asmRebuilt() {
    if (!_asm2.on) return;
    _asm2.steps = _asmCollect();
    _explodeShown = 0;
    _asmApply(false);
    _asmCounter();
  }

  /* Line-art glyphs, 24px grid, stroked not filled -- they inherit the button's
     colour, so one icon works on white, grey and red without a second asset. */
  function _ico(d) {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" ' +
           'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + d + '</svg>';
  }

  /* A COLOUR SCHEME IS SHOWN IN ITS OWN COLOURS, not in a word or a paint pot.
     Three tiles, each a little elevation of the thing itself: the roof over
     the facade, drawn in that scheme's roof and facade colour, with the deck
     as a line under it. You pick by looking, which is the only way anybody
     picks a colour (Marek: barvy do Edit, stejna nabidka barev). */
  function _hex6(n) { return '#' + ('000000' + (n >>> 0).toString(16)).slice(-6); }
  /* A TILE THAT REDRAWS ITSELF. The kit reads ic.svg and ic.title as plain
     strings and rebuilds the icon row on every refresh -- which it does after
     every tap. So a tile whose picture must follow the state carries getters:
     each read returns the string for the state as it is now, and the kit
     never has to know it is looking at anything but a string. */
  function _liveTile(kind, svgFn, titleFn, tap) {
    var t = { kind: kind, tap: tap };
    Object.defineProperty(t, 'svg',   { get: svgFn,   enumerable: true });
    Object.defineProperty(t, 'title', { get: titleFn, enumerable: true });
    return t;
  }
  var ICO = {
    pin:      _ico('<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>'),
    /* three courses of covering on a slope */
    covering: _ico('<path d="M3 15L12 6l9 9"/><path d="M6 12l3 3M9 9l3 3M12 12l3 3M15 9l3 3" opacity="0.7"/><path d="M4.5 18h15" opacity="0.5"/>'),
    resize:   _ico('<path d="M4 4h6M4 4v6M4 4l7 7"/><path d="M20 20h-6M20 20v-6M20 20l-7-7"/>'),
    /* The room is context and the wall is the subject, so the room is drawn
       faint and the wall runs a little past it. Two icons that differ only in
       which way the line goes -- which is the entire choice being offered. */
    scenery:  _ico('<path d="M12 3l4 6h-2.5l3.5 5h-10l3.5-5H8.5z"/><path d="M12 14v6"/><path d="M3 20h18" opacity="0.5"/>'),
    asmSauna: _ico('<path d="M5 4v16M9 4v16M13 4v16"/><path d="M13 4h6v16h-6" opacity="0.38"/><circle cx="17" cy="12" r="1.6"/>'),
    asmPartS: _ico('<path d="M9 3v18M15 3v18"/><circle cx="5" cy="12" r="1.6"/><path d="M4 8c1-1 1 1 2 0M4 16c1-1 1 1 2 0" opacity="0.6"/>'),
    asmFloorS: _ico('<path d="M3 15h18M3 19h18"/><circle cx="7" cy="11" r="1.4"/><path d="M11 11h8" opacity="0.5"/>'),
    asmCeilS:  _ico('<path d="M3 5h18M3 9h18"/><circle cx="7" cy="13" r="1.4"/><path d="M11 13h8" opacity="0.5"/>'),
    asmFloor: _ico('<path d="M3 15h18M3 19h18"/><path d="M6 15v4M12 15v4M18 15v4" opacity="0.5"/>'),
    asmCeil:  _ico('<path d="M3 5h18M3 9h18"/><path d="M6 5v4M12 5v4M18 5v4" opacity="0.5"/>'),
    asmRoof:  _ico('<path d="M3 14L12 5l9 9"/><path d="M6 11v6M12 7v10M18 11v6" opacity="0.4"/>'),
    asmSaunaInt: _ico('<path d="M5 4v16M9 4v16"/><circle cx="7" cy="12" r="1.4"/><path d="M13 4v16M17 4v16" opacity="0.5"/>'),
    asmStd:   _ico('<path d="M6 4v16M11 4v16M16 4v16M20 4v16"/>'),
    asmPart:  _ico('<path d="M8 4v16M16 4v16"/><path d="M10 8h4M10 12h4M10 16h4" opacity="0.5"/>'),
    wallV:    _ico('<rect x="3" y="4" width="18" height="16" rx="1.5" opacity="0.38"/><path d="M12 3v18"/>'),
    wallH:    _ico('<rect x="3" y="4" width="18" height="16" rx="1.5" opacity="0.38"/><path d="M2 12h20"/>'),
    rooms:    _ico('<rect x="3" y="4" width="18" height="16" rx="1.5"/><path d="M12 4v16"/>'),
    door:     _ico('<path d="M4 21V4a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v17"/><path d="M2 21h18"/><circle cx="12" cy="12" r="1"/>'),
    window:   _ico('<rect x="4" y="4" width="16" height="16" rx="1"/><path d="M12 4v16M4 12h16"/>'),
    bench:    _ico('<path d="M3 10h18M3 14h18"/><path d="M5 14v6M19 14v6"/><path d="M3 10V7"/>'),
    heater:   _ico('<rect x="6" y="9" width="12" height="11" rx="1.5"/><path d="M9 6c0-1.2 1-1.5 1-3M12 6c0-1.2 1-1.5 1-3M15 6c0-1.2 1-1.5 1-3"/>'),
    roof:     _ico('<path d="M2 12L12 4l10 8"/><path d="M5 12v8h14v-8"/>'),
    base:     _ico('<path d="M3 20h18"/><path d="M7 20v-6M17 20v-6"/><path d="M5 14h14"/>'),
    facade:   _ico('<rect x="3" y="3" width="18" height="18" rx="1.5"/><path d="M3 9h18M3 15h18"/>'),
    boards:   _ico('<rect x="3" y="3" width="18" height="18" rx="1.5"/><path d="M3 7h18M3 10h18M3 13h18M3 16h18M3 19h18"/>'),
    slats:    _ico('<rect x="3" y="3" width="18" height="18" rx="1.5"/><path d="M3 8h18M3 13h18M3 18h18"/>'),
    envelope: _ico('<rect x="3" y="3" width="18" height="18" rx="1.5"/><rect x="7" y="7" width="10" height="10" rx="1"/>'),
    pergola:  _ico('<path d="M3 7h18"/><path d="M5 7v13M19 7v13"/><path d="M8 7v3M12 7v3M16 7v3"/>'),
    terrace:  _ico('<rect x="3" y="8" width="18" height="12" rx="1"/><path d="M7 8v12M11 8v12M15 8v12"/>'),
    section:  _ico('<path d="M4 4h16v16H4z"/><path d="M4 10h16"/><path d="M8 4v6"/><path d="M14 4v6"/>'),
    list:     _ico('<path d="M8 6h13M8 12h13M8 18h13"/><path d="M3 6h.01M3 12h.01M3 18h.01"/>'),
    scissors: _ico('<circle cx="6" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><line x1="20" y1="4" x2="8.12" y2="15.88"/><line x1="14.47" y1="14.48" x2="20" y2="20"/><line x1="8.12" y1="8.12" x2="12" y2="12"/>')
  };

  /* THE CLIMATE ZONE IS A PLACE, NOT A NUMBER. The select read "0.8 m" -- a
     frost depth, which answers a question nobody has asked at that point. The
     band is shown instead and the metres stay underneath, so STATE.climateZone,
     the BoQ and the PDF are untouched. The keys were already in en.default and
     unused. */
  var FROST_ZONES = [
    { d: 0.0, i18n: 'base_frost_zone_subtropical', def: 'Subtropical' },
    { d: 0.6, i18n: 'base_frost_zone_warm',        def: 'Warm temperate' },
    { d: 0.8, i18n: 'base_frost_zone_temperate',   def: 'Temperate' },
    { d: 1.0, i18n: 'base_frost_zone_cool',        def: 'Cool temperate' },
    { d: 1.2, i18n: 'base_frost_zone_cold',        def: 'Cold / boreal' },
    { d: 1.5, i18n: 'base_frost_zone_arctic',      def: 'Arctic' }
  ];
  function _frostZoneNames() {
    var sel = document.getElementById('cf-map-frost-zone');
    if (!sel || sel.dataset.named === '1') return;
    var i, o;
    for (i = 0; i < sel.options.length; i++) {
      o = sel.options[i];
      if (o.value === '' || o.value === 'auto') {
        o.textContent = _t('base_frost_zone_auto', 'Auto (from address)');
        continue;
      }
      var v = parseFloat(o.value), best = null, bd = 9;
      for (var q = 0; q < FROST_ZONES.length; q++) {
        var dd = Math.abs(FROST_ZONES[q].d - v);
        if (dd < bd) { bd = dd; best = FROST_ZONES[q]; }
      }
      if (best) o.textContent = _t(best.i18n, best.def);
    }
    sel.dataset.named = '1';
  }
  /* what the scenery was before the map was switched on, so switching it off
     returns there instead of landing on whatever comes next in the cycle */
  var _sceneryWas = null;

  var WIZ_STEPS = [
    { key: 'place',    mode: 1, i18n: 'sauna_wiz_1', def: 'PLACE',
      hint: 'sauna_wiz_h1', hintDef: 'Tap the pin to find your garden',
      /* ON A DESKTOP THE DIALOG BECOMES THE STEP. Open Place and the address
         field, its suggestions and the climate zone are simply there -- no
         second window to open and dismiss. The dialog is MOVED, not rebuilt, so
         every handler MapManager already bound goes on working; on a phone it
         is untouched and stays a dialog, because there is nowhere else for it
         to go (Marek). */
      panel: function (pane, api) {
        if (!api || typeof api.shape !== 'function' || api.shape() !== 'drawer') return;
        var bd = document.getElementById('cf-map-modal-backdrop');
        if (!bd) return;
        if (bd.parentNode !== pane) {
          bd.classList.add('cfg-ui-in-step');
          bd.classList.add('open');
          pane.appendChild(bd);
        }
        _frostZoneNames();
      },
      /* THE PIN SWITCHES THE MAP, and says so by being lit or not. On a desktop
         the address itself is in the step, so opening a dialog from here was a
         second door to the same room; the useful thing this button can do is
         show or hide the map under the model (Marek). */
      icons: [{ svg: ICO.pin, kind: 'toggle', title: _t('sauna_ui_address', 'Address'),
                on: function () { return (STATE.scenery || 'map') === 'map'; },
                tap: function () {
                  try {
                    if (_wiz && _wiz.shape && _wiz.shape() !== 'drawer' &&
                        _MapManager && _MapManager._openModal) { _MapManager._openModal(); return; }
                    var S = global.SaunaScenery;
                    if ((STATE.scenery || 'map') === 'map') {
                      STATE.scenery = _sceneryWas || 'meadow';
                    } else {
                      _sceneryWas = STATE.scenery;
                      STATE.scenery = 'map';
                    }
                    if (S && S.apply) S.apply(); else if (S && S.rebuild) S.rebuild();
                    savePref(); rebuild(); refreshUI();
                  } catch (e) {}
                } },
              { svg: ICO.scenery, kind: 'toggle', title: _t('sauna_ui_scenery', 'Scenery'),
                on: function () { return (STATE.scenery || 'map') !== 'map'; },
                tap: function () { try { global.SaunaScenery.cycle(); } catch (e) {} } }] },

    /* Roof before size: the shape is what the building IS, the dimensions are
       how big it is -- and people decide in that order. It also costs nothing,
       because _clampPitch() already runs whenever the footprint moves, so a
       pitch chosen here survives a later resize or gets pulled down to fit. */
    { key: 'roof',     mode: 2, i18n: 'sauna_wiz_6', def: 'ROOF & BASE',
      hint: 'sauna_wiz_h6', hintDef: 'Shape, pitch, footings',
      panel: function (pane) { _roofPanel(pane); },
      /* key / word / options / slider are for the icon strip (shelter-v114,
         ui-v200): the arrows walk the options both ways, the lit one's word
         over them; the pitch rides the roof's arrows as their slider */
      icons: [{ key: 'rooftype', svg: ICO.roof, kind: 'cycle', title: _t('roof_variant_selector', 'Roof type'), word: _t('roof', 'Roof'), tap: nextRoofType,
                options: function () { return _roofTypeOpts(); },
                slider: { core: _pitchSlider, sync: function () { _syncPitchSlider(); }, read: _pitchBubbleText, on: function () { return true; } } },
              /* the covering, next to the shape it goes on; the title says
                 which one is on, because a cycle button has to */
              _with(_liveTile('cycle',
                function () { return ICO.covering; },
                function () { var c = _covDef(); return _t(c.i18n, c.def); },
                nextCovering),
                { key: 'covering', word: _t('roof_covering', 'Roof covering'), options: function () { return _covOpts(); } }),
              { key: 'foundation', svg: ICO.base, kind: 'cycle', title: _t('aframe_foundation', 'Foundation'), tap: nextFoundation,
                options: function () { return _foundOpts(); } }] },

    /* SIZE, PARTITIONS, INTERIOR DOORS and ROOMS were four steps, and splitting
       them was a mistake made twice over.

       Once because they are one decision. Nobody sizes a sauna and then finds
       out what fits: they want a hot room and a changing room, so the building
       is as big as those two need. Four steps made the person guess the size,
       walk forward three steps to find out they were wrong, and walk back --
       and the plan they were checking against was not on screen while they
       were fixing it.

       And once because three of the four were the floorplan module's internal
       stages, which existed so that ONE button could cycle through them. A
       wizard is not one button. The stages were an implementation detail wearing
       a step's clothes.

       One drawing, four things you can do to it. Drag a corner and the building
       grows. Take a wall off the icon row and it is a wall. Tap a wall and it is
       a door. Tap a name and it is a room. Nothing to step between, and the
       area of every room updates under your finger while you decide. */

    /* COLOR -- THE ADU'S STEP, THE ADU'S PANEL. Same key, same label, same
       hint, same panel of rows-of-dots; no tiles at all, the panel is the step
       (Marek: colour ma byt udelane jako u ADU, stejne UI, vlastni step).
       The three schemes are the first row, because they are curation this
       product has and the ADU has not; under them the slots the ADU has. */
    { key: 'colors',   mode: 5, i18n: 'toggle_colors', def: 'Color',
      hint: 'texture_colour', hintDef: 'Texture / Colour',
      panel: function (pane) { _colorsPanel(pane); },
      /* the strip walks the four slots with the arrows, their dots over
         them -- the rows the panel draws (shelter-v114) */
      colorSlots: function () { return _colorSlotsS(); },
      icons: [] },

    { key: 'look',     mode: 5, i18n: 'sauna_wiz_7', def: 'EXTERIOR',
      hint: 'sauna_wiz_h7', hintDef: 'Cladding and colour',
      panel: function (pane) { _lookPanel(pane); },
      /* on the strip the panel's one slider rides the boards / slats arrows,
         so the step has no + of its own there (shelter-v114) */
      stripPanel: false,
      /* CLADDING ONLY. The colours have a step of their own -- the same one
         the ADU has -- and the envelope tile is gone: it toggled interior
         against exterior insulation, which on a shelter with screen walls is
         no decision at all, and all it visibly did was take the gutters off
         (Marek). */
      icons: [
              /* named by the pattern in force, with the keys the sauna already
                 has for them -- no new key, and the tile says which is on */
              _with(_liveTile('cycle',
                function () { return ICO.facade; },
                function () { return (STATE.facadePattern === 'wood2') ? _t('sauna_facpat_2', 'Wood 2') : _t('sauna_facpat_1', 'Wood 1'); },
                nextFacade),
                { key: 'pattern', word: _t('grp_facade', 'Facade'), options: function () { return _patternOpts(); } }),
              /* short words on the tiles (Marek); the slider under them is the
                 one called Cladding. On the strip the two are one choice with
                 the arrows, the slat gap their slider -- greyed on boards */
              { key: 'boards', group: 'fill', groupTitle: _t('sauna_ui_cladding', 'Cladding'),
                svg: ICO.boards, kind: 'toggle', title: _t('shelter_fill_boards', 'Boards'),
                on: function () { return STATE.fillType !== 'slats'; }, tap: function () { STATE.fillType = 'boards'; rebuild(); savePref(); try { _showSlatSlider(); } catch (eS1) {} } },
              { key: 'slats', group: 'fill', groupTitle: _t('sauna_ui_cladding', 'Cladding'),
                svg: ICO.slats, kind: 'toggle', title: _t('container_perg_1', 'Timber slats'),
                on: function () { return STATE.fillType === 'slats'; }, tap: function () { STATE.fillType = 'slats'; rebuild(); savePref(); try { _showSlatSlider(); } catch (eS2) {} },
                slider: { core: _slatSlider, sync: function () { _syncSlatSlider(); }, read: _slatText,
                          on: function () { return STATE.fillType === 'slats'; } } }
      ] }

    /* no ASSEMBLY step: see the step list -- the walk lives on the rail */
  ];

  /* SHELTER: a door or a window needs a side that is actually there. While
     every side is still open, the drawers would offer openings with nowhere
     to sit, so they stay shut and say why. */

  /* Master toggle for the partition CUT / SCISSORS handles. It used to be owned
     by the shared module so every project read one source of truth; that module
     is gone from this template and its half was a no-op here anyway, so what is
     left is the one that was doing the work: this lib's own SaunaPartitions. */

  /* The ordered bench cycle for the sauna ROOM (bbox x0,z0,x1,z1). Its four
     edges are z0 (min Z), z1 (max Z), x0 (min X), x1 (max X). An edge is usable
     for a bench UNLESS it is an EXTERIOR wall carrying a window or door --
     partition edges are always fine (no glass). Order: per usable edge an
     I-bench (2 rows, 1 row), then per usable corner (a z-edge + an x-edge) an
     L-bench (2, 1). No off state. Empty only if every edge is a glazed/opened
     exterior wall. */

  function _intBar(show) {
    var bar = $('cf-int-bar');
    if (bar) bar.style.display = show ? '' : 'none';
  }

  /* Which stage the interior editor opens in, read off the step. LAYOUT is the
     editor; every other mode-3 step wants the same drawing with nothing to
     grab, so it gets the plan as a plan. */
  function _wizOpen() {
    return !!(global.ConfiguratorUI && global.ConfiguratorUI.wizard && _wiz && _wiz.isOpen());
  }

  var _wiz = null;
  function _initWizard() {
    if (_wiz || !global.ConfiguratorUI || !global.ConfiguratorUI.wizard) return;
    var defs = [], i;
    /* SHELTER: no FLOORPLAN step -- there are no rooms, partitions or interior
       doors to draw. And DOORS & WINDOWS moves BEHIND EXTERIOR, because a
       shelter has no walls until they are switched on with "+" in that step
       (Marek). */
    var _steps = WIZ_STEPS;
    if (SHELTER_SCREEN_WALLS) {
      var _byKey = {};
      for (i = 0; i < WIZ_STEPS.length; i++) _byKey[WIZ_STEPS[i].key] = WIZ_STEPS[i];
      _steps = [];
      /* COLOR THIRD, before EXTERIOR -- the same order as the house line, so
         the same thumb lands on the same step on every product (Marek) */
      /* no DOORS & WINDOWS and no ASSEMBLY step on a shelter: the drawer's
         assembly step exists to show wall build-ups, and a shelter has none --
         the walk is driven from the rail button alone (Marek) */
      ['place', 'roof', 'colors', 'look'].forEach(function (k9) { if (_byKey[k9]) _steps.push(_byKey[k9]); });
    }
    for (i = 0; i < _steps.length; i++) {
      (function (w) {
        defs.push({
          key: w.key,
          label: _t(w.i18n, w.def),
          hint: _t(w.hint, w.hintDef),
          /* THE FIELD NEVER REACHED THE WIZARD. A step is not handed over as
             itself -- this builds a NEW object and copies the fields it knows
             about -- so anything added to the step definition is dropped here
             without a sound unless it is named again (Marek). */
          slider: w.slider,
          panel: w.panel,
          icons: w.icons || [],
          /* the icon strip's (shelter-v114) -- named here, or dropped */
          colorSlots: w.colorSlots,
          stripPanel: w.stripPanel,
          exit: function () {},
          enter: function () {
            setMode(w.mode);
            /* the step owns the interior stage, so its own bar has no job */
            _intBar(false);
            /* No setStage() here any more. configurator-floorplan.js is wired
               and its Partitions never bound a pointer -- _ensure() is called
               from nowhere -- so its stages drew a second, dead set of walls
               into SaunaEngine.group, which the next rebuild() wiped anyway.
               Two partition stores, one of them read by nothing. The module
               stays loaded at stage 0, where it renders nothing, until it is
               either given the sauna's data or taken out. */
            /* The address dialog waits for the pin. Throwing a modal at someone
               the moment they arrive at a step takes the choice away -- they may
               only want to look. */

          }
        });
      })(_steps[i]);
    }
    _wiz = global.ConfiguratorUI.wizard({
      steps: defs,
      /* the wizard IS the banner now -- the old info bar would only be a second
         place saying the same things, and two of anything drift apart */
      hideBar: true,
      onStep: function (key) {
        _dbg('wizard -> ' + key);
        try { if (global.SaunaFootprint) global.SaunaFootprint.refresh(); } catch (e) {}
        try { _showPitchSlider(); } catch (e2) {}
        /* the pergola ghost keys on the step now, so the step change is what
           has to redraw it -- entering EXTERIOR summons it, leaving clears it */
      },
      onExit: function () {
        /* Back to what the customer bought: their configuration, fitouts and
           all, with none of the editing scaffolding. Whatever step they left
           from -- a bare cabin in SIZE, a wall sample in ASSEMBLY -- is a way of
           working, not a thing to be left looking at. Mode 5 is the finished
           view, so the mode gates take over from here. */
        STATE.showDetail = false;
        setMode(5);
        rebuild();
        _panToCabin();
        try { if (global.SaunaFootprint) global.SaunaFootprint.refresh(); } catch (e) {}
        try { _showPitchSlider(); } catch (e2) {}
        _dbg('wizard closed -> finished view');
      }
    });
  }

  var MODES = [
    { key: 1, i18n: 'container_mode_1', def: 'Layout' },
    { key: 2, i18n: 'container_mode_2', def: 'Foundations' },
    { key: 3, i18n: 'container_mode_3', def: 'Interior' },
    { key: 4, i18n: 'container_mode_4', def: 'Windows & doors' },
    { key: 5, i18n: 'container_mode_5', def: 'Finished' },
    { key: 6, i18n: 'nearby_type_pergola', def: 'Pergola' }
  ];
  function setMode(n, silent) {
    var prev = STATE.mode | 0;
    STATE.mode = clamp(n | 0, 1, 6);
    /* stepping INTO or OUT OF the floorplan step changes who owns the drawing,
       so the 2D overlay has to answer straight away rather than wait for the
       next view switch */
    try {
      if (global.SaunaPlan2D) {
        if (STATE.view === '2d' && (STATE.mode | 0) !== 3) global.SaunaPlan2D.show();
        else global.SaunaPlan2D.hide();
      }
    } catch (eP4) {}
    if (prev === 3 && STATE.mode !== 3 && global.SaunaPartitions) global.SaunaPartitions.exitEditor(true);
    rebuild();
    /* Guarded on whether the editor is actually open, not on whether the mode
       NUMBER changed. A reload restores STATE.mode = 3 from the pref, so the
       first setMode(3) the wizard runs has prev === 3 and did nothing at all --
       the editor never entered, _floor stayed -1, and _refreshOverlay returns on
       its first line when _floor is -1. An empty step you cannot get out of.
       prev was always the wrong question: the answer it stands in for is "is it
       already in", and now that is the question asked. */
    if (!silent) savePref();
    _log('mode -> ' + STATE.mode);
    try { _showPitchSlider(); } catch (ePs) {}
    try { if (global.SaunaFootprint) global.SaunaFootprint.refresh(); } catch (eFr) {}
  }
  function nextMode() { setMode((STATE.mode % 6) + 1); }
  /* Tallest apex a garden sauna is allowed to reach. The pitch ceiling falls
     out of this rather than being a flat 45: a 5 m wide mono roof at 45 degrees
     puts the ridge at 7.66 m, which is a tower, not a cabin. */
  var APEX_MAX = 5.0;

  /* Horizontal run the rise is measured over. Mono climbs across the whole
     roof width, gable only across half of it. */
  function _pitchBase() {
    /* Wall span, not roof rectangle. The apex stands over the walls now, so
       the overhang has no say in how high the roof can go -- and the pitch
       ceiling stops silently shifting under the person dragging the eaves. */
    var f = STATE.footprint;
    return (STATE.roofType === 'gable') ? (f.w / 2) : f.w;
  }
  /* Floor is per roof type, not a shared constant. A mono roof only has to
     shed water, so 2 degrees is enough. A gable is a covering job: below about
     10 degrees no shingle or sheet system is warranted, and the two planes stop
     reading as a roof at all. */
  var PITCH_MIN = { mono: 2, gable: 10 };
  function _pitchMin() {
    return PITCH_MIN[STATE.roofType] || PITCH_MIN.mono;
  }
  function _pitchMax() {
    var lo = _pitchMin();
    var head = APEX_MAX - STATE.footprint.h;
    if (head <= 0) return lo;
    /* the apex ceiling never wins over the minimum: a roof that cannot be built
       flat enough is still a roof, so the floor stays reachable */
    return clamp(Math.floor(Math.atan2(head, _pitchBase()) * 180 / Math.PI), lo, 45);
  }
  function _clampPitch() {
    STATE.roofPitch = clamp(STATE.roofPitch, _pitchMin(), _pitchMax());
  }

  var ROOF_TYPE_ORDER = ['mono', 'gable'];
  function selectRoofType(key, silent) {
    STATE.roofType = (ROOF_TYPE_ORDER.indexOf(key) >= 0) ? key : 'mono';
    _clampPitch();
    try { _syncPitchSlider(); } catch (eP) {}
    rebuild();
    if (!silent) savePref();
    _log('roofType -> ' + STATE.roofType);
  }
  function nextRoofType() {
    var i = ROOF_TYPE_ORDER.indexOf(STATE.roofType);
    selectRoofType(ROOF_TYPE_ORDER[(i + 1) % ROOF_TYPE_ORDER.length]);
  }

  /* Pitch slider. Markup, CSS and positioning all come from
     ConfiguratorUI.slider -- this only supplies the range and the callbacks.
     The number itself is not on the slider; it goes in the info bar next to
     the roof type it describes, which is where the bench puts it. */
  var _pitchSl = null;
  function _pitchSlider() {
    if (_pitchSl) return _pitchSl;
    if (!global.ConfiguratorUI || !global.ConfiguratorUI.slider) return null;
    _pitchSl = global.ConfiguratorUI.slider({
      id: 'cf-sauna-pitch',
      /* centred, no target. It used to track cf-info-roof, which stopped
         existing the moment the wizard hid the info bar -- a zero-width target
         left the row unplaced. It belongs to ROOF & BASE now, so it sits above
         that step's icons on its own. */
      /* WHERE THE HOUSE LINE KEEPS IT: pitch at 44, height at 100, both in
         ROOF & BASE -- the same two controls in the same two places on every
         product (Marek) */
      bottom: 44,
      width: 190,
      min: _pitchMin(), max: _pitchMax(), step: 1, value: STATE.roofPitch,
      onInput: function (v) {
        var deg = clamp(v, _pitchMin(), _pitchMax());
        if (deg !== STATE.roofPitch) { STATE.roofPitch = deg; rebuildLive(); }
        _syncPitchValue();
      },
      onChange: function () {
        if (_dragRafPending()) _dragCommit();
        savePref();
        _dbg('pitch -> ' + STATE.roofPitch + ' deg (range ' + _pitchMin() + '..' + _pitchMax() + ')');
      }
    });
    return _pitchSl;
  }
  function _syncPitchValue() {
    var v = $('cf-stat-pitch-val'), u = $('cf-stat-pitch-unit');
    if (v && u) {
      v.textContent = Math.round(STATE.roofPitch); u.textContent = '\u00B0';
    }
    _syncPitchBubble();
  }
  /* THE ANGLE BELONGS TO THE THUMB (Marek). The value lived only in the side
     rail, far from the control setting it, so dragging the slider meant
     watching the wrong corner of the screen. This is a small chip that rides
     the thumb and reads out the pitch as you drag. */
  /* DEGREES IN BOTH SYSTEMS (Marek). The x/12 rise read as a fraction of
     something else to everyone who was not a US framer; a degree is a degree. */
  function _pitchBubbleText() { return Math.round(STATE.roofPitch) + '\u00B0'; }
  function _syncPitchBubble() {
    _valText('cf-shelter-pitch-val', _pitchBubbleText());
    return;   /* seated in the drawer: the title carries the value, no bubble */
    try {
      var row = $('cf-sauna-pitch');
      var root = $('cf-root');
      if (!row || !root) return;
      var el = $('cf-pitch-bubble');
      if (!el) {
        el = document.createElement('div');
        el.id = 'cf-pitch-bubble';
        el.style.cssText = 'position:absolute;z-index:41;pointer-events:none;transform:translate(-50%,-100%);' +
          'background:#1c1d1f;color:#fff;font:700 12px/1 Montserrat,system-ui,sans-serif;' +
          'padding:6px 9px;border-radius:9px;box-shadow:0 3px 10px rgba(0,0,0,0.28);white-space:nowrap;';
        root.appendChild(el);
      }
      if (row.style.display === 'none' || !row.offsetParent) { el.style.display = 'none'; return; }
      el.style.display = '';
      el.textContent = _pitchBubbleText();
      /* the thumb sits at value/(max-min) along the track; measure the real
         input when the slider exposes one, otherwise the row itself */
      var track = row.querySelector ? (row.querySelector('input[type=range]') || row) : row;
      var rr = track.getBoundingClientRect(), rt = root.getBoundingClientRect();
      var lo = _pitchMin(), hi = _pitchMax();
      var f = (hi > lo) ? (clamp(STATE.roofPitch, lo, hi) - lo) / (hi - lo) : 0.5;
      el.style.left = (rr.left - rt.left + rr.width * f) + 'px';
      el.style.top = (rr.top - rt.top - 8) + 'px';
    } catch (e) {}
  }
  /* THE HEIGHT SLIDER -- the posts' height, in ROOF & BASE above the pitch,
     the way the house line has its wall height there. A bubble rides the
     thumb exactly as the pitch one does. */
  var _heightSl = null;
  function _heightSlider() {
    if (_heightSl) return _heightSl;
    if (!global.ConfiguratorUI || !global.ConfiguratorUI.slider) return null;
    _heightSl = global.ConfiguratorUI.slider({
      id: 'cf-sauna-height',
      bottom: 100,
      width: 190,
      min: FOOTPRINT.minH, max: FOOTPRINT.maxH, step: 0.05, value: STATE.footprint.h,
      onInput: function (v) {
        var h = _snapH(clamp(v, FOOTPRINT.minH, FOOTPRINT.maxH));
        if (h !== STATE.footprint.h) { STATE.footprint.h = h; rebuildLive(); }
        _syncHeightBubble();
      },
      onChange: function () {
        if (_dragRafPending()) _dragCommit();
        savePref(); refreshUI();
        _dbg('height -> ' + STATE.footprint.h.toFixed(2) + ' m (range ' + FOOTPRINT.minH + '..' + FOOTPRINT.maxH + ')');
      }
    });
    return _heightSl;
  }
  function _syncHeightBubble() {
    _valText('cf-shelter-height-val', global.ConfiguratorUI.fmtDim(STATE.footprint.h, !!STATE.isMetric));
    return;   /* seated in the drawer: the title carries the value, no bubble */
    try {
      var row = $('cf-sauna-height'), root = $('cf-root');
      if (!row || !root) return;
      var el = $('cf-height-bubble');
      if (!el) {
        el = document.createElement('div');
        el.id = 'cf-height-bubble';
        el.style.cssText = 'position:absolute;z-index:41;pointer-events:none;transform:translate(-50%,-100%);' +
          'background:#1c1d1f;color:#fff;font:700 12px/1 Montserrat,system-ui,sans-serif;' +
          'padding:6px 9px;border-radius:9px;box-shadow:0 3px 10px rgba(0,0,0,0.28);white-space:nowrap;';
        root.appendChild(el);
      }
      if (row.style.display === 'none' || !row.offsetParent) { el.style.display = 'none'; return; }
      el.style.display = '';
      el.textContent = global.ConfiguratorUI.fmtDim(STATE.footprint.h, !!STATE.isMetric);
      var track = row.querySelector ? (row.querySelector('input[type=range]') || row) : row;
      var rr = track.getBoundingClientRect(), rt = root.getBoundingClientRect();
      var lo = FOOTPRINT.minH, hi = FOOTPRINT.maxH;
      var f = (hi > lo) ? (clamp(STATE.footprint.h, lo, hi) - lo) / (hi - lo) : 0.5;
      el.style.left = (rr.left - rt.left + rr.width * f) + 'px';
      el.style.top = (rr.top - rt.top - 8) + 'px';
    } catch (e) {}
  }
  function _syncHeightSlider() {
    var sl = _heightSlider();
    if (sl) sl.set({ min: FOOTPRINT.minH, max: FOOTPRINT.maxH, value: STATE.footprint.h });
    _syncHeightBubble();
  }

  /* SEATED IN THE DRAWER (Marek: posuvniky do draweru, mame to tak i v ADU).
     The kit's sliders are built for the canvas, anchored to its bottom edge;
     the ADU takes each one and re-parents it into the step's pane, relative
     and full-width, with a small-caps title above it that carries the value.
     This is that, line for line, for whichever sliders a step owns. */
  function _seatSliders(pane, list) {
    if (!pane) return;
    var host = document.createElement('div');
    host.id = 'cf-shelter-sliders';
    host.style.cssText = 'display:flex;flex-direction:column;gap:10px;padding:18px 12px 8px;width:100%;box-sizing:border-box;';
    list.forEach(function (it) {
      var sl = it.slider(); if (!sl || !sl.el) return;
      var wrap = document.createElement('div');
      /* room under the track for the value that rides the thumb */
      /* room under the track for the chip that rides the thumb */
      wrap.style.cssText = 'position:relative;padding-top:4px;padding-bottom:34px;';
      var tt = document.createElement('div');
      tt.style.cssText = 'font:700 10px Montserrat,sans-serif;letter-spacing:.08em;text-transform:uppercase;color:#6B7280;margin-bottom:6px;';
      tt.textContent = _t(it.i18n, it.def);
      wrap.appendChild(tt);
      wrap.__slider = sl; wrap.__it = it;
      var el = sl.el;
      el.style.position = 'relative'; el.style.left = 'auto'; el.style.right = 'auto'; el.style.bottom = 'auto'; el.style.top = 'auto';
      el.style.transform = 'none'; el.style.width = '100%'; el.style.maxWidth = 'none'; el.style.margin = '0'; el.style.boxSizing = 'border-box';
      try { var inp2 = el.querySelector('input'); if (inp2) { inp2.style.width = '100%'; inp2.style.maxWidth = 'none'; } } catch (eI) {}
      try { if (sl.input) { sl.input.style.width = '100%'; sl.input.style.maxWidth = 'none'; } } catch (eI2) {}
      wrap.appendChild(el);
      /* THE CHIP LIVES ON THE TRACK, NOT IN THE WRAP (Marek: hodnoty
         problikavaji a preskakuji na zacatek).

         It used to be placed by measuring the input's box on every move --
         and a measurement taken while the drawer is laying out comes back
         useless, the function returns, and the chip is left at left:0, which
         is the start of the track. Hence the flicker between the thumb and
         the beginning. Nothing is measured now: the chip is a child of the
         slider row itself and sits at a PERCENTAGE of it, so the browser
         does the arithmetic and it cannot be wrong or late. */
      el.style.position = 'relative';
      var val = document.createElement('div');
      val.id = it.valId;
      val.style.cssText = 'position:absolute;top:100%;margin-top:4px;transform:translateX(-50%);' +
        'font:700 12px/1 Montserrat,sans-serif;color:#fff;background:#1c1d1f;padding:5px 8px;' +
        'border-radius:8px;white-space:nowrap;pointer-events:none;z-index:2;';
      val.textContent = it.value();
      el.appendChild(val);
      host.appendChild(wrap);
      /* show() re-anchors the row to the canvas bottom, 190 px wide and
         centred -- the canvas contract. Seated, that would tug it out of the
         drawer on every refresh, so the seated slider's position() becomes a
         no-op and the styles above stand. */
      sl.position = function () { return sl; };
      it.sync(); sl.show();
      _thumbChip(wrap);
    });
    pane.appendChild(host);
    /* laid out a moment later; the thumb is measured once the drawer has a width */
    try { global.requestAnimationFrame(function () { _thumbChipsAll(); }); } catch (eR) {}
    global.setTimeout(_thumbChipsAll, 350);
  }
  /* WHERE THE THUMB IS, AS A PERCENTAGE. The thumb's centre travels between
     half a thumb in from each end, so the chip follows the same line: a
     browser calc, no measuring, nothing to be late. */
  var THUMB_W = 14;
  function _thumbChip(wrap) {
    try {
      var sl = wrap.__slider, it = wrap.__it, chip = $(it.valId); if (!sl || !chip) return;
      var track = (sl.el.querySelector && sl.el.querySelector('input[type=range]')) || sl.input;
      var lo, hi, v;
      if (track) { lo = +track.min; hi = +track.max; v = +track.value; }
      if (!(hi > lo)) { chip.textContent = it.value(); return; }   /* the value still updates */
      var f = Math.max(0, Math.min(1, (v - lo) / (hi - lo)));
      chip.style.left = 'calc(' + (THUMB_W / 2) + 'px + (100% - ' + THUMB_W + 'px) * ' + f.toFixed(4) + ')';
      chip.textContent = it.value();
    } catch (e) {}
  }
  /* only the slider that moved, never the others -- repositioning all three on
     every input is how the two idle ones flickered too */
  function _thumbChipOf(valId) {
    try {
      var host = $('cf-shelter-sliders'); if (!host) return;
      var i, kids = host.children;
      for (i = 0; i < kids.length; i++) if (kids[i].__slider && kids[i].__it && kids[i].__it.valId === valId) { _thumbChip(kids[i]); return; }
    } catch (e) {}
  }
  function _thumbChipsAll() {
    try {
      var host = $('cf-shelter-sliders'); if (!host) return;
      var i, kids = host.children;
      for (i = 0; i < kids.length; i++) if (kids[i].__slider) _thumbChip(kids[i]);
    } catch (e) {}
  }
  /* the value in the title, kept current; the floating bubbles are off while
     a slider sits in the drawer, the title is where the eye already is */
  function _valText(id, text) { var v = $(id); if (v) v.textContent = text; _thumbChipOf(id); }
  function _roofPanel(pane) {
    _seatSliders(pane, [
      { slider: _heightSlider, i18n: 'mc_wall_height', def: 'Wall height', valId: 'cf-shelter-height-val',
        value: function () { return global.ConfiguratorUI.fmtDim(STATE.footprint.h, !!STATE.isMetric); },
        sync: _syncHeightSlider },
      { slider: _pitchSlider, i18n: 'roof_pitch', def: 'Roof pitch', valId: 'cf-shelter-pitch-val',
        value: _pitchBubbleText, sync: _syncPitchSlider }
    ]);
  }
  function _lookPanel(pane) {
    _seatSliders(pane, [
      { slider: _slatSlider, i18n: 'sauna_ui_cladding', def: 'Cladding', valId: 'cf-shelter-slat-val',
        value: function () { return Math.round((STATE.slatGap != null ? STATE.slatGap : 0.04) * 1000) + ' mm'; },
        sync: function () { var s = _slatSlider(); if (s) s.set({ min: 0, max: 0.3, value: (STATE.slatGap != null ? STATE.slatGap : 0.04) }); } }
    ]);
    _showSlatSlider();   /* it only shows with slats on */
  }

  function _syncPitchSlider() {
    var sl = _pitchSlider();
    if (sl) sl.set({ min: _pitchMin(), max: _pitchMax(), value: STATE.roofPitch });
    _syncPitchValue();
  }
  /* Editing controls exist only inside the wizard. Falling back to STEP 1 with
     the wizard shut meant the slider and the drag handles were up before anyone
     had asked to edit anything -- mode defaults to 1, so they appeared on load,
     over a view that is supposed to be just the building.

     The one exception is the wizard failing to load at all: it is the only UI
     left now that the info bar is retired, so rather than a dead configurator,
     fall back to the old STEP 1 behaviour. */
  /* Pan, not fit. The first version recomputed the distance from the cabin's
     size, which threw away whatever the user had zoomed or orbited to -- every
     2D/3D toggle snapped the camera home, and the map, which follows the
     camera, went with it.

     Aiming at the cabin needs only the TARGET to move. Shift the camera by the
     same vector and the distance and angle are untouched: the view slides over,
     it does not reset. Where the subject genuinely changes -- ASSEMBLY -- a real
     fit is right, and that is called separately. */
  /* CLOSER. The stage's fit frames the whole group with room around it; on a
     shelter that is a small building in a large empty field. After every fit
     the camera is pulled in along its own line of sight to a fraction of that
     distance, so the page opens on the building and not on the grass (Marek). */
  /* THE CAMERA FRAMES THE BUILDING, NOT THE FIELD.

     ConfiguratorStage measures its whole group, and on this product the group
     is mostly scenery: the snapshot has it as Box3 [-3.3,-2.4]..[5.2,5.4],
     r = 6.28 m, for a building 4.88 x 5.49 whose own radius is 4.19. So the
     shelter was framed for a meadow and came out small whatever factor was
     applied afterwards (Marek: porad je kamera moc daleko).

     This does the framing itself, from STATE: the footprint plus its roof
     overhang, up to the apex. No scenery, no handles, no scale figure. The
     direction and the near/far rules are the stage's own, so nothing else
     about the view changes. */
  /* 0.85 -- THE STAGE'S OWN NUMBER, on the right box. 0.62 was a mistake of
     mine: the sphere fit already frames the sphere exactly, so a pad under 1
     crops it, and at 0.62 the visible width came to 5.5 m for a building
     whose plan diagonal is 7.1 -- the camera stood inside it (Marek: skoci
     pred kameru). At 0.85 the building is framed the way ConfiguratorStage
     frames anything, so boot and every later fit look the same. */
  /* THE SHED'S FRAMING, WHICH IS TO SAY THE STAGE'S OWN -- AND THEN A STEP IN.

     With the shadow out of the group, fitCameraToFootprint frames the building
     and nothing else, which is what the shed does. The stage leaves 15 percent
     of air around whatever it frames; this takes a little of that back, since
     a shelter is a single object and does not need the room a site plan does
     (Marek: jeste kousek zvetsit).

     ONE NUMBER, and it does what it says: 1.0 is the stage's own framing, 0.66
     is a third closer. It is applied by moving the camera along its own line of
     sight, so the angle and the target are the stage's. */
  /* the stage fit, then the product's own framing -- _fit3D is the one that
     decides the size and it must have the last word */
  function _fitCamera() {
    try { global.SaunaEngine.fitCameraToFootprint(); } catch (e) {}
    try { _fit3D(); } catch (e2) {}
  }

  function _panToCabin() {
    var eng = global.SaunaEngine, T = global.THREE;
    if (!eng || !T || !eng.persCam || !eng.persControls) return false;
    var m = STATE.modules[0];
    if (!m) return false;
    var t = _typeOf(m);
    var tgt = new T.Vector3(m.x || 0, t.H * 0.45, m.z || 0);
    var d = new T.Vector3().subVectors(tgt, eng.persControls.target);
    if (d.lengthSq() < 1e-8) return true;
    eng.persControls.target.copy(tgt);
    eng.persCam.position.add(d);
    eng.persControls.update();
    return true;
  }


  /* What a step shows. Visibility used to hang off STATE.mode, and modes are
     shared: the pergola drew in 1, 5 and 6, and SIZE is mode 1 -- so dragging a
     corner happened behind a pergola that has nothing to do with it. A mode is
     which controls are live; a step is what you are looking at, and those are
     not the same question.

     Each step shows its own subject and the cabin it hangs off. Nothing else.
     With the wizard closed the old mode gates apply, so the expert view is
     untouched. */
  var STEP_SHOWS = {
    place:    { cabin: true, roof: true },
    roof:     { cabin: true, roof: true, found: true },
    /* Read from above, so no roof -- it would cover the thing being drawn. The
       size grips live on this plan now, which is the one place a roof would
       have been useful, and it is still not worth hiding the walls for: the
       overhang belongs to ROOF, where the roof is on screen. */
    layout:   { cabin: true, fitouts: true },
    openings: { cabin: true, roof: true },
    interior: { cabin: true },
    look:     { cabin: true, roof: true, found: true, lining: true, facade: true, fitouts: true },
    /* COLOR shows everything EXTERIOR shows -- a colour is judged on the whole
       building. Missing from this table, the step drew the cabin and nothing
       else, which is why the roof vanished the moment the step opened (Marek). */
    colors:   { cabin: true, roof: true, found: true, lining: true, facade: true, fitouts: true },
    assembly: { cabin: true, roof: true, found: true, lining: true, facade: true, fitouts: true }
  };
  function _stepShows(what) {
    if (!_wiz || !_wiz.isOpen()) return null;   /* null = fall back to modes */
    var c = _wiz.current();
    var set = (c && STEP_SHOWS[c.key]) || {};
    return !!set[what];
  }
  function _shows(what, modeGate) {
    var v = _stepShows(what);
    return (v === null) ? modeGate : v;
  }

  function _editable(stepKey) {
    /* Only a genuinely absent ConfiguratorUI falls back to the old mode gates.
       A null _wiz used to mean the same thing, but it is also true for the
       moment between the lib loading and the wizard being built -- and anything
       drawn in that window stays drawn. */
    if (!global.ConfiguratorUI || !global.ConfiguratorUI.wizard) return (STATE.mode | 0) === 1;
    if (!_wiz || !_wiz.isOpen()) return false;
    var c = _wiz.current();
    return !!(c && c.key === stepKey);
  }
  function _pitchStepActive() { return _editable('roof'); }
  function _showPitchSlider() {
    try { _showSlatSlider(); } catch (eSS) {}
    var sl = _pitchSlider();
    if (!sl) return;
    var hs = _heightSlider();
    if (!_pitchStepActive()) {
      sl.hide(); var _pb = $('cf-pitch-bubble'); if (_pb) _pb.style.display = 'none';
      if (hs) { hs.hide(); var _hb = $('cf-height-bubble'); if (_hb) _hb.style.display = 'none'; }
      return;
    }
    _syncPitchSlider();
    sl.show();
    if (hs) { _syncHeightSlider(); hs.show(); }
  }

  var _slatSl = null;
  function _slatSlider() {
    if (_slatSl) return _slatSl;
    if (!global.ConfiguratorUI || !global.ConfiguratorUI.slider) return null;
    _slatSl = global.ConfiguratorUI.slider({
      id: 'cf-shelter-slat',
      bottom: 216,
      width: 190,
      min: 0, max: 0.3, step: 0.005, value: (STATE.slatGap != null ? STATE.slatGap : 0.04),
      onInput: function (v) {
        var g = Math.max(0, Math.min(0.3, v));
        if (g !== STATE.slatGap) { STATE.slatGap = g; rebuildLive(); }
        _valText('cf-shelter-slat-val', Math.round(g * 1000) + ' mm');
      },
      onChange: function () { savePref(); _dbg('slat gap -> ' + STATE.slatGap + ' m'); }
    });
    return _slatSl;
  }
  function _showSlatSlider() {
    var sl = _slatSlider();
    if (!sl) return;
    if (!_editable('look') || STATE.fillType !== 'slats') { sl.hide(); return; }
    sl.set({ min: 0, max: 0.3, value: (STATE.slatGap != null ? STATE.slatGap : 0.04) });
    sl.show();
  }

  var FOUNDATION_ORDER = ['screws', 'pads'];
  function selectFoundation(key, silent) {
    STATE.foundation = (FOUNDATION_ORDER.indexOf(key) >= 0) ? key : 'screws';
    rebuild();
    if (!silent) savePref();
    _log('foundation -> ' + STATE.foundation);
  }
  function nextFoundation() {
    var i = FOUNDATION_ORDER.indexOf(STATE.foundation);
    selectFoundation(FOUNDATION_ORDER[(i + 1) % FOUNDATION_ORDER.length]);
  }

  /* THE STRIP'S CHOICES (shelter-v114, ui-v200, Marek: rozepsat na sipky a
     on/off u vsech config -- container, shelter ...). A cycle tile hands the
     icon strip the list it walks, so its arrows go both ways and the lit
     option's word stands over them; each pick is the drawer's own. */
  function _optsOf(list, isOn, pick, label) {
    return list.map(function (k) {
      return { label: label(k), on: function () { return isOn(k); }, go: function () { pick(k); } };
    });
  }
  /* the strip's fields put on a tile that already exists (a live tile keeps
     its getters) */
  function _with(t, x) {
    for (var k in x) if (x.hasOwnProperty(k)) t[k] = x[k];
    return t;
  }
  function _roofTypeOpts() {
    return _optsOf(ROOF_TYPE_ORDER, function (k) { return STATE.roofType === k; }, function (k) { selectRoofType(k); },
      function (k) { return (k === 'gable') ? _t('roof_v1', 'Gable') : _t('roof_v4', 'Mono-pitch'); });
  }
  /* only the coverings the pitch allows, as the drawer's cycle */
  function _covOpts() {
    return COVERINGS.filter(function (c) { return _covAllowed(c.key); }).map(function (c) {
      return { label: _t(c.i18n, c.def), on: function () { return _covDef().key === c.key; },
               go: function () { STATE.covering = c.key; rebuild(); savePref(); refreshUI(); _log('covering -> ' + c.key + ' (strip)'); } };
    });
  }
  function _foundOpts() {
    return _optsOf(FOUNDATION_ORDER, function (k) { return STATE.foundation === k; }, function (k) { selectFoundation(k); },
      function (k) { return (k === 'pads') ? _t('base_v3', 'Concrete pads') : _t('base_v5', 'Ground screws'); });
  }
  /* the two patterns a screen wall has, in nextFacade's order */
  function _patternOpts() {
    var pats = SHELTER_SCREEN_WALLS ? [FACADE_PATTERNS[1], FACADE_PATTERNS[0]] : FACADE_PATTERNS;
    return pats.map(function (d) {
      return { label: _t(d.i18n, d.def), on: function () { return STATE.facadePattern === d.key; },
               go: function () { STATE.facadePattern = d.key; rebuild(); savePref(); _log('facade pattern -> ' + d.key + ' (strip)'); } };
    });
  }
  function _slatText() { return Math.round((STATE.slatGap != null ? STATE.slatGap : 0.04) * 1000) + ' mm'; }
  function _syncSlatSlider() {
    var s = _slatSlider();
    if (s) s.set({ min: 0, max: 0.3, value: (STATE.slatGap != null ? STATE.slatGap : 0.04) });
  }
  /* the colours panel's four rows as the strip's slots */
  function _colorSlotsS() {
    return COLOR_ROWS.map(function (row) {
      return { key: row.key, label: _t(row.i18n, row.def),
               dots: (SLOT_PAL[row.key] || []).map(function (k) { return { key: k, name: k, hex: PALETTE[k] }; }),
               cur: _ov()[row.key],
               set: function (k) { setSlot(row.key, k); savePref(); } };
    });
  }

  var _toastTimer = 0;
  function _toast(msg) {
    var el = $('cf-toast');
    if (!el) {
      var root = $('cf-root') || document.body;
      el = document.createElement('div');
      el.id = 'cf-toast';
      el.style.cssText = 'position:absolute;left:50%;bottom:150px;transform:translateX(-50%);z-index:60;' +
        'background:#1c1d1f;color:#fff;font:600 12px/1.45 Montserrat,sans-serif;letter-spacing:0.02em;' +
        'padding:10px 16px;border-radius:12px;box-shadow:0 8px 24px rgba(0,0,0,0.30);max-width:min(420px,86%);' +
        'text-align:center;opacity:0;transition:opacity 0.25s;pointer-events:none;';
      root.appendChild(el);
    }
    el.textContent = msg;
    el.style.opacity = '1';
    if (_toastTimer) clearTimeout(_toastTimer);
    _toastTimer = setTimeout(function () { el.style.opacity = '0'; }, 3200);
  }

  function _coverFrac(mi) {
    var mod = STATE.modules[mi];
    var ax0 = (mod.x || 0) - _fpW(mod) / 2, ax1 = (mod.x || 0) + _fpW(mod) / 2;
    var az0 = (mod.z || 0) - _fpL(mod) / 2, az1 = (mod.z || 0) + _fpL(mod) / 2;
    var aA = (ax1 - ax0) * (az1 - az0);
    if (aA <= 0) return 0;
    var ov = 0;
    for (var j = 0; j < STATE.modules.length; j++) {
      var up = STATE.modules[j];
      if ((up.lvl | 0) !== (mod.lvl | 0) + 1) continue;
      var bx0 = (up.x || 0) - _fpW(up) / 2, bx1 = (up.x || 0) + _fpW(up) / 2;
      var bz0 = (up.z || 0) - _fpL(up) / 2, bz1 = (up.z || 0) + _fpL(up) / 2;
      var ox = Math.min(ax1, bx1) - Math.max(ax0, bx0);
      var oz = Math.min(az1, bz1) - Math.max(az0, bz0);
      if (ox > 0 && oz > 0) ov += ox * oz;
    }
    return Math.min(1, ov / aA);
  }

  function _terraceAttach(t2) {
    var hw = t2.w / 2, hl = t2.l / 2;
    var edges = { xmin: t2.x - hw, xmax: t2.x + hw, zmin: t2.z - hl, zmax: t2.z + hl };
    for (var i = 0; i < STATE.modules.length; i++) {
      var m = STATE.modules[i];
      if (!_isOrtho(m)) continue;
      if ((m.lvl | 0) !== (t2.lvl | 0)) continue;
      var mx0 = (m.x || 0) - _fpW(m) / 2, mx1 = (m.x || 0) + _fpW(m) / 2;
      var mz0 = (m.z || 0) - _fpL(m) / 2, mz1 = (m.z || 0) + _fpL(m) / 2;
      var zOv = Math.min(edges.zmax, mz1) - Math.max(edges.zmin, mz0);
      var xOv = Math.min(edges.xmax, mx1) - Math.max(edges.xmin, mx0);
      if (Math.abs(edges.xmin - mx1) < 0.15 && zOv > 1.0) return { side: 'xmin', mi: i };
      if (Math.abs(edges.xmax - mx0) < 0.15 && zOv > 1.0) return { side: 'xmax', mi: i };
      if (Math.abs(edges.zmin - mz1) < 0.15 && xOv > 1.0) return { side: 'zmin', mi: i };
      if (Math.abs(edges.zmax - mz0) < 0.15 && xOv > 1.0) return { side: 'zmax', mi: i };
    }
    return null;
  }


  function _terraceForModule(mi) {
    var mod = STATE.modules[mi];
    if ((mod.lvl | 0) !== 0) return null;
    if (!_isOrtho(mod)) return null;
    var frac = _coverFrac(mi);
    if (frac > 0.98) return null;
    var q = _quarter(mod);
    var t = _typeOf(mod);
    var hx = _extW() / 2, hz = t.L / 2;
    var covers = [];
    for (var j = 0; j < STATE.modules.length; j++) {
      var up = STATE.modules[j];
      if ((up.lvl | 0) !== 1) continue;
      var bx0 = (up.x || 0) - _fpW(up) / 2, bx1 = (up.x || 0) + _fpW(up) / 2;
      var bz0 = (up.z || 0) - _fpL(up) / 2, bz1 = (up.z || 0) + _fpL(up) / 2;
      var a = _rotQ(bx0 - (mod.x || 0), bz0 - (mod.z || 0), (4 - q) % 4);
      var b = _rotQ(bx1 - (mod.x || 0), bz1 - (mod.z || 0), (4 - q) % 4);
      var x0 = Math.max(-hx, Math.min(a[0], b[0])), x1 = Math.min(hx, Math.max(a[0], b[0]));
      var z0 = Math.max(-hz, Math.min(a[1], b[1])), z1 = Math.min(hz, Math.max(a[1], b[1]));
      if (x1 - x0 > 0.05 && z1 - z0 > 0.05) covers.push({ x0: x0, x1: x1, z0: z0, z1: z1 });
    }
    if (!covers.length) {
      var fullCells = [{ x0: -hx, x1: hx, z0: -hz, z1: hz }];
      var fullRails = [[-hx, -hz, hx, -hz], [hx, -hz, hx, hz], [hx, hz, -hx, hz], [-hx, hz, -hx, -hz]];
      return { cells: fullCells, rails: fullRails, area: (2 * hx) * (2 * hz), q: q, hx: hx, hz: hz };
    }
    var gx = [-hx, hx], gz = [-hz, hz], i;
    for (i = 0; i < covers.length; i++) {
      gx.push(covers[i].x0, covers[i].x1);
      gz.push(covers[i].z0, covers[i].z1);
    }
    gx.sort(function (a2, b2) { return a2 - b2; });
    gz.sort(function (a2, b2) { return a2 - b2; });
    function coveredAt(cx, cz2) {
      for (var k = 0; k < covers.length; k++) {
        var c = covers[k];
        if (cx > c.x0 && cx < c.x1 && cz2 > c.z0 && cz2 < c.z1) return true;
      }
      return false;
    }
    var cells = [], area = 0;
    var grid = [];
    for (var gi = 0; gi < gx.length - 1; gi++) {
      grid[gi] = [];
      for (var gj = 0; gj < gz.length - 1; gj++) {
        var w2 = gx[gi + 1] - gx[gi], l2 = gz[gj + 1] - gz[gj];
        var cx2 = (gx[gi] + gx[gi + 1]) / 2, cz3 = (gz[gj] + gz[gj + 1]) / 2;
        var cov = coveredAt(cx2, cz3);
        grid[gi][gj] = cov;
        if (!cov && w2 > 0.02 && l2 > 0.02) {
          cells.push({ x0: gx[gi], x1: gx[gi + 1], z0: gz[gj], z1: gz[gj + 1] });
          area += w2 * l2;
        }
      }
    }
    if (area < 1.0) return null;
    var rails = [], railLen = 0;
    function edge(x0, z0, x1, z1) {
      rails.push([x0, z0, x1, z1]);
      railLen += Math.hypot(x1 - x0, z1 - z0);
    }
    var EPS = 1e-4;
    for (gi = 0; gi < gx.length - 1; gi++) {
      for (var gj2 = 0; gj2 < gz.length - 1; gj2++) {
        if (grid[gi][gj2]) continue;
        var X0 = gx[gi], X1 = gx[gi + 1], Z0 = gz[gj2], Z1 = gz[gj2 + 1];
        if (X1 - X0 < 0.02 || Z1 - Z0 < 0.02) continue;
        if (Math.abs(X0 + hx) < EPS) edge(X0, Z0, X0, Z1);
        if (Math.abs(X1 - hx) < EPS) edge(X1, Z0, X1, Z1);
        if (Math.abs(Z0 + hz) < EPS) edge(X0, Z0, X1, Z0);
        if (Math.abs(Z1 - hz) < EPS) edge(X0, Z1, X1, Z1);
      }
    }
    return { cells: cells, rails: rails, railLen: railLen, area: area };
  }

  var RAIL_POST_SPACING = 1.2;
  function _railFigures(edges) {
    var posts = {}, bays = 0, bm = 0, infillA = 0;
    for (var i = 0; i < edges.length; i++) {
      var e = edges[i];
      var len = Math.hypot(e[2] - e[0], e[3] - e[1]);
      if (!isFinite(len) || len < 0.15) continue;
      bm += len;
      var nP = Math.max(2, Math.ceil(len / RAIL_POST_SPACING) + 1);
      bays += nP - 1;
      infillA += len * 0.92;
      for (var p = 0; p < nP; p++) {
        var t2 = p / (nP - 1);
        var key = (e[0] + (e[2] - e[0]) * t2).toFixed(2) + '|' + (e[1] + (e[3] - e[1]) * t2).toFixed(2);
        posts[key] = [e[0] + (e[2] - e[0]) * t2, e[1] + (e[3] - e[1]) * t2];
      }
    }
    var list = [];
    for (var k in posts) list.push(posts[k]);
    return { posts: list, postN: list.length, bays: bays, bm: bm, infillA: Math.round(infillA * 100) / 100 };
  }

  function _guardEdgesFor(hole) {
    var w = hole.x1 - hole.x0, l = hole.z1 - hole.z0;
    var alongZ = l >= w;
    if (alongZ) {
      return [
        [hole.x0, hole.z0, hole.x0, hole.z1],
        [hole.x1, hole.z0, hole.x1, hole.z1],
        [hole.x0, hole.z0, hole.x1, hole.z0]
      ];
    }
    return [
      [hole.x0, hole.z0, hole.x1, hole.z0],
      [hole.x0, hole.z1, hole.x1, hole.z1],
      [hole.x0, hole.z0, hole.x0, hole.z1]
    ];
  }

  /* Recentring is TWO moves that only make sense together: the model slides to
     the origin, and the map slides the other way by the same metres, so the
     building does not budge over the plot.

     It used to do the first unconditionally and the second only if a map
     happened to exist -- and the map only exists after an address is entered,
     and then only once the Mapbox SDK has finished loading. Toggle the view in
     that window and the model jumped while the ground stayed put. They never
     came back: the offset was gone from STATE, so nothing could ever undo it.

     Now it is all or nothing. No map, nothing to stay aligned with, so the
     model recentres freely. Map present, both move or neither does. */
  function _recenterLayout() {
    var mods = STATE.modules || [];
    if (!mods.length) return;
    var miX = Infinity, maX = -Infinity, miZ = Infinity, maZ = -Infinity;
    for (var i = 0; i < mods.length; i++) {
      var m = mods[i];
      miX = Math.min(miX, (m.x || 0) - _fpW(m) / 2); maX = Math.max(maX, (m.x || 0) + _fpW(m) / 2);
      miZ = Math.min(miZ, (m.z || 0) - _fpL(m) / 2); maZ = Math.max(maZ, (m.z || 0) + _fpL(m) / 2);
    }
    var cx = (miX + maX) / 2, cz = (miZ + maZ) / 2;
    if (Math.abs(cx) < 0.01 && Math.abs(cz) < 0.01) return;

    var hasMap = false;
    try { hasMap = !!(_MapManager && _MapManager.map && _MapManager.center && _MapManager._panMeters); } catch (eH) {}

    if (hasMap) {
      /* Pan first: if it throws, the model has not moved and nothing is out of
         step. _panMeters takes metres and converts them itself, handling the
         bearing on the way -- rotating the offset here as well counted the
         rotation twice and slid the building sideways on a turned map. */
      try {
        _MapManager._panMeters(-cx, -cz);
      } catch (eP) {
        _log('recenter aborted: map pan failed (' + (eP && eP.message ? eP.message : eP) + ')');
        return;
      }
    }

    for (var j = 0; j < mods.length; j++) { mods[j].x = (mods[j].x || 0) - cx; mods[j].z = (mods[j].z || 0) - cz; }
    var pgs = STATE.pergolas || [];
    for (var p = 0; p < pgs.length; p++) { pgs[p].x -= cx; pgs[p].z -= cz; }
    var trs = STATE.terraces || [];
    for (var t = 0; t < trs.length; t++) { trs[t].x -= cx; trs[t].z -= cz; }

    _log('layout recentered by ' + cx.toFixed(2) + ', ' + cz.toFixed(2) + (hasMap ? ' (map panned)' : ' (no map)'));
  }

  function selectEnvelope(key, silent) {
    if (key !== 'interior' && key !== 'exterior') return;

    STATE.envelope = key;
    rebuild();
    if (!silent) savePref();
    _log('envelope -> ' + STATE.envelope);
  }
  /* the tile is gone; kept callable for anything that still walks it */
  function nextEnvelope() { selectEnvelope(STATE.envelope === 'interior' ? 'exterior' : 'interior'); }

  var FACADE_COLORS = [
    { key: 'larch', i18n: 'sauna_fac_1', def: 'Larch', col: 0xB9855A },
    { key: 'shou', i18n: 'sauna_fac_2', def: 'Shou sugi ban', col: 0x26262a },
    { key: 'stain', i18n: 'sauna_fac_3', def: 'Stained', col: 0x6f4428 },
    { key: 'corten', i18n: 'sauna_fac_4', def: 'Corten', col: 0x8a4a2e }
  ];
  var FACADE_PATTERNS = [
    { key: 'wood1', i18n: 'sauna_facpat_1', def: 'Wood 1' },
    { key: 'wood2', i18n: 'sauna_facpat_2', def: 'Wood 2' },
    { key: 'bond', i18n: 'sauna_facpat_3', def: 'Bond' }
  ];
  function _facadeColorDef() {
    for (var i = 0; i < FACADE_COLORS.length; i++) if (FACADE_COLORS[i].key === STATE.facadeColor) return FACADE_COLORS[i];
    return FACADE_COLORS[0];
  }
  function _facadePatternDef() {
    for (var i = 0; i < FACADE_PATTERNS.length; i++) if (FACADE_PATTERNS[i].key === STATE.facadePattern) return FACADE_PATTERNS[i];
    return FACADE_PATTERNS[0];
  }
  function _tt2(key, def) {
    var TT = (global.SAUNA_BOOT && global.SAUNA_BOOT.i18n) || {};
    var v = TT[key];
    return (typeof v === 'string' && v && !/translation missing/i.test(v)) ? _deent(v) : def;
  }
  var PERGOLA_VARIANTS = [
    { key: 'wood_slat', i18n: 'sauna_perg_1', def: 'Wooden \u00B7 slats', frame: 'wood', floor: 'none' },
    { key: 'wood_deck', i18n: 'sauna_perg_2', def: 'Wooden \u00B7 decking', frame: 'wood', floor: 'deck' },
    { key: 'alu_slat', i18n: 'sauna_perg_3', def: 'Aluminium \u00B7 slats', frame: 'alu', floor: 'none' },
    { key: 'alu_tile', i18n: 'sauna_perg_4', def: 'Aluminium \u00B7 tiles', frame: 'alu', floor: 'tile' }
  ];
  function _pergolaDef() {
    for (var i = 0; i < PERGOLA_VARIANTS.length; i++) if (PERGOLA_VARIANTS[i].key === STATE.pergolaVariant) return PERGOLA_VARIANTS[i];
    return PERGOLA_VARIANTS[0];
  }
  function nextPergolaVariant() {
    var i = 0;
    for (var k = 0; k < PERGOLA_VARIANTS.length; k++) if (PERGOLA_VARIANTS[k].key === STATE.pergolaVariant) i = k;
    STATE.pergolaVariant = PERGOLA_VARIANTS[(i + 1) % PERGOLA_VARIANTS.length].key;
    rebuild();
    savePref();
    var pd = _pergolaDef();
    _toast(_tt2(pd.i18n, pd.def));
    _log('pergola variant -> ' + STATE.pergolaVariant);
  }
  function nextTerraceSurface() {
    STATE.terraceSurface = (STATE.terraceSurface === 'deck') ? 'pavers' : 'deck';
    rebuild();
    savePref();
    _toast(_tt2(STATE.terraceSurface === 'pavers' ? 'sauna_terr_2' : 'sauna_terr_1',
      STATE.terraceSurface === 'pavers' ? 'Terrace \u00B7 pavers' : 'Terrace \u00B7 decking'));
    _log('terrace surface -> ' + STATE.terraceSurface);
  }
  function toggleTerraceMode() {
    /* The terrace is a wrap-around deck driven by an overhang footprint (edited
       like the roof). ON reveals the drag handles; a deck is generated on each
       side where the reach is at least TERR_MIN. First ON seeds a 1.2 m reach on
       the front (+z) so the footprint and its foundation are visible at once. */
    STATE.terraceOn = !STATE.terraceOn;
    if (STATE.terraceOn && !(STATE.terraceOver.x0 || STATE.terraceOver.x1 || STATE.terraceOver.z0 || STATE.terraceOver.z1)) {
      STATE.terraceOver = { x0: 0, x1: 0, z0: 0, z1: clamp(1.2, TERR_LIM.min, TERR_LIM.max) };
    }
    rebuild();
    try { if (global.SaunaFootprint) global.SaunaFootprint.refresh(); } catch (e) {}
    savePref();
    _toast(_t('rooms.terrace', 'Terrace') + ' ' + (STATE.terraceOn ? _t('sauna_ui_on', 'ON') : _t('sauna_ui_off', 'OFF')));
    _log('terrace -> ' + (STATE.terraceOn ? 'on' : 'off'));
  }
  function toggleDetail() {
    STATE.showDetail = !STATE.showDetail;
    rebuild();
    savePref();
    _log('assembly detail -> ' + (STATE.showDetail ? 'on' : 'off'));
  }
  function nextFacade() {
    /* SHELTER: two stages only -- horizontal boards/slats and vertical. The
       third ('bond') drew its own batten grid, which showed up even on sides
       that were never switched on (Marek). */
    var _pats = SHELTER_SCREEN_WALLS ? [FACADE_PATTERNS[1], FACADE_PATTERNS[0]] : FACADE_PATTERNS;
    var i = 0;
    for (var k = 0; k < _pats.length; k++) if (_pats[k].key === STATE.facadePattern) i = k;
    STATE.facadePattern = _pats[(i + 1) % _pats.length].key;
    rebuild();
    savePref();
    _log('facade pattern -> ' + STATE.facadePattern);
  }
  function nextFacadeColor() {
    var i = 0;
    for (var k = 0; k < FACADE_COLORS.length; k++) if (FACADE_COLORS[k].key === STATE.facadeColor) i = k;
    STATE.facadeColor = FACADE_COLORS[(i + 1) % FACADE_COLORS.length].key;
    rebuild();
    savePref();
    var fd2 = _facadeColorDef();
    _toast(_tt2(fd2.i18n, fd2.def));
    _log('facade color -> ' + STATE.facadeColor);
  }
  /* PICKED, NOT CYCLED. The tiles name the scheme they set, so the step can
     ask for one directly; nextColorView stays for anything that still walks
     them in order. */
  function setColorView(i) {
    STATE.colorView = ((i | 0) % COLOR_VIEWS.length + COLOR_VIEWS.length) % COLOR_VIEWS.length;
    /* the schemes are not in the buyer's hands any more; nothing to reset */
    var cv = _cv();
    STATE.facadePattern = cv.pattern;
    STATE.facadeColor = cv.facCol;
    /* the scheme's roofPat used to be a paint pattern on one billed sheet;
       it is a covering now, so the charcoal scheme really buys shingles */
    STATE.covering = (cv.roofPat === 'shingle') ? 'shingle' : 'trapez';
    rebuild();
    savePref();
    refreshUI();
    _log('color view -> ' + cv.key + ' (facade ' + _hex6(cv.facade) +
         ', roof ' + _hex6(cv.roof) + ', deck ' + _hex6(cv.deck) + ')');
  }

  function nextColorView() { setColorView((STATE.colorView | 0) + 1); }

  function cycleColor(i) {
    var mod = STATE.modules[i];
    mod.color = ((mod.color | 0) % COLORS.length) + 1;
    rebuild();
    savePref();
    _log('module ' + i + ' color -> ' + _colorOf(mod).key);
  }


  function _selectModule(i) {
    STATE.selected = clamp(i | 0, 0, STATE.modules.length - 1);
    var key = STATE.modules[STATE.selected].type;
    for (var v = 0; v < VARIANT_KEYS.length; v++) if (VARIANT_KEYS[v] === key) STATE.variant = v + 1;
    _ModHandles.refresh();
    refreshUI();
  }

  function selectCount(n, silent) {
    n = clamp(n | 0, 1, MAX_MODULES);
    while (STATE.modules.length > n) STATE.modules.pop();
    STATE.openings = (STATE.openings || []).filter(function (o) { return o.mi < n; });
    STATE.partitions = (STATE.partitions || []).filter(function (p) { return p.mi < n; });
    STATE.doors = (STATE.doors || []).filter(function (dd) { return dd.mi < n; });
    while (STATE.modules.length < n) {
      var maxX = -1e9;
      for (var i = 0; i < STATE.modules.length; i++) {
        if ((STATE.modules[i].lvl | 0) === 0) maxX = Math.max(maxX, (STATE.modules[i].x || 0) + _fpW(STATE.modules[i]) / 2);
      }
      STATE.modules.push({ type: STATE.modules[0].type, x: maxX + _extW() / 2, z: 0, rot: 0, lvl: 0, color: STATE.modules[0].color | 0 || 1 });
    }
    if (STATE.selected >= n) STATE.selected = n - 1;
    _ModHandles.refresh();
    rebuild();
    _fitCamera();
    if (!silent) savePref();
    _log('count -> ' + n);
  }
  function nextCount() { selectCount((STATE.modules.length % MAX_MODULES) + 1); }

  function toggleBody() {
    STATE.bodyVisible = !STATE.bodyVisible;
    var btn = $('cf-btn-body');
    if (btn) {
      btn.setAttribute('aria-pressed', STATE.bodyVisible ? 'true' : 'false');
      btn.style.opacity = STATE.bodyVisible ? '' : '0.45';
    }
    var handle = $('cf-body-handle');
    if (handle) handle.classList.toggle('active', STATE.bodyVisible);
    rebuild();
    savePref();
    _log('scale figure ' + (STATE.bodyVisible ? 'on' : 'off'));
  }

  function _initBodyDragHandle() {
    var handle = $('cf-body-handle');
    if (!handle) return;
    var dragging = false;
    var T = global.THREE;
    var plane = null, raycaster = null, ndc = null, hit = null;

    function pointerToWorld(clientX, clientY) {
      var eng = global.SaunaEngine;
      if (!eng || !eng.camera || !eng.renderer) return null;
      var rect = eng.renderer.domElement.getBoundingClientRect();
      if (!plane) plane = new T.Plane(new T.Vector3(0, 1, 0), 0);
      if (!raycaster) {
        raycaster = new T.Raycaster();
        raycaster.params.Line = { threshold: 0.0001 };
      }
      if (!ndc) ndc = new T.Vector2();
      if (!hit) hit = new T.Vector3();
      ndc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
      ndc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(ndc, eng.camera);
      return raycaster.ray.intersectPlane(plane, hit);
    }

    function onDown(e) {
      if (!STATE.bodyVisible) return;
      e.preventDefault();
      dragging = true;
      try { handle.setPointerCapture(e.pointerId); } catch (_) {}
      if (global.SaunaEngine.controls) global.SaunaEngine.controls.enabled = false;
    }
    function onMove(e) {
      if (!dragging) return;
      var p = pointerToWorld(e.clientX, e.clientY);
      if (!p) return;
      STATE.bodyX = p.x;
      STATE.bodyZ = p.z;
      var bg = global.SaunaEngine && global.SaunaEngine.group;
      if (bg) {
        for (var i = 0; i < bg.children.length; i++) {
          var c = bg.children[i];
          if (c.userData && c.userData.partType === 'person') {
            c.position.set(STATE.bodyX, 0, STATE.bodyZ);
            if (c.userData.basePos) c.userData.basePos.set(STATE.bodyX, 0, STATE.bodyZ);
            break;
          }
        }
      }
    }
    function onUp(e) {
      if (!dragging) return;
      dragging = false;
      try { handle.releasePointerCapture(e.pointerId); } catch (_) {}
      if (global.SaunaEngine.controls) global.SaunaEngine.controls.enabled = true;
      savePref();
    }
    handle.addEventListener('pointerdown', onDown);
    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onUp);
    handle.addEventListener('pointercancel', onUp);
  }

  function _startBodyHandleTracker() {
    var T = global.THREE;
    var v3 = new T.Vector3();
    function tick() {
      var handle = $('cf-body-handle');
      var eng = global.SaunaEngine;
      if (handle && eng && eng.camera && eng.renderer && STATE.bodyVisible) {
        var bx = (STATE.bodyX != null) ? STATE.bodyX : _defaultBodyX();
        var bz = (STATE.bodyZ != null) ? STATE.bodyZ : _defaultBodyZ();
        v3.set(bx, 1.20, bz);
        v3.project(eng.camera);
        var canvasRect = eng.renderer.domElement.getBoundingClientRect();
        var parent = handle.offsetParent;
        var parentRect = parent ? parent.getBoundingClientRect() : { left: 0, top: 0 };
        handle.style.left = (canvasRect.left - parentRect.left + (v3.x + 1) * 0.5 * canvasRect.width) + 'px';
        handle.style.top = (canvasRect.top - parentRect.top + (1 - (v3.y + 1) * 0.5) * canvasRect.height) + 'px';
        handle.style.opacity = (v3.z > -1 && v3.z < 1) ? '' : '0';
      }
      requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  function _setArStatus(msg) {
    var el = document.getElementById('cf-ar-modal-status');
    if (el) el.textContent = msg || '';
  }
  function _setArQR(dataUrl) {
    var el = document.getElementById('cf-ar-modal-qr');
    if (!el) return;
    el.innerHTML = '';
    if (!dataUrl) return;
    var img = document.createElement('img');
    img.src = dataUrl;
    img.alt = 'AR QR code';
    img.style.width = '200px';
    img.style.height = '200px';
    img.style.borderRadius = '8px';
    img.style.background = '#fff';
    img.style.padding = '8px';
    el.appendChild(img);
  }
  function _setArLink(viewerUrl) {
    var el = document.getElementById('cf-ar-modal-link');
    if (!el) return;
    if (viewerUrl) {
      el.style.display = 'block';
      el.innerHTML = '<a href="' + viewerUrl + '" target="_blank" rel="noopener noreferrer" style="color:inherit;text-decoration:underline;">' + viewerUrl + '</a>';
    } else {
      el.style.display = 'none';
      el.innerHTML = '';
    }
  }

  function _arMergeExport(sourceGroup, opts) {
    opts = opts || {};
    return new Promise(function (resolve, reject) {
      var T = global.THREE;
      var Exporter = global.GLTFExporter;
      if (!T || !Exporter || !sourceGroup) {
        reject(new Error('THREE / exporter / source missing'));
        return;
      }
      var AR_TONE = 0.7;
      var buckets = {};
      var decals = [];
      var nrmMat = new T.Matrix3();
      var v = new T.Vector3();

      function bucketFor(mat) {
        var col = (mat.color ? '#' + mat.color.getHexString() : 'fff');
        var key = col + '|' + (mat.transparent ? 'a' + (mat.opacity || 1) : 'o') +
                  '|' + (mat.roughness != null ? mat.roughness : 0.8) +
                  '|' + (mat.metalness != null ? mat.metalness : 0);
        if (!buckets[key]) {
          var c = new T.MeshStandardMaterial({
            color: mat.color ? mat.color.clone().multiplyScalar(AR_TONE) : 0xaaaaaa,
            roughness: (mat.roughness != null ? mat.roughness : 0.8),
            metalness: (mat.metalness != null ? mat.metalness : 0),
            transparent: !!mat.transparent,
            opacity: (mat.opacity != null ? mat.opacity : 1)
          });
          c.side = T.DoubleSide;   /* every AR surface visible from both sides: single-sided walls
                                      render from outside only, so from inside you cannot see out
                                      through a window (the far wall is culled) -- DoubleSide fixes it */
          buckets[key] = { mat: c, pos: [], nrm: [] };
        }
        return buckets[key];
      }

      sourceGroup.updateMatrixWorld(true);
      sourceGroup.traverse(function (node) {
        if (!node.isMesh || !node.geometry || !node.material) return;
        var p = node;
        var isDecal = false;
        while (p && p !== sourceGroup) {
          var pd = p.userData || {};
          if (pd.partType === 'person' || pd.ctl || pd.cPick || typeof pd.opIdx === 'number' || pd.arSkip) return;
          if (pd.decal) isDecal = true;
          p = p.parent;
        }
        if (opts.skipCorr && node.userData && node.userData.corr) return;
        if (opts.skipFound && node.userData && node.userData.found) return;
        if (!node.visible) return;
        if (isDecal) {
          if (opts.decals !== false && node.userData && node.userData.arTex && decals.length < 12) {
            node.updateMatrixWorld(true);
            decals.push(node);
          }
          return;
        }
        var src = node.geometry.index ? node.geometry.toNonIndexed() : node.geometry;
        var posA = src.attributes.position;
        if (!posA || posA.count < 3) return;
        var nrmA = src.attributes.normal;
        if (!nrmA) { src.computeVertexNormals(); nrmA = src.attributes.normal; }
        var mats = Array.isArray(node.material) ? node.material : [node.material];
        var b = bucketFor(mats[0]);
        nrmMat.getNormalMatrix(node.matrixWorld);
        var tmpP = [], tmpN = [], bad = false;
        for (var i = 0; i < posA.count; i++) {
          v.fromBufferAttribute(posA, i).applyMatrix4(node.matrixWorld);
          if (!isFinite(v.x) || !isFinite(v.y) || !isFinite(v.z)) { bad = true; break; }
          tmpP.push(v.x, v.y, v.z);
          v.fromBufferAttribute(nrmA, i).applyMatrix3(nrmMat).normalize();
          if (!isFinite(v.x) || !isFinite(v.y) || !isFinite(v.z)) { bad = true; break; }
          tmpN.push(v.x, v.y, v.z);
        }
        if (bad) return;
        for (var j = 0; j < tmpP.length; j++) { b.pos.push(tmpP[j]); b.nrm.push(tmpN[j]); }
        if (src !== node.geometry) src.dispose();
      });

      var mnX = Infinity, mxX = -Infinity, mnY = Infinity, mnZ = Infinity, mxZ = -Infinity, mxY = -Infinity;
      var key0, bk0, i0;
      for (key0 in buckets) {
        bk0 = buckets[key0];
        for (i0 = 0; i0 < bk0.pos.length; i0 += 3) {
          if (bk0.pos[i0] < mnX) mnX = bk0.pos[i0];
          if (bk0.pos[i0] > mxX) mxX = bk0.pos[i0];
          if (bk0.pos[i0 + 1] < mnY) mnY = bk0.pos[i0 + 1];
          if (bk0.pos[i0 + 1] > mxY) mxY = bk0.pos[i0 + 1];
          if (bk0.pos[i0 + 2] < mnZ) mnZ = bk0.pos[i0 + 2];
          if (bk0.pos[i0 + 2] > mxZ) mxZ = bk0.pos[i0 + 2];
        }
      }
      var cX = (mnX + mxX) / 2, cZ = (mnZ + mxZ) / 2;
      var sc = opts.scale || 1;
      for (key0 in buckets) {
        bk0 = buckets[key0];
        for (i0 = 0; i0 < bk0.pos.length; i0 += 3) {
          bk0.pos[i0] = (bk0.pos[i0] - cX) * sc;
          bk0.pos[i0 + 1] = (bk0.pos[i0 + 1] - mnY) * sc;
          bk0.pos[i0 + 2] = (bk0.pos[i0 + 2] - cZ) * sc;
        }
      }
      _log('[ar] bbox ' + (mxX - mnX).toFixed(1) + 'x' + (mxY - mnY).toFixed(1) + 'x' + (mxZ - mnZ).toFixed(1) + 'm scale=' + sc + (opts.skipCorr ? ' noCorr' : ''));

      var exportRoot = new T.Group();
      var totalTris = 0;
      var keysSorted = Object.keys(buckets).sort();
      var kFrom = 0, kTo = keysSorted.length;
      if (opts.bucketRange) {
        kFrom = Math.max(0, opts.bucketRange[0]);
        kTo = Math.min(keysSorted.length, opts.bucketRange[1]);
      }
      for (var ki = 0; ki < keysSorted.length; ki++) {
        var key = keysSorted[ki];
        var bk = buckets[key];
        var triVerts = Math.floor(bk.pos.length / 9) * 9;
        var inRange = ki >= kFrom && ki < kTo;
        _log('[ar] bucket ' + ki + (inRange ? ' IN ' : ' out ') + key + ' verts=' + (triVerts / 3));
        if (!inRange || triVerts < 9) continue;
        var pos = bk.pos, nrm = bk.nrm;
        var map = {}, outP = [], outN = [], index = [];
        for (var vi = 0; vi < triVerts; vi += 3) {
          var hk = (Math.round(pos[vi] * 5000)) + ',' + (Math.round(pos[vi + 1] * 5000)) + ',' + (Math.round(pos[vi + 2] * 5000)) + '|' +
                   (Math.round(nrm[vi] * 100)) + ',' + (Math.round(nrm[vi + 1] * 100)) + ',' + (Math.round(nrm[vi + 2] * 100));
          var id = map[hk];
          if (id === undefined) {
            id = outP.length / 3;
            map[hk] = id;
            outP.push(pos[vi], pos[vi + 1], pos[vi + 2]);
            outN.push(nrm[vi], nrm[vi + 1], nrm[vi + 2]);
          }
          index.push(id);
        }
        var g = new T.BufferGeometry();
        g.setAttribute('position', new T.Float32BufferAttribute(outP, 3));
        g.setAttribute('normal', new T.Float32BufferAttribute(outN, 3));
        g.setIndex(index);
        var m = new T.Mesh(g, bk.mat);
        m.name = 'part_' + exportRoot.children.length;
        totalTris += triVerts / 9;
        exportRoot.add(m);
      }
      var decTexCache = {};
      function _cpuTex(spec) {
        var key = spec.kind + '|' + spec.a + '|' + (spec.sub || '') + '|' + spec.b + '|' + spec.ink;
        if (decTexCache[key]) return decTexCache[key];
        var w = 250, h;
        if (spec.kind === 'dim') h = 102;
        else if (spec.kind === 'area') h = 78;
        else h = 94;
        var cv = document.createElement('canvas');
        cv.width = w; cv.height = h;
        var ctx = cv.getContext('2d', { willReadFrequently: true });
        ctx.clearRect(0, 0, w, h);
        ctx.fillStyle = spec.ink || '#ffffff';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        if (spec.kind === 'dim') {
          ctx.font = '800 ' + (spec.sub ? 40 : 46) + 'px Montserrat, Arial, sans-serif';
          ctx.fillText(spec.a, w / 2, spec.sub ? 26 : 34);
          if (spec.sub) {
            ctx.font = '700 16px Montserrat, Arial, sans-serif';
            ctx.fillText(spec.sub, w / 2, 56);
          }
          ctx.font = '700 15px Montserrat, Arial, sans-serif';
          ctx.globalAlpha = 0.85;
          ctx.fillText(spec.b, w / 2, spec.sub ? 84 : 76);
          ctx.globalAlpha = 1;
        } else if (spec.kind === 'area') {
          ctx.textAlign = 'right';
          ctx.font = '800 32px Montserrat, Arial, sans-serif';
          ctx.fillText(spec.a, w - 6, 30);
          ctx.font = '700 13px Montserrat, Arial, sans-serif';
          ctx.fillText(String(spec.b).toUpperCase(), w - 6, 58);
        } else {
          ctx.font = '800 44px Montserrat, Arial, sans-serif';
          ctx.fillText(spec.a, w / 2, 34);
          ctx.font = '700 13px Montserrat, Arial, sans-serif';
          ctx.fillText(spec.b, w / 2, 70);
        }
        var tx = new T.CanvasTexture(cv);
        tx.colorSpace = T.SRGBColorSpace;
        var mt = new T.MeshBasicMaterial({ map: tx, transparent: true });
        decTexCache[key] = mt;
        return mt;
      }
      var decCount = 0;
      for (var dci = 0; dci < decals.length; dci++) {
        try {
          var dn = decals[dci];
          var dg = dn.geometry.clone();
          dg.applyMatrix4(dn.matrixWorld);
          dg.translate(-cX, -mnY, -cZ);
          dg.scale(sc, sc, sc);
          ['uv1', 'uv2', 'color', 'tangent'].forEach(function (a) { if (dg.getAttribute(a)) dg.deleteAttribute(a); });
          var dm = new T.Mesh(dg, _cpuTex(dn.userData.arTex));
          dm.name = 'decal_' + dci;
          exportRoot.add(dm);
          decCount++;
        } catch (eDec) {}
      }
      _log('[ar] merged: ' + exportRoot.children.length + ' meshes (' + decCount + ' decals), ' + totalTris + ' tris');
      if (exportRoot.children.length === 0) {
        reject(new Error('No exportable meshes'));
        return;
      }
      var exporter = new Exporter();
      exporter.parse(
        exportRoot,
        function (glb) {
          if (glb instanceof ArrayBuffer) resolve(glb);
          else reject(new Error('Expected GLB ArrayBuffer, got JSON'));
        },
        function (err) { reject(err); },
        { binary: true }
      );
    });
  }

  function _arExportScene() {
    /* AR must carry the COMPLETE building, not just what the current step leaves
       on screen. Rebuild at the exterior mode (5) first: that brings the roof,
       walls, floors, partitions, benches and the ABOVE-ground foundation -- the
       below-ground screw is drawn only in the foundation step (mode 2), so it
       stays out, which is exactly what we want. The working mode is restored
       afterwards, whether the export succeeds or fails. */
    var prevMode = STATE.mode | 0;
    function restore() { if ((STATE.mode | 0) !== prevMode) { try { setMode(prevMode, true); } catch (eR) {} } }
    if (prevMode !== 5) { try { setMode(5, true); } catch (eB) {} }
    return _arMergeExport(global.SaunaEngine && global.SaunaEngine.group, { scale: 1, skipFound: false })
      .then(function (glb) { restore(); return glb; }, function (err) { restore(); throw err; });
  }

  function _arDbgSource(kind) {
    var grp = global.SaunaEngine.group;
    if (kind === 'A') return { grp: grp, opts: { scale: 1, bucketRange: [3, 4] } };
    if (kind === 'B') return { grp: grp, opts: { scale: 1, bucketRange: [4, 5] } };
    if (kind === 'C') return { grp: grp, opts: { scale: 1, bucketRange: [5, 6] } };
    return { grp: grp, opts: { scale: 1, bucketRange: [0, 3] } };
  }

  async function _arDebugRun(modal) {
    var kinds = ['A', 'B', 'C', 'D'];
    var qrWrap = document.getElementById('cf-ar-modal-qr');
    if (!qrWrap) return;
    qrWrap.innerHTML = '';
    qrWrap.style.display = 'flex';
    qrWrap.style.flexWrap = 'wrap';
    qrWrap.style.gap = '10px';
    qrWrap.style.justifyContent = 'center';
    qrWrap.style.minHeight = '0';
    var cells = {};
    for (var ci = 0; ci < kinds.length; ci++) {
      var cell = document.createElement('div');
      cell.style.cssText = 'display:flex;flex-direction:column;align-items:center;gap:4px;width:132px;';
      var slot = document.createElement('div');
      slot.style.cssText = 'width:124px;height:124px;border-radius:10px;background:#f4f1ea;display:flex;align-items:center;justify-content:center;font:600 11px Montserrat,sans-serif;color:#8a7c68;text-align:center;padding:4px;';
      slot.textContent = '\u2026';
      var lab = document.createElement('div');
      lab.style.cssText = 'font:700 12px Montserrat,sans-serif;color:#1c1d1f;';
      lab.textContent = kinds[ci];
      cell.appendChild(slot);
      cell.appendChild(lab);
      qrWrap.appendChild(cell);
      cells[kinds[ci]] = slot;
    }
    _setArStatus('singles: A=bucket3 \u00B7 B=bucket4 \u00B7 C=bucket5 \u00B7 D=0-2 sanity');
    for (var i = 0; i < kinds.length; i++) {
      var k = kinds[i];
      cells[k].textContent = 'export\u2026';
      try {
        var srcd = _arDbgSource(k);
        var glb = await _arMergeExport(srcd.grp, srcd.opts);
        _log('[ar][dbg ' + k + '] ' + (glb.byteLength / 1024).toFixed(1) + 'KB');
        cells[k].textContent = 'upload\u2026';
        var info = await _arUpload(glb);
        _log('[ar][dbg ' + k + '] uuid=' + info.uuid);
        var qr = await _arGenerateQR(info.viewerUrl);
        cells[k].innerHTML = '';
        var img = document.createElement('img');
        img.src = qr;
        img.style.cssText = 'width:124px;height:124px;border-radius:10px;background:#fff;padding:4px;box-sizing:border-box;';
        cells[k].appendChild(img);
      } catch (e) {
        _log('[ar][dbg ' + k + '] FAILED ' + (e && e.message ? e.message : e));
        cells[k].textContent = 'FAIL: ' + (e && e.message ? e.message : e);
      }
    }
  }

  async function _arUpload(glbBuffer) {
    var BOOT = global.SAUNA_BOOT || {};
    var TT = BOOT.i18n || {};
    var AR_WORKER_FALLBACK = 'https://soft-art-55b9.long-cloud-d006.workers.dev';
    var workerUrl = BOOT.arWorkerUrl || TT.arWorkerUrl || AR_WORKER_FALLBACK;
    if (!workerUrl) throw new Error('Worker URL missing');
    _log('[ar] worker URL = ' + workerUrl);

    _log('[ar] GET ' + workerUrl + '/upload-url');
    var r1;
    try {
      r1 = await fetch(workerUrl + '/upload-url');
    } catch (netErr) {
      _log('[ar] network error on /upload-url: ' + netErr);
      throw new Error('Network error: ' + (netErr && netErr.message ? netErr.message : netErr));
    }
    _log('[ar] /upload-url status=' + r1.status);
    if (!r1.ok) throw new Error('upload-url failed: HTTP ' + r1.status);
    var info = await r1.json();
    if (!info || !info.uploadUrl) throw new Error('upload-url response invalid');

    _log('[ar] PUT ' + (glbBuffer.byteLength / 1024).toFixed(1) + ' KB');
    var r2;
    try {
      r2 = await fetch(info.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'model/gltf-binary' },
        body: glbBuffer
      });
    } catch (netErr) {
      _log('[ar] network error on PUT: ' + netErr);
      throw new Error('Network error on PUT: ' + (netErr && netErr.message ? netErr.message : netErr));
    }
    _log('[ar] PUT status=' + r2.status);
    if (!r2.ok) {
      var errBody = '';
      try { errBody = await r2.text(); } catch (_) {}
      throw new Error('PUT failed: HTTP ' + r2.status + ' ' + errBody);
    }
    return info;
  }

  function _arGenerateQR(url) {
    return new Promise(function (resolve) {
      function tryLib() {
        if (global.QRCode && global.QRCode.toDataURL) {
          global.QRCode.toDataURL(url, { width: 400, margin: 1, errorCorrectionLevel: 'M' }, function (err, dataUrl) {
            if (err || !dataUrl) {
              resolve('https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=' + encodeURIComponent(url));
            } else {
              resolve(dataUrl);
            }
          });
          return true;
        }
        return false;
      }
      if (tryLib()) return;
      var attempts = 0;
      var iv = setInterval(function () {
        attempts++;
        if (tryLib()) { clearInterval(iv); return; }
        if (attempts > 20) {
          clearInterval(iv);
          resolve('https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=' + encodeURIComponent(url));
        }
      }, 100);
    });
  }

  var _arExporterPromise = null;
  function _ensureGLTFExporter() {
    if (global.GLTFExporter) return Promise.resolve();
    if (_arExporterPromise) return _arExporterPromise;
    _arExporterPromise = import('https://esm.sh/three@0.160.0/examples/jsm/exporters/GLTFExporter.js')
      .then(function (mod) {
        global.GLTFExporter = mod.GLTFExporter || (mod.default && mod.default.GLTFExporter) || mod.default;
        if (!global.GLTFExporter) throw new Error('GLTFExporter import returned no export');
        _log('[ar] GLTFExporter loaded dynamically');
      })
      .catch(function (e) {
        _arExporterPromise = null;
        throw new Error('Could not load GLTFExporter: ' + (e && e.message ? e.message : e));
      });
    return _arExporterPromise;
  }

  function _ensureArModal() {
    if (document.getElementById('cf-ar-modal')) return;
    var TT = (global.SAUNA_BOOT && global.SAUNA_BOOT.i18n) || {};
    var title = _deent(TT.arModalTitle || 'Scan to view in AR');
    var subtitle = _deent(TT.arModalSubtitle || 'Point your phone camera at the QR code to place this model in your space.');
    var closeTxt = _deent(TT.arClose || 'Close');

    if (!document.getElementById('cf-ar-modal-style')) {
      var st = document.createElement('style');
      st.id = 'cf-ar-modal-style';
      st.textContent =
        '#cf-ar-modal{position:fixed;inset:0;display:flex;align-items:center;justify-content:center;z-index:100000;}' +
        '#cf-ar-modal[hidden]{display:none !important;}' +
        '#cf-ar-modal .cf-arm-backdrop{position:absolute;inset:0;background:rgba(0,0,0,.55);backdrop-filter:blur(2px);}' +
        '#cf-ar-modal .cf-arm-card{position:relative;background:#fff;border-radius:16px;padding:32px 28px 24px;max-width:380px;width:calc(100% - 32px);box-shadow:0 20px 60px rgba(0,0,0,.25);text-align:center;animation:cfArModalIn .25s cubic-bezier(.16,1,.3,1);}' +
        '@keyframes cfArModalIn{from{opacity:0;transform:translateY(8px) scale(.98);}to{opacity:1;transform:none;}}' +
        '#cf-ar-modal .cf-arm-close{position:absolute;top:12px;right:12px;width:32px;height:32px;border:none;background:transparent;cursor:pointer;padding:0;display:flex;align-items:center;justify-content:center;color:#888;border-radius:8px;transition:background .15s ease,color .15s ease;}' +
        '#cf-ar-modal .cf-arm-close:hover{background:#f4f4f4;color:#222;}' +
        '#cf-ar-modal .cf-arm-icon{width:56px;height:56px;margin:0 auto 14px;border-radius:50%;background:linear-gradient(135deg,#d2a878 0%,#b88752 100%);display:flex;align-items:center;justify-content:center;color:#fff;}' +
        '#cf-ar-modal .cf-arm-title{font:700 18px/1.3 var(--cf-font-display,sans-serif);color:#1a1a1a;margin:0 24px 6px;letter-spacing:-.01em;}' +
        '#cf-ar-modal .cf-arm-subtitle{font:500 13px/1.5 var(--cf-font-display,sans-serif);color:#555;margin:0 0 20px;}' +
        '#cf-ar-modal #cf-ar-modal-status{font:500 12px/1.4 var(--cf-font-display,sans-serif);color:#888;margin-bottom:12px;min-height:16px;}' +
        '#cf-ar-modal #cf-ar-modal-qr{display:flex;justify-content:center;align-items:center;min-height:200px;margin:8px 0 14px;}' +
        '#cf-ar-modal #cf-ar-modal-link{font:500 11px/1.4 var(--cf-font-display,sans-serif);color:#b88752;word-break:break-all;margin-bottom:14px;display:none;}' +
        '#cf-ar-modal .cf-arm-actions{display:flex;gap:10px;justify-content:center;margin-top:6px;}' +
        '#cf-ar-modal .cf-arm-btn{border:none;border-radius:10px;padding:12px 18px;font:600 13px/1.2 var(--cf-font-display,sans-serif);cursor:pointer;transition:background .15s ease,transform .1s ease;min-width:110px;background:#f4f4f4;color:#333;}' +
        '#cf-ar-modal .cf-arm-btn:hover{background:#e8e8e8;}' +
        '#cf-ar-modal .cf-arm-btn:active{transform:translateY(1px);}';
      document.head.appendChild(st);
    }

    var wrap = document.createElement('div');
    wrap.id = 'cf-ar-modal';
    wrap.hidden = true;
    wrap.setAttribute('role', 'dialog');
    wrap.setAttribute('aria-modal', 'true');
    wrap.innerHTML =
      '<div class="cf-arm-backdrop" data-cf-ar-close></div>' +
      '<div class="cf-arm-card">' +
        '<button class="cf-arm-close" type="button" data-cf-ar-close aria-label="' + closeTxt + '">' +
          '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
          '<line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>' +
        '</button>' +
        '<div class="cf-arm-icon" aria-hidden="true">' +
          '<svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">' +
          '<path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>' +
          '<polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>' +
          '<line x1="12" y1="22.08" x2="12" y2="12"></line></svg>' +
        '</div>' +
        '<h3 class="cf-arm-title">' + title + '</h3>' +
        '<p class="cf-arm-subtitle">' + subtitle + '</p>' +
        '<div id="cf-ar-modal-status"></div>' +
        '<div id="cf-ar-modal-qr"></div>' +
        '<div id="cf-ar-modal-link"></div>' +
        '<div class="cf-arm-actions">' +
          '<button class="cf-arm-btn" type="button" data-cf-ar-close>' + closeTxt + '</button>' +
        '</div>' +
      '</div>';
    document.body.appendChild(wrap);
  }

  async function _openArModal() {
    _ensureArModal();
    var modal = document.getElementById('cf-ar-modal');
    if (!modal) return;
    var TT = (global.SAUNA_BOOT && global.SAUNA_BOOT.i18n) || {};

    _setArQR(null);
    _setArLink(null);
    _setArStatus(_deent(TT.arPreparing || 'Preparing scene\u2026'));
    modal.hidden = false;

    function close() {
      modal.hidden = true;
      modal.removeEventListener('click', onClick);
    }
    function onClick(e) {
      if (e.target.closest('[data-cf-ar-close]')) {
        e.preventDefault();
        close();
      }
    }
    modal.addEventListener('click', onClick);

    try {
      await _ensureGLTFExporter();
      var dbg = false;
      try { dbg = global.localStorage.getItem('cf_ardbg') === '1'; } catch (eD) {}
      if (dbg) { await _arDebugRun(modal); return; }
      await _arGenerate();
    } catch (e) {
      _log('[ar] FAILED: ' + (e && e.message ? e.message : e));
      _setArStatus(_deent(TT.arError || 'AR export failed: ') + (e && e.message ? e.message : e));
    }
  }

  var _arBusy = false;
  async function _arGenerate() {
    if (_arBusy) return;
    _arBusy = true;
    var TT = (global.SAUNA_BOOT && global.SAUNA_BOOT.i18n) || {};
    try {
      _setArQR(null);
      _setArLink(null);
      _setArStatus(_deent(TT.arPreparing || 'Preparing scene\u2026'));
      var glb = await _arExportScene();
      _log('[ar] glb size=' + (glb.byteLength / 1024).toFixed(1) + 'KB (on-screen scene)');
      _setArStatus(_deent(TT.arUploading || 'Uploading to cloud\u2026'));
      var info = await _arUpload(glb);
      _log('[ar] uploaded uuid=' + info.uuid);
      var qrDataUrl = await _arGenerateQR(info.viewerUrl);
      _setArQR(qrDataUrl);
      _setArLink(info.viewerUrl);
      _setArStatus(_deent(TT.arReady || 'Ready \u2014 scan with your phone'));
    } catch (e) {
      _log('[ar] FAILED: ' + (e && e.message ? e.message : e));
      _setArStatus(_deent(TT.arError || 'AR export failed: ') + (e && e.message ? e.message : e));
    }
    _arBusy = false;
  }


  function _initArButton() {
    var openArFn = function (e) {
      if (e) { e.preventDefault(); e.stopPropagation(); }
      _openArModal();
    };
    var existing = document.getElementById('cf-btn-ar');
    if (existing) {
      existing.addEventListener('click', openArFn);
      return;
    }
    var tmpl = document.getElementById('cf-btn-share') ||
               document.getElementById('cf-btn-pdf') ||
               document.getElementById('cf-btn-view');
    var anchor = document.getElementById('cf-btn-assembly');
    var styleSrc = tmpl || anchor;
    var parent = (anchor && anchor.parentNode) || (styleSrc && styleSrc.parentNode);
    if (!parent) return;

    var TT = (global.SAUNA_BOOT && global.SAUNA_BOOT.i18n) || {};
    var label = _deent(TT.arButton || 'View in AR');

    var btn = document.createElement((styleSrc || { tagName: 'BUTTON' }).tagName);
    btn.id = 'cf-btn-ar';
    btn.className = 'cf-icon-btn';
    if (btn.tagName === 'BUTTON') btn.type = 'button';
    btn.setAttribute('title', label);
    btn.setAttribute('aria-label', label);
    btn.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
      'stroke-linecap="round" stroke-linejoin="round" style="width:22px;height:22px;display:block;margin:auto;pointer-events:none;">' +
      '<path d="M12 2 3 7v10l9 5 9-5V7z"></path>' +
      '<path d="M12 22V12"></path>' +
      '<path d="M3 7l9 5 9-5"></path>' +
      '</svg>';

    if (anchor && anchor.parentNode === parent) {
      parent.insertBefore(btn, anchor);
    } else if (tmpl && tmpl.parentNode) {
      tmpl.parentNode.insertBefore(btn, tmpl.nextSibling);
    } else {
      parent.appendChild(btn);
    }
    btn.addEventListener('click', openArFn);
  }

  function openBudgetDrawer() {
    try { if (global.SaunaBoQ && global.SaunaBoQ.publish) global.SaunaBoQ.publish(); } catch (e) {}
    if (typeof global.openShareBudgetDrawer === 'function') {
      global.openShareBudgetDrawer('data');
    } else if (typeof global.openRefreshDrawer === 'function') {
      global.openRefreshDrawer();
    }
  }

  function bindBtn(id, fn) {
    var el = $(id);
    if (!el) return;
    el.addEventListener('click', function (ev) { ev.preventDefault(); fn(el); });
  }

  /* The map is a second camera, under the first one, and nothing reconciled the
     two except this button's own click handler.

     _syncMapTo2D has always existed and has always been right: pitch 0, the
     user's own bearing back, zoom matched to the ortho scale. It was called from
     exactly one place -- here. But the wizard does not click this button. It
     switches the view itself, through _fitCamera and exitEditor, and mapbox was
     never told. So the drawing went orthographic and the satellite photo stayed
     at whatever angle the last orbit left it: a plan on a perspective ground.

     Every route into a view goes through _setView now, so there is no route that
     can forget. */
  /* The 3D framing.

     Nobody touched the camera and that is exactly the problem. Boot puts it at
     cameraPos [7, 4, 8] -- 11 m from the target, hard-coded, whatever is
     standing there. A 2.4 x 3.6 sauna needs 7.2 m and gets 11: fine. This one,
     five metres square under a big gable with a pergola, needs 14.7 and gets 11.
     It has always overflowed. It was invisible because a reload used to land in
     the plan, so the boot camera was a view nobody arrived at.

     And ConfiguratorStage.fitCameraToFootprint, which runs on setView('3d'),
     multiplies the fitting distance by 0.85 -- deliberately 18% inside a fit, so
     the model overflows by design. That constant is in the shared asset and
     every other configurator is framed by it, so it stays where it is.

     This is the sauna asking for its own framing, from its own file: fit the
     whole group, then stand back far enough to see it. 1.28 is air -- a building
     touching all four edges reads as a crop, not a building. Direction is kept
     from wherever the camera already was, so a re-fit never spins the model. */
  /* THIS IS THE ONE. 1.28 means the camera is pulled 28 percent FURTHER OUT
     than the distance at which the model exactly fills the frame -- which is
     why the shelter has always looked small, and why nothing I did to
     fitCameraToFootprint ever showed: _fit3D runs last, at the end of boot and
     again on every view switch, and re-frames over the top of it.

     0.82 puts the model at four fifths of that distance, so it fills the frame
     with a little air left. One number; say bigger or smaller and it moves
     (Marek). */
  var FIT_3D = 0.92;
  /* Box3.setFromObject is all-or-nothing: one NaN vertex anywhere in the group
     and the whole box is NaN, and everything downstream of the box -- the 2D
     span, the 3D fit -- quietly declines to run. That is the snapshot's
     "Box3(group) = [NaN,NaN] r=NaN", and it is why the model keeps loading
     unframed however many times the framing is fixed: the framing is fine, the
     ruler is poisoned.

     This box measures object by object and skips any whose own bounds are not
     finite, so one bad mesh costs its own volume and nothing else. The snapshot
     names the offenders; this keeps the lights on while they are hunted. */
  function _safeBox() {
    var T = global.THREE, eng = global.SaunaEngine;
    if (!T || !eng || !eng.group) return null;
    var out = new T.Box3(), tmp = new T.Box3(), any = false;
    eng.group.updateWorldMatrix(true, true);
    eng.group.traverse(function (o) {
      if (!o.isMesh && !o.isLine && !o.isPoints && !o.isSprite) return;
      if (!o.visible) return;
      /* P4: the pergola and terrace are furniture in the garden, not the
         building. Measuring them into the fit box is what made adding a
         pergola SHIFT THE SCENE CENTRE and the orbit pivot -- the camera
         started circling the gap between two things. The centre of the scene
         is the centre of the sauna, always. */
      var vz = o.userData && o.userData.viz;
      if (vz === 'pergola' || vz === 'terrace') return;
      var g = o.geometry;
      if (!g || !g.attributes || !g.attributes.position) return;
      if (!g.boundingBox) { try { g.computeBoundingBox(); } catch (e) { return; } }
      var b = g.boundingBox;
      if (!b || !isFinite(b.min.x) || !isFinite(b.max.x) ||
          !isFinite(b.min.y) || !isFinite(b.max.y) ||
          !isFinite(b.min.z) || !isFinite(b.max.z)) return;
      tmp.copy(b).applyMatrix4(o.matrixWorld);
      if (!isFinite(tmp.min.x) || !isFinite(tmp.max.x)) return;
      out.union(tmp); any = true;
    });
    return any ? out : null;
  }
  function _fit3D() {
    var eng = global.SaunaEngine, T = global.THREE;
    if (!eng || !T || !eng.persCam || !eng.persControls || !eng.group) return false;
    var box = _safeBox();
    if (!box || box.isEmpty()) return false;
    var sph = box.getBoundingSphere(new T.Sphere());
    var r = sph.radius;
    if (!(r > 0)) return false;
    var cam = eng.persCam, ctl = eng.persControls;
    var vFov = cam.fov * Math.PI / 180;
    var aspect = cam.aspect || 1.4;
    var hFov = 2 * Math.atan(Math.tan(vFov / 2) * aspect);
    var minFov = Math.min(vFov, hFov);
    var dist = (r / Math.sin(minFov / 2)) * FIT_3D;
    var dir = new T.Vector3().subVectors(cam.position, ctl.target);
    if (!(dir.lengthSq() > 1e-6)) dir.set(1, 0.45, 1);
    dir.normalize();
    ctl.target.copy(sph.center);
    cam.position.copy(sph.center.clone().add(dir.multiplyScalar(dist)));
    cam.near = Math.max(0.05, dist / 200);
    cam.far = dist * 6;
    cam.updateProjectionMatrix();
    ctl.minDistance = dist * 0.25;
    ctl.maxDistance = dist * 4;
    ctl.update();
    /* shelter-v119: what was framed, so a later change can be told */
    _shFitB = { r: r, c: sph.center.clone() };
    return true;
  }

  function _syncMapView(animate) {
    try {
      if (!_MapManager || !_MapManager.map || !_MapManager.center) return;
      if (STATE.view === '2d') { _MapManager._set3DLayerVisibility(false); _MapManager._syncMapTo2D(animate); }
      else { _MapManager._set3DLayerVisibility(true); _MapManager._syncMapTo3D(animate); }
    } catch (eMV) { _log('map view sync: ' + (eMV && eMV.message ? eMV.message : eMV)); }
  }
  var _prevView = null;
  /* THE SECTION CUT IS THE KIT'S, NOT THIS FILE'S. ConfiguratorUI.cutPlanes
     cuts with the renderer's own clipping planes and caps the cut with a
     stencil, so it does not care that this product has no layers -- it cuts
     whatever is in the stage group. What it needs from here is only where
     the stage is, which button is its own, how to write a length, and how to
     switch the orbit controls off while the plane is being dragged. */
  var _cutTool = null;
  function _cutInit() {
    var U = global.ConfiguratorUI;
    if (_cutTool) return _cutTool;
    if (!U || typeof U.cutPlanes !== 'function') return null;
    _cutTool = U.cutPlanes({
      stage:     function () { return global.SaunaEngine; },
      rootId:    'cf-root',
      buttonId:  'cf-btn-cut',
      unitBtnId: 'cf-btn-unit',
      fmtLen:    function (m) { return U.fmtDim(m, !!STATE.isMetric); },
      log:       _log,
      controls:  function () { return global.SaunaEngine ? global.SaunaEngine.controls : null; },
      view:      function () { return STATE.view; }
    });
    return _cutTool;
  }
  /* off, with a reason in the log -- the plan view has nothing to cut and a
     rebuild that swaps the group would otherwise leave the caps on ghosts */
  function _cutOff(why) {
    try { if (_cutTool && _cutTool.on()) { _cutTool.toggle(); _log('section off -- ' + why); } } catch (e) {}
  }


  function _setView(mode, focus) {
    STATE.view = (mode === '2d') ? '2d' : '3d';
    if (STATE.view === '2d') _cutOff('the plan');
    /* Loud. This was a bare try/catch, and it hid the whole failure: if
       SaunaEngine.setView throws, STATE.view says '3d' while the engine is still
       rendering the ortho camera. Nothing disagrees out loud, so the plan simply
       stays on screen forever, the step name and the icons keep updating over
       the top of it, and nothing on the canvas can be touched because the editor
       has already exited. Everything reported works, and yet nothing works. */
    try { global.SaunaEngine.setView(STATE.view, focus); }
    catch (eSV) { _noteError('setView(' + STATE.view + ') THREW: ' + (eSV && eSV.message ? eSV.message : eSV)); }
    /* after, not instead: setView('3d') runs the shared fit first and this
       re-frames what it left, so the shared asset is never second-guessed --
       only the distance it stops at */
    if (STATE.view === '3d') {
      try { _fit3D(); }
      catch (eF3) { _noteError('_fit3D THREW: ' + (eF3 && eF3.message ? eF3.message : eF3)); }
    }
    var root = $('cf-root');
    if (root) {
      root.classList.toggle('cf-view-2d', STATE.view === '2d');
      root.classList.toggle('cf-view-3d', STATE.view === '3d');
    }
    /* visibility is decided during the BUILD, so a view switch has to rebuild
       -- otherwise the roof strip above only lands on the next edit. */
    if (_prevView !== STATE.view) { _prevView = STATE.view; try { rebuild(); } catch (eRV) {} }
    try {
      if (global.SaunaPlan2D) { if (STATE.view === '2d' && (STATE.mode | 0) !== 3) global.SaunaPlan2D.show(); else global.SaunaPlan2D.hide(); }
    } catch (eP2) {}
    try { _updateSnapBtn(); } catch (eSB2) {}
    _syncViewButton();
    _syncMapView(true);
    /* easeTo takes 400 ms and the ortho fit settles a frame or two after us;
       one late re-sync costs nothing and catches both */
    setTimeout(function () { _syncMapView(false); }, 500);
  }

  function _syncViewButton() {
    var el = $('cf-btn-view');
    if (el) el.setAttribute('aria-pressed', STATE.view === '2d' ? 'true' : 'false');
    var ic = $('cf-view-icon');
    if (ic) {
      ic.src = (STATE.view === '2d')
        ? 'https://cdn.shopify.com/s/files/1/0803/9815/6119/files/3d.png?v=1772625064'
        : 'https://cdn.shopify.com/s/files/1/0803/9815/6119/files/2d.png?v=1772625064';
      ic.alt = STATE.view === '2d' ? '3D' : '2D';
    }
  }

  function bindUI() {
    bindBtn('cf-btn-budget', openBudgetDrawer);
    bindBtn('cf-btn-unit', toggleUnits);
    /* The button draws itself: ConfiguratorUI owns the glyph, the Restart swap
       and the click. We hand it the stack and the two things to do. */
    try {
      _undoBtn = global.ConfiguratorUI.undoButton({
        canUndo: function () { return _undo.length > 0; },
        onUndo: undo,
        onReset: function () { undo(); },
        undoLabel: _t('floorplan_undo', 'Undo'),
        resetLabel: _t('reset_changes', 'Reset to default')
      });
    } catch (eUB) { _noteError('undo button: ' + (eUB && eUB.message ? eUB.message : eUB)); }
    /* NO COLOURS ON THE RAIL. They are tiles in the EXTERIOR step now, where
       the step is already called Cladding and colour. The slot they left is
       the SECTION CUT (Marek). */
    bindBtn('cf-btn-cut', function () {
      var tool = _cutInit();
      if (!tool) { _noteError('section: ConfiguratorUI.cutPlanes missing -- kit too old'); return; }
      tool.toggle();
      _log('section ' + (tool.on() ? 'ON ' + JSON.stringify(tool.state()) : 'off'));
    });
    bindBtn('cf-btn-viz', function () { global.SaunaViz.cycle(); });
    bindBtn('cf-btn-view', function (el) {
      /* Centre the VIEW on the model. Not the model on the world.
         _recenterLayout() used to run here, and it slid the building to the
         origin and dragged the map along to compensate. That threw away the
         module's x/z -- which was the only thing remembering where the sauna
         stood, because it is what gets saved. From then on the map had to hold
         the position, and the map cannot: a finger in 2D moves the ortho
         camera, not mapbox, so its centre never changed and every reload came
         back to the address.

         The model stays where it was put. The camera goes to it. */
      rebuild();
      _setView((STATE.view === '2d') ? '3d' : '2d');
      /* 2D has its own framing below; 3D aims at the cabin, not at the group */
      if (STATE.view === '3d') _panToCabin();
    });
    bindBtn('cf-btn-body', function () { toggleBody(); });
    bindBtn('cf-btn-pdf', function () {});
    /* THE WALK IS THE RAIL BUTTON AND NOTHING ELSE (Marek): one tap starts it
       on the exploded view, each tap lands the next item, the last tap puts
       the building back together. No drawer opens, no step changes. */
    bindBtn('cf-btn-assembly', function () { asmNext(); });
  }

  function stub(name) { return function () { _log('stub: ' + name); }; }

  function boot() {
    if (!global.THREE) { _log('boot deferred -- THREE not on window yet'); return; }
    loadPref();
    try { _cfgBootApply(); } catch (eCB) {}   /* a share link overrides the local restore */
    /* A reload is not a resumption.

       mode rides along in the saved payload because the payload is what a
       project IS -- but the mode is not part of the project. It is part of the
       session: which step somebody had open when they last touched it. Putting
       it back drops the tab straight into the editor with nothing driving it.
       Nothing opens a wizard, so _editable() answers false to everything and
       there is nothing to grab; the interior editor comes up anyway on its own
       timer, with the black bar the wizard was built to retire; and the camera
       lands in a plan of a building nobody asked to see.

       And reopening the wizard to make that state coherent was the wrong end of
       the stick -- it made a reload into an editing session, which is not what a
       reload is. A reload lands where a first visit lands: the default page,
       not editing. Whatever was drawn is still there, because THAT is the
       project and it did come back in the payload. */
    STATE.mode = 1;
    STATE.showDetail = false;
    /* now that isMetric is restored: dimensions land on the market's module
       and the height lands on a sheet stop */
    try {
      STATE.footprint.w = clamp(_snapDim(STATE.footprint.w), FOOTPRINT.minW, FOOTPRINT.maxW);
      STATE.footprint.l = clamp(_snapDim(STATE.footprint.l), FOOTPRINT.minL, FOOTPRINT.maxL);
      STATE.footprint.h = _snapH(STATE.footprint.h);
    } catch (eSN) {}
    try { _reclampOpenings(); } catch (eRO) {}
    /* the saved scenery comes back with the project -- and if it is a preset,
       the map hides before it ever flashes */
    try { global.SaunaScenery.apply(); } catch (eSC) {}
    if (!_initEngine()) return;
    bindUI();
    _Banner.init();
    global.SaunaViz.init();
    try { _updateSnapBtn(); } catch (eSB3) {}
    /* THE CATALOG ARRIVES LATE. The budget bridge loads the sheet after the
       first render, so the first plan drew its labels with no productData in
       the world -- and nothing ever asked again, which is why the catalog
       names never appeared no matter how correct the lookup was. A small
       watcher waits for the sheet and orders ONE rebuild when it lands;
       if the data was already there at boot, it does nothing at all. */
    try {
      if (!(global.productData && global.productData.length)) {
        var _pdTries = 0;
        var _pdWatch = setInterval(function () {
          _pdTries++;
          if (global.productData && global.productData.length) {
            clearInterval(_pdWatch);
            try { rebuild(); } catch (ePD) {}
            /* the sheet is here: audit the bill against it once, out loud */
            try {
              var _bqA = global.SaunaBoQ ? global.SaunaBoQ.compute() : null;
              _log(_billAudit((_bqA && _bqA.rows) || []).line);
            } catch (eAu2) {}
          } else if (_pdTries > 40) {
            clearInterval(_pdWatch);
          }
        }, 500);
      }
    } catch (ePW) {}
    /* the deployed version, readable without opening a snapshot */
    try {
      var bA = $('cf-btn-assembly'); if (bA) bA.title = (bA.title || 'Assembly') + ' \u00B7 ' + VERSION;
      var bV = $('cf-btn-viz'); if (bV) bV.title = (bV.title || '') + ' \u00B7 ' + VERSION;
    } catch (eVT) {}
    var _r3 = $('cf-root'); if (_r3) _r3.classList.add('cf-view-3d');
    _initBodyDragHandle();
    _startBodyHandleTracker();
    _ModCtl.init();
    _initArButton();
    if (STATE.bodyVisible) {
      var bb = $('cf-btn-body');
      if (bb) { bb.setAttribute('aria-pressed', 'true'); bb.style.opacity = ''; }
      var bh = $('cf-body-handle');
      if (bh) bh.classList.add('active');
    }

    selectVariant(STATE.variant, true);
    _fitCamera();
    /* the map is created after the scene, so the first zoom has to wait for it */
    /* NO LATE REFRAMING. Two timeouts used to re-fit at 400 and 1200 ms, and
       a camera that moves a second after the page has settled is a jump the
       visitor watches happen. Everything goes through the patched fit now, so
       whoever frames -- boot, a view switch, a resize -- frames the same way
       and there is nothing left to correct afterwards (Marek). */
    _initPdfButtonState();

    try {
      global.addEventListener('error', function (ev) {
        try {
          if (ev && ev.filename) {
            _log('JS ERROR detail: ' + (ev.message || '?') + ' @ ' + ev.filename + ':' + (ev.lineno || '?'));
          }
        } catch (e1) {}
      }, true);
      setTimeout(function () {
        try {
          var out = [];
          var sc = document.scripts || [];
          for (var i = 0; i < sc.length; i++) {
            var src = sc[i].src || '';
            if (!src) continue;
            if (src.indexOf(location.origin) === 0) continue;
            if (!sc[i].crossOrigin) out.push(src.replace(/\?.*$/, ''));
          }
          if (out.length) _log('cross-origin scripts (masked-error candidates): ' + out.join(' | '));
        } catch (e2) {}
      }, 2500);
    } catch (eDG) {}
    try { _initMap(); } catch (eM) { _log('map init failed: ' + (eM && eM.message ? eM.message : eM)); }
    try { _init2DMapPan(); } catch (eP2) {}
    try { _init2DZoom(); } catch (eZ2) {}
    try { _rebuildGrid(); } catch (eG2) {}
    try { if (global.SaunaFootprint) { global.SaunaFootprint.bind(); global.SaunaFootprint.refresh(); } } catch (eFb) { _noteError('footprint: ' + (eFb && eFb.message ? eFb.message : eFb)); }
    /* Wizard first: every visibility decision below asks it what step we are
       on, and until it exists they all fall through to the mode gates. That is
       the fallback for a missing asset, not for the half second before boot
       finishes -- and it was showing the pitch slider outside edit mode. */
    try { _initWizard(); } catch (eWz) { _noteError('wizard: ' + (eWz && eWz.message ? eWz.message : eWz)); }
    try { _clampPitch(); _showPitchSlider(); } catch (ePb) { _noteError('pitch slider: ' + (ePb && ePb.message ? ePb.message : ePb)); }
    /* Boot never framed anything. It set a camera position and trusted it, and
       nothing since has had reason to look -- a reload used to land in the plan.
       Now it lands here, so here has to be right. */
    try { _fit3D(); } catch (eFB) {}
    _log('boot complete -- ' + VERSION);
    try { _snapshot(); } catch (eSn) { _log('snapshot failed: ' + (eSn && eSn.message ? eSn.message : eSn)); }
  }

  var _errBuf = [];
  function _noteError(msg) {
    _errBuf.push(msg);
    if (_errBuf.length > 20) _errBuf.shift();
  }

  /* configurator-ui.js now carries ConfiguratorStage. If map-manager.js is
     still in the page too, Stage gets defined twice and the second one wins,
     leaving every handle bound to a scene nobody renders. Cheap to detect, so
     detect it. */
  function _mapAssetLoaded() {
    try {
      var t = document.getElementsByTagName('script'), i;
      for (i = 0; i < t.length; i++) {
        if (t[i].src && t[i].src.indexOf('map-manager') >= 0) return 'DUPLICATE';
      }
    } catch (e) {}
    return 'no';
  }

  /* THE BILL, AUDITED AGAINST THE CENTRAL SHEET.

     This lib builds its bill by hand -- add(code, label, qty, unit) -- and
     until now nothing checked those codes against anything. The house line
     has a registry and prints NOT BILLED on every snapshot, and that one line
     caught three real faults in a month. This is the shelter's equivalent,
     turned the way this product fails: a code the sheet does not know is a
     row that reaches the customer's budget and is quietly never priced.

     It reads the live sheet the budget bridge loads (window.productData), so
     it audits against what the shop actually sells today, not against a copy
     baked in here. When the sheet has not landed yet it says so instead of
     pretending the bill is clean (Marek). */
  function _billAudit(rows) {
    var seen = {}, codes = [], i, c;
    for (i = 0; i < (rows || []).length; i++) {
      c = rows[i] && rows[i].code;
      if (!c || seen[c]) continue;
      seen[c] = true; codes.push(c);
    }
    var pd = global.productData;
    if (!pd || !pd.length) {
      return { codes: codes.length, missing: null,
               line: 'NOT ON SHEET  cannot audit yet -- the central catalogue has not loaded (' + codes.length + ' code(s) billed)' };
    }
    var bq = global.SaunaBoQ, missing = [];
    for (i = 0; i < codes.length; i++) {
      var row = null;
      try { row = bq && bq._sheetRow ? bq._sheetRow(codes[i]) : null; } catch (e) { row = null; }
      if (!row) missing.push(codes[i]);
    }
    return { codes: codes.length, missing: missing,
             line: missing.length
               ? ('NOT ON SHEET  ' + missing.length + ' of ' + codes.length + ' billed code(s) are not in the central catalogue: ' +
                  missing.join(', ') + ' -- these reach the budget and are never priced')
               : ('NOT ON SHEET  none -- all ' + codes.length + ' billed codes are rows in the central catalogue') };
  }

  /* the footprint as a polygon, for the snapshot line that reported the old
     floorplan module's polygon -- the module is gone and the function with
     it, and the snapshot has been throwing on that line ever since */
  function _fpPolygon() {
    var m = STATE.modules[0] || {}, f = STATE.footprint || {};
    var hx = _extW() / 2, hz = (f.l || 0) / 2, x = m.x || 0, z = m.z || 0;
    return [[x - hx, z - hz], [x + hx, z - hz], [x + hx, z + hz], [x - hx, z + hz]];
  }
  function _snapshot() {
    var f = STATE.footprint || {};
    var out = [];
    function P(x) { out.push(x); _dbg(x); }
    var t, rows = [];
    try { t = _saunaType(); } catch (eT) { t = null; }
    try {
      var _bq = global.SaunaBoQ ? global.SaunaBoQ.compute() : null;
      rows = (_bq && _bq.rows) ? _bq.rows : [];
    } catch (eB) { rows = []; _noteError('BoQ compute threw: ' + (eB && eB.message ? eB.message : eB)); }
    P('===== SNAPSHOT ' + VERSION + ' =====');
    /* first, because it is the one line that says whether the bill can be
       trusted; everything under it is detail */
    try { P(_billAudit(rows).line); } catch (eAu) { P('NOT ON SHEET  audit threw: ' + (eAu && eAu.message ? eAu.message : eAu)); }
    P('footprint  w=' + (+f.w).toFixed(3) + ' l=' + (+f.l).toFixed(3) + ' h=' + (+f.h).toFixed(3));
    var _tp = Math.tan(clamp(STATE.roofPitch || 0, 0, 45) * Math.PI / 180);
    var _rw = (+f.w) + 0.36;
    var _rise = (STATE.roofType === 'gable') ? ((+f.w) / 2) * _tp : (+f.w) * _tp;
    P('roof       type=' + STATE.roofType + ' pitch=' + STATE.roofPitch +
      ' range=' + _pitchMin() + '..' + _pitchMax() +
      ' over=' + STATE.roofOver.x0 + '/' + STATE.roofOver.x1 + '/' + STATE.roofOver.z0 + '/' + STATE.roofOver.z1 +
      ' rise=' + _rise.toFixed(3) + ' eaveDrop=' + (STATE.roofOver.x0 * _tp).toFixed(3) + '/' + (STATE.roofOver.x1 * _tp).toFixed(3) +
      ' apex=' + ((+f.h) + _rise).toFixed(3) + ' (walls only)');
    P('foundation ' + STATE.foundation + ' | mode=' + STATE.mode + ' | envelope=' + STATE.envelope);
    if (t) {
      P('centre     x=' + ((STATE.modules[0] && STATE.modules[0].x) || 0).toFixed(3) +
      ' z=' + ((STATE.modules[0] && STATE.modules[0].z) || 0).toFixed(3));
    P('derived    extW=' + _extW().toFixed(3) + ' panelH=' + _panelH(t).toFixed(3) +
        ' PANEL_BASE=' + PANEL_BASE.toFixed(3) + ' FLOOR_TOP=' + FLOOR_TOP.toFixed(3) +
        ' BEARER.H=' + BEARER.H.toFixed(3));
    } else {
      P('derived    _saunaType() FAILED');
    }
    P('counts     modules=' + STATE.modules.length + ' openings=' + (STATE.openings || []).length +
      ' partitions=' + (STATE.partitions || []).length + ' boqRows=' + rows.length);
    P('wizard     ' + (_wiz ? (WIZ_STEPS.length + ' steps, open=' + _wiz.isOpen() +
      ', step=' + ((_wiz.current() && _wiz.current().key) || '-')) : 'MISSING') +
      ' | undo=' + _undo.length + '/' + UNDO_MAX);
    /* The layout step is four features on one drawing and every one of them was
       silent about itself. sub tells you whether the gestures are live at all;
       parts/doors/rooms tell you whether the thing you cannot see exists. */
    try {
      var PP = global.SaunaPartitions;
      P('layout     sub=' + (PP ? PP._sub : '-') + ' floor=' + (PP ? PP._floor : '-') +
        ' | parts=' + (STATE.partitions || []).length +
        ' doors=' + (STATE.doors || []).length +
        ' rooms=' + (PP && PP.inEditor() ? PP._roomsNamed(0).length : '-') +
        ' names=' + (STATE.roomNames || []).length +
        ' | scissors=' + (PP && PP._scissorList ? PP._scissorList.length : '-') +
        ' grips=' + (global.SaunaFootprint ? global.SaunaFootprint._rects().length : '-') +
        ' icons=' + ((_wiz && _wiz.current() && _wiz.current().icons) ? _wiz.current().icons.length : '-'));
    } catch (eLy) { P('layout     THREW ' + (eLy && eLy.message ? eLy.message : eLy)); }
    /* The plan went orthographic and the map stayed tilted for a whole session
       before anyone could say so, because nothing here ever printed the angle
       mapbox was actually at. */
    try {
      P('mapview    scene=' + STATE.view +
        ' | ENGINE view=' + (global.SaunaEngine ? global.SaunaEngine.view : '-') +
        (global.SaunaEngine && global.SaunaEngine.view !== STATE.view ? '  <-- DISAGREE: setView THREW' : '') +
        ' | cam=' + (global.SaunaEngine && global.SaunaEngine.camera
          ? (global.SaunaEngine.camera.isOrthographicCamera ? 'ortho' : 'persp') : '-') +
        ' | map pitch=' + (_MapManager && _MapManager.map ? _MapManager.map.getPitch().toFixed(1) : '-') +
        ' bearing=' + (_MapManager && _MapManager.map ? _MapManager.map.getBearing().toFixed(1) : '-') +
        ' | base bearing=' + (_MapManager ? (_MapManager.bearing || 0) : '-') +
        (STATE.view === '2d' && _MapManager && _MapManager.map && _MapManager.map.getPitch() > 0.5
          ? '  <-- PLAN ON A TILTED MAP' : ''));
    } catch (eMv) { P('mapview    ' + (eMv && eMv.message ? eMv.message : eMv)); }
    /* "Maximum call stack size exceeded" inside three.mjs, seven times over, is
       not arithmetic -- it is Box3.expandByObject / updateMatrixWorld recursing
       down a graph that does not end. Either a node is its own ancestor, or the
       tree is absurdly deep. three.js's add() only refuses object === this, so
       a.add(b); b.add(a) builds a loop it will happily walk forever.

       Walked with an explicit stack and a seen-set, never with recursion, so the
       probe cannot die of the thing it is looking for. */
    try {
      (function () {
        function walk(root, name) {
          if (!root) { P('graph      ' + name + ': absent'); return; }
          var seen = [], stack = [[root, 0]], n = 0, maxD = 0, cyc = null, guard = 0;
          while (stack.length && guard++ < 200000) {
            var it = stack.pop(), o = it[0], d = it[1];
            if (seen.indexOf(o) >= 0) { cyc = o; break; }
            seen.push(o); n++;
            if (d > maxD) maxD = d;
            var ch = o.children || [];
            for (var i = 0; i < ch.length; i++) stack.push([ch[i], d + 1]);
          }
          var line = 'graph      ' + name + ': nodes=' + n + ' depth=' + maxD;
          if (cyc) {
            line += '  <-- CYCLE at ' + (cyc.type || '?') + ' name=' + (cyc.name || '-') +
                    ' ud=' + JSON.stringify(cyc.userData || {}).slice(0, 60);
          }
          if (guard >= 200000) line += '  <-- RUNAWAY (200k nodes, gave up)';
          P(line);
        }
        var eng = global.SaunaEngine;
        walk(eng && eng.scene, 'scene');
        walk(eng && eng.group, 'group');
        var PP2 = global.SaunaPartitions;
        walk(PP2 && PP2._grp, 'overlay');
        /* the box the two fits both build -- if this throws, that is the bug */
        try {
          var T = global.THREE;
          var b = new T.Box3().setFromObject(eng.group);
          P('graph      Box3(group) = ' + (b.isEmpty() ? 'EMPTY'
            : ('[' + b.min.x.toFixed(1) + ',' + b.min.z.toFixed(1) + '] .. [' +
               b.max.x.toFixed(1) + ',' + b.max.z.toFixed(1) + '] r=' +
               b.getBoundingSphere(new T.Sphere()).radius.toFixed(2) + 'm')));
          var sb = _safeBox();
          if (sb) P('graph      safeBox     = [' + sb.min.x.toFixed(1) + ',' + sb.min.z.toFixed(1) + '] .. [' +
            sb.max.x.toFixed(1) + ',' + sb.max.z.toFixed(1) + '] r=' +
            sb.getBoundingSphere(new T.Sphere()).radius.toFixed(2) + 'm');
          /* the hunt: name every object whose own numbers are not numbers */
          var bad = [];
          eng.group.updateWorldMatrix(true, true);
          eng.group.traverse(function (o) {
            var why = null;
            if (o.position && (!isFinite(o.position.x) || !isFinite(o.position.y) || !isFinite(o.position.z))) why = 'position';
            else if (o.scale && (!isFinite(o.scale.x) || !isFinite(o.scale.y) || !isFinite(o.scale.z))) why = 'scale';
            else if (o.geometry && o.geometry.attributes && o.geometry.attributes.position) {
              var a = o.geometry.attributes.position.array;
              for (var i = 0; i < a.length; i++) if (!isFinite(a[i])) { why = 'vertex[' + i + ']'; break; }
            }
            if (why && bad.length < 6) {
              var path = [], w = o;
              while (w && w !== eng.group) { path.unshift(w.type + (w.name ? ':' + w.name : '')); w = w.parent; }
              bad.push(why + ' @ ' + path.join('/') + ' ud=' + JSON.stringify(o.userData || {}).slice(0, 80));
            }
          });
          if (bad.length) { P('graph      NaN OFFENDERS (' + bad.length + '):'); for (var bi = 0; bi < bad.length; bi++) P('  ! ' + bad[bi]); }
          else P('graph      no NaN in the graph right now');
        } catch (eB) {
          P('graph      Box3(group) THREW ' + (eB && eB.message ? eB.message : eB));
        }
      })();
    } catch (eG) { P('graph      probe failed: ' + (eG && eG.message ? eG.message : eG)); }
    try {
      var _bq = global.SaunaBoQ;
      P('catalog    sheet=' + (global.productData && global.productData.length ? global.productData.length + ' rows' : 'NOT LOADED (fallback EN)') +
        ' | locale=' + ((global.SAUNA_BOOT && global.SAUNA_BOOT.locale) || 'en'));
      /* three witnesses, each printed with the row it matched: if the sheet is
         shifted or the CZ cells are wrong, this line says WHICH, instead of the
         assembly quietly wearing another item's name */
      var _wit = ['KVR10015', 'KVR03050', 'FFW00004'];
      for (var _wi = 0; _wi < _wit.length; _wi++) {
        var _wr = _bq._sheetRow(_wit[_wi]);
        P('  ' + _wit[_wi] + ' -> "' + _bq.catName(_wit[_wi], '?') + '"' +
          (_wr ? ('  [row id=' + _wr[0] + ' en="' + String(_wr[3]).slice(0, 30) + '"]') : '  [no sheet row, CAT fallback]'));
      }
    } catch (eCat) { P('catalog    ' + (eCat && eCat.message ? eCat.message : eCat)); }
    try {
      var _cw = global.SaunaWalls, _ct = _typeOf(STATE.modules[0]);
      var _cl = _cw.layout();
      var _cf = null, _cs = null;
      for (var _ci = 0; _ci < _cl.panels.length; _ci++) {
        if (_cl.panels[_ci].wall === 'front' && !_cf) _cf = _cl.panels[_ci];
        if (_cl.panels[_ci].wall === 'sideR' && !_cs) _cs = _cl.panels[_ci];
      }
      var _cin = (INNER_MARGIN + PANEL_INSET) / 2;
      var _crx = _extW() / 2 - _cin, _crz = _ct.L / 2 - _cin;
      try {
        var opsW2 = (STATE.openings || []);
        P('opcat      productData rows=' + ((global.productData && global.productData.length) | 0) + ' | openings=' + opsW2.length);
        for (var ow2 = 0; ow2 < opsW2.length; ow2++) {
          var oW = opsW2[ow2];
          var ciW = global.SaunaBoQ.opCatalog(oW);
          var why = ciW ? ('HIT id=' + ciW.id + ' name="' + ciW.name + '" dim="' + ciW.dim + '"')
                        : (!(global.productData && global.productData.length) ? 'MISS: no productData'
                          : (oW.sku ? ('MISS: sku ' + oW.sku + ' not in sheet & dims ' + Math.round(oW.w * 1000) + 'x' + Math.round(oW.h * 1000) + ' unmatched')
                                    : ('MISS: no sku, dims ' + Math.round(oW.w * 1000) + 'x' + Math.round(oW.h * 1000) + ' unmatched')));
          P('  op[' + ow2 + '] ' + oW.wall + ' ' + (oW.sku || '(bez sku)') + ' -> ' + why);
        }
      } catch (eOC) { P('opcat      witness failed: ' + eOC.message); }
      try {
        var CLW = (global.SaunaRenderer && global.SaunaRenderer._cladW) || null;
        P('cladwitness ' + (CLW ? ('planks=' + CLW.n + ' roof=' + (CLW.gable ? 'gable' : 'mono')) : 'none'));
        var PDs = (global.SaunaRenderer && global.SaunaRenderer._partDoor) || null;
        P('partdoor   ' + (PDs ? ('doors=' + PDs.doors + ' strips=' + PDs.strips + ' leaves=' + PDs.leaves +
          (PDs.errs.length ? ' | ' + PDs.errs.slice(0, 3).join(' ; ') : '  (vse postaveno)')) : 'no partition doors'));
      } catch (ePDs) {}
      try {
        var CWt = (global.SaunaRenderer && global.SaunaRenderer._ceilWitness) || null;
        P('ceilbuild  ' + (CWt ? (CWt.built ? ('lining OK y=' + CWt.y.toFixed(3) + ' pieces=' + (CWt.pieces || 1) + ' (sauna=' + (CWt.sauna || 0) + ')') : ('FAILED: ' + CWt.err)) : 'not attempted (roof block skipped?)'));
      } catch (eCWt) {}
      try {
        var FGs = (global.SaunaRenderer && global.SaunaRenderer._floorGuard) || { n: 0, hits: [] };
        P('floorguard bodies=' + FGs.n + ' konflikty=' + FGs.hits.length + (FGs.hits.length ? '' : '  (zadne prieniky, zadne koplanarni pary)'));
        for (var fgh = 0; fgh < Math.min(6, FGs.hits.length); fgh++) P('  ' + FGs.hits[fgh]);
      } catch (eFGs) {}
      P('nangate    partition segments refused for non-finite coords: ' + ((global.SaunaRenderer && global.SaunaRenderer._nanSkipped) | 0));
      P('frameguard studs removed by the collision guard: ' + (_cw._guarded | 0) +
        (_cw._guarded ? '  <-- an origin mismatch still feeds it, spans vs studs' : '  (grid and openings agree)'));
      if (_cf && _cs) P('corner     frontRailEnd=' + (_cf.to).toFixed(3) +
        ' sideStudOuterFace=' + (_crx + _cw.STUD.d / 2).toFixed(3) +
        ' | sideRunEnd=' + (_cs.to).toFixed(3) + ' frontInnerFace=' + (_crz - _cw.STUD.d / 2).toFixed(3) +
        ' | extW=' + _extW().toFixed(3) + ' t.L=' + _ct.L.toFixed(3) + ' fp=' + STATE.footprint.w + 'x' + STATE.footprint.l);
    } catch (eCW) { P('corner     ' + (eCW && eCW.message ? eCW.message : eCW)); }
    /* THIS LINE HAS BEEN KILLING THE SNAPSHOT, AND NOT BECAUSE OF THE MODULE.

       _FP appears exactly once in this file: here. It is never declared and
       never assigned -- shelters copied the line from a product that has one.
       Reading an undeclared name throws a ReferenceError, and this P() is
       OUTSIDE the try above it, so the snapshot stopped dead right here and
       everything below it was simply absent. Not empty, not wrong -- absent,
       with nothing to say it had been cut off.

       And the module it asked about is going: configurator-floorplan.js is the
       editor lifted out of the master house, this product never wired it, and
       its own note four hundred lines up says INERT. So the line reports the
       one thing now worth reporting -- whether the file is still on the page. */
    P('floorplan  old module=' + (global.ConfiguratorFloorplan
        ? (global.ConfiguratorFloorplan.VERSION + ' STILL LOADED -- the tag can come out')
        : 'gone, as intended') +
      ' | polygon=' + JSON.stringify(_fpPolygon()));
    /* Twice today I inspected a button that was not on the page. So stop
       naming one and list the rail: every button, its id, and what is actually
       inside it. Whatever is rendering that icon has to appear here. */
    try {
      P('undobtn    api=' + (global.ConfiguratorUI && global.ConfiguratorUI.undoButton ? 'yes' : 'MISSING -- ui asset older than v29') +
        ' | wired=' + (!!_undoBtn));
      var _rails = document.querySelectorAll('.cf-sidebar, .cf-rail, nav');
      P('rails      ' + _rails.length + ' found');
      for (var _ri = 0; _ri < _rails.length; _ri++) {
        var _btns = _rails[_ri].querySelectorAll('button, a');
        P('  rail ' + _ri + ' (' + (_rails[_ri].className || _rails[_ri].tagName) + '): ' + _btns.length + ' buttons');
        for (var _bi = 0; _bi < _btns.length; _bi++) {
          var _b = _btns[_bi];
          var _kid = _b.querySelector('svg') ? 'svg'
                   : _b.querySelector('img') ? ('img ' + (_b.querySelector('img').src || '').split('/').pop().split('?')[0])
                   : _b.querySelector('[data-cfg-icon]') ? ('data-cfg-icon=' + _b.querySelector('[data-cfg-icon]').getAttribute('data-cfg-icon'))
                   : (_b.innerHTML.trim() ? 'html(' + _b.innerHTML.trim().length + ' ch)' : 'EMPTY');
          P('    #' + (_b.id || '(no id)') + '  ->  ' + _kid + '  title=' + (_b.title || '-'));
        }
      }
    } catch (eUD) { P('undobtn    ' + (eUD && eUD.message ? eUD.message : eUD)); }
    P('prefKey    ' + PREF_KEY + ' | roofCycler=' + (!!$('cf-info-roof')) +
      ' | footDrag=' + (!!(global.SaunaFootprint && global.SaunaFootprint._h)));
    P('ui         ' + (global.ConfiguratorUI ? global.ConfiguratorUI.VERSION : 'MISSING') +
      ' | stage=' + (!!global.ConfiguratorStage) + ' | map=' + (!!global.MapManager) +
      ' | mapAsset=' + _mapAssetLoaded());
    P('mapwire    sdk=' + (!!global.mapboxgl) + ' | init=' + (!!_MapManager) +
      ' | btn=' + (!!$('cf-btn-map')) + ' | canvas=' + (!!$('cf-mapbox')) +
      ' | ctrls=' + (!!$('cf-map-controls')) + ' | modal=' + (!!$('cf-map-modal-backdrop')) +
      ' | locked=' + (!!STATE.mapLocked));
    P('three      ' + ((global.THREE && global.THREE.REVISION) ? ('r' + global.THREE.REVISION) : 'MISSING') +
      ' | engine=' + (!!(global.SaunaEngine && global.SaunaEngine.scene)) +
      ' | boqLib=' + (!!global.SaunaBoQ));
    if (_errBuf.length) {
      P('errors     ' + _errBuf.length);
      for (var ei = 0; ei < _errBuf.length; ei++) P('  ! ' + _errBuf[ei]);
    } else {
      P('errors     none');
    }
    P('===== END SNAPSHOT =====');
    return out.join('\n');
  }

  function _t(key, def) {
    /* liquid escapes what it injects, so a locale value can arrive as
       'ROOF &amp; BASE' -- and textContent then prints the entity verbatim.
       Every hit is decoded ONCE here at the root, so no caller ever has to
       remember; misses return the JS default, which was never escaped. */
    var TT = (global.SAUNA_BOOT && global.SAUNA_BOOT.i18n) || {};
    var v = TT[key];
    return (typeof v === 'string' && v && !/translation missing/i.test(v)) ? _deent(v) : def;
  }
  function _esc(x) {
    return String(x).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
  function _loadMapboxSDK() {
    if (global.mapboxgl) return Promise.resolve();
    if (global.__cfBaseMapSDKPromise) return global.__cfBaseMapSDKPromise;
    global.__cfBaseMapSDKPromise = new Promise(function (resolve, reject) {
      if (!document.getElementById('cf-mapbox-gl-css')) {
        var css = document.createElement('link');
        css.id = 'cf-mapbox-gl-css';
        css.rel = 'stylesheet';
        css.href = 'https://api.mapbox.com/mapbox-gl-js/v3.0.1/mapbox-gl.css';
        document.head.appendChild(css);
      }
      var existing = document.getElementById('cf-mapbox-gl-js');
      if (existing) {
        existing.addEventListener('load', function () { resolve(); });
        existing.addEventListener('error', function () { reject(new Error('Mapbox SDK load failed')); });
        return;
      }
      var script = document.createElement('script');
      script.id = 'cf-mapbox-gl-js';
      script.src = 'https://api.mapbox.com/mapbox-gl-js/v3.0.1/mapbox-gl.js';
      script.onload = function () { resolve(); };
      script.onerror = function () { reject(new Error('Mapbox SDK load failed')); };
      document.head.appendChild(script);
    });
    return global.__cfBaseMapSDKPromise;
  }

  var _ProjectStore = null;


  function _applyMapLockState() {
    if (!_MapManager || !_MapManager.map) return;
    var m = _MapManager.map;
    var locked = !!STATE.mapLocked;
    try {
      if (locked) {
        m.dragPan.disable();
        m.scrollZoom.disable();
        m.boxZoom.disable();
        m.dragRotate.disable();
        m.doubleClickZoom.disable();
        m.touchZoomRotate.disable();
      } else {
        m.dragPan.enable();
        m.scrollZoom.enable();
        m.boxZoom.enable();
        m.dragRotate.enable();
        m.doubleClickZoom.enable();
        m.touchZoomRotate.enable();
      }
    } catch (e) {}
    var rotateInput = document.getElementById('cf-map-rotate');
    if (rotateInput) {
      rotateInput.disabled = locked;
      rotateInput.style.opacity = locked ? '0.4' : '';
      rotateInput.style.pointerEvents = locked ? 'none' : '';
    }
  }

  function _init2DZoom() {
    function getCanvas() {
      return global.SaunaEngine && global.SaunaEngine.renderer && global.SaunaEngine.renderer.domElement;
    }
    function applyZoom(factor) {
      var eng = global.SaunaEngine;
      var cam = eng && eng.orthoCam;
      if (!cam) return;
      cam.zoom = Math.max(0.2, Math.min(8, cam.zoom * factor));

      /* Zoom changes scale, not position. This used to slam the camera and the
         orbit target back to the origin on every wheel notch and every pinch --
         inherited from the container, where the model was the thing that moved
         and the ground never did. Here the map is the only memory of where the
         building stands, so snapping home threw that memory away: pan, zoom,
         and you were back at the address. Keep where we are looking. */
      var tgt = (eng.orthoControls && eng.orthoControls.target) || { x: 0, z: 0 };
      var upY = cam.position.y || 40;
      cam.position.set(tgt.x, upY, tgt.z + 0.0001);
      cam.up.set(0, 0, -1);
      cam.lookAt(tgt.x, 0, tgt.z);
      cam.updateProjectionMatrix();
      if (eng.renderer && eng.camera) eng.renderer.render(eng.scene, eng.camera);
      try { if (eng.orthoControls && eng.orthoControls.dispatchEvent) eng.orthoControls.dispatchEvent({ type: 'change' }); } catch (eZ) {}
    }

    var canvas = getCanvas();
    if (!canvas) { setTimeout(_init2DZoom, 100); return; }

    canvas.addEventListener('wheel', function (e) {
      if (STATE.view !== '2d') return;
      e.preventDefault();
      var factor = e.deltaY > 0 ? (1 / 1.1) : 1.1;
      applyZoom(factor);
    }, { passive: false });

    var pinch = { active: false, lastDist: 0 };
    canvas.addEventListener('touchstart', function (e) {
      if (STATE.view !== '2d') return;
      if (e.touches.length === 2) {
        pinch.active = true;
        pinch.lastDist = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY
        );
      }
    }, { passive: true });
    canvas.addEventListener('touchmove', function (e) {
      if (STATE.view !== '2d' || !pinch.active || e.touches.length !== 2) return;
      e.preventDefault();
      var dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      if (pinch.lastDist > 0) applyZoom(dist / pinch.lastDist);
      pinch.lastDist = dist;
    }, { passive: false });
    function endPinch(e) {
      if (!e.touches || e.touches.length < 2) pinch.active = false;
    }
    canvas.addEventListener('touchend',    endPinch);
    canvas.addEventListener('touchcancel', endPinch);
  }

  function _init2DMapPan() {
    var canvas = global.SaunaEngine && global.SaunaEngine.renderer && global.SaunaEngine.renderer.domElement;
    if (!canvas) { setTimeout(_init2DMapPan, 100); return; }
    var drag = { active: false, lastX: 0, lastY: 0, pointerId: null };

    var activePointers = 0;
    canvas.addEventListener('pointerdown', function () { activePointers++; if (activePointers > 1) drag.active = false; }, true);
    function _decPointers() { activePointers = Math.max(0, activePointers - 1); }
    canvas.addEventListener('pointerup', _decPointers, true);
    /* panel isolation tap: plain click path, frame stage only, never steals
       from an editor gesture */
    (function () {
      var d0 = null;
      canvas.addEventListener('pointerdown', function (e) { d0 = { x: e.clientX, y: e.clientY }; }, true);
      canvas.addEventListener('pointerup', function (e) {
        if (!d0) return;
        var moved = Math.abs(e.clientX - d0.x) + Math.abs(e.clientY - d0.y) > 8;
        d0 = null;
        if (moved) return;
        try {
          if (global.SaunaViz) global.SaunaViz.tapAt(e.clientX, e.clientY);
        } catch (eT) {}
      }, true);
    })();
    canvas.addEventListener('pointercancel', _decPointers, true);

    function canPan() {
      if (STATE.view !== '2d') return false;
      if (!_MapManager || !_MapManager.map) return false;
      var mapDiv = document.getElementById('cf-mapbox');
      if (!mapDiv || !mapDiv.classList.contains('cf-map-visible')) return false;

      return true;
    }

    canvas.addEventListener('pointerdown', function (e) {
      if (!canPan()) return;
      if (activePointers > 1) return;
      if (e.button !== 0 && e.pointerType === 'mouse') return;
      drag.active = true;
      drag.lastX = e.clientX;
      drag.lastY = e.clientY;
      drag.pointerId = e.pointerId;
      try { canvas.setPointerCapture(e.pointerId); } catch (_) {}
    });

    canvas.addEventListener('pointermove', function (e) {
      if (!drag.active || e.pointerId !== drag.pointerId) return;
      if (activePointers > 1) { drag.active = false; return; }
      if (!canPan()) { drag.active = false; return; }
      var dx = e.clientX - drag.lastX;
      var dy = e.clientY - drag.lastY;
      drag.lastX = e.clientX;
      drag.lastY = e.clientY;
      if (_MapManager.map && (dx !== 0 || dy !== 0)) {
        _MapManager.map.panBy([-dx, -dy], { animate: false });
      }
    });

    function endDrag(e) {
      if (!drag.active) return;
      drag.active = false;
      try { if (e) canvas.releasePointerCapture(e.pointerId); } catch (_) {}
    }
    canvas.addEventListener('pointerup',     endDrag);
    canvas.addEventListener('pointercancel', endDrag);
    canvas.addEventListener('pointerleave',  endDrag);
  }

  function _rebuildGrid() {
    var S = global.ConfiguratorStage;
    if (!S || !S.rebuildGrid) return;
    S.rebuildGrid({
      size: STATE.isMetric ? 25 : 24.384,
      step: STATE.isMetric ? 1.0 : 0.3048
    });
  }

  function _injectMapCSS() {
    if (document.getElementById('cf-container-map-css')) return;
    var css = [
      '.cf-map-modal-backdrop {',
      '  position: fixed; inset: 0; z-index: 9999;',
      '  background: rgba(0,0,0,0.45); display: none;',
      '  align-items: center; justify-content: center;',
      '}',
      '.cf-map-modal-backdrop.open { display: flex; }',
      '.cf-map-modal-sugg-item {',
      '  padding: 9px 12px; font: 12px/1.35 Montserrat, sans-serif; color: #333;',
      '  cursor: pointer; border-bottom: 1px solid #f0f0f0;',
      '}',
      '.cf-map-modal-sugg-item:last-child { border-bottom: none; }',
      '.cf-map-modal-sugg-item:hover { background: #f5f5f0; }'
    ].join('\n');
    var style = document.createElement('style');
    style.id = 'cf-container-map-css';
    style.appendChild(document.createTextNode(css));
    document.head.appendChild(style);
  }

  function _wireFrostZone() {
    var sel = document.getElementById('cf-map-frost-zone');
    if (!sel || sel.getAttribute('data-cf-bound')) return;
    sel.setAttribute('data-cf-bound', '1');
    sel.value = String(STATE.climateZone || 0);
    sel.addEventListener('change', function () {
      STATE.climateZone = parseFloat(sel.value) || 0;
      savePref();
      _log('climate zone -> ' + STATE.climateZone);
    });
  }


  var _MapManager = null;

  /* Reading the code has now failed three times, so stop reading it. Every
     link that can move the map announces itself with a timestamp and the centre
     before and after. Whatever writes the wrong value has to appear in this
     list, and the order tells us whether restore ran too early, ran and was
     overwritten, or never ran at all. */
  function _initMap() {
    var token = (global.SAUNA_BOOT && global.SAUNA_BOOT.mapboxToken) || global.MAPBOX_TOKEN;
    if (!token) {
      _log('map: no MAPBOX_TOKEN -- map feature disabled');
      return;
    }
    _injectMapCSS();
    _loadMapboxSDK().then(function () {
      _MapManager = global.MapManager;
      if (!_MapManager) { _log('map: map-manager.js asset not loaded'); return; }
      var boot = global.SAUNA_BOOT || {};
      var storageKey = 'levaMap:p' + (boot.productId || '0') + ':c' + (boot.customerId || 'anon');
      _MapManager.init({
        token: token,
        storageKey: storageKey,
        i18n: boot.i18n || {},
        lock: true,
        getLocked: function () { return !!STATE.mapLocked; },
        onLockChange: function (locked) {
          STATE.mapLocked = !!locked;
          _applyMapLockState();
          savePref();
          if (_ProjectStore && _ProjectStore._initialized) _ProjectStore.publish();
        },
        modalExtraHTML:
          '<div class="cf-map-modal-zone" style="margin:12px 0 4px;display:flex;flex-direction:column;gap:5px;">' +
          '  <label for="cf-map-frost-zone" style="font-size:12px;color:#555;">' +
          _esc(_t('base_frost_zone_label', 'Climate zone (or pick directly, no address):')) + '</label>' +
          '  <select id="cf-map-frost-zone" style="padding:8px;border:1px solid #ccc;border-radius:6px;font-size:14px;width:100%;background:#fff;">' +
          '    <option value="0">' + _esc(_t('base_frost_zone_auto', 'Auto (from address)')) + '</option>' +
          '    <option value="0.6">0.6 m</option>' +
          '    <option value="0.8">0.8 m</option>' +
          '    <option value="1.0">1.0 m</option>' +
          '    <option value="1.2">1.2 m</option>' +
          '    <option value="1.5">1.5 m</option>' +
          '  </select>' +
          '</div>',
        onMarkupReady: function () { _wireFrostZone(); },
        engine: {
          camera:        global.SaunaEngine.camera,
          controls:      global.SaunaEngine.controls,
          orthoCamera:   global.SaunaEngine.orthoCam,
          orthoControls: global.SaunaEngine.orthoControls
        },
        getView: function () { return STATE.view === '2d' ? '2d-top' : '3d'; },
        getRootSize: function () {
          var w = document.getElementById('cf-root');
          return { w: w ? w.clientWidth : 0, h: w ? w.clientHeight : 0 };
        },
        getPadStep: function () {
          return 2;
        },
        onChange: function () {},
        requestRender: function () {
          var eng = global.SaunaEngine;
          if (eng && eng.renderer && eng.camera) eng.renderer.render(eng.scene, eng.camera);
        }
      });
      _MapManager.loadFromStorage();
      _applyMapLockState();
    }).catch(function (err) {
      _log('map: ' + (err && err.message ? err.message : err));
    });
  }


  /* ---------- 2D PLAN OVERLAY ------------------------------------------
     Switching to 2D gives a top-down ORTHO camera; the roof strip (v59) takes
     the roof off, but a bare frame seen from above is not a plan. This draws
     the plan ON TOP of the canvas: rooms, wall band, opening symbols and room
     names -- the same drawing the PDF uses, so the screen and the document can
     never drift apart. Mapping comes from the live camera, so it follows pan
     and zoom for free.

     A canvas overlay rather than SVG: it lets the plan reuse _pdfDrawFloor
     verbatim instead of a second implementation of every symbol. */
  global.SaunaPlan2D = {
    el: null,
    ensure: function () {
      if (this.el) return this.el;
      if (typeof document === 'undefined') return null;
      var root = $('cf-root'); if (!root) return null;
      var cv = document.createElement('canvas');
      cv.id = 'cf-plan2d';
      cv.style.cssText = 'position:absolute;left:0;top:0;width:100%;height:100%;' +
                         'pointer-events:none;display:none;z-index:6;';
      root.appendChild(cv);
      this.el = cv;
      return cv;
    },
    show: function () { var c = this.ensure(); if (!c) return; c.style.display = 'block'; this._bind(); this.refresh(); },
    /* the plan is painted in SCREEN space, so it has to be repainted whenever
       the camera or the canvas moves -- pan, zoom, orientation change */
    _bind: function () {
      if (this._bound) return;
      var eng = global.SaunaEngine, self = this;
      var redraw = function () { if (STATE.view === '2d') self.refresh(); };
      try { if (eng && eng.controls && eng.controls.addEventListener) eng.controls.addEventListener('change', redraw); } catch (e1) {}
      try { if (eng && eng.orthoControls && eng.orthoControls.addEventListener) eng.orthoControls.addEventListener('change', redraw); } catch (e2) {}
      try { if (global.addEventListener) global.addEventListener('resize', redraw); } catch (e3) {}
      this._bound = true;
    },
    hide: function () { if (this.el) this.el.style.display = 'none'; },
    /* world -> screen, read off the camera itself: project the origin and one
       metre along each axis. Top-down ortho keeps the axes aligned, so two
       probes are enough and the result tracks any zoom the user does. */
    _map: function () {
      var eng = global.SaunaEngine, T = global.THREE;
      if (!eng || !eng.camera || !eng.renderer || !T) return null;
      var el = eng.renderer.domElement;
      var w = el.clientWidth || el.width, h = el.clientHeight || el.height;
      if (!w || !h) return null;
      function p(x, z) {
        var v = new T.Vector3(x, 0, z).project(eng.camera);
        return { x: (v.x * 0.5 + 0.5) * w, y: (-v.y * 0.5 + 0.5) * h };
      }
      var o = p(0, 0), ux = p(1, 0), uz = p(0, 1);
      var sx = ux.x - o.x, sz = uz.y - o.y;
      if (!isFinite(sx) || !isFinite(sz) || Math.abs(sx) < 1e-6) return null;
      return { ppm: sx, ox: o.x, oy: o.y, w: w, h: h, flip: sz < 0 };
    },
    refresh: function () {
      try {
        if (STATE.view !== '2d') { this.hide(); return; }
        /* THE WIZARD OWNS ITS OWN DRAWING. The FLOORPLAN step already renders
           rooms, names and symbols its own way; painting this plan on top of
           it doubled every room name and dropped koty into an editor that
           never had them. The steps stay exactly as they were -- this overlay
           is only for the plain 2D view (Marek). */
        if ((STATE.mode | 0) === 3) { this.hide(); return; }
        var cv = this.ensure(); if (!cv) return;
        var m = this._map(); if (!m) return;
        var dpr = global.devicePixelRatio || 1;
        if (cv.width !== Math.round(m.w * dpr) || cv.height !== Math.round(m.h * dpr)) {
          cv.width = Math.round(m.w * dpr); cv.height = Math.round(m.h * dpr);
        }
        var ctx = cv.getContext('2d'); if (!ctx) return;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, m.w, m.h);
        /* one shared drawing with the PDF: same rooms, same wall band, same
           window and door symbols, same room names */
        var lvl = 0;
        try { lvl = (STATE.modules && STATE.modules[0] ? (STATE.modules[0].lvl | 0) : 0); } catch (eL) {}
        _pdfDrawFloor(ctx, lvl, m.ppm, m.ox, m.oy);
      } catch (e) { _log('plan2d: ' + (e && e.message ? e.message : e)); }
    }
  };

/* ===== ZONE A -- replace from here to the END of the file ============ */

  /* -------------------------------------------------------------------------
     WHAT THIS BUILDING IS, FOR THE ASSISTANT.

     A SHELTER IS POSTS AND A ROOF, and what makes it a shelter rather than a
     cabin is which sides are filled in. That is the first thing the assistant
     has to know: three open sides and one closed is a carport, four closed is
     a shed, and the budget in front of it differs by the cladding on however
     many walls were switched on.

     It is the only one of these products with a real roof - a pitch, a type
     and an overhang - so it declares one, unlike the container and the pool.
     Openings and rooms are declared only when there are any: an open shelter
     has neither and would otherwise report zeroes as though they were facts.

     totalFloorArea is the lib's own function, so the assistant and the bill of
     quantities measure the same building.
     ------------------------------------------------------------------------- */
  var SHELTER_SIDE_NAMES = { front: 'front', rear: 'rear', sideL: 'left side', sideR: 'right side' };

  function _factSides() {
    var F = STATE.wallFill || {}, closed = [], open = [], k;
    for (k in SHELTER_SIDE_NAMES) if (SHELTER_SIDE_NAMES.hasOwnProperty(k)) {
      if (F[k]) closed.push(SHELTER_SIDE_NAMES[k]); else open.push(SHELTER_SIDE_NAMES[k]);
    }
    return { closed: closed, open: open };
  }

  function _factGlazed() {
    var ops = STATE.openings || [], i, o, w, h, ar, glazed = 0, big = null, bigA = -1;
    for (i = 0; i < ops.length; i++) {
      o = ops[i];
      w = Number(o.w) || 0; h = Number(o.h) || 0; ar = w * h;
      if (o.kind === 'window' || o.kind === 'glazed' || (Number(o.sill) || 0) > 0.05) glazed += ar;
      if (ar > bigA) { bigA = ar; big = { w: Math.round(w * 100) / 100, h: Math.round(h * 100) / 100 }; }
    }
    return { count: ops.length, glazedArea: Math.round(glazed * 10) / 10, largest: big };
  }

  function shelterFacts() {
    var S = STATE, fp = S.footprint || {};
    var floor = 0;
    try { floor = totalFloorArea(); } catch (e) { floor = 0; }
    if (!floor) floor = (Number(fp.w) || 0) * (Number(fp.l) || 0);
    var sides = _factSides();
    var over = S.roofOver || {};
    var reach = Math.max(Number(over.x0) || 0, Number(over.x1) || 0,
                         Number(over.z0) || 0, Number(over.z1) || 0);

    var extra = [];
    extra.push('this is a shelter: posts and a roof, and the sides are filled in or left open one at a time. '
      + (sides.closed.length === 0
          ? 'Every side is open, so it is a roof on posts - a carport or a shaded seating area, not a room.'
          : (sides.closed.length === 4
              ? 'All four sides are closed, so it is an enclosed building.'
              : ('Closed on the ' + sides.closed.join(' and the ')
                 + ', open on the ' + sides.open.join(' and the ') + '.')))
      + ' How many sides are closed is what most of the cladding and framing in this budget follows.');
    if (sides.closed.length){
      extra.push('the closed sides are filled with ' + (S.fillType === 'slats'
        ? ('slats at a ' + (Math.round((S.slatGap != null ? S.slatGap : 0.04) * 1000)) + ' mm gap, so they screen rather than seal')
        : 'solid boards') + '.');
    }
    extra.push('the wall build-up is the ' + (S.envelope === 'interior' ? 'interior' : 'exterior')
      + ' one' + (S.envelope === 'interior' ? ' - no membrane, no cavity, no cladding, because it stands inside another building' : ' - the full outdoor wall with membrane, cavity and cladding') + '.');
    if (S.terraceOn){
      extra.push('there is a terrace decked around it, surfaced in ' + (S.terraceSurface || 'deck') + '.');
    }
    if (S.pergolaOn || (S.pergolas || []).length){
      extra.push('a pergola is on the project.');
    }

    var out = {
      product: 'shelter',
      version: VERSION,
      units: S.isMetric ? 'metric' : 'imperial',
      width: Math.round((Number(fp.w) || 0) * 100) / 100,
      length: Math.round((Number(fp.l) || 0) * 100) / 100,
      footprintArea: Math.round(floor * 10) / 10,
      storeys: 1,
      floorArea: Math.round(floor * 10) / 10,
      clearHeight: Math.round((Number(fp.h) || 0) * 100) / 100,
      roof: {
        type: S.roofType || 'mono',
        pitch: (S.roofPitch === 0 || S.roofPitch) ? S.roofPitch : null,
        overhang: Math.round(reach * 100) / 100,
        covering: ''
      },
      foundation: S.foundation || '',
      foundationOptions: ['screws', 'pads'],
      extra: extra,
      site: { address: String(S.address || ''), frostDepth: Number(S.climateZone) || 0 },
      changeable: [
        'the footprint - the span and the depth, and the clear height under the roof',
        'the roof type - mono pitch or gable - and its pitch and overhang',
        'which sides are closed in and which are left open, one side at a time',
        'what a closed side is filled with - solid boards or slats, and the gap between the slats',
        'the wall build-up - the outdoor one, or the interior one for a shelter standing inside another building',
        'what it stands on - ground screws or pads',
        'the colours and the cladding pattern',
        'a terrace around it and a pergola',
        'where it stands on the map, and the climate zone that sets the footing depth',
        'metric or imperial display'
      ],
      fixed: [
        'a second storey - this product builds one level under one roof',
        'a basement or a cellar - what it stands on is ground screws or pads',
        'the structural system - it is a timber post and beam frame carrying the roof',
        'a roof covering chosen by hand: the covering follows the colour scheme rather than being picked on its own'
      ]
    };

    /* DECLARED ONLY WHEN THERE ARE ANY. An open shelter has no windows and no
       rooms, and a zero printed as a fact reads as a fact. */
    var g = _factGlazed();
    if (g.count) out.openings = g;
    if ((S.roomNames || []).length) out.rooms = [];

    return out;
  }

  /* ===== THE REFERENCE DESIGNS (shelter-v120-ref) ===========================
     Marek 2026-10-22 16:51: "chtelo by to jeste udelat referencni projekty
     sauna, shelters. Aspon 20 od kazdeho." The bench's REF (bref, snippet
     configurator-bench.liquid) copied: the snippet's admin REF button puts
     every design of refSet() on the stage (refApply -- a whole payload, the
     way a ?cfg= link opens one), waits for the budget, saves it to
     leva_configs (cfgSave, its ?cfg= id) and writes budget_details
     <admin>__shelters__ref-<id> with a US town of the design's own list (the
     bench's pool when the list is used up), 30 km from every other pin.
     Sizes in feet (built in US lumber), the post height in feet, the pitch in
     degrees, the slat gap in inches; walls: f(ront) r(ear) l(eft) R(ight)
     filled. Colours are not part of a design (every load opens on natural
     wood -- Marek); facadeColor is the finish the bill prices. */
  var REF_SET = [
    { id: 'r1', t: 1, use: 'single carport', w: 12, l: 20, h: 8, roof: 'gable', p: 18, cov: 'trapez', fnd: 'screws', fac: 'wood1', fill: 'boards', gap: 0, walls: '', col: 'larch',
      places: [['Gilbert','AZ',33.35,-111.79],['Round Rock','TX',30.51,-97.68],['Murfreesboro','TN',35.85,-86.39],['Bentonville','AR',36.37,-94.21]] },
    { id: 'r2', t: 1, use: 'double carport', w: 20, l: 20, h: 9, roof: 'gable', p: 20, cov: 'shingle', fnd: 'pads', fac: 'wood2', fill: 'boards', gap: 0, walls: 'r', col: 'shou',
      places: [['Conroe','TX',30.31,-95.46],['Lakeland','FL',28.04,-81.95],['Clarksville','TN',36.53,-87.36],['Dothan','AL',31.22,-85.39]] },
    { id: 'r3', t: 1, use: 'carport, a slatted side', w: 12, l: 24, h: 8.5, roof: 'mono', p: 6, cov: 'seam', fnd: 'screws', fac: 'wood1', fill: 'slats', gap: 1.5, walls: 'l', col: 'stain',
      places: [['Henderson','NV',36.04,-114.98],['Surprise','AZ',33.63,-112.37],['Victorville','CA',34.54,-117.29],['Lancaster','CA',34.69,-118.15]] },
    { id: 'r4', t: 1, use: 'RV cover', w: 14, l: 38, h: 11, roof: 'gable', p: 15, cov: 'trapez', fnd: 'pads', fac: 'wood1', fill: 'boards', gap: 0, walls: '', col: 'corten',
      places: [['Kingman','AZ',35.19,-114.05],['Brownsville','TX',25.90,-97.50],['Pahrump','NV',36.21,-115.98],['Quartzsite','AZ',33.66,-114.23]] },
    { id: 'r5', t: 1, use: 'boat cover', w: 12, l: 30, h: 10, roof: 'mono', p: 5, cov: 'trapez', fnd: 'screws', fac: 'wood2', fill: 'boards', gap: 0, walls: '', col: 'larch',
      places: [['Hot Springs','AR',34.50,-93.06],['Osage Beach','MO',38.15,-92.62],['Grove','OK',36.59,-94.77],['Branson','MO',36.64,-93.22]] },
    { id: 'r6', t: 1, use: 'patio pavilion', w: 12, l: 16, h: 9, roof: 'gable', p: 30, cov: 'shingle', fnd: 'pads', fac: 'wood1', fill: 'boards', gap: 0, walls: '', col: 'shou',
      places: [['Cary','NC',35.79,-78.78],['Greenville','NC',35.61,-77.37],['Florence','SC',34.20,-79.76],['Rock Hill','SC',34.92,-81.03]] },
    { id: 'r7', t: 1, use: 'picnic pavilion', w: 20, l: 30, h: 10, roof: 'gable', p: 22, cov: 'seam', fnd: 'pads', fac: 'wood2', fill: 'boards', gap: 0, walls: '', col: 'stain',
      places: [['Ames','IA',42.03,-93.62],['Muncie','IN',40.19,-85.39],['Stillwater','OK',36.12,-97.06],['Manhattan','KS',39.18,-96.57]] },
    { id: 'r8', t: 1, use: 'hot tub cover', w: 10, l: 12, h: 8.5, roof: 'gable', p: 25, cov: 'shingle', fnd: 'screws', fac: 'wood1', fill: 'slats', gap: 1.5, walls: 'rl', col: 'corten',
      places: [['Breckenridge','CO',39.48,-106.04],['Park City','UT',40.65,-111.50],['Steamboat Springs','CO',40.48,-106.83],['Big Sky','MT',45.28,-111.37]] },
    { id: 'r9', t: 1, use: 'firewood shelter', w: 8, l: 12, h: 7, roof: 'mono', p: 8, cov: 'trapez', fnd: 'screws', fac: 'wood2', fill: 'boards', gap: 0, walls: 'rlR', col: 'larch',
      places: [['Montpelier','VT',44.26,-72.58],['Augusta','ME',44.31,-69.78],['Houghton','MI',47.12,-88.57],['Littleton','NH',44.31,-71.77]] },
    { id: 'r10', t: 1, use: 'tractor shelter', w: 16, l: 24, h: 10, roof: 'mono', p: 6, cov: 'trapez', fnd: 'pads', fac: 'wood1', fill: 'boards', gap: 0, walls: 'rlR', col: 'shou',
      places: [['Lancaster','PA',40.04,-76.31],['Dodge City','KS',37.75,-100.02],['Lewistown','MT',47.06,-109.43],['Mitchell','SD',43.71,-98.03]] },
    { id: 'r11', t: 1, use: 'loafing shed', w: 12, l: 24, h: 9, roof: 'mono', p: 7, cov: 'seam', fnd: 'pads', fac: 'wood2', fill: 'boards', gap: 0, walls: 'rlR', col: 'stain',
      places: [['Ocala','FL',29.19,-82.14],['Aiken','SC',33.56,-81.72],['Paris','KY',38.21,-84.25],['Middleburg','VA',38.97,-77.74]] },
    { id: 'r12', t: 1, use: 'livestock shelter', w: 20, l: 36, h: 11, roof: 'gable', p: 15, cov: 'trapez', fnd: 'screws', fac: 'wood1', fill: 'boards', gap: 0, walls: 'r', col: 'corten',
      places: [['Amarillo','TX',35.22,-101.83],['Garden City','KS',37.97,-100.87],['Scottsbluff','NE',41.87,-103.67],['Miles City','MT',46.41,-105.84]] },
    { id: 'r13', t: 1, use: 'outdoor kitchen cover', w: 12, l: 14, h: 9, roof: 'gable', p: 28, cov: 'shingle', fnd: 'pads', fac: 'wood2', fill: 'slats', gap: 1, walls: 'r', col: 'larch',
      places: [['Pensacola','FL',30.42,-87.22],['Biloxi','MS',30.40,-88.89],['Corpus Christi','TX',27.80,-97.40],['Panama City','FL',30.16,-85.66]] },
    { id: 'r14', t: 1, use: 'garden tool shelter', w: 8, l: 10, h: 7.5, roof: 'mono', p: 10, cov: 'shingle', fnd: 'screws', fac: 'wood2', fill: 'boards', gap: 0, walls: 'rR', col: 'shou',
      places: [['Corvallis','OR',44.56,-123.26],['Walla Walla','WA',46.06,-118.34],['Missoula','MT',46.87,-113.99],['Hood River','OR',45.71,-121.52]] },
    { id: 'r15', t: 1, use: 'bike and kayak shelter', w: 6, l: 12, h: 7, roof: 'mono', p: 5, cov: 'seam', fnd: 'screws', fac: 'wood1', fill: 'slats', gap: 1, walls: 'r', col: 'stain',
      places: [['Duluth','MN',46.79,-92.10],['Ithaca','NY',42.44,-76.50],['Bemidji','MN',47.47,-94.88],['Petoskey','MI',45.37,-84.96]] },
    { id: 'r16', t: 1, use: 'open garden pavilion', w: 16, l: 16, h: 9, roof: 'gable', p: 35, cov: 'shingle', fnd: 'pads', fac: 'wood1', fill: 'boards', gap: 0, walls: '', col: 'corten',
      places: [['Beaufort','SC',32.43,-80.67],['Charlottesville','VA',38.03,-78.48],['Brunswick','GA',31.15,-81.49],['Fernandina Beach','FL',30.67,-81.46]] },
    { id: 'r17', t: 1, use: 'market stall shelter', w: 10, l: 20, h: 8, roof: 'gable', p: 20, cov: 'seam', fnd: 'screws', fac: 'wood2', fill: 'boards', gap: 0, walls: '', col: 'larch',
      places: [['Taos','NM',36.41,-105.57],['Durango','CO',37.28,-107.88],['Moab','UT',38.57,-109.55],['Silver City','NM',32.77,-108.28]] },
    { id: 'r18', t: 1, use: 'bus stop shelter', w: 8, l: 20, h: 8, roof: 'mono', p: 4, cov: 'seam', fnd: 'pads', fac: 'wood2', fill: 'slats', gap: 2, walls: 'rlR', col: 'shou',
      places: [['La Crosse','WI',43.80,-91.24],['Mankato','MN',44.16,-94.00],['Dubuque','IA',42.50,-90.66],['Wausau','WI',44.96,-89.63]] },
    { id: 'r19', t: 1, use: 'workshop shelter', w: 16, l: 32, h: 10, roof: 'gable', p: 18, cov: 'trapez', fnd: 'screws', fac: 'wood1', fill: 'boards', gap: 0, walls: 'rl', col: 'stain',
      places: [['Roseburg','OR',43.22,-123.34],['Coos Bay','OR',43.37,-124.22],['Redding','CA',40.59,-122.39],['Ukiah','CA',39.15,-123.21]] },
    { id: 'r20', t: 1, use: 'hay shelter', w: 24, l: 36, h: 11, roof: 'gable', p: 12, cov: 'trapez', fnd: 'screws', fac: 'wood2', fill: 'boards', gap: 0, walls: '', col: 'corten',
      places: [['Lewiston','ID',46.42,-117.02],['Pierre','SD',44.37,-100.35],['Norfolk','NE',42.03,-97.42],['Twin Falls','ID',42.56,-114.46]] },
    { id: 'r21', t: 1, use: 'carport with a storage wall', w: 22, l: 24, h: 9, roof: 'gable', p: 20, cov: 'shingle', fnd: 'pads', fac: 'wood2', fill: 'boards', gap: 0, walls: 'r', col: 'larch',
      places: [['Tulsa','OK',36.15,-95.99],['Wichita','KS',37.69,-97.34],['Fayetteville','AR',36.06,-94.16],['Springfield','IL',39.78,-89.65]] },
    { id: 'r22', t: 1, use: 'car and boat shelter', w: 24, l: 30, h: 10, roof: 'mono', p: 6, cov: 'seam', fnd: 'screws', fac: 'wood1', fill: 'slats', gap: 1.5, walls: 'l', col: 'shou',
      places: [['Lake Charles','LA',30.23,-93.22],['Gulf Shores','AL',30.25,-87.70],['Port Arthur','TX',29.90,-93.93],['Houma','LA',29.60,-90.72]] },
    { id: 'r23', t: 1, use: 'event pavilion', w: 30, l: 36, h: 11, roof: 'gable', p: 18, cov: 'seam', fnd: 'pads', fac: 'wood1', fill: 'boards', gap: 0, walls: '', col: 'stain',
      places: [['Sioux City','IA',42.50,-96.40],['Joplin','MO',37.08,-94.51],['Fort Smith','AR',35.39,-94.40],['Kearney','NE',40.70,-99.08]] },
    { id: 'r24', t: 1, use: 'deck cover', w: 12, l: 12, h: 8, roof: 'mono', p: 3, cov: 'seam', fnd: 'screws', fac: 'wood2', fill: 'slats', gap: 2, walls: 'R', col: 'corten',
      places: [['San Luis Obispo','CA',35.28,-120.66],['Chico','CA',39.73,-121.84],['Grand Junction','CO',39.06,-108.55],['Ashland','OR',42.19,-122.71]] }
  ];
  var REF_POOL_US = [
    ['Concord','NH',43.21,-71.54],['Portsmouth','NH',43.07,-70.76],['Bangor','ME',44.80,-68.77],['Hartford','CT',41.76,-72.69],
    ['Springfield','MA',42.10,-72.59],['Worcester','MA',42.26,-71.80],['Albany','NY',42.65,-73.76],['Syracuse','NY',43.05,-76.15],
    ['Rochester','NY',43.16,-77.61],['Buffalo','NY',42.89,-78.88],['Binghamton','NY',42.10,-75.91],['Scranton','PA',41.41,-75.66],
    ['State College','PA',40.79,-77.86],['Erie','PA',42.13,-80.09],['Harrisburg','PA',40.27,-76.88],['Allentown','PA',40.60,-75.49],
    ['Wilmington','DE',39.74,-75.55],['Frederick','MD',39.41,-77.41],['Morgantown','WV',39.63,-79.96],['Charleston','WV',38.35,-81.63],
    ['Greenville','NC',35.61,-77.37],['Fayetteville','NC',35.05,-78.88],['Florence','SC',34.20,-79.76],['Macon','GA',32.84,-83.63],
    ['Valdosta','GA',30.83,-83.28],['Dothan','AL',31.22,-85.39],['Montgomery','AL',32.37,-86.30],['Tuscaloosa','AL',33.21,-87.57],
    ['Pensacola','FL',30.42,-87.22],['Panama City','FL',30.16,-85.66],['Lakeland','FL',28.04,-81.95],['Daytona Beach','FL',29.21,-81.02],
    ['Jackson','MS',32.30,-90.18],['Hattiesburg','MS',31.33,-89.29],['Johnson City','TN',36.31,-82.35],['Jackson','TN',35.61,-88.81],
    ['Clarksville','TN',36.53,-87.36],['Bowling Green','KY',36.99,-86.44],['Paducah','KY',37.08,-88.60],['Danville','VA',36.59,-79.40],
    ['Harrisonburg','VA',38.45,-78.87],['Toledo','OH',41.65,-83.54],['Dayton','OH',39.76,-84.19],['Akron','OH',41.08,-81.52],
    ['Lansing','MI',42.73,-84.56],['Kalamazoo','MI',42.29,-85.59],['Saginaw','MI',43.42,-83.95],['Marquette','MI',46.54,-87.40],
    ['South Bend','IN',41.68,-86.25],['Evansville','IN',37.97,-87.57],['Terre Haute','IN',39.47,-87.41],['Peoria','IL',40.69,-89.59],
    ['Springfield','IL',39.80,-89.64],['Champaign','IL',40.12,-88.24],['Rockford','IL',42.27,-89.09],['Green Bay','WI',44.51,-88.02],
    ['La Crosse','WI',43.80,-91.24],['Wausau','WI',44.96,-89.63],['St. Cloud','MN',45.56,-94.16],['Mankato','MN',44.16,-94.00],
    ['Cedar Rapids','IA',41.98,-91.67],['Sioux City','IA',42.50,-96.40],['Dubuque','IA',42.50,-90.66],['Springfield','MO',37.21,-93.29],
    ['Joplin','MO',37.08,-94.51],['Cape Girardeau','MO',37.31,-89.52],['Topeka','KS',39.05,-95.68],['Salina','KS',38.84,-97.61],
    ['Lincoln','NE',40.81,-96.70],['Grand Island','NE',40.92,-98.34],['Sioux Falls','SD',43.55,-96.73],['Rapid City','SD',44.08,-103.23],
    ['Fargo','ND',46.88,-96.79],['Bismarck','ND',46.81,-100.78],['Billings','MT',45.78,-108.50],['Great Falls','MT',47.50,-111.30],
    ['Casper','WY',42.87,-106.31],['Cheyenne','WY',41.14,-104.82],['Idaho Falls','ID',43.49,-112.03],['Twin Falls','ID',42.56,-114.46],
    ['Logan','UT',41.74,-111.83],['Elko','NV',40.83,-115.76],['Medford','OR',42.33,-122.87],['Klamath Falls','OR',42.22,-121.78],
    ['Yakima','WA',46.60,-120.51],['Wenatchee','WA',47.42,-120.31],['Kennewick','WA',46.21,-119.14],['Chico','CA',39.73,-121.84],
    ['Eureka','CA',40.80,-124.16],['Modesto','CA',37.64,-120.99],['Visalia','CA',36.33,-119.29],['Show Low','AZ',34.25,-110.03],
    ['Sierra Vista','AZ',31.55,-110.30],['Farmington','NM',36.73,-108.22],['Roswell','NM',33.39,-104.52],['Durango','CO',37.28,-107.88],
    ['Steamboat Springs','CO',40.48,-106.83],['San Angelo','TX',31.46,-100.44],['Laredo','TX',27.51,-99.51],['Brownsville','TX',25.90,-97.50],
    ['Victoria','TX',28.81,-97.00],['Texarkana','TX',33.43,-94.05],['Lawton','OK',34.61,-98.39],['Stillwater','OK',36.12,-97.06],
    ['Fort Smith','AR',35.39,-94.40],['Jonesboro','AR',35.84,-90.70],['Monroe','LA',32.51,-92.12],['Lake Charles','LA',30.23,-93.22]
  ];
  var _REF_HOLD = 0;
  function holdWrites(on) { _REF_HOLD = Math.max(0, _REF_HOLD + (on ? 1 : -1)); }
  function refSet() {
    return REF_SET.map(function (r) {
      var o = JSON.parse(JSON.stringify(r));
      o.pool = REF_POOL_US.slice();
      return o;
    });
  }
  /* a design on the stage: the shelter's defaults, then its keys -- one payload, as a ?cfg= opens */
  function refApply(r) {
    if (!r) return false;
    var FTm = 0.3048, IN = 0.0254, wl = String(r.walls || '');
    var p = JSON.parse(JSON.stringify(_configPayload()));
    p.modules = [{ type: (STATE.modules[0] && STATE.modules[0].type) || '20', x: 0, z: 0, rot: 0, lvl: 0, color: 1 }];
    p.selected = 0;
    p.openings = []; p.partitions = []; p.doors = []; p.roomNames = []; p.pergolas = []; p.pergolaOn = false;
    p.footprint = { w: r.w * FTm, l: r.l * FTm, h: r.h * FTm };
    p.roofType = r.roof; p.roofPitch = r.p; p.covering = r.cov; p.foundation = r.fnd;
    p.facadePattern = r.fac; p.facadeColor = r.col; p.fillType = r.fill; p.slatGap = (r.gap || 0) * IN;
    p.wallFill = { front: wl.indexOf('f') >= 0, rear: wl.indexOf('r') >= 0, sideL: wl.indexOf('l') >= 0, sideR: wl.indexOf('R') >= 0 };
    p.bodyX = null; p.bodyZ = null; p.isMetric = STATE.isMetric;
    _applyConfig(p);
    STATE.footprint.w = clamp(_snapDim(STATE.footprint.w), FOOTPRINT.minW, FOOTPRINT.maxW);
    STATE.footprint.l = clamp(_snapDim(STATE.footprint.l), FOOTPRINT.minL, FOOTPRINT.maxL);
    STATE.footprint.h = _snapH(STATE.footprint.h);
    _clampPitch();
    rebuild();
    try { _syncPitchSlider(); } catch (eS) {}
    try { _fitCamera(); } catch (eF) {}
    _log('ref      ' + r.id + ' (' + r.use + '): ' + r.w + ' x ' + r.l + ' ft, posts ' + r.h + ' ft, ' + STATE.roofType + ' ' + STATE.roofPitch + ' deg ' + _covDef().key + ', ' + STATE.foundation + ', ' + STATE.facadePattern + ' ' + STATE.fillType +
         (STATE.fillType === 'slats' ? (' gap ' + Math.round(STATE.slatGap * 1000) + ' mm') : '') + ', filled ' + (['front', 'rear', 'sideL', 'sideR'].filter(function (k) { return STATE.wallFill[k]; }).join(' ') || 'none') + ', finish ' + STATE.facadeColor);
    return true;
  }
  function designPayload() { return JSON.parse(JSON.stringify(_configPayload())); }
  function cfgSave(payload) { return _cfgSaveDirect(payload || _configPayload()).then(function (id) { return { id: id }; }); }
  function stateSnap() { return JSON.parse(JSON.stringify(_configPayload())); }
  function stateBack(o) {
    if (!o) return;
    _applyConfig(o);
    _clampPitch();
    rebuild();
    try { _syncPitchSlider(); } catch (eS) {}
    try { _fitCamera(); } catch (eF) {}
  }
  function variantOfType() {
    var V = (global.SAUNA_BOOT && global.SAUNA_BOOT.variants) || [];
    return V.length ? { id: String(V[0].id), title: String(V[0].title || '') } : null;
  }

  global.SaunaLib = {
    __version: VERSION,
    /* WHAT THIS BUILDING IS. Read by the AI drawer, which asks every lib
       for facts() and otherwise reads STATE and guesses. */
    facts: shelterFacts,
    STATE: STATE,
    DEFAULTS: DEFAULTS,
    VARIANT_KEYS: VARIANT_KEYS,
    PRESET_COUNT: PRESET_COUNT,
    TYPES: { sauna: null },
    COLORS: COLORS,

    boot: boot,
    reset: reset,
    undo: undo,
    refreshUI: refreshUI,
    rebuild: rebuild,
    toggleUnits: toggleUnits,
    savePref: savePref,
    fmtLen: fmtLen,

    selectVariant: selectVariant,
    nextVariant: nextVariant,
    previousVariant: previousVariant,
    selectCount: selectCount,
    nextCount: nextCount,
    totalFloorArea: totalFloorArea,
    cycleColor: cycleColor,
    selectRoofType: selectRoofType,
    nextRoofType: nextRoofType,
    wizard: function () { return _wiz; },
    snapshot: _snapshot,
    noteError: _noteError,
    setMode: setMode,
    nextMode: nextMode,
    toggleBoQDebug: function () {
      try {
        var on = global.localStorage.getItem('cf_boqdbg') === '1';
        global.localStorage.setItem('cf_boqdbg', on ? '0' : '1');
      } catch (e) {}
      global.SaunaBoQ.publish();
    },
    _boqCompute: function () { return global.SaunaBoQ.compute(); },
    selectFoundation: selectFoundation,
    nextFoundation: nextFoundation,
    selectEnvelope: selectEnvelope,
    nextEnvelope: nextEnvelope,
    nextFacade: nextFacade,
    nextFacadeColor: nextFacadeColor,
    nextPergolaVariant: nextPergolaVariant,
    nextTerraceSurface: nextTerraceSurface,
    toggleDetail: toggleDetail,
    exportPdf: _containerExportPdf,
    _pdfBuildBoq: _containerBuildBoq,
    _pdfDrawPlan: _pdfDrawPlan,
    _wallSegsTest: _modWallSegs,
    /* Hooks for saunatest.js. These are the values a screenshot cannot check:
       derived geometry, the roof maths, and the drag contract. */
    _fpPolygonTest: function () { return _fpPolygon(); },
    _typeTest: function () { return _saunaType(); },
    _panelHTest: function (t) { return _panelH(t || _saunaType()); },
    _constsTest: function () {
      return { PANEL_BASE: PANEL_BASE, FLOOR_TOP: FLOOR_TOP, BEARER: BEARER,
               POST: POST, WALL_THK: WALL_THK, FOOTPRINT: FOOTPRINT, OVER_LIM: OVER_LIM,
               PITCH_MIN: PITCH_MIN, APEX_MAX: APEX_MAX, MAX_MODULES: MAX_MODULES };
    },
    _pitchTest: function () { return { min: _pitchMin(), max: _pitchMax(), base: _pitchBase() }; },
    _roofRectTest: function () { return _RoofRect(STATE.footprint.w, STATE.footprint.l, STATE.roofOver); },
    _applyTest: function (key, r) { return global.SaunaFootprint._apply(key, r); },
    _rectsTest: function () { return global.SaunaFootprint._rects(); },
    _configPayloadTest: function () { return _configPayload(); },
    shareUrl: function () { return _cfgId ? _cfgUrl(_cfgId) : (location.origin + location.pathname + location.search); },
    saveConfig: function () { try { return _cfgSaveDirect(_configPayload()).then(function (id) { _cfgId = id; _cfgPutUrl(id); return _cfgUrl(id); }); } catch (e) { return Promise.reject(e); } },
    loadConfig: function (id) { return _cfgLoadDirect(id).then(function (p) { if (p) { _applyConfig(p); _clampPitch(); rebuild(); } return !!p; }); },
    _loadPrefTest: loadPref, _lastCfgGetTest: _lastCfgGet, _recenterTest: _recenterLayout,
    _initMapTest: _initMap,
    _arExportTest: _arExportScene,
    _wallTagTest: function (mod, dx, dz) { return _wallTagFor(mod, dx, dz); },
    selectPreset: selectPreset,
    nextPreset: nextPreset,
    previousPreset: previousPreset,

    openAr: _openArModal,
    openBudgetDrawer: openBudgetDrawer,
    share: stub('share (handled by snippet glue)'),
    saveConfig: savePref,
    toggleBody: toggleBody,
    addModule: function () { selectCount(STATE.modules.length + 1); },
    removeModule: function () { selectCount(STATE.modules.length - 1); },
    selectModule: _selectModule,
    clearSelection: function () { _selectModule(0); },
    /* shelter-v120 the snippet's REF */
    refSet: refSet, refApply: refApply, designPayload: designPayload, cfgSave: cfgSave,
    holdWrites: holdWrites, stateSnap: stateSnap, stateBack: stateBack, variantOfType: variantOfType
  };

})(window);